// Editor de planta sobre el plano original.
//
// La idea que lo sostiene: el PDF del modelo va DE FONDO, dentro del mismo
// sistema de coordenadas que las paredes editables. Así el diseño nunca se
// pierde —abajo quedan el mobiliario, las cadenas de cota, el barrido de las
// puertas y la escalera, pixel a pixel— y la extracción deja de tener que ser
// perfecta: lo que el importador no reconozca se corrige a mano sobre el dibujo
// de verdad.
//
// Todo el lienzo trabaja en METROS. El `<image>` del PDF se coloca en metros
// también (su página mide `ancho_pt / ptPorMetro`), así que no hay dos
// transformaciones que mantener sincronizadas: hay una sola, la del viewBox.
//
// Las áreas NO se recalculan mientras se arrastra. Si el área manda en cada
// cuadro, el editor empieza a impedir movimientos para defender un número, y lo
// que no se puede perder es la libertad de diseñar.

import { Children, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Armchair, ArrowClockwise, ArrowLineDown, ArrowLineRight, ArrowLineUp,
  ArrowsOutLineHorizontal, ArrowUUpLeft, ArrowUUpRight,
  Broom, CaretDown, Copy, Cube, Divide, Door, DotsSix, DotsThreeVertical, Export, FlipHorizontal,
  FloppyDisk, GridFour, Info, Lightning, SpinnerGap,
  Hammer, LineSegment, LineSegments, Lock,
  LockOpen, MagicWand, MinusCircle, Polygon, Rectangle, RectangleDashed, Ruler, Scissors,
  SelectionBackground, Swap,
  Tag, Trash, Unite, UniteSquare, VectorTwo, X,
} from '@phosphor-icons/react';
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  TransformWrapper, TransformComponent, type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';
import {
  medirPlanta, anilloDeCeldas, areaLibre, areaPoligono, aristas, caja, cajaDe,
  cajaDeVertices, cuadradosDeEsquina, enCeldas, rectangulosDePoligono,
  repartirReposo, tramos, zonaEncerrada,
  type Medicion, type Rect,
  medirSegunVista, plantaSegunVista,
} from './medirPlanta';
import {
  CLAVE_A_MEDIDA, GRUPO_PROPIO, MiniaturaMueble, SimboloMueble, TINTA_ELECTRICO, TINTA_MUEBLE,
  aprenderHuella, arrimar, catalogoVivo, estaApagada, fichaMueble, gruposVivos, guardarPropio,
  huellaDe, iEsquina, muebleFlexible, nodosMueble, nombreMueble, topeRadio,
  type ModeloMueble,
} from './muebles';
import { api } from '@/api/client';
import { MisObjetos, TAB_MIS_OBJETOS } from './MisObjetos';
import { publicarObjeto, traerObjetos } from './objetos-servidor';
import { rasterizarLienzo, type Mancha } from './rasterizar';
import { bajarLamina, ESCALA_POR_DEFECTO } from './exportar-lamina';
import { ESTILOS_RENDER, ESTILO_POR_DEFECTO } from './estilos-render';
import {
  alturaDe, anclaCerca, asientoDeAncla, esElectrica, reasentarAnclados, resumenElectrico,
} from './electrico';
import {
  CLASES, ESTADOS, ROTULO_CLASE, ROTULO_ESTADO, ROTULO_VISTA, VISTAS_OBRA, VISTAS_SIN_TINTA,
  anilloOrtogonal, mismaGeometria, seVe,
} from './tipos';
import type {
  AnclaPared, Clase, Cota, Divisor, Estado, Libre, ModeloPlanta, Mueble, Orientacion, Pared,
  Planta, TipoVano, Vano, VistaObra,
} from './tipos';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

/** Paso de la rejilla al mover, en metros. */
const PASO = 0.05;
/** Una pared nunca queda más corta que esto. */
const MIN_LARGO = 0.3;
/**
 * Un mueble nunca queda más chico que esto, en metros.
 *
 * Más chico que el de una pared a propósito: una silla de 45 cm es un mueble
 * legítimo y un lavatorio de 45 × 40 también, así que el piso de 30 cm de la
 * pared los dejaría afuera al girarlos o al escribirles la medida. Y sigue
 * habiendo un piso porque un rectángulo de 2 cm no se puede volver a agarrar
 * con el dedo: sería un mueble perdido en el plano.
 */
const MIN_MUEBLE = 0.2;
/**
 * Lo menos que puede medir una cota puesta a mano, en metros.
 *
 * Más permisivo que el de una pared porque hay cosas legítimas de medir que son
 * cortas —un vano de 70 cm, un escalón, el espesor de un muro— y sigue habiendo
 * un piso porque una cota de 2 cm no se puede volver a tocar con el dedo para
 * borrarla: quedaría un rótulo pegado al plano para siempre.
 */
const MIN_COTA = 0.1;
/**
 * Área mínima, en m², para que lo que levanta la varita cuente como un espacio.
 *
 * Se le exige el ÁREA y no el lado, que es lo que se le exige a un rectángulo
 * dibujado a mano: un espacio real puede tener un brazo de 40 cm —el paso al
 * lado de la escalera— y seguir siendo el cuarto. Lo que no puede ser un cuarto
 * es una ranura de 0,04 m² entre dos pedazos de pared, que es lo que este
 * número descarta (el mismo criterio y el mismo valor que `MINIMA` en la
 * medición).
 */
const MINIMA_LIBRE = 0.2;
/** Margen de aire alrededor de la casa, en metros. */
const AIRE = 1.0;
/**
 * Alcance del imán, en PÍXELES de pantalla.
 *
 * En píxeles y no en metros a propósito: un umbral fijo en metros es grosero
 * con el plano acercado —se come movimientos de 10 cm que uno quiere hacer— e
 * invisible con el plano entero en pantalla, donde 12 cm son dos píxeles y el
 * imán no agarra nunca. Medido contra el zoom vivo, el tirón se siente igual
 * siempre.
 */
const IMAN_PX = 14;
/**
 * El ancho del dedo, en píxeles de pantalla. Es el alcance con el que se busca
 * la pared bajo un toque.
 *
 * En píxeles por lo mismo que el imán: la yema no se achica porque el plano
 * esté alejado. Con el alcance clavado en metros, a plano entero en un celular
 * eran 21 px y el editor contestaba «tocá encima de la pared» con el dedo
 * encima de la pared — el caso que reportó Daniel con la foto.
 */
const DEDO_PX = 44;
/**
 * Cuánto hay que correr el puntero, en píxeles, para que el gesto cuente como
 * ARRASTRE y no como toque.
 *
 * Es lo que separa los dos modos de trazar sin preguntarle a nadie qué aparato
 * tiene: con mouse uno aprieta y arrastra, y la pared nace al soltar; con el
 * dedo uno toca, ve el punto, y toca otra vez para cerrar. El mismo código
 * sirve para los dos porque el gesto se distingue solo.
 */
const ARRASTRE_PX = 12;
/**
 * Cuánto se levanta el punto de trabajo POR ENCIMA del dedo, en píxeles de
 * pantalla.
 *
 * Daniel lo vio de casualidad y lo pidió de verdad (4-oct-2026): «el punto de
 * donde se va a crear me sale más arriba del dedo […] con esto es genial porque
 * puedo ver a dónde lo voy a ubicar. El problema que vi es cuando hago zoom […]
 * ahí sí ya se pierde». Lo que había visto era un accidente de perspectiva —el
 * código ponía la mira EXACTAMENTE bajo el dedo— y por eso con zoom se
 * «perdía»: nunca existió.
 *
 * Acá existe, y se mide en PÍXELES DE PANTALLA a propósito: se resta al
 * `clientY` antes de pasar por la matriz del SVG, así el alzado se ve igual a
 * cualquier zoom. Medirlo en metros sería el mismo defecto con otro nombre —se
 * agrandaría al acercar y desaparecería al alejar.
 *
 * Sólo aplica al DEDO: el mouse no tapa nada y correrle el cursor al usuario
 * sería mentirle.
 */
const ALZADO_PX = 44;
/**
 * De qué TAMAÑO se dibuja la mira, también en píxeles de pantalla.
 *
 * Es la otra mitad del pedido del 4-oct, y la que faltaba. El alzado quedó bien
 * —sube sus 44 px a cualquier zoom, medido—, pero el dibujo seguía acotado en
 * METROS adentro del grupo que el zoom agranda, así que crecía con él: medido en
 * un celular, el disco pasa de 36 px de diámetro con el plano completo a 267 px
 * con un pinch de ×7,3, sobre una pantalla de 390 px de ancho. No desaparecía
 * —tapaba—, y por eso Daniel lo reportó como que «se pierde por completo. Nunca
 * se ve el cursor, nada más se ve como la línea»: lo único que a ese tamaño
 * sigue pareciendo algo son las guías, que engordaron de 3 px a 24.
 *
 * Una mira es un instrumento de puntería, no una pieza del plano: tiene que
 * medir lo mismo en pantalla esté donde esté el zoom, igual que el cursor en la
 * computadora. Lo que SÍ es del plano —dónde cae, qué largo tiene la pared que
 * nace, dónde cruza la raya del corte— sigue en metros.
 */
const MIRA_PX = {
  aro: 14,        // el anillo exterior: el tamaño que uno lee como «el punto»
  macizo: 7,      // el relleno que avisa que el imán agarró un nodo
  cruz: 9,        // medio brazo de la crucecita del centro
  borde: 3,       // el grosor del anillo
  linea: 2,       // las líneas finas: la cruz y las guías
  raya: 18,       // el punteado de las guías: raya …
  hueco: 11,      // … y hueco
  corte: 11,      // el botón de la marca de dónde se parte una pared
};

/**
 * Cuánto se ve el plano POR DEBAJO de los dos rellenos blancos de la puntería:
 * el halo de la mira y el disco de la lupa.
 *
 * Los dos nacieron macizos, y macizo un relleno blanco no es un fondo: es un
 * tapón, puesto encima de lo único que el gesto está tratando de mirar. Daniel
 * lo mandó velar el 22-oct-2026 —«el fondo blanco de ese círculo tal vez lo
 * podemos hacer traslúcido para que se vea un poco lo que está detrás; al final
 * es importante tener ese contexto, para eso es el valor de este zoom»—.
 *
 * 0,45 no es un número redondo cualquiera: por debajo de ~0,35 el aro deja de
 * despegarse de un muro negro, que es la única razón por la que el halo existe.
 */
const VELO_MIRA = 0.45;

/**
 * El estilo que esconde un texto DENTRO de la lupa y lo deja intacto en el plano.
 *
 * La lupa es un `<use href="#escena-planta">`, así que arrastra TODO lo que viva
 * en ese grupo. Sacada la mira de ahí, el que quedó llenando el disco fue el
 * TEXTO: a ×3, un rótulo de área o un número de cota tapa la lupa entera
 * (fotografiado el 22-oct-2026 — la lupa se leía «Cocina + Recinto · 18,31 m²» y
 * nada más). Daniel: «escondé también los rótulos y los números dentro de la
 * lupa». Es la misma doctrina que Láminas, donde la lupa tampoco replica las
 * marcas guardadas: es un instrumento de PUNTERÍA, no una segunda vista del
 * plano. Y el número vive a ×1 a dos centímetros del dedo: no hace falta a ×3.
 *
 * Por qué así y no con una regla CSS: el contenido clonado por un `<use>` vive
 * en un shadow tree, y los selectores del documento NO entran ahí. Lo que SÍ
 * cruza son las propiedades heredadas y las variables CSS, que bajan desde el
 * propio `<use>`. Entonces el texto declara su visibilidad como una variable con
 * respaldo —en el plano nadie la define y gana `visible`— y la lupa la pisa
 * poniendo `--lupa-texto: hidden` en su `<use>`. Un solo interruptor, del lado
 * de la lupa, sin tocar el plano ni duplicar el render.
 */
const SOLO_PLANO = {
  visibility: 'var(--lupa-texto, visible)',
} as unknown as CSSProperties;

/**
 * Cuánto puede tardar y cuánto correrse el segundo toque para que los dos
 * cuenten como DOBLE TOQUE, que es el gesto que entra a selección múltiple.
 *
 * Antes se entraba dejando el dedo estripado, y Daniel lo mandó cambiar
 * (6-oct-2026): «que el gesto de selección múltiple sea con double tap no con
 * el long press porque me está pasando por ejemplo que si quiero mover una
 * pared tengo que seleccionarla y moverla muy rápido para que no interprete que
 * estoy en selección múltiple». Es un choque de diseño, no un ajuste de
 * milisegundos: el hold y el arrastre EMPIEZAN igual —dedo apoyado y quieto—,
 * así que uno siempre le va a robar el gesto al otro. El doble toque no compite
 * con ninguno de los dos.
 */
const DOBLE_MS = 320;
const DOBLE_PX = 24;
/**
 * Cuánto hay que sostener un botón de capa para FIJARLA sin abrir su paleta.
 *
 * El hold volvió el 6-oct-2026 y no contradice el párrafo de arriba: lo que
 * Daniel sacó fue el hold SOBRE EL LIENZO, donde competía con el arrastre
 * porque los dos empiezan igual. Un botón no se arrastra, así que acá el gesto
 * no le roba nada a nadie. Pedido textual: «si por alguna razón quiero ponerme
 * en una capa sin agregar algo, que sea a través de dejar hold tap en uno de
 * esos botones; con eso quedaría con la prioridad de la capa».
 *
 * Pasa de `DOBLE_MS` a propósito: el toque simple de un botón se resuelve a los
 * 320 ms y recién después se cuenta como sostenido, así que los tres gestos del
 * mismo botón —uno, dos y sostenido— no se pisan.
 */
const HOLD_MS = 500;
/**
 * Lo que tiene gesto propio arriba del lienzo.
 *
 * Es la lista de la que cuelga «tocar el vacío suelta la capa»: si el dedo cayó
 * en algo de acá, ese algo manda. Se escribe en un solo lugar porque ya iba por
 * la tercera copia suelta —`tomarRemoto` tenía la suya—.
 */
const TOMABLE = '[data-pared], [data-punta], [data-vano], [data-libre],'
  + ' [data-divisor], [data-mueble], [data-lado], [data-esquina],'
  + ' [data-region], [data-cota], [data-paleta], [data-catalogo]';
/** Cuántos pasos atrás guarda el historial. */
const TOPE_HISTORIAL = 30;
/** Un vano nunca queda más angosto que esto, en metros. */
const MIN_VANO = 0.3;
/**
 * Con qué ancho nace cada carpintería. Son las medidas de catálogo de la
 * empresa, no un promedio: así el caso corriente no se escribe, se acepta.
 */
const ANCHO: Record<Exclude<TipoVano, 'paso'>, number> = { puerta: 0.9, ventana: 1.2 };
/**
 * El color de cada carpintería. Celeste la ventana y café la puerta, que es como
 * se leen de un vistazo y sin leyenda. El `paso` no se pinta: es un hueco, y
 * pintarlo haría que las láminas importadas —donde TODO vano entra como paso—
 * amanecieran con barras de color que nadie puso.
 */
const COLOR_VANO: Record<TipoVano, string | null> = {
  paso: null,
  puerta: '#9a6b43',
  ventana: '#7dd3fc',
};
const NOMBRE_VANO: Record<TipoVano, string> = {
  paso: 'Paso',
  puerta: 'Puerta',
  ventana: 'Ventana',
};

/**
 * La tinta de la SELECCIÓN MÚLTIPLE: lo elegido se pinta de morado en vez del
 * marrón de siempre cuando van dos o más.
 *
 * Lo pidió Daniel el 5-oct-2026: «las paredes en lugar del naranja, que se
 * ponga de otro color, morado o algún color distinto para denotar que está en
 * modo selección múltiple». El modo múltiple cambia lo que hacen los gestos
 * —desaparecen las puntas, la paleta pasa a ser la de grupo— y antes no se veía
 * de ningún lado: la tercera pared se pintaba igual que la primera.
 *
 * El tono va desaturado a propósito. Tiene que ser ajeno a los dos colores que
 * ya hablan acá —el marrón `#8a5a3b` de lo interno y el verde `#4f9d78` de lo
 * externo— sin competirles en saturación, porque esto no es una categoría de la
 * casa: es un estado del editor.
 */
const TINTA_MULTI = '#6f5c9e';

/**
 * El color de cada clase de área. UNA tabla para todo el editor.
 *
 * Hubo tres —una para la pastilla de la barra, una para el relleno del plano y
 * una para el rótulo— y Daniel las cazó el 8-oct-2026: «no hay congruencia
 * entre los colores que yo había definido de área interna, externa, lote y
 * azotea versus los colores de las áreas de las geometrías y de la tabla
 * detallada; todo debería ser los mismos cuatro colores para todo». Tenía
 * razón: un área que suma en la cara naranja se pintaba verde en el dibujo, así
 * que el color —que es el único idioma que dice en qué columna cae cada metro—
 * decía dos cosas distintas en la misma pantalla.
 *
 * Mandan los CUATRO que él definió para la barra, que son los que viene
 * leyendo: azul la interna, naranja la externa, verde el lote —el color que
 * dejó apartado para esto, y la razón por la que no se gastó en ningún estado
 * del editor— y ocre la azotea.
 */
const TINTA_CLASE: Record<Clase, string> = {
  interna: '#2f6bd4',
  externa: '#e2642a',
  lote: '#3f8f5f',
  azotea: '#8a6d1f',
};

/**
 * Mezcla un color hacia el negro. `f` es cuánto negro entra.
 *
 * Existe para una sola cosa: el rótulo. Lo que se lee bien como relleno al 15 %
 * no se lee escrito en letra de 0,28 m sobre líneas de pared, así que el texto
 * va el mismo color de su clase pero oscurecido. Derivarlo en vez de escribir
 * una segunda tabla es justamente lo que evita que las dos se desalineen —el
 * defecto que Daniel encontró—.
 */
function aNegro(hex: string, f: number) {
  const n = parseInt(hex.slice(1), 16);
  const c = (d: number) => Math.round(((n >> d) & 255) * (1 - f));
  return `rgb(${c(16)}, ${c(8)}, ${c(0)})`;
}

/** El color del RÓTULO de cada clase: el suyo, oscurecido hasta que se lea. */
const TINTA_ROTULO: Record<Clase, string> = {
  interna: aNegro(TINTA_CLASE.interna, 0.4),
  externa: aNegro(TINTA_CLASE.externa, 0.4),
  lote: aNegro(TINTA_CLASE.lote, 0.4),
  azotea: aNegro(TINTA_CLASE.azotea, 0.4),
};

/**
 * El color de cada estado de rehabilitación.
 *
 * Los dos que pidió Daniel el 10-oct-2026 —«objetos nuevos se pondrían en azul,
 * objetos a demoler en rojo»— más el existente, que él dejó abierto («pueden
 * quedar igual o se puede asignar un color») y queda IGUAL: en una planta
 * entera de existente, que es toda planta antes de marcar nada, un color propio
 * pintaría los 50 modelos del catálogo de un color que nadie pidió.
 *
 * `null` es exactamente eso: no lo toques, va con la tinta que ya tenía.
 *
 * ⚠️ El azul NO es el de `interna` ni el rojo el de ninguna clase: son ejes
 * distintos y un área interna nueva tiene que poder decir las dos cosas. El
 * estado manda sobre el TRAZO y la clase sigue mandando sobre el RELLENO.
 */
const TINTA_ESTADO: Record<Estado, string | null> = {
  existente: null,
  nuevo: '#1d6fd0',
  demoler: '#c22e2e',
};

/**
 * Con qué color se DIBUJA algo: el par (estado, vista), no el estado solo.
 *
 * Desde el 10-oct-2026 hay dos vistas que enseñan la misma planta que otras dos
 * pero sin teñirla —«todo en negro sin que se vea azul»—, así que el color dejó
 * de ser una propiedad del elemento y pasó a ser una del momento que se está
 * mirando. Ver `VISTAS_SIN_TINTA` en tipos.ts.
 *
 * Es sólo para el dibujo. Las PASTILLAS de las paletas siguen leyendo
 * `TINTA_ESTADO` directo: ahí el color no describe lo que se ve, es la leyenda
 * de lo que uno va a marcar, y apagarla dejaría el selector mudo justo en las
 * vistas donde más falta hace saber qué significa cada botón.
 */
function tintaDe(estado: Estado | undefined, vista: VistaObra): string | null {
  if (VISTAS_SIN_TINTA.includes(vista)) return null;
  return TINTA_ESTADO[estado ?? 'existente'];
}

/** Qué dice la pastilla de cada estado al tocarla. */
const AYUDA_ESTADO: Record<Estado, string> = {
  existente: 'Ya está construido. Es lo que es todo mientras nadie diga lo contrario',
  nuevo: 'Se construye en esta obra: no existe en el estado actual',
  demoler: 'Se tumba. Desaparece del estado final, y si es pared deja de separar',
};

/** Qué dice cada vista al tocarla: qué muestra y para qué sirve. */
const AYUDA_VISTA: Record<VistaObra, string> = {
  inicial: 'La casa como está hoy, dibujada limpia: todo en negro, sin el rojo de lo que se tumba',
  actual: 'Lo que está construido hoy: existente + a demoler, con lo que se tumba en rojo',
  planteado: 'Lo que va a quedar, con lo nuevo en azul. Es contra esto que se mide el área',
  final: 'Lo que va a quedar, dibujado limpio: el plano de la casa terminada, todo en negro',
  nuevo: 'Sólo lo que se construye',
  demoler: 'Sólo lo que se tumba',
  todo: 'Todo junto, los tres estados superpuestos',
};

/** Qué dice la pastilla de cada clase al tocarla. */
const AYUDA_CLASE: Record<Clase, string> = {
  interna: 'Suma al área interna: es la que se compara contra el modelo',
  externa: 'Suma aparte: terraza, pilas, cochera. Se mide y se muestra, fuera del área interna',
  lote: 'El terreno. Suma en su propia columna, fuera del área interna',
  azotea: 'Lo de arriba. Suma en su propia columna, fuera del área interna',
};

/**
 * Marca lo que se agarra para EDITAR —el cuerpo de una pared y los puntos de
 * sus puntas—, para que `react-zoom-pan-pinch` no se lo lleve como pan.
 *
 * Sin esto el editor se siente roto de una forma que engaña: la pared se
 * selecciona, se arrastra, y en pantalla no pasa nada. Lo que pasa de verdad es
 * que el lienzo entero se traslada EXACTAMENTE lo mismo que el dedo (medido:
 * arrastrar 60 px deja el contenido en `matrix(1,0,0,1,60,0)`), así que la
 * pared se mueve respecto al plano pero las dos cosas se mueven juntas respecto
 * al ojo. Peor: `enMetros` lee el punto contra la caja del lienzo, que YA
 * incluye ese traslado, de modo que el dedo parece quedarse quieto y la pared se
 * corre un solo paso y se congela.
 *
 * El reparto queda así: tocar una pared la edita, tocar el vacío panea el plano.
 */
const AGARRE = 'planta-agarre';

const m2 = (v: number) => v.toFixed(2).replace('.', ',');

/**
 * Metros pasados a CENTÍMETROS enteros, para las medidas del catálogo.
 *
 * En centímetros porque así las dictó Daniel —«closets de 60 por 1.50», «módulo
 * de cocina de 60 por 60»— y así se las nombra en obra. «0,60 × 1,50» en una
 * casilla de catálogo de 2 cm de ancho no se lee; «60×150» sí.
 */
const cm = (v: number) => Math.round(v * 100);

/** Redondea al paso de la rejilla y mata la basura de coma flotante. */
const aPaso = (v: number) => Math.round(Math.round(v / PASO) * PASO * 1000) / 1000;

/**
 * El valor que pide la rejilla de 5 cm, SIN tocar lo que todavía no se movió.
 *
 * Daniel, 5-oct-2026: «cuando estoy moviendo una pared […] que lo vaya haciendo
 * en múltiplos de 5 cm». Redondear el DELTA —que es lo que se hacía— no
 * alcanza: una pared que nace en 1,383 m sigue en 1,433 y 1,483, y el número de
 * la cota nunca es redondo. Lo que engancha a la rejilla es redondear el valor
 * ABSOLUTO.
 *
 * El `antes` existe para no corregir de prepo, que era el miedo legítimo del
 * código viejo: sin él, apoyar el dedo sobre una pared de 1,383 ya la manda
 * sola a 1,40 sin que nadie la haya arrastrado. Hasta medio paso no se toca
 * nada; pasado eso cae en la rejilla y de ahí en adelante va de 5 en 5.
 */
const aRejilla = (antes: number, crudo: number) =>
  Math.abs(crudo - antes) < PASO / 2 ? antes : aPaso(crudo);

/** Cuánto puede correrse y durar un toque de varios dedos y seguir siendo tap. */
const TAP_PX = 18;
const TAP_MS = 400;

/** El punto medio de los dedos apoyados, en pantalla. */
function centroDedos(ts: React.TouchList) {
  let x = 0, y = 0;
  for (let i = 0; i < ts.length; i++) { x += ts[i].clientX; y += ts[i].clientY; }
  return { x: x / (ts.length || 1), y: y / (ts.length || 1) };
}

/** Cuánto se separaron los dos primeros dedos. Es lo que delata un pellizco. */
const aperturaDedos = (ts: React.TouchList) => (ts.length < 2 ? 0
  : Math.hypot(ts[0].clientX - ts[1].clientX, ts[0].clientY - ts[1].clientY));

/**
 * El espesor con el que nace una pared nueva: la mediana de las que ya están.
 * Dibujar un tabique de 10 cm fijos en una planta de bloque de 15 lo delata a
 * simple vista; copiar el espesor que domina la lámina lo integra.
 */
function espesorTipico(ws: Pared[]): number {
  if (!ws.length) return 0.1;
  const es = ws.map((w) => w.e).sort((a, b) => a - b);
  return es[Math.floor(es.length / 2)];
}

/**
 * Las coordenadas NOTABLES de un eje: dónde hay pared.
 *
 * Sobre el eje x son los ejes de las paredes verticales —para alinearse con una
 * paralela— y las puntas de las horizontales —donde una pared TERMINA—; sobre el
 * eje y, al revés. Es la lista a la que se pega el imán, y es literalmente lo
 * que pidió Daniel: que reconozca dónde hay una pared y dónde termina.
 *
 * Se mide contra el EJE de la pared vecina, no contra su cara, porque es la
 * misma convención con la que el editor ya decide qué paredes están pegadas: en
 * estas plantas las fachadas se cruzan sobre sus ejes, no se topan de cara.
 */
function nodos(ws: Pared[], eje: 'x' | 'y', salvo: number[]): number[] {
  const propio: Orientacion = eje === 'x' ? 'v' : 'h';
  const out: number[] = [];
  for (const w of ws) {
    if (salvo.includes(w.id)) continue;
    if (w.o === propio) out.push(w.c);
    else out.push(w.a, w.b);
  }
  return out;
}

/**
 * Lo mismo, pero sin las puntas que VIAJAN con la pared que se está moviendo.
 *
 * Hace falta porque esas puntas son parte de lo que se mueve, no vecinos: al
 * correr una fachada, sus perpendiculares la siguen con el mismo delta (ver
 * `pegadas`), así que su punta queda siempre pegada al dedo. Metida en la lista
 * del imán, la pared se imanta A SÍ MISMA —hay un nodo a cero de distancia en
 * cada cuadro— y la rejilla de 5 cm nunca llega a aplicarse.
 *
 * Medido el 5-oct-2026 en Ujué: arrastrando una fachada el eje terminaba en
 * 1,588 m en vez de 1,60, y el síntoma es exactamente el que trajo Daniel
 * («que lo vaya haciendo en múltiplos de 5 cm»). La punta del OTRO extremo de
 * la pegada sí es un vecino legítimo y se conserva.
 */
function nodosLibres(ws: Pared[], eje: 'x' | 'y', salvo: number[],
                     viajan: Pegada[]): number[] {
  const propio: Orientacion = eje === 'x' ? 'v' : 'h';
  const out: number[] = [];
  for (const w of ws) {
    if (salvo.includes(w.id)) continue;
    if (w.o === propio) { out.push(w.c); continue; }
    if (!viajan.some((g) => g.id === w.id && g.fin === 0)) out.push(w.a);
    if (!viajan.some((g) => g.id === w.id && g.fin === 1)) out.push(w.b);
  }
  return out;
}

/** El nodo más cercano al valor si hay uno a tiro, o null si no hay ninguno. */
function nodoCerca(v: number, cands: number[], umbral: number): number | null {
  let mejor: number | null = null;
  let d = umbral;
  for (const c of cands) {
    const k = Math.abs(c - v);
    if (k < d) { d = k; mejor = c; }
  }
  return mejor;
}

/** Los vanos que sobreviven dentro de un tramo, recortados a sus límites. */
function vanosEn(vanos: Vano[], a: number, b: number): Vano[] {
  return vanos
    .map((v) => ({ ...v, a: Math.max(v.a, a), b: Math.min(v.b, b) }))
    .filter((v) => v.b - v.a > 0.05);
}

/** El `id` libre siguiente de la lista de vanos de una pared. */
function idVano(vanos: Vano[]): number {
  return vanos.reduce((m, v) => Math.max(m, v.id), 0) + 1;
}

/** Al milímetro. Los metros con catorce decimales no son una medida de obra. */
function mm(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Lleva hasta `destino` la punta de la pared que ya estaba más cerca, estirando
 * o recortando. Es el verbo que comparten intersección y ajustar, que es también
 * lo que comparten en Archicad: las dos mueven una punta contra una referencia y
 * se diferencian sólo en quién pone la referencia.
 *
 * Elegir la punta más cercana —y no siempre la misma— es lo que hace que el
 * gesto no tenga sentido: una pared que sobra por el norte se recorta por el
 * norte, y la misma pared que se queda corta por el sur se estira por el sur.
 */
function llevarPunta(w: Pared, destino: number): void {
  const d = Math.round(destino * 1000) / 1000;
  if (Math.abs(w.a - d) <= Math.abs(w.b - d)) w.a = Math.min(d, w.b - MIN_LARGO);
  else w.b = Math.max(d, w.a + MIN_LARGO);
  w.vanos = vanosEn(w.vanos, w.a, w.b);
}

interface Props {
  /** URL del PDF del modelo (el blob que ya abre el botón «PDF Plano»). */
  planoUrl: string | null;
  modelo: ModeloPlanta;
  /** Se dispara al tocar «Recalcular áreas», con el área de cada planta en m². */
  onAreas?: (areasPorPlanta: Record<number, number>) => void;
  /**
   * Lo que va en el centro de la cabecera. Es el nombre del modelo, que el
   * editor no conoce: lo sabe quien lo abrió.
   */
  titulo?: string;
  /** Si está, la cabecera dibuja la X. Sin esto el editor no se puede cerrar. */
  onCerrar?: () => void;
  /**
   * Qué hace GUARDAR y qué hace EXPORTAR.
   *
   * Los dos botones existen desde el 6-oct-2026 aunque todavía no hagan nada:
   * «es para funciones que le vamos a agregar […] pero que queden los botones
   * para luego simplemente cablearles». Sin prop puesta avisan que no están
   * conectados en vez de quedarse mudos, que es lo que se lee como roto.
   */
  onGuardar?: () => void;
  onExportar?: () => void;
  /** Botón propio del que abrió el editor, a la izquierda de guardar. */
  acciones?: ReactNode;
}

/**
 * De dónde se tomó un área libre. `cuerpo` la mueve entera; las letras de los
 * puntos cardinales son los bordes y las esquinas, y estiran.
 *
 * Son los cardinales de la PANTALLA, no los del lote: `n` es el borde de arriba
 * —la `y` más chica—, porque en el lienzo la `y` crece hacia abajo.
 */
type Lado = 'cuerpo' | 'n' | 's' | 'e' | 'o' | 'ne' | 'no' | 'se' | 'so';

type Agarre =
  | { tipo: 'mover'; id: number; c0: number; m0: number; pegadas: Pegada[] }
  | { tipo: 'punta'; id: number; fin: 0 | 1; v0: number; m0: number }
  /** Un vano corriéndose A LO LARGO de su pared: `vano` es su id, `a0` dónde
   *  arrancaba. No cambia de pared ni de ancho. */
  | { tipo: 'vano'; id: number; vano: number; a0: number; m0: number }
  /**
   * Un área libre moviéndose o estirándose. Guarda el rectángulo COMPLETO de
   * donde arrancó (`x0 y0 w0 h0`) y el punto donde se apoyó el dedo (`mx my`):
   * el paso de 5 cm se cuenta desde ahí y no desde el origen, o apoyar el dedo
   * ya corre el área sola.
   *
   * `yaSel` recuerda si estaba tomada antes del apoyo, y con eso un toque que
   * no mueve nada la suelta —el mismo toggle de siempre— mientras un arrastre
   * de verdad la deja tomada.
   */
  | {
      tipo: 'libre'; id: number; lado: Lado; yaSel: boolean;
      x0: number; y0: number; w0: number; h0: number; mx: number; my: number;
    }
  /**
   * Un LADO de un área hecha de vértices, empujándose.
   *
   * `lado` es el índice del vértice donde arranca el lado —va de ese al
   * siguiente, cerrando con el último— y `vs0` es el anillo COMPLETO de donde
   * arrancó el gesto, por lo mismo que el área libre guarda su rectángulo: el
   * paso de 5 cm se cuenta contra el estado inicial o apoyar el dedo ya mueve
   * el lado solo.
   *
   * Empujar un lado mueve sus DOS puntas en perpendicular, y eso es lo que
   * conserva la ortogonalidad sin tener que arreglar nada después: los lados
   * vecinos son perpendiculares a este, así que correrle las puntas a lo largo
   * de ellos sólo los alarga o los acorta.
   */
  | {
      tipo: 'poli'; id: number; lado: number;
      vs0: Array<[number, number]>; mx: number; my: number;
    }
  /**
   * Un VÉRTICE suelto, moviéndose en cualquier dirección.
   *
   * Es el agarre del polígono irregular y el único que rompe la ortogonalidad a
   * propósito: mueve UN punto y no le arregla nada a los vecinos, así que los
   * dos lados que salen de él quedan en diagonal. Eso es exactamente lo que
   * Daniel pidió el 7-oct-2026 —«una forma con ángulos distintos… desde un
   * triángulo»— y por eso el polígono ortogonal no lo tiene.
   *
   * `vs0` es el anillo completo de donde arrancó el gesto, por lo mismo que el
   * lado: el paso de 5 cm se cuenta contra el estado inicial o apoyar el dedo ya
   * corre el vértice solo.
   */
  | {
      tipo: 'vertice'; id: number; indice: number;
      vs0: Array<[number, number]>; mx: number; my: number;
    }
  /**
   * Los dos de arriba, pero sobre la forma de un OBJETO «a medida».
   *
   * Son variantes y no los mismos porque lo único que cambia es de qué lista
   * sale la pieza —`planta.muebles` en vez de `planta.libres`— y qué pasa
   * después de moverla: un objeto no mide, así que no rehace la medición. La
   * geometría es idéntica y por eso corren las MISMAS funciones puras
   * (`partirLado`, `quitarVertice`, `empujarLado`, `imantarVertice`): tener un
   * juego paralelo sería garantizar que se separen al primer arreglo. Ver
   * `Mueble.vertices`.
   */
  | {
      tipo: 'mpoli'; id: number; lado: number;
      vs0: Array<[number, number]>; mx: number; my: number;
    }
  | {
      tipo: 'mvertice'; id: number; indice: number;
      vs0: Array<[number, number]>; mx: number; my: number;
    }
  /**
   * Un mueble moviéndose o estirándose. Mismos campos que el área libre —es la
   * misma caja con los mismos agarres— y por eso los dos comparten `estirar`.
   *
   * Lo que NO comparten es el imán: el área libre no tiene ninguno y el mueble
   * se arrima a caras de pared y a bordes de otros muebles, que es lo que pidió
   * Daniel para poder apilar la cocina.
   */
  /**
   * Una cota puesta a mano, SEPARÁNDOSE de lo que mide.
   *
   * Sólo cambia un escalar —la distancia perpendicular— y por eso el agarre no
   * guarda geometría: los dos puntos medidos no se tocan. `off0` es de dónde
   * salió y `mx/my` dónde se apoyó el dedo, por lo mismo que el área libre
   * guarda su rectángulo: el corrimiento se mide contra el apoyo o tocar la
   * cota ya la movería sola. Ver `Cota.off`.
   */
  | { tipo: 'cotaoff'; id: number; off0: number; mx: number; my: number }
  /**
   * Un NODO de una cota puesta a mano: cambia QUÉ mide.
   *
   * Es el otro gesto que pidió Daniel el 22-oct-2026 —«se puede editar los
   * nodos de dónde está midiendo»— y es el que sí mueve el número. Va suelto en
   * cualquier dirección como el vértice de un área, porque una cota diagonal es
   * legítima: el retiro a un lindero en ángulo se mide así.
   */
  | { tipo: 'cotanodo'; id: number; fin: 0 | 1 }
  | {
      tipo: 'mueble'; id: number; lado: Lado; yaSel: boolean;
      x0: number; y0: number; w0: number; h0: number; mx: number; my: number;
      /** Puesto sólo cuando el gesto NACIÓ en el catálogo: es lo que deja
       *  cerrar el catálogo al soltar y no al apoyar. Cerrarlo al apoyar
       *  desmontaría el botón que tiene la captura del puntero y el mueble se
       *  quedaría clavado donde nació. */
      nacido?: true;
    };

/** Una caja en metros: lo que `estirar` recibe y devuelve. */
interface Caja { x: number; y: number; w: number; h: number }

/**
 * Lo que el gesto le hace a una caja: moverla entera o estirar los bordes que
 * dice el `lado`.
 *
 * Es función pura y está acá afuera porque la comparten el área libre y el
 * mueble, que tienen los mismos ocho agarres. El tope de `min` se aplica al
 * CORRIMIENTO y no al resultado: limitar el ancho dejaría el borde opuesto
 * corriéndose solo cuando el dedo pasa de largo.
 *
 * `min` es parámetro y no la constante porque el piso no es el mismo: un área
 * no baja de `MIN_LARGO` y un mueble sí, hasta `MIN_MUEBLE` (una silla de
 * 45 cm, un lavatorio de 40).
 */
function estirar(c: Caja, lado: Lado, dx: number, dy: number, min = MIN_LARGO): Caja {
  // `cuerpo` se chequea primero porque la palabra lleva una `e` y una `o`
  // adentro, y los `includes` de abajo la leerían como esquina noreste.
  if (lado === 'cuerpo') return { ...c, x: c.x + dx, y: c.y + dy };
  let { x, y, w, h } = c;
  // Cada letra del lado mueve su borde, y por eso una esquina —dos letras—
  // estira los dos de una pasada, que es el gesto que pidió Daniel: «agarrar un
  // lado y extenderlo. O agarrar dos lados».
  if (lado.includes('o')) { const d = Math.min(dx, c.w - min); x = c.x + d; w = c.w - d; }
  if (lado.includes('e')) w = Math.max(c.w + dx, min);
  if (lado.includes('n')) { const d = Math.min(dy, c.h - min); y = c.y + d; h = c.h - d; }
  if (lado.includes('s')) h = Math.max(c.h + dy, min);
  return { x, y, w, h };
}

/**
 * Empuja el lado `i` de un anillo ortogonal, en perpendicular.
 *
 * Es función pura y está acá afuera para poder probarla: mueve las dos puntas
 * del lado y NO toca a nadie más. Que eso alcance para mantener todo ortogonal
 * no es casualidad del dibujo, es la propiedad que hace editable al polígono:
 * los dos lados vecinos son perpendiculares a este, así que correrle las puntas
 * a lo largo de ellos los alarga o los acorta y siguen siendo rectos.
 *
 * Un lado horizontal se empuja en `y` y uno vertical en `x`; el otro
 * componente del arrastre se ignora, que es lo que hace que el dedo pueda ir
 * torcido —en un celular siempre va torcido— y el lado salga derecho.
 *
 * Un lado de largo cero —el que queda justo al partir— no se mueve: todavía no
 * tiene dirección, y adivinarle una lo mandaría para cualquier lado.
 */
function empujarLado(
  vs: Array<[number, number]>, i: number, dx: number, dy: number,
): Array<[number, number]> {
  const j = (i + 1) % vs.length;
  const [ax, ay] = vs[i];
  const [bx, by] = vs[j];
  const horizontal = Math.abs(ay - by) < 1e-6;
  const vertical = Math.abs(ax - bx) < 1e-6;
  if (horizontal === vertical) return vs;        // largo cero, o diagonal
  const salida = vs.map(([x, y]) => [x, y] as [number, number]);
  if (horizontal) {
    salida[i] = [ax, mm(ay + dy)];
    salida[j] = [bx, mm(by + dy)];
  } else {
    salida[i] = [mm(ax + dx), ay];
    salida[j] = [mm(bx + dx), by];
  }
  return salida;
}

/**
 * Los vértices de un objeto «a medida», sembrando los del rectángulo si todavía
 * no tiene forma propia.
 *
 * Es lo que hace que DARLE FORMA no necesite un botón aparte: el objeto nace
 * rectángulo y la primera vez que alguien le parte un lado, los cuatro vértices
 * aparecen solos. Un paso previo de «convertir a polígono» sería un trámite que
 * no le enseña nada a nadie.
 *
 * En el mismo orden que el resto del archivo —no, ne, se, so, en el sentido del
 * reloj— para que `partirLado` y `areaPoligono` lean lo mismo que en un área.
 */
function verticesMueble(m: Mueble): Array<[number, number]> {
  if (m.vertices?.length) return m.vertices.map(([x, y]) => [x, y] as [number, number]);
  return [
    [m.x, m.y], [m.x + m.w, m.y],
    [m.x + m.w, m.y + m.h], [m.x, m.y + m.h],
  ];
}

/**
 * Hasta dónde tira el imán ortogonal, en metros de mundo.
 *
 * 12 cm: más corto que el lado más corto que alguien dibuja a propósito y más
 * largo que el temblor de un dedo en un celular. Va en metros y no en píxeles
 * porque todo este archivo piensa en metros; la consecuencia —que con mucho
 * zoom el imán agarre desde más lejos en pantalla— es la correcta: con mucho
 * zoom uno está afinando, y es justo cuando quiere que pegue.
 */
const IMAN_ORTO = 0.12;

/**
 * Lo que el imán ortogonal agarró en este cuadro, para poder dibujarlo.
 *
 * `x` e `y` son las COORDENADAS del eje al que se pegó el vértice, no un
 * desplazamiento: es lo que la guía punteada necesita para pintarse.
 */
type Iman = { x: number | null; y: number | null };

/**
 * Pega un vértice a la horizontal o la vertical de sus vecinos, si está cerca.
 *
 * Lo pidió Daniel el 14-oct-2026: «me gustaría que tenga siempre como por
 * defecto, no completamente restrictivo, pero más bien que ayude, a la hora de
 * uno mover un vértice, que normalmente sea ortogonal, pero con la posibilidad
 * de poder moverlo hacia una dirección que sea diferente».
 *
 * Las dos mitades de esa frase son el umbral: adentro de `IMAN_ORTO` el vértice
 * se acuesta sobre el eje del vecino, afuera va exactamente donde lo llevan. No
 * hay modo que prender ni tecla que sostener — la distancia ES el interruptor,
 * y por eso ayuda sin encerrar.
 *
 * Sólo corre en el polígono de ÁNGULO LIBRE. El ortogonal de verdad no lo
 * necesita: ahí no se arrastran vértices sino LADOS enteros (`empujarLado`), que
 * son rectos por construcción.
 *
 * Cada vecino aporta UN SOLO eje —el más cercano de los dos— porque pegarle los
 * dos al mismo vecino sería ponerle el vértice encima: el lado se iría a cero y
 * la esquina desaparecería. Así el vecino de un lado fija la vertical y el del
 * otro la horizontal, que es exactamente lo que uno quiere al cuadrar una
 * esquina.
 */
function imantarVertice(
  vs: Array<[number, number]>, i: number, x: number, y: number,
): { x: number; y: number; iman: Iman } {
  const n = vs.length;
  const iman: Iman = { x: null, y: null };
  let fx = x;
  let fy = y;
  for (const v of [vs[(i + n - 1) % n], vs[(i + 1) % n]]) {
    if (!v) continue;
    const dx = Math.abs(v[0] - x);
    const dy = Math.abs(v[1] - y);
    // El eje más cercano gana: el vecino que está casi en la misma columna
    // quiere un lado vertical, no uno horizontal.
    if (dx <= dy && dx <= IMAN_ORTO && iman.x == null) { fx = v[0]; iman.x = v[0]; }
    else if (dy < dx && dy <= IMAN_ORTO && iman.y == null) { fy = v[1]; iman.y = v[1]; }
  }
  return { x: fx, y: fy, iman };
}

/**
 * Los vértices de un área creada, sea rectángulo o polígono.
 *
 * Existe para poder ACOTARLA sin repetir dos veces la misma cuenta: un
 * rectángulo dibujado a mano no guarda vértices —le alcanza con x, y, w, h— pero
 * sus cuatro lados se miden igual que los de un polígono. Devuelve `null` para
 * la que tiene FORMA DEL PLANO (celdas): ahí el contorno no es un polígono
 * simple y no hay «lados» que rotular.
 */
function poliDe(l: Libre): Array<[number, number]> | null {
  if (l.vertices?.length) return l.vertices;
  if (l.celdas?.length) return null;
  return [[l.x, l.y], [l.x + l.w, l.y], [l.x + l.w, l.y + l.h], [l.x, l.y + l.h]];
}

/** Hacia dónde corre el lado `i`: `null` si es diagonal o de largo cero. */
function orientaLado(vs: Array<[number, number]>, i: number): 'h' | 'v' | null {
  const [ax, ay] = vs[i];
  const [bx, by] = vs[(i + 1) % vs.length];
  const h = Math.abs(ay - by) < 1e-6;
  const v = Math.abs(ax - bx) < 1e-6;
  return h === v ? null : h ? 'h' : 'v';
}

/**
 * Parte el lado `i` en dos mitades que después se arrastran por separado.
 *
 * Es el gesto que pidió Daniel —«añadir posibilidad de dividir lados de
 * áreas»—. En el polígono ORTOGONAL mete DOS vértices y no uno, los dos en el
 * punto del corte: con uno solo el polígono tendría tres puntos alineados y
 * arrastrar una mitad dejaría la otra en diagonal, porque el vértice del medio
 * es de las dos mitades a la vez y no puede quedarse y moverse al mismo tiempo.
 * Con dos, cada mitad tiene su propia punta y entre ellas nace un lado de largo
 * cero que se vuelve perpendicular en cuanto una de las dos se mueve.
 *
 * El corte va en el punto que se tocó, pegado a la rejilla de 5 cm, y no en el
 * medio: tocar un lado de 8 m y que el vértice aparezca en los 4 m obligaría a
 * arrastrarlo siempre después.
 *
 * UN LADO EN DIAGONAL también se parte, y eso entró con el polígono irregular
 * (7-oct-2026): sin esto a un triángulo no se le puede agregar un vértice —sus
 * tres lados son diagonales— y la forma quedaba congelada en la que nació. El
 * corte se proyecta sobre el lado igual que en los rectos, sólo que la cuenta es
 * la paramétrica en vez de quedarse con una coordenada.
 *
 * `cuantos` es 2 por el razonamiento de arriba —el ortogonal necesita las dos
 * puntas para poder empujar una mitad— pero el IRREGULAR pide 1: ahí lo que se
 * agrega es «un punto de anclaje más» (así lo pidió Daniel el 8-oct-2026) y ese
 * punto se arrastra solo, sin necesitar un gemelo. Dejar los dos encimados en el
 * irregular deja un vértice fantasma que nadie puso y que igual se cuenta en el
 * rótulo de la paleta.
 */
function partirLado(
  vs: Array<[number, number]>, i: number, px: number, py: number, cuantos: 1 | 2 = 2,
): Array<[number, number]> {
  const j = (i + 1) % vs.length;
  const [ax, ay] = vs[i];
  const [bx, by] = vs[j];
  const horizontal = Math.abs(ay - by) < 1e-6;
  const vertical = Math.abs(ax - bx) < 1e-6;
  let corte: [number, number];
  if (horizontal && vertical) return vs;          // largo cero
  if (horizontal || vertical) {
    // El corte se proyecta sobre el lado: el dedo nunca cae exactamente encima.
    corte = horizontal
      ? [mm(Math.round(px / PASO) * PASO), ay]
      : [ax, mm(Math.round(py / PASO) * PASO)];
    // Y no se acepta pegado a una de las puntas: ahí las dos mitades no serían
    // dos, una nacería de largo cero.
    const lo = horizontal ? Math.min(ax, bx) : Math.min(ay, by);
    const hi = horizontal ? Math.max(ax, bx) : Math.max(ay, by);
    const v = horizontal ? corte[0] : corte[1];
    if (v <= lo + PASO || v >= hi - PASO) return vs;
  } else {
    // Diagonal: el pie de la perpendicular del dedo sobre el lado. `t` es qué
    // tan avanzado va ese pie, de 0 a 1, y el margen se mide en esa misma
    // escala —5 cm de un lado de 4 m es 0,0125— para que la regla de «no
    // pegado a la punta» sea la misma que arriba.
    const lx = bx - ax;
    const ly = by - ay;
    const largo2 = lx * lx + ly * ly;
    const t = ((px - ax) * lx + (py - ay) * ly) / largo2;
    const margen = PASO / Math.sqrt(largo2);
    if (!(t > margen && t < 1 - margen)) return vs;
    corte = [mm(ax + t * lx), mm(ay + t * ly)];
  }
  return cuantos === 1
    ? [...vs.slice(0, i + 1), corte, ...vs.slice(i + 1)]
    : [...vs.slice(0, i + 1), corte, [corte[0], corte[1]], ...vs.slice(i + 1)];
}

/**
 * Saca el vértice `i` del anillo.
 *
 * Devuelve el mismo arreglo cuando no se puede, que es el aviso de «no hice
 * nada»: con tres vértices el polígono ya es un triángulo y sacarle uno lo deja
 * en una línea, que no es un área sino un error con forma de área.
 *
 * Es el segundo verbo que le faltaba al vértice —hasta el 8-oct-2026 un toque
 * sobre un nodo no hacía nada y no había manera de deshacer una partición de
 * más— y lo pidió Daniel con esas palabras: «borrar un punto de anclaje que
 * actualmente no existe».
 */
function quitarVertice(
  vs: Array<[number, number]>, i: number,
): Array<[number, number]> {
  if (vs.length <= 3) return vs;
  return [...vs.slice(0, i), ...vs.slice(i + 1)];
}

/**
 * El cuerpo de un área, en rectángulos, sea cual sea su forma.
 *
 * Un área se guarda de tres maneras —vértices, celdas de la varita, o nada más
 * su caja— y se leen SIEMPRE en ese orden. Todo lo que el editor hace con áreas
 * por debajo del dibujo (preguntar si el dedo cayó adentro, frenar la varita)
 * habla en rectángulos, así que este es el único lugar donde está escrito cómo
 * se baja cada forma a rectángulos: el resto llama acá y no vuelve a decidir.
 *
 * Se usa a propósito en vez de la caja envolvente: en una L o en el lote con el
 * garaje mordido, la esquina que le falta NO es ella, y preguntar por la caja
 * mandaba a «tocá del otro lado» justo donde sí había espacio libre.
 */
function cuerpoLibre(l: Libre): Rect[] {
  const caja: Rect[] = [[l.x, l.y, l.w, l.h]];
  // NUNCA vacío, y eso es el contrato: todo el editor —el hit test, la barrera
  // de la varita, el pintado y el ancla del rótulo— asume que acá hay al menos
  // un rectángulo, y `forma[0]` indefinido tumba el componente entero (pantalla
  // en blanco, 7-oct-2026). Un anillo que `rectangulosDePoligono` no puede
  // partir en franjas cae a su caja: se ve de más, pero se ve.
  if (l.vertices?.length) {
    const franjas = rectangulosDePoligono(l.vertices);
    return franjas.length ? franjas : caja;
  }
  if (l.celdas?.length) return l.celdas;
  return caja;
}

/** Los cuatro métodos con que un área puede estar modelada. */
type Metodo = 'rect' | 'celdas' | 'poli' | 'irregular';

/**
 * Con cuál de los métodos está modelada esta área.
 *
 * Son tres los que Daniel puede ELEGIR —rectángulo, polígono ortogonal y
 * polígono irregular— y uno que sólo se recibe: `celdas`, lo que levanta la
 * varita del plano. Se lee acá, en un solo lugar, porque antes el método se
 * deducía en cada sitio con una cadena de ternarios distinta y agregar el
 * cuarto habría significado tocarlas todas.
 */
function metodoLibre(l: Libre): Metodo {
  if (l.vertices?.length) return l.anguloLibre ? 'irregular' : 'poli';
  if (l.celdas?.length) return 'celdas';
  return 'rect';
}

/**
 * El centroide de área del anillo, que es donde va el rótulo de un polígono
 * irregular.
 *
 * No sirve el centro de la caja —en un triángulo cae afuera de la figura— ni el
 * de la franja más grande, porque un anillo con diagonales no tiene franjas. En
 * una forma MUY cóncava el centroide puede caer fuera igual; ahí cae al
 * promedio de los vértices, que al menos queda dentro de la envolvente.
 */
function centroidePoligono(vs: Array<[number, number]>): [number, number] {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < vs.length; i++) {
    const [x0, y0] = vs[i];
    const [x1, y1] = vs[(i + 1) % vs.length];
    const cruz = x0 * y1 - x1 * y0;
    a += cruz;
    cx += (x0 + x1) * cruz;
    cy += (y0 + y1) * cruz;
  }
  if (Math.abs(a) < 1e-9) {
    return [
      vs.reduce((s, [x]) => s + x, 0) / vs.length,
      vs.reduce((s, [, y]) => s + y, 0) / vs.length,
    ];
  }
  return [cx / (3 * a), cy / (3 * a)];
}

/** Qué está tomado. El editor toma de a una cosa: pared(es), un vano o un área. */
interface VanoSel {
  pared: number;
  vano: number;
}

interface Pegada {
  id: number;
  fin: 0 | 1;
  v0: number;
}

/**
 * Los gestos que necesitan tomar el lienzo entero y no una pared: trazar una
 * pared nueva, elegir dónde partir una existente y trazar la línea que parte un
 * área. Mientras uno está activo, una capa transparente se come los eventos y el
 * hit-test se hace a mano en metros —así el gesto no pelea con el pan, que ya
 * quedó excluido por clase.
 */
type Modo = 'pared' | 'dividir' | 'area' | 'libre' | 'irregular' | 'varita'
  | 'varita-pared' | 'rect-pared' | 'cota' | 'objeto';

/**
 * Las CUATRO maneras de agregar una pared, en el orden en que Daniel las pidió
 * (8-oct-2026): «la que por defecto estaría sería crear una pared individual;
 * otra sería crear varias paredes de manera seguida; y una última que sería una
 * varita mágica, que al darle clic reconozca los lados y genere las paredes».
 * La cuarta llegó esa misma tarde: «se me olvidó mencionar que debería haber
 * otra opción más de modelar paredes, que sería por medio de rectángulo; así
 * que el ícono sería el rectángulo, y que esté pues esa función que va a ser
 * muy, muy utilizada también».
 *
 * Dos de las cuatro NO son un modo del editor sino el mismo modo `'pared'` con
 * distinto final: `'una'` apaga el gesto al nacer la pared y `'seguidas'` lo
 * deja vivo clavando el arranque siguiente en la punta recién puesta. Tenerlo
 * como estado aparte y no como dos miembros de `Modo` es lo que deja que los
 * quince sitios que preguntan `modo === 'pared'` —la vista previa, la pastilla,
 * el hit-test, la capa que se come los eventos— sigan sin enterarse.
 *
 * Las otras dos sí son modo propio, porque su GESTO es otro: la varita resuelve
 * con un solo toque y el rectángulo dibuja una caja de esquina a esquina, no
 * una línea.
 */
type MetodoPared = 'una' | 'seguidas' | 'rect' | 'varita';

/** Los dos modos que resuelven al APOYAR, sin trazo ni mira que alzar. */
const esVarita = (m: Modo | null) => m === 'varita' || m === 'varita-pared';

/**
 * Los dos modos de dibujo que levantan un área a mano: el rectángulo y el
 * polígono irregular. Se dibujan con el MISMO gesto —una caja de esquina a
 * esquina— y la única diferencia es con qué nace: el rectángulo con sus dos
 * medidas escribibles, el irregular con los cuatro vértices sueltos.
 *
 * Es una función y no `modo === 'libre' || modo === 'irregular'` repetido ocho
 * veces porque son ocho lugares —la vista previa, el aviso del segundo apoyo,
 * el cierre del trazo, el cerrar la paleta— y olvidarse de uno deja el tercer
 * método a medio existir.
 *
 * El rectángulo de PAREDES entra acá por el gesto, que es el mismo: se dibuja
 * la caja igual y se avisa igual. Lo que cambia es qué nace al soltar, y eso lo
 * decide `trazarFin` antes de llegar a `crearLibre`.
 */
const dibujaCaja = (m: Modo | null) =>
  m === 'libre' || m === 'irregular' || m === 'rect-pared';

/**
 * Las capas del editor: la estructura, la medición, el equipamiento y la
 * acotación.
 *
 * Son las cosas que se editan y se miden distinto —las paredes definen la casa,
 * las áreas son el número que vale, el mobiliario no mide nada, las cotas sólo
 * dicen cuánto mide lo que ya está— y desde el 5-oct-2026 son también las que
 * se prenden, se apagan y se protegen por separado.
 *
 * La cota entró como capa y no como un botón aparte justo por eso: así hereda
 * las tres cosas que Daniel ya pidió para las otras —su ojo, que la capa activa
 * sea lo único manipulable, y que el resto quede protegido— sin inventar un
 * segundo mecanismo que haga lo mismo.
 */
type Capa = 'pared' | 'area' | 'mueble' | 'cota';

/**
 * El molde de un mueble copiado: lo que define la PIEZA, sin lo que la ata a un
 * lugar.
 *
 * Se arma con `Pick` del propio `Mueble` y no con los campos escritos a mano
 * para que los tipos finos no se aflojen en el camino: el giro es `0 | 90 | 180
 * | 270` y los radios son una tupla de cuatro, y declararlos como `number` y
 * `number[]` obligaba a castear al pegar —que es exactamente donde un giro de 45
 * o un radio de más entrarían sin que nadie los frene—.
 */
type Copia = Pick<Mueble, 'tipo' | 'w' | 'h' | 'giro' | 'radios' | 'altura' | 'espejo'>;

/** El trazo en curso de una pared nueva o de un divisor, en metros. */
interface Trazo {
  x0: number; y0: number; x1: number; y1: number;
  /**
   * El punto de arranque ya quedó puesto y el trazo espera el segundo toque.
   *
   * Es lo que distingue el gesto del dedo del gesto del mouse sin preguntarle
   * nada al navegador: un arrastre nunca clava nada —nace la pared al soltar—,
   * y un toque suelto sí, que es cómo se ve dónde va a caer antes de cerrar.
   */
  clavado: boolean;
}

/**
 * El mismo trazo con los dos lados en múltiplos de 5 cm.
 *
 * Daniel, 9-oct-2026: «cuando estoy creando el rectángulo de paredes debería
 * crearse con medidas de múltiplos de 5 cm, ya que en este caso lo hace de una
 * manera muy irregular». Lo que se redondea es el LARGO de cada lado y no las
 * cuatro coordenadas sueltas —la misma regla que `aRejilla` para el arrastre—:
 * la esquina donde apoyó el dedo manda y no se mueve, que es la que el imán
 * pudo haber pegado a una pared que ya estaba; la de enfrente se corre lo justo
 * para que el cuarto mida redondo.
 *
 * Se aplica EN VIVO, mientras se arrastra, para que la cota de la previa y la
 * del cuarto que nace sean el mismo número.
 */
const enRejilla = (t: Trazo): Trazo => ({
  ...t,
  x1: t.x0 + Math.sign(t.x1 - t.x0) * aPaso(Math.abs(t.x1 - t.x0)),
  y1: t.y0 + Math.sign(t.y1 - t.y0) * aPaso(Math.abs(t.y1 - t.y0)),
});

/**
 * Dónde va a caer la acción, ya con el imán aplicado, y a qué se pegó.
 *
 * Existe porque sin verlo el editor obliga a adivinar: Daniel lo pidió con estas
 * palabras —«para cuando voy a crear una pared, saber dónde la voy a crear […]
 * no tengo visibilidad»—. La mira se dibuja antes de que el gesto cambie nada,
 * así que el imán deja de ser una sorpresa al soltar.
 */
interface Mira {
  x: number;
  y: number;
  guias: Guia[];
  /**
   * Una mira MUDA monta la lupa pero NO se dibuja en el plano.
   *
   * `mira` venía diciendo dos cosas a la vez —«acá estoy apuntando», que es lo
   * que la lupa necesita, y «dibujá el retículo», que es otra cosa—. Mientras
   * los dos gestos que la usaban nacían de un dedo en el aire eso no molestaba;
   * moviendo el NODO de un área sí: el nodo YA es un retículo —disco, aro y
   * crucecita— y escribir la mira encima apila dos miras en el mismo punto.
   * Con esto el nodo agarrado hace de mira y la lupa igual sale. Lo pidió
   * Daniel el 22-oct-2026 («que la lupa salga cuando edito un área moviendo un
   * nodo») y eligió esta variante viéndola en maqueta.
   */
  muda?: boolean;
}

/**
 * La marca del corte de una pared: en qué pared y en qué punto a lo largo.
 *
 * `firme` separa el paso que ya se confirmó con un toque del que es sólo
 * previsión del puntero. Es lo que hace posible el doble toque que pidió Daniel
 * —«una para definir el punto y el segundo ya para cortar»— sin que el mouse,
 * que va dejando posición sin tocar nada, corte en el primer clic.
 */
interface Corte {
  pared: number;
  /** La coordenada A LO LARGO de la pared donde queda el corte. */
  v: number;
  firme: boolean;
}

/** Dónde se planta la paleta, en píxeles del contenedor del lienzo. */
interface Anclaje {
  x: number;
  y: number;
}

/** La línea de referencia a la que se acaba de pegar el imán. */
interface Guia {
  eje: 'x' | 'y';
  v: number;
}

/**
 * Lo que el trazo va a crear. Se calcula igual para la vista previa y al soltar.
 *
 * NO redondea a la rejilla: el trazo llega ya resuelto por `imanar`, que o lo
 * pegó al nodo de una pared vecina o lo puso en la rejilla. Redondear otra vez
 * acá rompía justo el caso que pidió Daniel —arrancar la pared nueva EN el nodo
 * de una existente—: un nodo en 2,57 volvía a 2,55 y la esquina quedaba abierta
 * 2 cm, invisibles en pantalla y suficientes para que la inundación se escapara.
 */
function paredDelTrazo(t: Trazo, e: number, id: number): Pared | null {
  const dx = Math.abs(t.x1 - t.x0);
  const dy = Math.abs(t.y1 - t.y0);
  const o: Orientacion = dy >= dx ? 'v' : 'h';
  const largo = o === 'v' ? dy : dx;
  if (largo < MIN_LARGO) return null;
  const c = mm(o === 'v' ? t.x0 : t.y0);
  const a = mm(Math.min(o === 'v' ? t.y0 : t.x0, o === 'v' ? t.y1 : t.x1));
  const b = mm(Math.max(o === 'v' ? t.y0 : t.x0, o === 'v' ? t.y1 : t.x1));
  return { id, o, c, e, a, b, vanos: [] };
}

/**
 * Cuánto se puede torcer un trazo y seguir contando como recto: la tangente del
 * ángulo contra el eje más cercano. 0,36 son ~20°.
 */
const TOL_RECTO = 0.36;

/**
 * Endereza un trazo que iba CASI recto: lo clava horizontal o vertical.
 *
 * Es el imán que pidió Daniel para la línea que divide un área (4-oct-2026):
 * «quiero que tenga cierto magnetismo o cierta inducción a que el corte sea
 * recto, porque paso luchando, porque siempre trata de hacerme como un ángulo, y
 * casi siempre los cortes de esas áreas van a ser rectos». El dedo no traza
 * recto nunca —sobre todo en un celular, y sobre todo cuando además tapa la
 * línea— así que sin esto todo corte sale con un grado o dos de inclinación que
 * nadie quiso.
 *
 * Los 20° de tolerancia dejan de sobra los 70° restantes para el corte diagonal
 * de verdad: no se pierde el gesto, se gana el caso corriente. Se mide contra el
 * eje LARGO del trazo, así una línea casi vertical se endereza en vertical y una
 * casi horizontal en horizontal, sin preguntar nada.
 */
function enderezar(t: Trazo): Trazo {
  const ax = Math.abs(t.x1 - t.x0);
  const ay = Math.abs(t.y1 - t.y0);
  if (ax >= ay) return ay <= ax * TOL_RECTO ? { ...t, y1: t.y0 } : t;
  return ax <= ay * TOL_RECTO ? { ...t, x1: t.x0 } : t;
}

/**
 * La cinta de cota al costado de una pared: el largo en metros, legible y con
 * fondo opaco.
 *
 * Va con fondo macizo y no con el halo que usan los rótulos de recinto porque
 * además de leerse tiene que INVITAR al toque — es el botón que abre el campo
 * para escribir la medida. El ancho se estima del largo del texto; en una cinta
 * de cinco caracteres el ojo no nota los milímetros de más.
 */
function Cinta({ w, texto, activa = false }: { w: Pared; texto: string; activa?: boolean }) {
  const h = 0.36;
  const ancho = texto.length * 0.16 + 0.22;
  const medio = (w.a + w.b) / 2;
  const x = w.o === 'v' ? w.c + w.e / 2 + 0.1 : medio - ancho / 2;
  const y = w.o === 'v' ? medio - h / 2 : w.c - w.e / 2 - 0.1 - h;
  return (
    <>
      <rect
        x={x} y={y} width={ancho} height={h} rx={0.06}
        fill={activa ? '#8a5a3b' : '#a8a29e'}
        stroke="#f8fafc" strokeWidth={0.03}
      />
      <text
        x={x + ancho / 2} y={y + h / 2}
        textAnchor="middle" dominantBaseline="central"
        fontSize={0.22} fill="#ffffff"
        style={{ ...SOLO_PLANO, pointerEvents: 'none' }}
      >
        {texto}
      </text>
    </>
  );
}

/**
 * UNA COTA DIBUJADA: la línea, sus dos marcas de punta y el número encima.
 *
 * La misma función pinta las tres cosas que son una cota en este editor —la
 * automática de cada pared, la que el usuario pone a mano y la vista previa
 * mientras la dibuja— y eso es a propósito: si el dibujo de la vista previa no
 * fuera EXACTAMENTE el de la cota final, uno apuntaría a una cosa y le quedaría
 * otra.
 *
 * Se dibuja como la dibuja un plano de verdad: línea fina, marcas perpendiculares
 * en las puntas (no flechas —a esta escala una flecha es una manchita—) y el
 * número leyéndose a lo largo, corrido hacia afuera para no tacharse con la
 * línea. El número va SIN unidad: en una planta con veinte cotas, veinte «m»
 * son ruido, y la unidad del editor es siempre el metro.
 *
 * El medio giro cuando la cota apunta a la izquierda o hacia arriba es lo que
 * hace que el número se LEA: sin eso, la cota de la pared de la izquierda sale
 * de cabeza.
 */
function Acote({ x1, y1, x2, y2, off = 0, tinta, tintaNumero, gruesa = false, onToque }: {
  x1: number; y1: number; x2: number; y2: number;
  /**
   * La separación perpendicular, en metros y con signo. Ver `Cota.off`.
   *
   * Con cero —el caso de TODAS las derivadas— el dibujo es exactamente el de
   * siempre: ni un testigo de más. Las automáticas no lo usan a propósito: una
   * cota de pared corre por el eje de su pared y ahí es donde se lee.
   */
  off?: number;
  tinta: string;
  /**
   * La tinta del NÚMERO, cuando tiene que ser otra que la de la línea. Existe
   * por una razón de dibujo, no de gusto: la línea de una cota automática corre
   * por el EJE de su pared, así que teñirla entera pinta la pared de arriba a
   * abajo y la hace ver tomada. El número flota al costado y no tiene ese
   * problema, así que es el que lleva la señal.
   */
  tintaNumero?: string;
  /** La cota tomada va más gruesa: es la única señal de que está seleccionada. */
  gruesa?: boolean;
  /**
   * Si la cota se puede TOCAR, qué hacer con el toque. Pasarlo monta una caja de
   * agarre invisible sobre el NÚMERO —no sobre la línea—, y eso es a propósito:
   * la línea de una cota automática corre por el eje de su pared, así que una
   * banda de agarre a lo largo le robaría el dedo a la pared misma. Daniel lo
   * pidió justamente sobre el número: «sería darle clic al número que dice dos
   * cuarenta y cinco y ahí pondría la cantidad que deseo».
   */
  onToque?: (e: React.PointerEvent) => void;
}) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  // El piso evita dividir por cero en el primer cuadro del gesto, cuando las
  // dos puntas son el mismo punto.
  const largo = Math.max(Math.hypot(dx, dy), 1e-6);
  const ux = dx / largo;
  const uy = dy / largo;
  // El perpendicular de la cota: su vector girado 90° —(ux, uy) pasa a (−uy, ux)—.
  // Sirve para las dos cosas, las marcas de punta y la separación.
  const nx = -uy;
  const ny = ux;
  // Las puntas de la LÍNEA DE COTA, que con separación ya no son los puntos
  // medidos. Todo lo que se dibuja —marcas, número, agarre— cuelga de acá; los
  // puntos medidos sólo los vuelven a tocar los testigos.
  const a1 = x1 + nx * off, b1 = y1 + ny * off;
  const a2 = x2 + nx * off, b2 = y2 + ny * off;
  const mx = (a1 + a2) / 2;
  const my = (b1 + b2) / 2;
  const grosor = gruesa ? 0.05 : 0.028;
  let ang = (Math.atan2(dy, dx) * 180) / Math.PI;
  if (ang > 90 || ang < -90) ang += 180;
  return (
    <>
      {/* Los TESTIGOS: las dos líneas finas que unen lo medido con la cota
          separada. Sin ellos una cota despegada es un número flotando al lado
          del plano, y el dibujo deja de decir qué mide.

          Arrancan con un hueco y terminan pasadas de largo, que es como las
          dibuja un plano de obra: el hueco separa el testigo de la esquina que
          señala —pegado se lee como si la pared siguiera—, y el sobrante hace
          ver que la línea de cota TERMINA ahí y no se quedó corta. */}
      {off !== 0 && ([[x1, y1], [x2, y2]] as const).map(([px, py], i) => {
        const s = Math.sign(off);
        return (
          <line
            key={`t${i}`}
            x1={px + nx * s * HUECO_TESTIGO} y1={py + ny * s * HUECO_TESTIGO}
            x2={px + nx * (off + s * PASADO_TESTIGO)}
            y2={py + ny * (off + s * PASADO_TESTIGO)}
            stroke={tinta} strokeWidth={grosor * 0.7} strokeLinecap="round"
          />
        );
      })}
      <line x1={a1} y1={b1} x2={a2} y2={b2}
            stroke={tinta} strokeWidth={grosor} strokeLinecap="round" />
      {/* Las marcas de punta, sobre la línea de cota. */}
      {([[a1, b1], [a2, b2]] as const).map(([px, py], i) => (
        <line
          key={i}
          x1={px - uy * MARCA_COTA} y1={py + ux * MARCA_COTA}
          x2={px + uy * MARCA_COTA} y2={py - ux * MARCA_COTA}
          stroke={tinta} strokeWidth={grosor} strokeLinecap="round"
        />
      ))}
      <text
        x={mx} y={my} dy={-0.09}
        transform={`rotate(${ang} ${mx} ${my})`}
        textAnchor="middle" fontSize={0.22} fontWeight={600}
        paintOrder="stroke" stroke="#f8fafc" strokeWidth={0.11}
        strokeLinejoin="round" fill={tintaNumero ?? tinta}
        style={{ ...SOLO_PLANO, userSelect: 'none' }}
      >
        {m2(largo)}
      </text>
      {/* La caja de agarre del número, en el mismo marco girado que él para que
          siga al texto cuando la cota está de pie. Alta como el renglón y
          corrida hacia afuera: así tapa el número y casi nada de la pared que
          mide, que es quien le disputa el dedo. */}
      {onToque && (() => {
        const ancho = m2(largo).length * 0.16 + 0.16;
        return (
          <rect
            x={mx - ancho / 2} y={my - 0.33} width={ancho} height={0.32}
            transform={`rotate(${ang} ${mx} ${my})`}
            fill="transparent"
            className={`${AGARRE} cursor-pointer`}
            style={{ pointerEvents: 'auto' }}
            onPointerDown={onToque}
          />
        );
      })()}
    </>
  );
}

/** Media marca de punta de cota, en metros. */
const MARCA_COTA = 0.11;

/**
 * El lado de la lupa en píxeles y cuánto amplía. Los tres números salen de
 * Láminas SIN TOCAR —ya se probaron en el teléfono de Daniel— y una lupa de otro
 * tamaño en la otra pantalla del mismo producto sería una diferencia sin motivo.
 *
 * Son DOS tallas porque 132 px es una cosa en una compu y otra en un celular: a
 * 390 px de ancho se come más de un tercio del lienzo y tapa media paleta. A
 * 104 px sigue mostrando ~35 px de plano a ×3, que alcanza de sobra para
 * apuntar a una esquina.
 *
 * Ver el bloque de la lupa al final del lienzo.
 */
const LUPA_PX = 132;
const LUPA_PX_ANGOSTA = 104;
/** Desde dónde el lienzo deja de ser un celular. Es el `sm` de Tailwind. */
const LIENZO_ANGOSTO = 640;
const LUPA_ZOOM = 3;

/** El `id` del `<g>` que envuelve el dibujo, para que la lupa lo clone. */
const ESCENA = 'escena-planta';

/** El hueco entre lo medido y el arranque de su testigo, en metros. */
const HUECO_TESTIGO = 0.05;

/** Cuánto pasa el testigo de largo tras cruzar la línea de cota, en metros. */
const PASADO_TESTIGO = 0.09;

/**
 * Clase de los iconos de herramienta, compartida por las dos caras.
 *
 * Las variantes `[.franja_&]` son el rediseño que pidió Daniel el 18-oct-2026
 * mirando la app de referencia: dentro de la franja de abajo el botón es
 * **ícono pelado con su palabra debajo**, sin el recuadro que lo encerraba.
 * Afuera de la franja (la paleta flotante de la compu) no cambia nada.
 *
 * El recuadro existió un día: la primera pasada le puso `box-content` + borde +
 * padding al propio SVG para imitar la tarjeta. Daniel lo vio y lo descartó —«sin
 * los recuadros que encierran el ícono»— así que ahora el SVG sólo crece a 22 px,
 * que es lo que sostiene la lectura cuando no hay marco que dé presencia.
 *
 * Sin caja, el apretón no se puede pintar de fondo: el feedback es el ícono y el
 * rótulo poniéndose negros. `[&:active>svg]` va escrito entero y no apilando
 * variantes, porque `active:[&>svg]` compila a `>svg:active` —un SVG apretado,
 * que no existe—. Medido en el CSS servido.
 */
const HERRAMIENTA = 'flex h-8 w-8 items-center justify-center rounded text-stone-700 '
  + 'hover:bg-stone-100 active:bg-stone-200 disabled:text-stone-300 disabled:hover:bg-transparent '
  + '[.franja_&]:h-auto [.franja_&]:w-[54px] [.franja_&]:shrink-0 [.franja_&]:flex-col '
  // Redondeado y no `rounded-none`: sin caja, lo único que pinta un fondo es el
  // estado HUNDIDO —el método elegido, el candado puesto—, y un fondo a toda la
  // tarjeta con esquinas vivas vuelve a leerse como el recuadro que Daniel sacó.
  // Apagado no se ve nada, porque no hay fondo que redondear.
  + '[.franja_&]:gap-1 [.franja_&]:rounded-[10px] [.franja_&]:py-0.5 '
  + '[.franja_&]:hover:bg-transparent [.franja_&]:active:bg-transparent '
  + '[.franja_&]:active:text-stone-900 [.franja_&>svg]:h-[22px] [.franja_&>svg]:w-[22px] '
  // El RÓTULO de la tarjeta sale de `data-rotulo` por `content: attr(...)`, no
  // de un `<span>` en cada botón: son 45 botones repartidos en siete paletas y
  // la mitad arma su `aria-label` en vivo. Un atributo por botón es una línea
  // mecánica; un hijo nuevo es reescribirle el cuerpo a cada uno.
  //
  // Un botón sin `data-rotulo` no pinta nada —`attr()` de un atributo ausente
  // es cadena vacía—, así que la tarjeta queda sólo con el ícono en vez de
  // romperse. Por eso vale la pena que el atributo falte antes que mentir.
  + '[.franja_&]:after:content-[attr(data-rotulo)] [.franja_&]:after:text-center '
  + '[.franja_&]:after:text-[10px] [.franja_&]:after:leading-[1.15] '
  // La palabra HEREDA el color del botón y se baja a 70 %, en vez de llevar un
  // gris clavado. El gris clavado se veía bien apagado y pésimo hundido: con el
  // método elegido el botón se pinta verde y el ícono se vuelve blanco, pero la
  // palabra se quedaba en `stone-500` —gris sobre verde, medido—. Heredar
  // también borró las dos reglas de `:active` y `:disabled` del rótulo: ahora
  // las hace el color del botón, que es de donde salían.
  + '[.franja_&]:after:font-normal [.franja_&]:after:opacity-70';

/**
 * La fila de acciones de una paleta. En la franja se vuelve la fila que CORRE
 * en horizontal —propuesta A— con la última tarjeta cortada a propósito: ese
 * recorte es la única pista de que hay más a la derecha.
 *
 * La barra de desplazamiento se esconde porque en un teléfono no se usa (se
 * arrastra con el dedo) y en el escritorio angosto robaba 8 px de alto a una
 * banda que ya es baja.
 */
const FILA_ACCIONES = 'flex items-center gap-0.5 p-1 '
  + '[.franja_&]:flex-nowrap [.franja_&]:gap-2 [.franja_&]:overflow-x-auto '
  + '[.franja_&]:px-2.5 [.franja_&]:py-2.5 [.franja_&]:[scrollbar-width:none] '
  + '[.franja_&]:[&::-webkit-scrollbar]:hidden';

/**
 * Las cajas de la barra, medidas sobre el dibujo que mandó Daniel el
 * 6-oct-2026: botones cuadrados de esquina blanda, nada de pastillas con texto.
 *
 * Los números salen de la proporción de la maqueta contra el ancho de un
 * teléfono (554 px de lámina ≈ 390 px de pantalla): la caja grande da 41 px y la
 * chica 31, y el radio es el 24 % del lado. Se redondea la grande a 44 porque es
 * el blanco mínimo de un dedo, y la chica a 36.
 *
 * - `CAJA` — las cuatro capas de abajo y el botón de la referencia.
 * - `CAJA_CHICA` — cerrar, guardar y exportar, que son de cabecera y se tocan
 *   una vez por sesión, no veinte veces por minuto.
 */
const CAJA = 'relative flex h-11 w-11 items-center justify-center rounded-[10px] border';
const CAJA_CHICA = 'flex h-9 w-9 items-center justify-center rounded-[10px] border '
  + 'border-stone-300 bg-white text-stone-700 disabled:text-stone-300';

/**
 * Las cuatro capas de la barra de abajo, que son el control de MÁS uso del
 * editor y por eso no comparten medida con el resto.
 *
 * 44 px era el mínimo, no el tamaño: es el piso de Apple (HIG, «Hit targets»)
 * y el nivel AAA de WCAG 2.2 «Target Size». Material pide 48 dp como mínimo
 * cómodo, y las tres guías coinciden en que un control que se toca decenas de
 * veces por sesión va POR ENCIMA del piso, no justo encima. 52 px con 24 de
 * ícono deja el blanco holgado sin comerse el lienzo: cuatro cajas con su aire
 * son 232 px de los 390 de un teléfono, así que siguen entrando centradas con
 * deshacer y rehacer a un lado.
 *
 * Pedido de Daniel el 7-oct-2026 mirando el editor en el celular: «que sean un
 * poco más grandes y más centrados, ya que es lo de más uso».
 *
 * Bajaron a 46 px el 19-oct-2026, con el recorte del 17 Pro Max: «hacer más
 * angosta la barra ya que sobra espacio a la derecha y subirla un poco». Los 6
 * px que suelta cada caja salen del aire sobrante, no del área tocable, y 46
 * sigue por encima del piso de 44 de las tres guías. El ícono se queda en 24.
 */
const CAJA_BARRA = 'relative flex h-[46px] w-[46px] items-center justify-center '
  + 'rounded-[12px] border';

/**
 * El punto de estado de cada capa, arriba a la izquierda de su botón.
 *
 * Es lo que reemplazó al ojo: el ojo era un segundo botón pegado al primero y
 * comía la mitad del ancho. El punto NO se toca —es un indicador—; lo que lo
 * cambia es el doble toque sobre el ícono, que es el gesto que pidió Daniel.
 * Verde = esa capa se ve sobre el plano; rojo = está apagada.
 */
const VERDE = '#5ab552';
const ROJO = '#e5484d';

/**
 * La tinta de las cotas.
 *
 * La cota NO es ninguna de las tres cosas que ya hablan en el dibujo —pared,
 * área, objeto—, así que tiene su propia: el gris pizarra del concreto de la
 * paleta de materiales. Pintarla del marrón de las paredes sería lo peor
 * posible, porque la cota se lee ENCIMA de las paredes y hay que poder
 * distinguir la línea que mide de la que construye.
 */
const TINTA_COTA = '#6b747c';

/**
 * La tinta de la LUPA: su aro, su cruz y el acuse del imán.
 *
 * Es el mismo `COLOR_MEDIDA` del visor de Láminas, y eso es el punto. La lupa
 * nació acá usando `TINTA_COTA`, que es un gris de plano —`#6b747c`—, y Daniel
 * la cazó mirándola en el iPhone el 22-oct-2026: «esa cruz la estoy viendo de
 * color gris; que sea del mismo color que se dejó en láminas, para que uno la
 * pueda identificar y no se pierda entre todo lo demás».
 *
 * Tiene razón en lo de fondo: el plano entero está dibujado en grises y cafés,
 * así que un gris más no se lee como instrumento, se lee como una línea más del
 * dibujo. El fucsia no existe en la paleta de materiales A PROPÓSITO — no es un
 * color de obra, es el color de «esto no es el plano, esto es dónde apuntás».
 */
const TINTA_LUPA = '#C2185B';

/**
 * Lo que miden las dos barras de botones, en píxeles.
 *
 * Desde el 8-oct-2026 las dos FLOTAN sobre el lienzo en vez de ser filas, así
 * que el lienzo corre por debajo de ellas y sus coordenadas ya no las
 * descuentan solas: lo que se planta «abajo del todo» cae tapado por las capas
 * y lo que nace «arriba del todo» cae tapado por la pastilla. Estos dos números
 * son lo que hay que devolverles.
 *
 * Salen de las clases, no del ojo: arriba una fila de 44 px (`h-11`) más su
 * `py-2`; abajo una caja de capa de 52 px (`CAJA_BARRA`) más su `py-2`.
 *
 * `ALTO_BARRA_PIE` es solo el arranque: el alto de verdad se mide en vivo
 * (`altoPie`), porque cada aviso hace crecer la barra hacia arriba.
 */
const ALTO_BARRA_SUP = 60;
const ALTO_BARRA_PIE = 68;

/**
 * Si la pantalla es ANGOSTA, que es lo único que decide dónde vive la paleta.
 *
 * Decisión de Daniel (18-oct-2026): «que en la compu siga siendo flotante». Así
 * que la franja clavada abajo —la gráfica de la app de referencia— es sólo del
 * teléfono, y en el escritorio la paleta sigue naciendo junto al elemento y
 * arrastrable, que es el pet palette de Archicad con el que él ya trabaja.
 *
 * El corte es por ANCHO y no por `pointer: coarse`: una tableta con teclado y
 * un portátil con pantalla táctil son los dos «coarse» y los dos tienen sitio
 * de sobra para la paleta junto al dibujo. 768 px es el `md` de Tailwind, el
 * mismo umbral que ya usa el resto de la app.
 */
function useAngosta() {
  const consulta = '(max-width: 767px)';
  const [angosta, setAngosta] = useState(
    () => typeof matchMedia === 'function' && matchMedia(consulta).matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(consulta);
    const mirar = () => setAngosta(mq.matches);
    mirar();
    mq.addEventListener('change', mirar);
    return () => mq.removeEventListener('change', mirar);
  }, []);
  return angosta;
}

/**
 * Cuánto dura la franja al entrar y al salir, en milisegundos.
 *
 * Medido cuadro por cuadro sobre el video que mandó Daniel, a 6 fps: la banda
 * tarda unos dos cuadros en entrar y algo menos en irse. La curva es la de
 * «sale rápido y frena suave» —`cubic-bezier(.22,1,.36,1)`—, que es lo que hace
 * que crezca como si la empujaran desde abajo y no como si se estirara.
 */
const FRANJA_ENTRA = 280;
const FRANJA_SALE = 200;

/**
 * La franja de abajo: la paleta contextual clavada ARRIBA de la barra de capas.
 *
 * Es la gráfica de la app que mandó Daniel el 16-oct-2026. Lo que la define no
 * es que esté abajo, son tres cosas que se midieron en el video:
 *
 * 1. El borde de ABAJO de la banda está clavado justo encima de la barra, y la
 *    banda crece en alto RECORTADA por abajo (`max-height` + `overflow:hidden`).
 *    Por eso la cabecera aparece primero, bien abajo, y el cuerpo la empuja
 *    hacia arriba: no es un panel que se desliza entero, es uno que se abre.
 * 2. La barra de capas se pinta POR ENCIMA de la banda que se desvanece, así
 *    que la banda parece salir de DETRÁS de ella. Acá sale gratis: la barra va
 *    después en el árbol y las dos están en `z-10`, así que gana la barra.
 * 3. La banda y la barra son UN solo sitio con dos pisos. Nunca hay dos bandas:
 *    abrir una capa cierra el creador de las otras, que ya era la regla.
 *
 * Al cerrarse NO se desmonta de golpe: `ultimo` guarda los hijos que había y
 * los sigue pintando mientras la banda se encoge, porque si no la salida es un
 * corte seco y Daniel pidió la animación «al salir y al esconderse». Es el ref
 * el que lo hace posible: esta cáscara está SIEMPRE montada, así que vuelve a
 * pintar aunque sus hijos se hayan vuelto nada.
 *
 * En pantalla ancha no existe: devuelve a sus hijos pelados y cada `Flotante`
 * se planta junto a su elemento como siempre.
 */
function Franja({ angosta, ranura, children }: {
  angosta: boolean;
  /**
   * El hueco DENTRO del pill de la barra, arriba de las capas.
   *
   * Es un portal y no un `absolute` apoyado en el alto de la barra, por el
   * pedido de Daniel del 18-oct-2026: «falta envolver toda la franja de iconos
   * de abajo en el pill, como en la maqueta». Dos cajas apoyadas una contra
   * otra no son un pill: entre ellas se cuela el lienzo, y cuando aparece un
   * aviso —«Pilas quedó abierto al exterior»— se mete en el medio y las parte.
   * El portal las vuelve UNA caja de verdad, con la franja arriba y las cuatro
   * capas abajo, sin mover de sitio las 13 paletas en el árbol (que en la compu
   * siguen naciendo junto a su elemento).
   */
  ranura: HTMLElement | null;
  children: ReactNode;
}) {
  const hijos = Children.toArray(children);
  const hay = hijos.length > 0;
  // Los últimos hijos con algo adentro, para poder pintarlos mientras se cierra.
  const ultimo = useRef<ReactNode>(null);
  if (hay) ultimo.current = children;

  // Entra en el repintado SIGUIENTE al montaje. En el mismo no sirve: el
  // navegador necesita ver el estado cerrado una vez para tener de dónde
  // animar, y poner `max-height` final en el primer pintado no transiciona.
  const [abierta, setAbierta] = useState(false);
  useEffect(() => {
    if (!hay) { setAbierta(false); return; }
    const t = requestAnimationFrame(() => setAbierta(true));
    return () => cancelAnimationFrame(t);
  }, [hay]);

  // Lo que quedó pintado se tira recién cuando terminó de encogerse.
  const [rastro, setRastro] = useState(false);
  useEffect(() => {
    if (hay) { setRastro(true); return; }
    const t = setTimeout(() => setRastro(false), FRANJA_SALE);
    return () => clearTimeout(t);
  }, [hay]);

  if (!angosta) return <>{children}</>;
  if (!hay && !rastro) return null;
  if (!ranura) return null;

  // El portal al pill de la barra. La caja del pill ya pone el borde, el fondo
  // y el redondeo —es UNA sola pastilla—, así que acá no van: lo único propio es
  // la raya que separa los dos pisos y la animación de abrirse.
  // `overflow-y-auto` y ya no `overflow-hidden`: el recorte es el que dibuja la
  // banda abriéndose, pero sin salida convierte el tope en una jaula. Daniel lo
  // reportó el 29-oct-2026 sobre el reemplazo de un objeto —«está encerrado el
  // tamaño y no se puede navegar»—: el catálogo de Sala pide más de 300 px
  // entre la cabecera, las dos filas de tabs y la rejilla, y la segunda fila de
  // piezas quedaba cortada contra la barra de capas sin forma de llegar a ella.
  // Con `auto` el recorte se ve igual mientras la banda crece —el contenido
  // está arriba y `scrollTop` arranca en 0— y cuando el contenido pasa el tope,
  // se recorre. En x se mantiene oculto: la paleta mide 16,5 rem fijas.
  return createPortal(
    <div
      data-paleta
      data-franja={abierta ? 'abierta' : 'cerrada'}
      className="franja overflow-y-auto overflow-x-hidden border-stone-200"
      style={{
        // El tope dejó de ser un número suelto. 300 px eran el 36 % de un
        // teléfono de 844 y el 31 % de uno de 956: cuanto más grande la
        // pantalla, más chico el panel, que es justo al revés de lo que uno
        // espera. En vh crece con el aparato y sigue dejando más de la mitad
        // del plano a la vista.
        maxHeight: abierta ? '42vh' : 0,
        opacity: abierta ? 1 : 0,
        transform: abierta ? 'none' : 'translateY(16px)',
        // La raya entre la franja y las capas se apaga junto con la caja: con la
        // banda en alto 0 un borde de 1 px deja una línea suelta sobre la barra.
        borderBottomWidth: abierta ? 1 : 0,
        transition: `max-height ${abierta ? FRANJA_ENTRA : FRANJA_SALE}ms cubic-bezier(.22,1,.36,1),`
          + ` transform ${abierta ? FRANJA_ENTRA : FRANJA_SALE}ms cubic-bezier(.22,1,.36,1),`
          + ` opacity ${Math.round((abierta ? FRANJA_ENTRA : FRANJA_SALE) * 0.6)}ms linear`,
      }}
    >
      {hay ? children : ultimo.current}
    </div>,
    ranura,
  );
}

/**
 * La cáscara flotante, en píxeles del contenedor del lienzo.
 *
 * Copia deliberada del tracker + pet palette de Archicad, que es con lo que
 * Daniel ya trabaja: la cota se escribe encima del dibujo y las acciones sobre
 * lo seleccionado viven ahí mismo, junto a la mano, en vez de en una barra al
 * otro extremo de la pantalla.
 *
 * El `data-paleta` lo lee el cierre por toque afuera: lo que caiga adentro de
 * esta caja no suelta la pared.
 */
function Flotante({ x, y, corrimiento, onCorrer, children }: {
  x: number;
  y: number;
  /** Lo que el usuario la corrió a mano, en píxeles, o null si está donde cae. */
  corrimiento: { dx: number; dy: number } | null;
  onCorrer: (c: { dx: number; dy: number }) => void;
  children: ReactNode;
}) {
  // Dónde se apoyó el dedo y cuánto estaba corrida la paleta en ese momento,
  // para que un segundo arrastre siga desde donde quedó en vez de saltar al
  // origen. `id` es el puntero, que hace falta para capturarlo tarde (ver abajo).
  const baseRef = useRef<{ px: number; py: number; dx0: number; dy0: number; id: number } | null>(null);
  /** Si el gesto en curso ya pasó el umbral y por lo tanto es un arrastre. */
  const movioRef = useRef(false);
  const angosta = useAngosta();

  /**
   * La paleta nace INERTE y despierta un instante después.
   *
   * Un toque que la hace aparecer dispara, al soltar, los eventos de ratón de
   * compatibilidad del navegador —el «click fantasma»—. Y como la paleta se
   * ancla a lo que acabás de tocar, aparece justo BAJO el dedo: el click
   * fantasma caía en el botón que quedó debajo. Medido: el segundo toque que
   * cerraba una pared nueva la creaba y la borraba sola, porque el bote de
   * basura de su paleta nacía exactamente ahí, y el editor no acusaba nada.
   * (Cancelar el `pointerdown` NO alcanza: Chromium igual manda el click.)
   *
   * Se rearma con cada reanclaje —`x`/`y`— porque el salto de una pared a otra
   * tiene el mismo problema. 200 ms: menos de lo que tarda alguien en ver la
   * paleta y apuntarle, más de lo que tarda el fantasma en llegar.
   */
  const [despierta, setDespierta] = useState(false);
  useEffect(() => {
    setDespierta(false);
    const t = setTimeout(() => setDespierta(true), 200);
    return () => clearTimeout(t);
  }, [x, y]);

  /**
   * El arrastre arranca en CUALQUIER parte de la paleta, no sólo en la manija.
   *
   * Pedido de Daniel (4-oct-2026): «que pueda funcionar también dándole un tap
   * hold en cualquier parte de esa paleta y uno la arrastra, para no depender
   * siempre de tocar el ícono, que a veces se puede ver o ser pequeño».
   *
   * Lo que separa mover de tocar es un UMBRAL de recorrido, no un reloj: hasta
   * 6 px el gesto sigue siendo un toque y el botón de abajo recibe su click;
   * pasados los 6 px el gesto se vuelve arrastre, se captura el puntero y el
   * click que venga después se come `onClickCapture`. Preferido al hold con
   * reloj porque no hace esperar a nadie, y «apretar y mover» —que es como lo
   * describió— también cae de este lado del umbral.
   *
   * El campo de texto queda afuera a propósito: ahí el dedo tiene que poder
   * poner el cursor y arrastrar para seleccionar letras, que es el gesto nativo
   * del teclado. Desde el campo se mueve con la manija, como siempre.
   */
  const empezar = (e: React.PointerEvent) => {
    if ((e.target as Element).closest('input, select, textarea')) return;
    baseRef.current = {
      px: e.clientX, py: e.clientY,
      dx0: corrimiento?.dx ?? 0, dy0: corrimiento?.dy ?? 0,
      id: e.pointerId,
    };
    movioRef.current = false;
  };
  const mover = (e: React.PointerEvent) => {
    const b = baseRef.current;
    if (!b) return;
    const mx = e.clientX - b.px;
    const my = e.clientY - b.py;
    if (!movioRef.current) {
      if (Math.hypot(mx, my) < 6) return;
      movioRef.current = true;
      // Se captura TARDE —no en el apoyo— justo para que un toque normal sobre
      // un botón no quede secuestrado por el contenedor.
      try { e.currentTarget.setPointerCapture(b.id); } catch { /* el puntero ya se fue */ }
    }
    onCorrer({ dx: b.dx0 + mx, dy: b.dy0 + my });
  };
  const soltar = () => { baseRef.current = null; };

  /**
   * En el teléfono esta cáscara NO EXISTE: la paleta vive en la franja de
   * abajo, que es la que trae el sitio, el borde y la animación. Devolver los
   * hijos pelados es lo que deja las 13 llamadas a `Flotante` sin tocar —el
   * `x`/`y` y el corrimiento quedan calculados y sin usar, que es más barato
   * que partir cada rama en dos—.
   *
   * El `data-paleta` del cierre por toque afuera lo pone la franja, así que no
   * se pierde: ver `Franja`.
   */
  if (angosta) return <>{children}</>;

  return (
    <div
      data-paleta
      className="absolute z-10 touch-none rounded-lg border border-stone-300 bg-white shadow-lg"
      onPointerDown={empezar}
      onPointerMove={mover}
      onPointerUp={soltar}
      onPointerCancel={soltar}
      onClickCapture={(e) => {
        if (!movioRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        movioRef.current = false;
      }}
      style={{
        pointerEvents: despierta ? undefined : 'none',
        left: x + (corrimiento?.dx ?? 0),
        // El tope es para que la paleta no nazca debajo de la barra de arriba.
        // Eran 86 px cuando el lienzo empezaba DEBAJO de esa barra; desde que
        // la barra flota encima, el lienzo arranca más arriba y el mismo
        // despeje son 86 más lo que mide la barra. Puesta a mano NO se topa: si
        // el usuario la llevó ahí, manda él, y topar con la pila de arrastre
        // pegada hace que la paleta se trabe al volver a bajarla.
        top: corrimiento ? y + corrimiento.dy : Math.max(y, 86 + ALTO_BARRA_SUP),
        transform: 'translate(-50%, -108%)',
      }}
    >
      {/*
        La manija. Daniel la pidió porque la paleta tapa justo lo que se está
        mirando —«en algunos momentos estorba y no hay manera de manipularla»—.

        Ya no tiene gesto propio: arrastra el contenedor entero y los eventos de
        acá suben solos. Se queda igual porque sigue siendo la PISTA de que la
        paleta se mueve; sin ella, que toda la caja arrastre es un secreto.
      */}
      <div
        className="flex cursor-grab items-center justify-center rounded-t-lg
                   border-b border-stone-200 bg-stone-50 py-1 active:cursor-grabbing"
        title="Arrastrá desde cualquier parte para mover la paleta"
        aria-label="Mover la paleta"
      >
        <DotsSix size={14} weight="bold" className="text-stone-400" />
      </div>
      {children}
    </div>
  );
}

/**
 * La paleta de UNA pared: la medida arriba, las operaciones abajo.
 *
 * La medida se aplica SOLA. No hay botón de confirmar porque en el celular ese
 * botón es un toque de más en el único momento en que el dedo ya está ocupado
 * escribiendo, y el Enter del teclado físico no existe ahí. Se aplica cuando la
 * mano se detiene (`ESPERA_MS`), al salir del campo, al cerrar la paleta y con
 * Enter —que no espera—. El precio es que escribiendo «3,40» la pared pasa un
 * instante por 3 m si uno teclea despacio; es visible y reversible, mientras un
 * número que no se aplicó hasta apretar algo se pierde en silencio.
 *
 * `inputMode="decimal"` es lo que hace que el celular abra el teclado de números
 * —el tipo `number` también, pero rechaza la coma, y acá se escribe «3,40»—.
 * Acepta coma o punto; lo resuelve quien aplica.
 */
const ESPERA_MS = 400;

/**
 * El campo que se aplica solo. Lo comparten los tres campos de las paletas —el
 * largo de la pared, el ancho del vano y el nombre del área— porque las tres
 * reglas que lo hacen funcionar son las mismas y son todas resultado de algo que
 * se rompió: aplicar con reloj para no exigir un OK, mandar lo pendiente al
 * desmontar para que un número escrito y no confirmado no se pierda en silencio,
 * y no refrescar desde afuera mientras el campo tiene el foco.
 *
 * `espera` en `null` apaga el reloj: entonces se aplica sólo al salir del campo,
 * con Enter o al desmontar. Es lo que corresponde cuando aplicar cuesta —
 * renombrar un área vuelve a inundar la planta dos veces— y no se quiere pagar
 * eso por cada letra tecleada.
 */
function useCampoDiferido(
  valor: string,
  onAplicar: (v: string) => void,
  espera: number | null = ESPERA_MS,
) {
  const [texto, setTexto] = useState(valor);
  const campoRef = useRef<HTMLInputElement | null>(null);
  const relojRef = useRef<number | undefined>(undefined);
  const pendienteRef = useRef<string | null>(null);
  // La función de aplicar cambia en cada repintado (lleva atrapada la pared);
  // el ref es lo que deja al desmontaje usar la última, no la primera.
  const aplicarRef = useRef(onAplicar);
  aplicarRef.current = onAplicar;

  /** Manda lo que haya quedado escrito y sin aplicar. */
  const vaciar = useCallback(() => {
    window.clearTimeout(relojRef.current);
    if (pendienteRef.current == null) return;
    const v = pendienteRef.current;
    pendienteRef.current = null;
    aplicarRef.current(v);
  }, []);

  // Al desmontar —se soltó la pared, se tocó afuera— lo escrito NO se tira.
  useEffect(() => vaciar, [vaciar]);

  // El valor puede cambiar por fuera del campo (arrastrando la pared, al
  // partirla). Eso sí lo refresca, pero nunca encima de lo que se está
  // escribiendo: si el campo tiene el foco, manda la mano.
  useEffect(() => {
    if (document.activeElement !== campoRef.current) setTexto(valor);
  }, [valor]);

  const escribir = useCallback((v: string) => {
    setTexto(v);
    pendienteRef.current = v;
    window.clearTimeout(relojRef.current);
    if (espera != null) relojRef.current = window.setTimeout(vaciar, espera);
  }, [espera, vaciar]);

  return { texto, campoRef, escribir, vaciar };
}

function PaletaPared({ valor, onAplicar, onGirar, onDividir, onBorrar, onVano,
                      estado, onEstado }: {
  valor: string;
  onAplicar: (v: string) => void;
  onGirar: () => void;
  onDividir: () => void;
  onBorrar: () => void;
  onVano: (tipo: 'puerta' | 'ventana') => void;
  estado: Estado;
  onEstado: (e: Estado) => void;
}) {
  const { texto, campoRef, escribir, vaciar } = useCampoDiferido(valor, onAplicar);
  // Plegado por defecto, igual que el tipo de área: la mayoría de las veces que
  // uno toma una pared es para moverla o abrirle un hueco, no para cambiarle el
  // estado, y tres pastillas siempre a la vista le roban el ancho a la paleta.
  const [verEstado, setVerEstado] = useState(false);

  return (
    <>
      <div className="flex items-center gap-1 border-b border-stone-200 p-1">
        <ArrowsOutLineHorizontal size={14} weight="bold" className="ml-0.5 text-stone-400" />
        <input
          ref={campoRef}
          inputMode="decimal"
          value={texto}
          onChange={(e) => escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') vaciar(); }}
          className="w-16 rounded border border-stone-200 px-1.5 py-1 text-right text-sm tabular-nums
                     outline-none focus:border-[#8a5a3b]"
          aria-label="Largo de la pared en metros"
        />
        <span className="pr-1 text-xs text-stone-500">m</span>
      </div>
      {/* Las operaciones sobre la pared tomada. Acá se suman las que vengan. */}
      <div className={FILA_ACCIONES}>
        {/* ELIMINAR va primero, de izquierda a derecha, en TODAS las paletas.
            Pedido de Daniel el 18-oct-2026: «que sea el primero, ya que es la
            función más utilizada y está quedando haciendo scroll». La fila corre
            en horizontal en el teléfono, así que el último botón no es «el
            último»: es el que no se ve. De ahí en adelante el orden es por
            cantidad de uso, no por parentesco de funciones. */}
        <button type="button" onClick={onBorrar} className={HERRAMIENTA}
                aria-label="Eliminar" data-rotulo="Eliminar" title="Eliminar la pared">
          <Trash size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        {/* De lo que se le hace a una pared en una visita de fin de semana,
            abrirle un hueco es lo que más se pide. */}
        <button type="button" onClick={() => onVano('puerta')} className={HERRAMIENTA}
                aria-label="Puerta" data-rotulo="Puerta" title="Abrir una puerta en esta pared">
          <Door size={17} weight="bold" />
        </button>
        <button type="button" onClick={() => onVano('ventana')} className={HERRAMIENTA}
                aria-label="Ventana" data-rotulo="Ventana" title="Abrir una ventana en esta pared">
          <GridFour size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        <button type="button" onClick={onDividir} className={HERRAMIENTA}
                aria-label="Dividir" data-rotulo="Dividir" title="Dividir la pared donde toques">
          <Divide size={17} weight="bold" />
        </button>
        <button type="button" onClick={onGirar} className={HERRAMIENTA}
                aria-label="Girar" data-rotulo="Girar" title="Girar la pared 90° sobre su centro">
          <ArrowClockwise size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        {/* Eliminar y demoler NO son lo mismo, aunque el reorden los haya
            separado: eliminar saca la pared del modelo y no deja rastro;
            demoler la deja escrita, la dibuja en rojo en el estado actual y la
            esconde en el final. Una remodelación necesita las dos. */}
        <button
          type="button"
          onClick={() => setVerEstado((v) => !v)}
          data-estado-pared={verEstado ? 'abierto' : 'plegado'}
          aria-pressed={verEstado}
          aria-label="Estado de rehabilitación" data-rotulo="Estado"
          title={`${ROTULO_ESTADO[estado]} — ${AYUDA_ESTADO[estado]}`}
          className={`${HERRAMIENTA} ${verEstado ? 'bg-stone-100' : ''}`}
          style={{ color: TINTA_ESTADO[estado] ?? undefined }}
        >
          <Hammer size={17} weight="bold" />
        </button>
      </div>
      {verEstado && (
        <div className="flex items-center gap-2 border-t border-stone-200 p-1">
          <SelectorEstado estado={estado} onEstado={onEstado} />
        </div>
      )}
    </>
  );
}

/**
 * La paleta de VARIAS paredes. Cambia de cara, no sólo de botones: con dos
 * paredes tomadas no hay un largo que escribir, así que arriba va el recuento y
 * el total, y abajo sólo lo que tiene sentido en lote.
 */
function PaletaGrupo({
  cuantas, total, motivoUnir, motivoInterseccion, motivoAjustar,
  onUnir, onInterseccion, onAjustar, onGirar, onBorrar,
}: {
  cuantas: number;
  total: string;
  /** Por qué no se puede unir, o null si sí se puede. */
  motivoUnir: string | null;
  /** Por qué no se puede intersecar, o null si sí se puede. */
  motivoInterseccion: string | null;
  /** Por qué no se puede ajustar, o null si sí se puede. */
  motivoAjustar: string | null;
  onUnir: () => void;
  onInterseccion: () => void;
  onAjustar: () => void;
  onGirar: () => void;
  onBorrar: () => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-stone-200 px-2 py-1.5 text-xs">
        <span className="font-medium">{cuantas} paredes</span>
        <span className="tabular-nums text-stone-500">{total} m</span>
      </div>
      <div className={FILA_ACCIONES}>
        {/* Eliminar primero, igual que en el resto de las paletas. */}
        <button type="button" onClick={onBorrar} className={HERRAMIENTA}
                aria-label="Eliminar" data-rotulo="Eliminar" title={`Eliminar las ${cuantas} paredes`}>
          <Trash size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        <button type="button" onClick={onUnir} className={HERRAMIENTA}
                disabled={motivoUnir != null}
                aria-label="Unir" data-rotulo="Unir"
                title={motivoUnir ?? 'Unir las dos paredes en una'}>
          <Unite size={17} weight="bold" />
        </button>
        <button type="button" onClick={onInterseccion} className={HERRAMIENTA}
                disabled={motivoInterseccion != null}
                aria-label="Intersección" data-rotulo="Esquina"
                title={motivoInterseccion ?? 'Cerrar la esquina: llevar la punta de cada una al cruce'}>
          <VectorTwo size={17} weight="bold" />
        </button>
        <button type="button" onClick={onAjustar} className={HERRAMIENTA}
                disabled={motivoAjustar != null}
                aria-label="Ajustar" data-rotulo="Ajustar"
                title={motivoAjustar ?? 'Ajustar: estirar o recortar hasta la última pared tomada'}>
          <ArrowLineRight size={17} weight="bold" />
        </button>
        <button type="button" onClick={onGirar} className={HERRAMIENTA}
                aria-label="Girar" data-rotulo="Girar" title="Girar cada pared 90° sobre su centro">
          <ArrowClockwise size={17} weight="bold" />
        </button>
      </div>
    </>
  );
}

/**
 * La paleta del vano tomado. Es la paleta de la pared con otro contenido: el
 * ancho en vez del largo, y en vez de operaciones de geometría el tipo de
 * carpintería.
 *
 * Lo único que se puede medir es el ANCHO, y a propósito: lo que mueve el precio
 * de un modelo es el área, y un vano no cambia el área —la inundación lo tapa—.
 * Alto, antepecho, hoja y barrido no entran porque no cambiarían ningún número
 * de los que se cierra una venta, y cada campo que se agrega es un campo que el
 * papá de Daniel tiene que entender un sábado con el cliente al lado.
 *
 * Los dos botones de tipo son INTERRUPTORES: tocar el tipo que ya está puesto lo
 * devuelve a paso. Así el hueco pelado —que es como entra todo lo que importa el
 * PDF— se alcanza sin un tercer botón que habría que explicar.
 *
 * El martillo lleva el estado del VANO, que es distinto del de su pared: una
 * puerta nueva en un muro que se queda de pie es el gesto más común de una
 * remodelación, y sin esto habría que mentirlo marcando el muro entero.
 */
function PaletaVano({ ancho, tipo, onAncho, onTipo, onBorrar, estado, onEstado }: {
  ancho: string;
  tipo: TipoVano;
  onAncho: (v: string) => void;
  onTipo: (t: TipoVano) => void;
  onBorrar: () => void;
  estado: Estado;
  onEstado: (e: Estado) => void;
}) {
  const { texto, campoRef, escribir, vaciar } = useCampoDiferido(ancho, onAncho);
  const [verEstado, setVerEstado] = useState(false);
  const boton = (t: 'puerta' | 'ventana', icono: ReactNode) => (
    <button
      type="button"
      onClick={() => onTipo(tipo === t ? 'paso' : t)}
      className={`${HERRAMIENTA} ${tipo === t ? 'bg-stone-900 text-white' : ''}`}
      aria-label={NOMBRE_VANO[t]} data-rotulo={NOMBRE_VANO[t]}
      aria-pressed={tipo === t}
      title={tipo === t ? `Dejarlo como paso, sin ${t}` : `Convertirlo en ${t}`}
    >
      {icono}
    </button>
  );

  return (
    <>
      <div className="flex items-center gap-1 border-b border-stone-200 p-1">
        <ArrowsOutLineHorizontal size={14} weight="bold" className="ml-0.5 text-stone-400" />
        <input
          ref={campoRef}
          inputMode="decimal"
          value={texto}
          onChange={(e) => escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') vaciar(); }}
          className="w-16 rounded border border-stone-200 px-1.5 py-1 text-right text-sm tabular-nums
                     outline-none focus:border-[#8a5a3b]"
          aria-label={`Ancho de la ${NOMBRE_VANO[tipo].toLowerCase()} en metros`}
        />
        <span className="pr-1 text-xs text-stone-500">m</span>
      </div>
      <div className={FILA_ACCIONES}>
        {/* Eliminar primero. El rótulo decía «Cerrar» —que es lo que le pasa a
            la pared— y ahora dice lo que hace el botón: el matiz se quedó en el
            `title`. En la primera posición de TODAS las paletas va la misma
            palabra, porque es el pulgar el que busca sin leer. */}
        <button type="button" onClick={onBorrar} className={HERRAMIENTA}
                aria-label="Eliminar el vano" data-rotulo="Eliminar"
                title="Eliminar el vano: la pared vuelve a ser maciza">
          <Trash size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        {boton('puerta', <Door size={17} weight="bold" />)}
        {boton('ventana', <GridFour size={17} weight="bold" />)}
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        {/* Eliminar y demoler, otra vez, no son lo mismo: eliminarlo deja la
            pared maciza y sin memoria; marcarlo «nuevo» la deja maciza HOY y
            abierta después, que es justo lo que dice un plano de remodelación. */}
        <button
          type="button"
          onClick={() => setVerEstado((v) => !v)}
          data-estado-vano={verEstado ? 'abierto' : 'plegado'}
          aria-pressed={verEstado}
          aria-label="Estado de rehabilitación" data-rotulo="Estado"
          title={`${ROTULO_ESTADO[estado]} — ${AYUDA_ESTADO[estado]}`}
          className={`${HERRAMIENTA} ${verEstado ? 'bg-stone-100' : ''}`}
          style={{ color: TINTA_ESTADO[estado] ?? undefined }}
        >
          <Hammer size={17} weight="bold" />
        </button>
      </div>
      {verEstado && (
        <div className="flex items-center gap-2 border-t border-stone-200 p-1">
          <SelectorEstado estado={estado} onEstado={onEstado} />
        </div>
      )}
    </>
  );
}

/**
 * El botón que despliega el tipo de área, en la fila de abajo de la paleta.
 *
 * Pedido de Daniel el 8-oct-2026: la etiqueta pliega el selector de tipo para
 * que la paleta no compita consigo misma. Va en la misma fila que el candado y
 * la papelera porque es de la misma familia: cambia QUÉ es el área, no cuánto
 * mide.
 *
 * El ícono se pinta del color de la clase puesta, así que plegado igual dice en
 * qué columna está sumando esa área — es el mismo idioma de color que el
 * relleno del plano y la pastilla de la barra, ahora que son una sola tabla.
 */
function BotonTipo({ clase, abierto, onTocar }: {
  clase: Clase;
  abierto: boolean;
  onTocar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onTocar}
      data-tipo-area={abierto ? 'abierto' : 'plegado'}
      aria-pressed={abierto}
      aria-label="Tipo de área" data-rotulo="Tipo"
      title={`Tipo de área: ${ROTULO_CLASE[clase]}. Tocá para cambiarlo.`}
      // Perdió el `-ml-1` el 18-oct-2026: lo tenía por ser el primero de la
      // fila y ahora el primero es el basurero.
      className={`${HERRAMIENTA} ${abierto ? 'bg-stone-100' : ''}`}
      style={{ color: TINTA_CLASE[clase] }}
    >
      <Tag size={17} weight="bold" />
    </button>
  );
}

/**
 * El estado de rehabilitación, en tres pastillas pegadas.
 *
 * Mismo molde que `SelectorClase` y a propósito: son dos ejes del mismo objeto
 * —de qué lado suma y si va a existir— y leerlos con dos formas distintas
 * obligaría a aprender dos cosas donde hay una.
 *
 * Aparece en la paleta de la pared, del área, del cuarto y del mueble: es lo que
 * Daniel pidió el 10-oct-2026 como «cualquier elemento». Lo que cambia entre
 * ellos no es el control sino la consecuencia, y por eso la ayuda de cada
 * pastilla la escribe `AYUDA_ESTADO` una sola vez.
 */
function SelectorEstado({ estado, onEstado }: {
  estado: Estado;
  onEstado: (e: Estado) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded border border-stone-300">
      {ESTADOS.map((v) => (
        <button
          key={v}
          type="button"
          data-estado={v}
          aria-pressed={estado === v}
          aria-label={ROTULO_ESTADO[v]}
          onClick={() => onEstado(v)}
          className={`px-2 py-1 text-xs ${
            estado === v ? 'font-medium text-white' : 'text-stone-600'
          }`}
          // El existente no tiene color propio —ver `TINTA_ESTADO`—, así que su
          // pastilla encendida va en gris: tiene que verse elegida sin inventarle
          // al estado de reposo un color que el dibujo no usa.
          style={estado === v ? { background: TINTA_ESTADO[v] ?? '#78716c' } : undefined}
          title={AYUDA_ESTADO[v]}
        >
          {ROTULO_ESTADO[v]}
        </button>
      ))}
    </div>
  );
}

/**
 * La clase del área, en cuatro pastillas pegadas.
 *
 * Botones y no un desplegable: son pocas opciones fijas, y un `select` en un
 * celular abre una rueda nativa para elegir entre cuatro cosas. Acá se ve cuál
 * está puesta y se cambia en un toque.
 *
 * Los colores no son decoración: salen de `TINTA_CLASE`, que es la misma tabla
 * que pinta el área en el plano, así que la pastilla encendida dice de qué lado
 * suma con el color que ese lado ya tiene en el dibujo.
 *
 * Esto vale en la PALETA, que es una por vez y tiene el ancho entero: ahí las
 * cuatro pastillas son un toque y se ven de un vistazo. En la tabla de áreas no
 * valía —una por fila, apretadas a tres letras y a 11 px—, y Daniel lo mandó a
 * cambiar el 8-oct-2026: «quedan muy pequeños los botones». Esa se fue a
 * `MenuClase`. Por eso ya no existe la variante `chico` que las recortaba.
 */
function SelectorClase({ clase, onClase }: {
  clase: Clase;
  onClase: (c: Clase) => void;
}) {
  const base = 'px-2 py-1 text-xs';
  return (
    <div className="inline-flex overflow-hidden rounded border border-stone-300">
      {CLASES.map((v) => (
        <button
          key={v}
          type="button"
          data-clase={v}
          aria-pressed={clase === v}
          aria-label={ROTULO_CLASE[v]}
          onClick={() => onClase(v)}
          className={`${base} ${
            clase === v ? 'font-medium text-white' : 'text-stone-600'
          }`}
          style={clase === v ? { background: TINTA_CLASE[v] } : undefined}
          title={AYUDA_CLASE[v]}
        >
          {ROTULO_CLASE[v]}
        </button>
      ))}
    </div>
  );
}

/**
 * La clase del área, en un desplegable. El de la TABLA, uno por fila.
 *
 * Daniel el 8-oct-2026: «en esa parte que uno cambia para el tipo de área, tal
 * vez cambiar ese control a que sea diferente, tal vez puede ser como un
 * dropdown o algo así, para que no quede tan sucio con tantas opciones, aparte
 * de que quedan muy pequeños los botones».
 *
 * Las dos cosas que dijo son la misma cosa: cuatro pastillas por fila son
 * dieciséis blancos en una casa de cuatro áreas, y repetir las tres opciones que
 * NO están puestas en cada línea es ruido que no informa. El desplegable muestra
 * una sola cosa —la que está— y las otras tres aparecen cuando se piden.
 *
 * Y es `<select>` nativo a propósito, que es justo lo que `SelectorClase` evita.
 * Allá el argumento era que abrir una rueda del sistema para elegir entre cuatro
 * cosas es desproporcionado, y sigue siendo cierto con el ancho de la paleta. En
 * una fila de tabla el cálculo se da vuelta: esa rueda nativa da renglones del
 * alto de un dedo —lo contrario de los botones de 11 px que Daniel no podía
 * acertar— y no cuesta una línea de ancho por opción.
 *
 * El texto va del color de su clase, igual que el metraje de la fila: el control
 * sigue diciendo de qué lado suma sin leerlo.
 */
function MenuClase({ clase, onClase, nombre }: {
  clase: Clase;
  onClase: (c: Clase) => void;
  /** Sólo para el `aria-label`: con uno por fila, «Tipo de área» no alcanza. */
  nombre: string;
}) {
  return (
    <select
      value={clase}
      data-clase-fila={nombre}
      aria-label={`Tipo de área de ${nombre}`}
      title={AYUDA_CLASE[clase]}
      onChange={(e) => onClase(e.target.value as Clase)}
      className="w-[5.5rem] shrink-0 rounded border border-stone-300 bg-white px-1 py-1
                 text-xs font-medium"
      style={{ color: TINTA_CLASE[clase] }}
    >
      {CLASES.map((v) => (
        <option key={v} value={v}>{ROTULO_CLASE[v]}</option>
      ))}
    </select>
  );
}

/**
 * La paleta del área tomada: su nombre, escribible, y lo que mide.
 *
 * El nombre se aplica al salir del campo y no con reloj: renombrar vuelve a
 * medir la planta dos veces —hay que reescribir el reposo, que se indexa por
 * nombre— y pagar eso por cada letra sería un editor que se traba al escribir.
 */
function PaletaRecinto({
  nombre, area, delta, clase, unida, estado,
  onRenombrar, onClase, onDividir, onSeparar, onQuitar, onEstado,
}: {
  nombre: string;
  area: string;
  /** Lo que ganó o perdió contra el reposo, ya formateado, o null si está igual. */
  delta: string | null;
  clase: Clase;
  /** Si esta región ya es la unión de varias. */
  unida: boolean;
  estado: Estado;
  onRenombrar: (v: string) => void;
  onClase: (c: Clase) => void;
  onDividir: () => void;
  onSeparar: () => void;
  onQuitar: () => void;
  onEstado: (e: Estado) => void;
}) {
  const { texto, campoRef, escribir, vaciar } = useCampoDiferido(nombre, onRenombrar, null);
  const [verTipo, setVerTipo] = useState(false);
  const [verEstado, setVerEstado] = useState(false);

  return (
    <>
      <div className="flex items-center gap-1 border-b border-stone-200 p-1">
        <input
          ref={campoRef}
          value={texto}
          onChange={(e) => escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-44 rounded border border-stone-200 px-1.5 py-1 text-sm
                     outline-none focus:border-[#8a5a3b]"
          aria-label="Nombre del área"
        />
      </div>
      {/* La clase. Va en la paleta porque así lo pidió Daniel: «cuando uno toca
          un área, uno pueda definirle alguno de esos tipos». Es decisión del que
          vende —una cochera techada es área cerrada que no es área interna— y no
          algo que la geometría pueda deducir.

          Desde el 8-oct-2026 llega PLEGADA detrás del botón de la etiqueta, por
          pedido de Daniel: «que esa selección esté oculta y que exista un botón
          más con un símbolo de tag, para que no haya tanta información que
          compita». El tipo se pone una vez por área y la paleta se abre muchas
          veces para mover, medir y dividir, así que ocupaba la fila más visible
          con lo que menos se toca. */}
      {verTipo && (
        <div className="flex items-center gap-2 border-b border-stone-200 px-2 py-1">
          <SelectorClase clase={clase} onClase={onClase} />
        </div>
      )}
      {verEstado && (
        <div className="border-b border-stone-200 px-2 py-1">
          <SelectorEstado estado={estado} onEstado={onEstado} />
          {/* La trampa de esta pantalla, dicha en el único lugar donde se puede
              caer en ella: un cuarto marcado a demoler se va de la tabla, pero
              el espacio sigue encerrado porque sus muros siguen de pie. Se
              comporta como «quitar del cómputo», no como una pared tumbada. */}
          {estado === 'demoler' && (
            <p className="mt-1 max-w-[13rem] text-[11px] leading-tight text-stone-500">
              Sale de la tabla, pero las paredes siguen. Para tumbarlo de verdad,
              marcá sus paredes.
            </p>
          )}
        </div>
      )}
      {/* LA FILA DEL MEDIO ES SÓLO DATO, sin un botón adentro. Es la regla que
          fijó Daniel el 8-oct-2026 mirando esta misma paleta: «siempre abajo son
          los botones de acción, el intermedio la información y arriba es en caso
          de cambiar el nombre». Dividir vivía acá y rompía las tres franjas: la
          fila que se lee de un barrido para ver cuánto mide tenía un botón que
          cambia la planta pegado al número. */}
      <div className="flex items-center gap-2 px-2 py-1 text-xs">
        <span className="font-medium tabular-nums">{area} m²</span>
        {delta && <span className="tabular-nums text-[#8a5a3b]">{delta}</span>}
      </div>
      {/* Separar y quitar van en su propia fila, abajo: son las operaciones que
          cambian QUÉ es un cuarto, no cuánto mide, y mezclarlas con la cota
          hacía una paleta donde no se sabía qué tocaba qué.

          Unir NO está acá: se hace tomando las áreas —dedo apoyado sobre una
          para entrar en múltiple, toque en las demás— y el botón sale en la
          paleta de varias. Acá hubo un desplegable «Unir con…»; Daniel lo
          cambió por el gesto (4-oct-2026) y tiene razón: es el mismo que ya se
          usa para paredes, y un segundo idioma para la misma idea no se
          aprende. */}
      <div className={`${FILA_ACCIONES} border-t border-stone-200`}>
        {/* EL BASURERO Y NO LA X. Lo pidió Daniel con el ojo puesto en la
            congruencia: «esa X debería ser el basurero de borrar». La X se leía
            como cerrar la paleta —que es lo que hace en todas las demás
            pantallas— cuando lo que hace es sacar el cuarto del cómputo.
            Va primero, como en todas las paletas desde el 18-oct-2026.

            Y es LA EXCEPCIÓN de la palabra: acá dice «Excluir» y no «Eliminar»
            porque el verbo no cambió —un cuarto medido no se borra, lo dibujan
            las paredes y volvería a aparecer—. Rotularlo «Eliminar» prometería
            algo que el botón no hace. */}
        <button type="button" onClick={onQuitar} className={HERRAMIENTA}
                aria-label="Quitar el área del cómputo" data-rotulo="Excluir"
                title="Quitar del cómputo: deja de sumar en los dos lados"
                data-quitar-area="">
          <Trash size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        {/* Dividir queda PEGADO al basurero, que es como lo pidió Daniel —«el
            botón de dividir debería estar a la par de la X»—: son las dos cosas
            que le pasan al cuarto entero, partirlo o sacarlo de la cuenta. */}
        <button type="button" onClick={onDividir} className={HERRAMIENTA}
                aria-label="Dividir el área" data-rotulo="Dividir"
                data-dividir-area=""
                title="Dividir: trazá la línea que parte el área en dos">
          <Divide size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        <BotonTipo clase={clase} abierto={verTipo} onTocar={() => setVerTipo((v) => !v)} />
        {unida && (
          <button type="button" onClick={onSeparar}
                  className="shrink-0 rounded border border-stone-200 px-1.5 py-0.5 text-xs"
                  data-separar=""
                  title="Separar: cada parte vuelve a contar por su cuenta">
            Separar
          </button>
        )}
        {/* Demoler cierra la fila: quitar saca el cuarto de la cuenta SIEMPRE;
            demoler lo saca sólo del estado final y lo deja en el actual. */}
        <button
          type="button"
          onClick={() => setVerEstado((v) => !v)}
          data-estado-area={verEstado ? 'abierto' : 'plegado'}
          aria-pressed={verEstado}
          aria-label="Estado de rehabilitación" data-rotulo="Estado"
          title={`${ROTULO_ESTADO[estado]} — ${AYUDA_ESTADO[estado]}`}
          className={`${HERRAMIENTA} -mr-1 ${verEstado ? 'bg-stone-100' : ''}`}
          style={{ color: TINTA_ESTADO[estado] ?? undefined }}
        >
          <Hammer size={17} weight="bold" />
        </button>
      </div>
    </>
  );
}

/**
 * La paleta de AGREGAR un área: las dos maneras de levantar una, en iconos.
 *
 * Reemplaza a los botones «+ Área» y «✨ Varita» de la barra de abajo. Pedido de
 * Daniel (4-oct-2026): «a la hora de agregar un área me gustaría que salga una
 * paleta flotante y que vengan dos íconos: uno que represente hacer un
 * rectángulo y el otro que sea una varita; así se elimina el botón de varita y
 * se hace sobre la misma dinámica a la que ya se está acostumbrando el usuario».
 *
 * El fondo de eso es que la barra de abajo no escala: cada herramienta nueva es
 * un botón más en una fila que en un teléfono ya se sale de la pantalla. La
 * paleta flotante, en cambio, está donde está la mano y se arrastra.
 */
function PaletaHerramientas({ modo, onRectangulo, onVarita }: {
  modo: Modo | null;
  onRectangulo: () => void;
  onVarita: () => void;
}) {
  const pinta = (activo: boolean) => (activo
    ? `${HERRAMIENTA} bg-[#4f9d78] text-white hover:bg-[#4f9d78]`
    : HERRAMIENTA);
  return (
    <div className={FILA_ACCIONES}>
      <button type="button" onClick={onRectangulo} className={pinta(modo === 'libre')}
              aria-pressed={modo === 'libre'} aria-label="Dibujar un rectángulo" data-rotulo="Rectángulo"
              data-herramienta-rectangulo=""
              title="Rectángulo: dibujar a mano un área que las paredes no encierran">
        <RectangleDashed size={18} weight="bold" />
      </button>
      <button type="button" onClick={onVarita} className={pinta(modo === 'varita')}
              aria-pressed={modo === 'varita'} aria-label="Varita" data-rotulo="Varita"
              data-herramienta-varita=""
              title="Varita: tocar adentro de un cuarto y levantar el área que lo encierra">
        <MagicWand size={18} weight="bold" />
      </button>
      {/* DOS MÉTODOS Y NO TRES. El polígono irregular estuvo acá un día —lo
          pidió Daniel el 8-oct-2026 por la mañana— y lo sacó esa misma tarde,
          después de probarlo: «el botón de ese triángulo ahora pues es
          innecesario, ya que al final a la hora de crear las áreas siempre va a
          empezar por un cuadrado; ahí ya la diferencia la va a hacer el crear
          nuevos nodos».

          Tenía razón y el código le daba la razón desde antes: `cambiarNodo`
          convierte el área a polígono irregular SOLA la primera vez que se pide
          un nodo. O sea que el camino al irregular nunca fue este botón — era
          dibujar la caja y partirle un lado—. El botón sólo adelantaba una
          conversión que iba a pasar igual, y a cambio obligaba a elegir con qué
          forma se iba a terminar ANTES de haber dibujado nada.

          El modo `irregular` sigue vivo en el editor: lo usa esa conversión
          automática. Lo que se fue es la elección por adelantado. */}
      {/* Sin botón de cerrar a propósito. Pedido de Daniel (4-oct-2026): «el de
          salir de la paleta de control, para mí básicamente es darle clic en
          otro lado que no sea la paleta de control». El toque afuera lo cierra
          —lo monta el efecto de `herramientas` en el editor— igual que ya pasa
          con la pared tomada. */}
    </div>
  );
}

/**
 * La paleta de AGREGAR una pared: las tres maneras de levantarla, en iconos.
 *
 * Es la hermana de `PaletaHerramientas` y nació por pedido de Daniel el
 * 8-oct-2026, el mismo día que le sacó el triángulo a la de áreas: «aprovechando
 * que te estoy hablando de esto, con el tema de las paredes me gustaría poder
 * generar distintos métodos de agregar, así como para las áreas están ahora».
 *
 * Mismo molde a propósito —misma caja, mismo verde de encendido, misma esquina
 * de la pantalla, mismo cierre por toque afuera—: lo que se aprende agregando un
 * área se usa igual agregando una pared, que es justo lo que Daniel pidió cuando
 * nació la primera paleta («se hace sobre la misma dinámica a la que ya se está
 * acostumbrando el usuario»).
 *
 * El método NO es el modo del editor, y por eso entra por `metodo` y no por
 * `modo`: individual y seguidas son el mismo gesto de dos apoyos y sólo se
 * diferencian en qué pasa cuando la pared nace. Ver `MetodoPared`.
 */
function PaletaCrearPared({ metodo, onUna, onSeguidas, onRect, onVarita }: {
  metodo: MetodoPared;
  onUna: () => void;
  onSeguidas: () => void;
  onRect: () => void;
  onVarita: () => void;
}) {
  const pinta = (activo: boolean) => (activo
    ? `${HERRAMIENTA} bg-[#4f9d78] text-white hover:bg-[#4f9d78]`
    : HERRAMIENTA);
  return (
    <div className={FILA_ACCIONES}>
      <button type="button" onClick={onUna} className={pinta(metodo === 'una')}
              aria-pressed={metodo === 'una'} aria-label="Una pared" data-rotulo="Una"
              data-pared-una=""
              title="Una pared: se dibuja y el gesto se apaga">
        <LineSegment size={18} weight="bold" />
      </button>
      <button type="button" onClick={onSeguidas} className={pinta(metodo === 'seguidas')}
              aria-pressed={metodo === 'seguidas'} aria-label="Paredes seguidas" data-rotulo="Seguidas"
              data-pared-seguidas=""
              title="Seguidas: cada pared arranca donde terminó la anterior">
        <LineSegments size={18} weight="bold" />
      </button>
      {/* El rectángulo va TERCERO, entre las dos que se dibujan a mano y la que
          resuelve sola: dibuja con el mismo gesto de caja que ya tiene el área,
          pero lo que nace son las cuatro paredes.

          El ícono es el rectángulo LLENO y no el punteado de la paleta de
          áreas: son dos herramientas distintas en dos paletas distintas y el
          punteado ya significa «área» en este editor. */}
      <button type="button" onClick={onRect} className={pinta(metodo === 'rect')}
              aria-pressed={metodo === 'rect'} aria-label="Paredes en rectángulo" data-rotulo="Rectángulo"
              data-pared-rect=""
              title="Rectángulo: de esquina a esquina, nacen las cuatro paredes">
        <Rectangle size={18} weight="bold" />
      </button>
      <button type="button" onClick={onVarita} className={pinta(metodo === 'varita')}
              aria-pressed={metodo === 'varita'} aria-label="Varita de paredes" data-rotulo="Varita"
              data-pared-varita=""
              title="Varita: tocar un área y levantar una pared en cada uno de sus lados">
        <MagicWand size={18} weight="bold" />
      </button>
      {/* Sin botón de cerrar, igual que la de áreas: se cierra tocando afuera o
          volviendo a tocar el botón de la capa. */}
    </div>
  );
}

/**
 * La paleta de VARIAS áreas. Mismo molde que la de varias paredes: arriba el
 * recuento y el total, abajo sólo lo que tiene sentido en lote.
 *
 * El total se canta porque es el número por el que se unen: el que vende quiere
 * ver cuánto suman Sala y Comedor ANTES de decidir que son un solo espacio.
 */
function PaletaAreas({ cuantas, total, motivoUnir, onUnir, onQuitar }: {
  cuantas: number;
  total: string;
  /** Por qué no se pueden unir, o null si sí se puede. */
  motivoUnir: string | null;
  onUnir: () => void;
  onQuitar: () => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-stone-200 px-2 py-1.5 text-xs">
        <span className="font-medium">{cuantas} áreas</span>
        <span className="tabular-nums text-stone-500">{total} m²</span>
      </div>
      <div className={FILA_ACCIONES}>
        {/* Primero el destructivo, como en todas las paletas. Sigue diciendo
            «Excluir» y no «Eliminar» por lo mismo que en la del cuarto: estas
            áreas las dibujan las paredes y no se pueden borrar. */}
        <button type="button" onClick={onQuitar} className={HERRAMIENTA}
                aria-label="Quitar las áreas del cómputo" data-rotulo="Excluir"
                data-quitar-area=""
                title="Quitar del cómputo: dejan de sumar en los dos lados">
          <X size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        <button type="button" onClick={onUnir} className={HERRAMIENTA}
                disabled={motivoUnir != null}
                aria-label="Unir las áreas" data-rotulo="Unir"
                data-unir-areas=""
                title={motivoUnir ?? `Unir: las ${cuantas} cuentan como un solo cuarto`}>
          {/* UniteSquare y no Unite: unir ÁREAS y unir PAREDES son dos
              operaciones distintas y en la misma pantalla. Daniel eligió los
              dos cuadrados fundidos para las áreas (4-oct-2026) y los dos
              círculos quedan para las paredes, así ningún ícono dice dos cosas. */}
          <UniteSquare size={17} weight="bold" />
        </button>
      </div>
    </>
  );
}

/**
 * La paleta de un divisor: no tiene nada que medir ni que escribir, así que es
 * un solo botón.
 *
 * Existe porque sin ella un divisor queda atrapado: no es una pared, no se
 * selecciona como pared, y el único modo de sacarlo sería deshacer —que también
 * se lleva todo lo que vino después—.
 *
 * Y lleva estado por la misma razón que la pared y no que el mueble: un divisor
 * SEPARA, así que quitarlo de una vista funde los dos cuartos y mueve la tabla.
 * Es la única paleta de una línea en la que el martillo cambia un número.
 */
function PaletaDivisor({ estado, onBorrar, onEstado }: {
  estado: Estado;
  onBorrar: () => void;
  onEstado: (e: Estado) => void;
}) {
  const [verEstado, setVerEstado] = useState(false);
  return (
    <>
      <div className="flex items-center gap-2 p-1 pl-2 text-xs">
        <span className="text-stone-600">División</span>
        <button type="button" onClick={onBorrar} className={HERRAMIENTA}
                aria-label="Eliminar la división" data-rotulo="Eliminar"
                title="Eliminar la división: las dos partes vuelven a ser un área">
          <Trash size={17} weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => setVerEstado((v) => !v)}
          data-estado-divisor={verEstado ? 'abierto' : 'plegado'}
          aria-pressed={verEstado}
          aria-label="Estado de rehabilitación" data-rotulo="Estado"
          title={`${ROTULO_ESTADO[estado]} — ${AYUDA_ESTADO[estado]}`}
          className={`${HERRAMIENTA} ${verEstado ? 'bg-stone-100' : ''}`}
          style={{ color: TINTA_ESTADO[estado] ?? undefined }}
        >
          <Hammer size={17} weight="bold" />
        </button>
      </div>
      {verEstado && (
        <div className="border-t border-stone-200 px-2 py-1">
          <SelectorEstado estado={estado} onEstado={onEstado} />
        </div>
      )}
    </>
  );
}

/**
 * La paleta de una cota puesta a mano: su medida y el tachito.
 *
 * La medida va de SÓLO LECTURA, y es la diferencia de fondo con la cinta de la
 * pared —donde escribir el número estira la pared—. Acá sería al revés: la cota
 * no construye nada, mide lo que hay. Un campo escribible prometería mover la
 * casa para que el número calce, y lo que haría es dejar un rótulo que miente.
 *
 * Por eso esta paleta no sirve para las cotas AUTOMÁTICAS: esas no se pueden
 * borrar —se derivan de las paredes, volverían en el repintado siguiente—. Lo
 * que uno quiere hacerles es mover la pared, y ése es otro campo: ver
 * `PaletaCotaVecina`.
 */
function PaletaCota({ medida, onBorrar }: { medida: string; onBorrar: () => void }) {
  return (
    <div className="flex items-center gap-2 p-1 pl-2 text-xs">
      <Ruler size={15} weight="bold" color={TINTA_COTA} />
      <span className="font-medium tabular-nums">{medida} m</span>
      <button type="button" onClick={onBorrar} className={HERRAMIENTA}
              aria-label="Eliminar la cota" data-rotulo="Eliminar"
              data-borrar-cota=""
              title="Eliminar la cota">
        <Trash size={17} weight="bold" />
      </button>
    </div>
  );
}

/**
 * EL CAMPO DE UNA COTA VECINA: se escribe la medida de OTRA pared y se corre la
 * que está tomada.
 *
 * Es el pedido de Daniel del 6-oct-2026: «si tomo una pared y está conectada
 * con otras, que yo pueda darle clic a la medida de alguna de las otras paredes
 * y eso me abriría el number input y ahí pongo la medida que quiero y me
 * modifica el espacio […] para básicamente poder modificar espacios que estén
 * conectados sin tener que hacerlo a mano».
 *
 * La vuelta de tuerca está en quién se mueve: el número es de la pared VECINA,
 * pero quien viaja es la TOMADA —decisión de Daniel, «sí, que se mueva la
 * tomada»—. Y tiene sentido: la vecina se agarró por su cota sólo para decir
 * cuánto tiene que medir el espacio; la pared que uno eligió es la que espera
 * ver correrse. La punta lejana de la vecina queda clavada y el resto de la casa
 * no se entera.
 *
 * El rótulo dice en voz alta cuál se mueve. Sin eso el campo es ambiguo —el
 * número que se está editando y la pared que se desplaza son dos paredes
 * distintas— y eso en un editor es lo que hace que uno desconfíe del resultado.
 */
function PaletaCotaVecina({ valor, onAplicar, nota = 'corre la pared tomada' }: {
  valor: string;
  onAplicar: (v: string) => void;
  /** Qué mueve el número. Cambia con el dueño: una pared se corre, un lado se empuja. */
  nota?: string;
}) {
  const { texto, campoRef, escribir, vaciar } = useCampoDiferido(valor, onAplicar);
  return (
    <div className="p-1">
      <div className="flex items-center gap-1">
        <ArrowsOutLineHorizontal size={14} weight="bold" className="ml-0.5 text-stone-400" />
        <input
          ref={campoRef}
          inputMode="decimal"
          value={texto}
          onChange={(e) => escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') vaciar(); }}
          className="w-16 rounded border border-stone-200 px-1.5 py-1 text-right text-sm tabular-nums
                     outline-none focus:border-[#8a5a3b]"
          aria-label={`Medida en metros; ${nota}`}
        />
        <span className="pr-1 text-xs text-stone-500">m</span>
      </div>
      <p className="px-1 pt-0.5 text-[10px] leading-tight text-stone-500">{nota}</p>
    </div>
  );
}

/**
 * Los dos verbos sobre los NODOS de un área, en el orden en que van en la
 * paleta: primero el que agrega, después el que saca.
 *
 * Son los que reemplazaron a los tres botones de método el 8-oct-2026, por
 * pedido de Daniel: «que no exista ninguno de esos tres botones del cuadrado,
 * del polígono y del polígono irregular, ya que realmente con una sola se
 * podrían hacer todos. Lo que le agregaríamos es un botón de dividir […] y más
 * bien le agregaríamos otro botón más, que sería también el de borrar un punto
 * de anclaje que actualmente no existe».
 *
 * Los ÍCONOS no se repiten con nada, y eso es regla de la casa: la tijera es
 * partir un lado y no se podía usar el ÷ de `Divide`, que en la paleta del
 * recinto ya significa «partir el área en dos con una línea» —otra operación,
 * en la misma pantalla—.
 */
const NODOS: Array<{
  id: 'partir' | 'quitar';
  marca: string;
  rotulo: string;
  /** Lo mismo en una palabra, para la tarjeta de la franja de abajo. */
  corto: string;
  icono: React.ReactNode;
  ayuda: string;
}> = [
  {
    id: 'partir',
    marca: 'data-partir-nodo',
    rotulo: 'Dividir un lado',
    corto: 'Dividir',
    icono: <Scissors size={17} weight="bold" />,
    ayuda: 'Dividir: tocá un lado y ahí nace un nodo nuevo para modelar la forma',
  },
  {
    id: 'quitar',
    marca: 'data-quitar-nodo',
    rotulo: 'Borrar un nodo',
    corto: 'Nodo',
    icono: <MinusCircle size={17} weight="bold" />,
    ayuda: 'Borrar nodo: tocá el punto que quieras sacarle a la forma',
  },
];

/**
 * La paleta de un área libre: nombre, lo que mide, de qué tipo es y con qué
 * método está dibujada.
 *
 * Lleva campo de nombre y la de un divisor no, porque son cosas distintas: el
 * divisor sólo parte un área que ya tiene nombre, y esta ES el área. «Pilas» o
 * «Terraza» es justamente el dato por el que Daniel la pidió.
 *
 * Y lleva ancho y alto escribibles, no sólo agarres: es la misma doctrina del
 * largo de la pared —la medida se escribe, no todo se arrastra—. Una terraza de
 * 3,50 × 6,00 se pone en dos campos; estirarla con el dedo hasta clavar esos
 * números es pelear con la rejilla.
 *
 * No trae Dividir: un rectángulo dibujado a mano se parte dibujando el otro.
 *
 * EL ORDEN DE LAS FILAS es lo que Daniel reacomodó el 7-oct-2026, y la razón es
 * de lectura: «lo lógico es que ahí a la par lo que venga es el área total, y
 * que más bien el método de dibujo esté abajo a la par del candado y del botón
 * de borrar». Entonces la segunda fila es la fila de la MEDIDA —lo que mide de
 * ancho, lo que mide de alto, y cuánto da— y el método se fue abajo con el
 * candado y la papelera, que es donde viven los botones que cambian el área
 * entera y no un número. Antes el área total estaba al pie, lejos de los dos
 * campos que la producen, y el paso a polígono colgaba de la fila de medidas
 * como un invitado.
 *
 * Con FORMA —la que levantó la varita, o un polígono— los dos campos no van: no
 * hay un ancho ni un alto que escribir en una L, y mostrar los de su caja sería
 * mentir sobre el número que se está cobrando. En su lugar la fila dice qué
 * tiene, y el área total sigue a la derecha igual que en el rectángulo.
 */
function PaletaLibre({
  nombre, ancho, alto, area, clase, forma, vertices, fijo, nodo, estado,
  onRenombrar, onAncho, onAlto, onClase, onBorrar, onNodo, onFijar,
  onFrente, onAtras, onEstado,
}: {
  nombre: string;
  /** null = tiene forma propia y no hay medida que escribir. */
  ancho: string | null;
  alto: string | null;
  area: string;
  clase: Clase;
  /** Con cuál de los métodos está modelada esta área. */
  forma: 'rect' | 'celdas' | 'poli' | 'irregular';
  /** Cuántos vértices, sólo para los polígonos. */
  vertices: number;
  /** Bloqueada: ni se mueve, ni se redimensiona, ni se borra. */
  fijo: boolean;
  /** Qué modo de nodo está puesto, para hundir el botón que corresponde. */
  nodo: 'partir' | 'quitar' | null;
  estado: Estado;
  onRenombrar: (v: string) => void;
  onAncho: (v: string) => void;
  onAlto: (v: string) => void;
  onClase: (c: Clase) => void;
  onBorrar: () => void;
  onNodo: (m: 'partir' | 'quitar') => void;
  onFijar: () => void;
  /** Al frente del montón: se dibuja encima y recibe el dedo primero. */
  onFrente: () => void;
  /** Al fondo: deja de tapar a las que encierra. */
  onAtras: () => void;
  onEstado: (e: Estado) => void;
}) {
  const { texto, campoRef, escribir, vaciar } = useCampoDiferido(nombre, onRenombrar, null);
  const w = useCampoDiferido(ancho ?? '', onAncho);
  const h = useCampoDiferido(alto ?? '', onAlto);
  const [verTipo, setVerTipo] = useState(false);
  const [verEstado, setVerEstado] = useState(false);

  const MEDIDA = `w-14 rounded border border-stone-200 px-1.5 py-1 text-right text-sm
                  tabular-nums outline-none focus:border-[#4f9d78]`;

  /** El área total, que cierra la fila de la medida en los dos casos. */
  const total = <span className="ml-auto pl-1 text-xs font-semibold tabular-nums">{area} m²</span>;

  /** Qué forma tiene, cuando no hay medidas que escribir. */
  const dice = forma === 'celdas' ? 'Forma del plano'
    : forma === 'irregular' ? `Irregular · ${vertices} vértices`
      : forma === 'poli' ? `Polígono · ${vertices} vértices`
        : `${ancho} × ${alto} m`;

  return (
    <>
      <div className="flex items-center gap-1 border-b border-stone-200 p-1">
        <input
          ref={campoRef}
          value={texto}
          onChange={(e) => escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-44 rounded border border-stone-200 px-1.5 py-1 text-sm
                     outline-none focus:border-[#4f9d78]"
          aria-label="Nombre del área libre"
        />
      </div>
      {/* LA FILA DE LA MEDIDA. El × entre los dos campos es el que hace leer la
          fila como una medida y no como dos datos sueltos, y el área total al
          final es el resultado de esos dos números: se lee «3,50 × 6,00 m =
          21,00 m²» de un tirón. Bloqueada o con forma propia no hay campos, pero
          la fila sigue siendo la misma: a la izquierda qué es, a la derecha
          cuánto da. */}
      {fijo || forma !== 'rect' ? (
        <div className="flex items-center gap-2 border-b border-stone-200 px-2 py-1 text-xs">
          {fijo && <Lock size={14} weight="bold" className="text-stone-400" />}
          <span className="text-stone-600">{dice}</span>
          {total}
        </div>
      ) : (
        <div className="flex items-center gap-1 border-b border-stone-200 p-1">
          <input
            ref={w.campoRef}
            inputMode="decimal"
            value={w.texto}
            onChange={(e) => w.escribir(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={w.vaciar}
            onKeyDown={(e) => { if (e.key === 'Enter') w.vaciar(); }}
            className={MEDIDA}
            aria-label="Ancho del área en metros"
          />
          <span className="text-xs text-stone-400">×</span>
          <input
            ref={h.campoRef}
            inputMode="decimal"
            value={h.texto}
            onChange={(e) => h.escribir(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={h.vaciar}
            onKeyDown={(e) => { if (e.key === 'Enter') h.vaciar(); }}
            className={MEDIDA}
            aria-label="Alto del área en metros"
          />
          <span className="text-xs text-stone-500">m</span>
          {total}
        </div>
      )}
      {/* Nace externa —para eso se inventaron las libres— pero la clase la manda
          el que vende: un cuarto nuevo dibujado sobre la terraza SÍ es área
          interna, y esa es justamente la que se cobra. La frase de abajo bajó
          con ella: explica qué hace la clase elegida, así que va pegada al
          selector y no al pie de la paleta, donde quedaba leyéndose como si
          explicara el candado.

          Plegada detrás del botón de la etiqueta desde el 8-oct-2026, igual que
          en la paleta de región: el tipo se pone una vez y esta fila competía
          con las medidas, que son a lo que se vuelve. */}
      {verTipo && (
        <div className="border-b border-stone-200 px-2 py-1">
          <SelectorClase clase={clase} onClase={onClase} />
          <p className="pt-0.5 text-[10px] leading-tight text-stone-500">
            {clase === 'interna'
              ? 'suma al área interna'
              : `suma en ${ROTULO_CLASE[clase]}, aparte del total`}
          </p>
        </div>
      )}
      {verEstado && (
        <div className="border-b border-stone-200 px-2 py-1">
          <SelectorEstado estado={estado} onEstado={onEstado} />
        </div>
      )}
      <div className={FILA_ACCIONES}>
        {/* ELIMINAR PRIMERO (18-oct-2026). Estaba casi al final, y en una fila
            que corre en horizontal eso significaba deslizar para llegar a lo que
            más se usa. Bloqueada no va —el candado es justamente lo que lo
            apaga— y entonces la fila arranca con el tipo. */}
        {!fijo && (
          <>
            <button type="button" onClick={onBorrar} className={HERRAMIENTA}
                    aria-label="Eliminar el área" data-rotulo="Eliminar"
                    title="Eliminar el área">
              <Trash size={17} weight="bold" />
            </button>
            <span className="mx-0.5 h-5 w-px bg-stone-200" />
          </>
        )}
        <BotonTipo clase={clase} abierto={verTipo} onTocar={() => setVerTipo((v) => !v)} />
        {/* LOS DOS VERBOS DEL NODO, donde antes estaban los tres métodos.
            Pedido de Daniel (8-oct-2026), y el razonamiento es el suyo: con un
            polígono de vértices sueltos «con una sola se podrían hacer todos»,
            así que elegir método acá era una vitrina para algo que ya no es una
            decisión. Lo que sí hacía falta eran las dos operaciones que esa
            forma única necesita: meter un nodo y sacarlo.

            Los dos son MODOS y se hunden, porque el botón solo no alcanza:
            falta decir CUÁL lado o CUÁL nodo, y eso se toca en el plano. El
            hundido dice qué va a pasar con el próximo toque.

            Sobre un rectángulo o sobre la forma de la varita el primer toque
            pasa el área a polígono irregular (ver `cambiarNodo`) y recién
            después entra el modo: es lo que hace que un solo botón sirva para
            llegar a cualquier forma.

            Bloqueada no van, igual que antes la papelera y el método: agregar o
            sacar un nodo le cambia el área, que es exactamente lo que el candado
            no deja. Lo único que sigue vivo es el tipo, como lo decidió Daniel:
            «no se mueve ni se borra, sí se ve y se le cambia el tipo». */}
        {!fijo && NODOS.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onNodo(n.id)}
            {...{ [n.marca]: nodo === n.id ? 'puesto' : 'suelto' }}
            className={`${HERRAMIENTA} ${nodo === n.id
              ? 'bg-[#4f9d78] text-white hover:bg-[#4f9d78]' : ''}`}
            aria-pressed={nodo === n.id}
            aria-label={n.rotulo} data-rotulo={n.corto}
            title={n.ayuda}
          >
            {n.icono}
          </button>
        ))}
        {/* EL ORDEN DE PINTADO. Van los dos aunque esté bloqueada: cambiar el
            orden no le toca el área, que es lo único que el candado protege, y
            el caso que Daniel contó es justamente un lote —lo primero que uno
            bloquea— tapando a la casa entera.

            Flecha ARRIBA = al frente, flecha ABAJO = atrás. Es la convención de
            cualquier editor de dibujo, y la alternativa —dos íconos de pila—
            obliga a mirar cuál de los dos tiene el rectángulo relleno adelante. */}
        <button type="button" onClick={onFrente} className={HERRAMIENTA}
                data-area-frente=""
                aria-label="Traer el área al frente" data-rotulo="Al frente"
                title="Traerla al frente: se dibuja encima de las demás y recibe el dedo primero">
          <ArrowLineUp size={17} weight="bold" />
        </button>
        <button type="button" onClick={onAtras} className={HERRAMIENTA}
                data-area-atras=""
                aria-label="Enviar el área atrás" data-rotulo="Atrás"
                title="Mandarla atrás: deja de tapar a las demás">
          <ArrowLineDown size={17} weight="bold" />
        </button>
        {/* El candado ya no está pegado a la papelera: la papelera se fue al
            arranque de la fila con el reorden del 18-oct-2026. Lo que no cambió
            es que bloqueada la papelera NO SALE —un botón que nunca hace nada es
            peor que un botón ausente—, y eso sigue siendo la pista de que es
            este candado el que la apaga.

            Mismo par de íconos que el candado del mueble (Lock/LockOpen) y verbo
            DISTINTO: el del mueble es estado de pantalla y devuelve agarres; este
            es dato, se guarda con la planta, y además saca el área del alcance
            del dedo en el dibujo. Se eligió repetir el ícono porque lo que el
            usuario lee es lo mismo —«esto no se toca»— y la diferencia es de
            quién se acuerda, no de qué se ve. */}
        <button type="button" onClick={onFijar}
                className={`${HERRAMIENTA} ${fijo ? 'bg-stone-100' : ''}`}
                data-candado-area={fijo ? 'puesto' : 'suelto'}
                aria-pressed={fijo}
                aria-label={fijo ? 'Desbloquear el área' : 'Bloquear el área'}
                title={fijo
                  ? 'Bloqueada: no se mueve, no se redimensiona y no se borra. Tocá para soltarla.'
                  : 'Bloquearla: deja de moverse y de recibir el dedo en el plano'}>
          {fijo ? <Lock size={17} weight="bold" /> : <LockOpen size={17} weight="bold" />}
        </button>
        {/* ÚLTIMO, como en la paleta de pared y en la de región: el martillo
            está siempre en el mismo lugar de las tres y con el mismo gesto —se
            pliega—, que es lo que hace que no haya que buscarlo.

            Va aunque esté bloqueada, igual que el tipo: el candado protege la
            FORMA —«no se mueve ni se borra, sí se ve y se le cambia el tipo»— y
            decir que una terraza se demuele no le toca un centímetro. */}
        <button
          type="button"
          onClick={() => setVerEstado((v) => !v)}
          data-estado-libre={verEstado ? 'abierto' : 'plegado'}
          aria-pressed={verEstado}
          aria-label="Estado de rehabilitación" data-rotulo="Estado"
          title={`${ROTULO_ESTADO[estado]} — ${AYUDA_ESTADO[estado]}`}
          className={`${HERRAMIENTA} -mr-1 ${verEstado ? 'bg-stone-100' : ''}`}
          style={{ color: TINTA_ESTADO[estado] ?? undefined }}
        >
          <Hammer size={17} weight="bold" />
        </button>
      </div>
    </>
  );
}

/**
 * La paleta de un mueble: cómo se llama, sus dos medidas, girar y borrar.
 *
 * El nombre NO se escribe y ahí está la diferencia con el área libre: el mueble
 * es un módulo del catálogo —«Clóset», «Módulo de cocina»— y su nombre es el
 * tipo, no una etiqueta que el usuario ponga. Lo que el usuario cambia son las
 * medidas, que es exactamente lo que pidió Daniel: «que se puedan editar, si
 * uno desea, las dos dimensiones».
 *
 * Tampoco trae el selector de interna/externa, y eso es la regla de fondo: un
 * mueble NO MIDE. No hay lado al que mandarlo porque no suma a ninguno.
 *
 * Girar es un botón y no un agarre de rotación: gira de 90 en 90, que para un
 * rectángulo es intercambiar las medidas, y un ángulo libre rompería el imán a
 * caras de pared sin servirle a nadie en casas donde todo es ortogonal.
 *
 * Desde el 5-oct-2026 esta paleta es el ÚNICO camino para medir cualquier
 * módulo del catálogo: los agarres del plano se fueron porque se disparaban sin
 * intención al moverlo. La fila del radio aparece cuando se tocó una esquina del
 * objeto «a medida», y no dice cuál: la pastilla rellena en el plano ya lo
 * señala, y nombrarla en palabras mentiría en un mueble girado —la esquina que
 * el dedo tocó arriba a la derecha es, en el dibujo, otra—.
 *
 * Los dos botones del 5-oct-2026 cierran el ciclo de la pieza:
 *
 * - REEMPLAZAR abre el catálogo para cambiar el módulo sin perder el sitio.
 *   Daniel lo pidió así —«aprieto un botón de reemplazar y pueda elegir otro
 *   objeto como tal»— y el camino que había era borrar y volver a poner, que en
 *   un teléfono significa buscar de nuevo la pared y volver a pegar la pieza.
 * - GUARDAR mete la pieza medida al catálogo con nombre y categoría: «le cambio
 *   las medidas o hago con el módulo variable alguna medida en específico y en
 *   base a eso lo puedo guardar con un nombre y lo meto a la categoría que
 *   desee». El formulario vive ACÁ ADENTRO, desplegable, y no en una segunda
 *   paleta: son dos campos, y una paleta más sobre el mismo ancla es el defecto
 *   de las dos paletas encimadas que Daniel ya reportó una vez.
 */
function PaletaMueble({
  nombre, ancho, alto, radio, candado, grupos, electrico, estado, espejo,
  forma, nodos, nodo,
  onAncho, onAlto, onRadio, onGirar, onEspejo, onCopiar, onCandado, onReemplazar,
  onGuardar, onBorrar, onAltura, onVoltear, onEstado, onNodo,
}: {
  nombre: string;
  ancho: string;
  alto: string;
  /**
   * Lo que hace distinta a una salida eléctrica, o `null` si esta pieza es
   * mobiliario corriente.
   *
   * Un tomacorriente no tiene medida que editar —su símbolo es convencional,
   * ver `TAM_ELECTRICO`— y sí tiene ALTURA de montaje, que es lo que un plano
   * eléctrico rotula al lado. Así que la fila de `ancho × alto` se CAMBIA por la
   * de altura en vez de sumarse: Daniel la pidió «editable en la paleta», y dos
   * filas de números donde una no significa nada es peor que una sola.
   *
   * `anclada` dice si cuelga de una pared. Cuando cuelga no se ofrece girar —el
   * giro lo manda la cara, ver `asientoDeAncla`— y sí se ofrece VOLTEAR, que es
   * el «o hacia el lado externo o interno» del pedido en un toque.
   */
  electrico: { altura: string; anclada: boolean } | null;
  /** El radio de la esquina en edición, o null si no hay ninguna tocada. */
  radio: string | null;
  /**
   * Si el candado está abierto, o null cuando esta pieza no lo tiene porque sus
   * agarres nunca se fueron (el objeto «a medida»).
   */
  candado: boolean | null;
  /** Las categorías del catálogo vivo, para el desplegable de guardar. */
  grupos: string[];
  /**
   * El estado de rehabilitación de la pieza. Acá es PURAMENTE visual —un mueble
   * no mide, así que esconderlo no mueve ningún número— y por eso es el único
   * de los seis que no toca la medición. Igual lleva estado, porque «la cocina
   * se queda y los clósets se botan» es media conversación de remodelación.
   */
  estado: Estado;
  /** Si el símbolo va volteado sobre su eje propio. Ver `Mueble.espejo`. */
  espejo: boolean;
  /**
   * Si esta pieza puede tener FORMA propia, que es sólo el objeto «a medida».
   * Es lo que decide si la paleta ofrece partir y borrar nodos.
   */
  forma: boolean;
  /**
   * Cuántos nodos tiene su forma, o null si todavía es un rectángulo. Es lo
   * que decide si la fila de la medida se escribe o sólo se lee: en una L no
   * existe «el ancho», y mostrar el de su caja sería un número que no es suyo.
   */
  nodos: number | null;
  /** Qué se le está haciendo a los nodos de la forma, o null si nada. */
  nodo: 'partir' | 'quitar' | null;
  onAncho: (v: string) => void;
  onAlto: (v: string) => void;
  onRadio: (v: string) => void;
  onGirar: () => void;
  onEspejo: () => void;
  onCopiar: () => void;
  onCandado: () => void;
  onReemplazar: () => void;
  onGuardar: (nombre: string, grupo: string) => void;
  onBorrar: () => void;
  onAltura: (v: string) => void;
  onVoltear: () => void;
  onEstado: (e: Estado) => void;
  onNodo: (m: 'partir' | 'quitar') => void;
}) {
  const w = useCampoDiferido(ancho, onAncho);
  const h = useCampoDiferido(alto, onAlto);
  // Igual que el radio: se llama SIEMPRE, con cadena vacía cuando la pieza no
  // es eléctrica. Saltarse un hook según el caso rompe el orden entre pintados.
  const z = useCampoDiferido(electrico?.altura ?? '', onAltura);
  // Se llama SIEMPRE, con cadena vacía cuando no hay esquina tocada: es un hook
  // y saltárselo según el caso rompe el orden que React exige entre repintados.
  const r = useCampoDiferido(radio ?? '', onRadio);
  // El formulario de guardar es estado LOCAL de la paleta: no lo lee nadie más
  // y la paleta se remonta con cada pieza (`key={mueble.id}`), así que se limpia
  // solo al cambiar de objeto sin que el editor tenga que acordarse.
  const [guardando, setGuardando] = useState(false);
  const [comoSeLlama, setComoSeLlama] = useState('');
  const [enQueGrupo, setEnQueGrupo] = useState(GRUPO_PROPIO);
  const [verEstado, setVerEstado] = useState(false);

  // El color va LITERAL y no interpolado de `TINTA_MUEBLE`: Tailwind genera las
  // clases leyendo el archivo, y `focus:border-[${TINTA_MUEBLE}]` no existiría
  // como clase en el CSS final. Es el mismo valor, escrito donde se lo ve.
  const MEDIDA = `w-14 rounded border border-stone-200 px-1.5 py-1 text-right text-sm
                  tabular-nums outline-none focus:border-[#5f6f80]`;

  return (
    <>
      <div className="flex items-center gap-2 border-b border-stone-200 px-2 py-1.5 text-xs">
        {electrico
          ? <Lightning size={15} weight="bold" color={TINTA_ELECTRICO} />
          : <Armchair size={15} weight="bold" color={TINTA_MUEBLE} />}
        <span className="font-medium">{nombre}</span>
        {electrico && (
          <span className="ml-auto whitespace-nowrap text-[11px] text-stone-400">
            {electrico.anclada ? 'En pared' : 'Suelto'}
          </span>
        )}
      </div>
      {/* La ALTURA DE MONTAJE, en lugar de la medida: ver `electrico`. Se rotula
          «h =» porque es como se lee en una lámina eléctrica. */}
      {electrico && (
        <div className="flex items-center gap-1 border-b border-stone-200 p-1">
          <span className="pl-1 text-xs text-stone-500">h =</span>
          <input
            ref={z.campoRef}
            inputMode="decimal"
            value={z.texto}
            onChange={(e) => z.escribir(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={z.vaciar}
            onKeyDown={(e) => { if (e.key === 'Enter') z.vaciar(); }}
            className={`${MEDIDA} ml-auto`}
            aria-label="Altura de montaje en metros"
            data-mueble-altura=""
          />
          <span className="pr-1 text-xs text-stone-500">m</span>
        </div>
      )}
      {/* CON FORMA PROPIA no hay medida que escribir, igual que en el área
          libre: en una L no existe «el ancho», y mostrar el de su caja sería
          decir un número que la pieza no tiene. La fila dice qué es. */}
      {!electrico && nodos != null && (
        <div className="flex items-center gap-1 border-b border-stone-200 p-1">
          <span className="px-1 text-xs text-stone-500" data-mueble-forma="">
            Forma · {nodos} nodos
          </span>
        </div>
      )}
      {/* El × entre los dos campos, igual que en el área libre: es lo que hace
          leer la fila como UNA medida y no como dos datos sueltos. */}
      {!electrico && nodos == null && (
      <div className="flex items-center gap-1 border-b border-stone-200 p-1">
        <input
          ref={w.campoRef}
          inputMode="decimal"
          value={w.texto}
          onChange={(e) => w.escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={w.vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') w.vaciar(); }}
          className={MEDIDA}
          aria-label="Ancho del mueble en metros"
          data-mueble-ancho=""
        />
        <span className="text-xs text-stone-400">×</span>
        <input
          ref={h.campoRef}
          inputMode="decimal"
          value={h.texto}
          onChange={(e) => h.escribir(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={h.vaciar}
          onKeyDown={(e) => { if (e.key === 'Enter') h.vaciar(); }}
          className={MEDIDA}
          aria-label="Alto del mueble en metros"
          data-mueble-alto=""
        />
        <span className="pr-1 text-xs text-stone-500">m</span>
      </div>
      )}
      {radio != null && (
        <div className="flex items-center gap-1 border-b border-stone-200 p-1">
          <span className="pl-1 text-xs text-stone-500">Radio</span>
          <input
            ref={r.campoRef}
            inputMode="decimal"
            value={r.texto}
            onChange={(e) => r.escribir(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={r.vaciar}
            onKeyDown={(e) => { if (e.key === 'Enter') r.vaciar(); }}
            className={`${MEDIDA} ml-auto`}
            aria-label="Radio de la esquina en metros"
            data-mueble-radio=""
          />
          <span className="pr-1 text-xs text-stone-500">m</span>
        </div>
      )}
      {/* El formulario de guardar, desplegable. Va ARRIBA de la fila de botones
          para que el desplegable de categorías no quede tapado por el borde de
          la paleta en una pantalla de teléfono. */}
      {guardando && (
        <div className="flex flex-col gap-1 border-b border-stone-200 p-1"
             data-guardar-pieza="">
          <input
            autoFocus
            value={comoSeLlama}
            onChange={(e) => setComoSeLlama(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              onGuardar(comoSeLlama, enQueGrupo);
              setGuardando(false);
            }}
            placeholder="Cómo se llama"
            className="w-full rounded border border-stone-200 px-1.5 py-1 text-sm
                       outline-none focus:border-[#5f6f80]"
            aria-label="Nombre de la pieza que se guarda"
            data-nombre-pieza=""
          />
          <div className="flex items-center gap-1">
            <select
              value={enQueGrupo}
              onChange={(e) => setEnQueGrupo(e.target.value)}
              className="min-w-0 flex-1 rounded border border-stone-200 bg-white px-1
                         py-1 text-xs outline-none focus:border-[#5f6f80]"
              aria-label="Categoría del catálogo donde se guarda"
              data-grupo-pieza=""
            >
              {grupos.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <button
              type="button"
              onClick={() => { onGuardar(comoSeLlama, enQueGrupo); setGuardando(false); }}
              disabled={!comoSeLlama.trim()}
              className="rounded bg-[#5f6f80] px-2 py-1 text-xs font-medium text-white
                         disabled:bg-stone-300"
              data-confirmar-pieza=""
              title="Guardar esta pieza en el catálogo"
            >
              Guardar
            </button>
          </div>
        </div>
      )}
      <div className={FILA_ACCIONES}>
        {/* ELIMINAR PRIMERO. Ésta es la paleta que más lo necesitaba: con la
            pieza flexible llega a tener nueve acciones, y la papelera estaba
            tan a la derecha que en un teléfono había que deslizar la fila
            entera para encontrarla. */}
        <button type="button" onClick={onBorrar} className={HERRAMIENTA}
                aria-label="Eliminar el objeto" data-rotulo="Eliminar"
                data-borrar-mueble=""
                title="Eliminar el objeto">
          <Trash size={17} weight="bold" />
        </button>
        <span className="mx-0.5 h-5 w-px bg-stone-200" />
        {/* Una pieza colgada de una pared no se gira: su giro lo manda la cara
            de la que cuelga. Lo que sí tiene sentido ahí es VOLTEARLA al otro
            lado del muro, que es el «hacia el lado externo o interno» del
            pedido, y el mismo botón ocupa el mismo sitio. */}
        {electrico?.anclada ? (
          <button type="button" onClick={onVoltear} className={HERRAMIENTA}
                  aria-label="Voltear al otro lado de la pared" data-rotulo="Voltear"
                  data-voltear-mueble=""
                  title="Pasarla a la otra cara de la pared">
            <FlipHorizontal size={17} weight="bold" />
          </button>
        ) : (
          <button type="button" onClick={onGirar} className={HERRAMIENTA}
                  aria-label="Girar el mueble" data-rotulo="Girar"
                  data-girar-mueble=""
                  title="Girar 90°: intercambia las dos medidas y voltea el símbolo">
            <ArrowClockwise size={17} weight="bold" />
          </button>
        )}
        {/* El espejo, y sólo para lo que NO es eléctrico: un tomacorriente
            espejado es el mismo tomacorriente, así que el botón diría una
            acción que no hace nada.

            Es ortogonal al giro y por eso va aparte y no como un quinto paso
            de la rueda: girar ACUESTA la pieza —intercambia las medidas— y
            espejar la INVIERTE sin tocar la caja. El caso que lo pidió es el
            sofá en L, que existe en las dos manos y con puros giros de 90° no
            se puede pasar de una a la otra. */}
        {!electrico && (
          <button type="button" onClick={onEspejo}
                  className={espejo
                    ? `${HERRAMIENTA} border-[#5f6f80] bg-[#5f6f80] text-white`
                    : HERRAMIENTA}
                  aria-label="Espejar el objeto" data-rotulo="Espejar"
                  aria-pressed={espejo}
                  data-espejo-mueble=""
                  title="Espejo: voltea el símbolo sin cambiar sus medidas">
            <FlipHorizontal size={17} weight="bold" />
          </button>
        )}
        {/* LOS DOS VERBOS DEL NODO, los mismos de la paleta del área y por el
            mismo camino: Daniel pidió el 14-oct-2026 «que el objeto a medida
            tenga las mismas funciones que tiene el área». Sólo los lleva la
            pieza flexible —el resto del catálogo tiene un símbolo que dice qué
            es y deformarlo lo convertiría en otra cosa— y por eso van aquí y
            no en el `NODOS.map` del área: es la misma lista, en otra paleta. */}
        {forma && NODOS.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onNodo(n.id)}
            {...{ [`${n.marca}-objeto`]: nodo === n.id ? 'puesto' : 'suelto' }}
            className={`${HERRAMIENTA} ${nodo === n.id
              ? 'bg-[#4f9d78] text-white hover:bg-[#4f9d78]' : ''}`}
            aria-pressed={nodo === n.id}
            aria-label={n.rotulo} data-rotulo={n.corto}
            title={n.ayuda}
          >
            {n.icono}
          </button>
        ))}
        <button type="button" onClick={onCopiar} className={HERRAMIENTA}
                aria-label="Copiar el mueble" data-rotulo="Copiar"
                data-copiar-mueble=""
                title="Copiar: deja una copia al lado y la guarda para pegarla en otra parte">
          <Copy size={17} weight="bold" />
        </button>
        <button type="button" onClick={onReemplazar} className={HERRAMIENTA}
                aria-label="Reemplazar el mueble" data-rotulo="Reemplazar"
                data-reemplazar-mueble=""
                title="Reemplazar: elegí otro módulo del catálogo y queda en este mismo sitio">
          <Swap size={17} weight="bold" />
        </button>
        {/* Guardar no manda nada al servidor: la pieza queda en ESTE navegador.
            Ver `leerPropios` en muebles.tsx —para que viaje del celular a la
            computadora hace falta una tabla, y eso es decisión aparte—.
            No aparece en el eléctrico: lo que se guarda de una pieza es su
            MEDIDA ajustada, y un símbolo eléctrico no tiene medida propia. */}
        {!electrico && (
        <button type="button" onClick={() => setGuardando((v) => !v)}
                className={guardando
                  ? `${HERRAMIENTA} border-[#5f6f80] bg-[#5f6f80] text-white`
                  : HERRAMIENTA}
                aria-label="Guardar la pieza en el catálogo" data-rotulo="Guardar"
                aria-pressed={guardando}
                data-guardar-mueble=""
                title="Guardar esta pieza con su medida en el catálogo, para reusarla">
          <FloppyDisk size={17} weight="bold" />
        </button>
        )}
        {/* El candado: cerrado de fábrica, y abierto devuelve los agarres de
            estirar sobre el plano. Es el pedido del 5-oct-2026 —«la función que
            se omitió antes es hacerla disponible en caso de que se requiera,
            pero que por defecto venga limitada a como está actualmente»—. No
            aparece en la pieza «a medida» porque esa nunca los perdió: un
            candado que ya está abierto y no se puede cerrar no dice nada. */}
        {candado != null && (
          <button type="button" onClick={onCandado}
                  className={candado
                    ? `${HERRAMIENTA} border-[#5f6f80] bg-[#5f6f80] text-white`
                    : HERRAMIENTA}
                  aria-label="Candado del mueble" data-rotulo="Candado"
                  aria-pressed={candado}
                  data-candado-mueble=""
                  title={candado
                    ? 'Abierto: se puede estirar desde el plano. Tocá para cerrarlo'
                    : 'Cerrado: el mueble sólo se mide desde acá. Tocá para estirarlo en el plano'}>
            {candado
              ? <LockOpen size={17} weight="bold" />
              : <Lock size={17} weight="bold" />}
          </button>
        )}
        {/* Último y plegado, como en las otras tres. */}
        <button
          type="button"
          onClick={() => setVerEstado((v) => !v)}
          data-estado-mueble={verEstado ? 'abierto' : 'plegado'}
          aria-pressed={verEstado}
          aria-label="Estado de rehabilitación" data-rotulo="Estado"
          title={`${ROTULO_ESTADO[estado]} — ${AYUDA_ESTADO[estado]}`}
          className={`${HERRAMIENTA} ${verEstado ? 'bg-stone-100' : ''}`}
          style={{ color: TINTA_ESTADO[estado] ?? undefined }}
        >
          <Hammer size={17} weight="bold" />
        </button>
      </div>
      {verEstado && (
        <div className="border-t border-stone-200 px-2 py-1">
          <SelectorEstado estado={estado} onEstado={onEstado} />
        </div>
      )}
    </>
  );
}

/**
 * El catálogo de mobiliario: la colección de módulos que se insertan.
 *
 * Es el pedido central de Daniel (4-oct-2026): «que estén como en ese
 * repositorio o esa colección que uno pueda insertar de una manera rápida». Las
 * medidas son las que él dictó —clóset 60 × 150, módulo de cocina 60 × 60,
 * fregadero 80 × 60, refri 90 × 90, cama individual 1,00 × 1,90, queen
 * 1,60 × 1,90, king 2,00 × 2,00, lavadora y secadora 70 × 70, ducha 90 × 90—
 * y viven en `CATALOGO`, no acá: esta es sólo la vitrina.
 *
 * Cada casilla muestra el SÍMBOLO de verdad, el mismo que va a caer en el
 * plano, dibujado por la misma función. No un ícono que lo represente: así lo
 * que se elige es literalmente lo que se ve aparecer, y encima esquiva que
 * Phosphor no tenga `Refrigerator`, `Washer` ni `Sink`.
 *
 * El gesto es APOYAR Y ARRASTRAR, no tocar: el módulo nace pegado al dedo y
 * viaja con el imán vivo hasta la pared. Por eso la casilla no tiene `onClick`
 * sino los tres manejadores del puntero, y los dos últimos van al MISMO
 * `arrastrar`/`soltar` del lienzo —la captura del puntero se queda en el botón,
 * y sin reenviarlos el mueble nacería y se quedaría clavado ahí—.
 *
 * Sin botón de cerrar, igual que la paleta de agregar área: el toque afuera la
 * cierra. Es la regla que puso Daniel para todas las paletas.
 *
 * En modo REEMPLAZAR la misma vitrina cambia de gesto: las casillas pasan a ser
 * de un TOQUE y lo que hacen es cambiarle el módulo a la pieza ya tomada, que se
 * queda donde está. Es la misma colección y el mismo dibujo —un segundo catálogo
 * para elegir lo mismo se desactualizaría— con la cabecera diciendo qué va a
 * pasar, porque el mismo toque significa dos cosas distintas.
 *
 * Lista las piezas PROPIAS junto a las de fábrica, en el grupo que cada una
 * eligió: para PONER una pieza no importa quién la guardó. Mantenerlas —ver la
 * colección, corregir una medida, borrar, importar un dibujo nuevo— vive en la
 * última pestaña, «Mis objetos», que es otra cosa y tiene su propio componente.
 */
function PaletaCatalogo({
  copiado, reemplazando, armada, onCrear, onArmar, onPegar, onArmarPegado,
  onArrastrar, onSoltar, onReemplazar, onCambioPropio, onPublicarPropio,
}: {
  /** El molde guardado por «Copiar», o null si no se copió nada todavía. */
  copiado: Copia | null;
  /** Si la vitrina está eligiendo el reemplazo de una pieza ya tomada. */
  reemplazando: boolean;
  /** La clave de la pieza armada esperando punto, o null. */
  armada: string | null;
  onCrear: (e: React.PointerEvent, m: ModeloMueble) => void;
  onArmar: (m: ModeloMueble) => void;
  onPegar: (e: React.PointerEvent) => void;
  onArmarPegado: () => void;
  onArrastrar: (e: React.PointerEvent) => void;
  onSoltar: () => void;
  onReemplazar: (m: ModeloMueble) => void;
  onCambioPropio: (aviso: string) => void;
  /** Publicar una pieza para todo el equipo. Llega sin poner cuando el usuario
   *  no tiene el rol, y entonces el botón ni se dibuja. */
  onPublicarPropio?: (clave: string) => void;
}) {
  // Las APAGADAS se filtran acá y no dentro de `catalogoVivo`: la pantalla de
  // Objetos usa la misma función y tiene que seguir viéndolas, o una pieza
  // apagada no se podría volver a encender nunca. Ver `LLAVE_APAGADAS`.
  const catalogo = catalogoVivo().filter((m) => !estaApagada(m.clave));
  // «Mis objetos» va SIEMPRE y al final, aunque no haya ninguna pieza propia:
  // es donde vive el botón de importar, y esconderlo hasta tener un objeto es
  // justo la falta de visibilidad que Daniel vino a señalar. Reemplazando no
  // aparece: ahí la vitrina está eligiendo con qué cambiar una pieza, y una
  // pestaña de mantenimiento no tiene nada que ofrecer a ese gesto.
  //
  // Y se come al grupo «Míos»: eran la misma idea con dos nombres, y la foto lo
  // delató —los tabs se iban a TRES filas, justo lo que Daniel había pedido
  // evitar—. La pestaña muestra las dos caras: arriba las piezas del grupo
  // propio para PONERLAS, abajo la lista para mantenerlas.
  const grupos = [
    ...gruposVivos().filter((g) => g !== GRUPO_PROPIO && catalogo.some((m) => m.grupo === g)),
    ...(reemplazando ? [] : [TAB_MIS_OBJETOS]),
  ];
  /**
   * Qué sección se está mirando.
   *
   * Daniel lo pidió el 10-oct-2026: «hay que buscar una manera más amigable
   * para navegar por los objetos, porque por un lado cuesta hacer scroll hacia
   * abajo, no siempre lo detecta. Tal vez hacerlo por medio de tabs». El scroll
   * no fallaba por capricho: los botones llevan `touch-none` —hace falta para
   * poder arrastrar un módulo fuera de la vitrina— y eso le come al navegador
   * justo el gesto con el que se desplaza una lista. Con tabs la lista nunca es
   * más larga que la pantalla y el conflicto desaparece en vez de pelearse.
   */
  const [seccion, setSeccion] = useState(grupos[0] ?? '');
  const activa = grupos.includes(seccion) ? seccion : (grupos[0] ?? '');
  /**
   * La pieza que se apoyó y todavía no se sabe si va a arrastrarse.
   *
   * Es el fiel de la balanza entre los dos caminos: arrastrar saca el módulo
   * pegado al dedo —como siempre— y un toque suelto lo ARMA para ponerlo
   * después con un toque en el plano. La captura del puntero se toma al apoyar
   * para que los `pointermove` sigan llegando aunque el dedo salga del botón;
   * si se tomara recién al nacer, el primer movimiento se perdería.
   */
  const pend = useRef<{
    /** El módulo del catálogo, o `null` cuando lo que se apoyó fue «Pegar». */
    m: ModeloMueble | null; x: number; y: number; nacido: boolean;
  } | null>(null);

  function apoyar(e: React.PointerEvent, m: ModeloMueble | null) {
    // El apoyo se queda ACÁ. Antes lo frenaba `crearMueble`, que corría en este
    // mismo evento; desde que la pieza nace en el primer movimiento, el apoyo
    // llegaría solo hasta el lienzo —que lo leería como «tocó el vacío», armaría
    // su propio agarre y al levantar soltaría todo, incluida la pieza recién
    // puesta—.
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    pend.current = { m, x: e.clientX, y: e.clientY, nacido: false };
  }

  function mover(e: React.PointerEvent) {
    const q = pend.current;
    if (!q) return;
    if (!q.nacido) {
      if (Math.hypot(e.clientX - q.x, e.clientY - q.y) < ARRASTRE_PX) return;
      q.nacido = true;
      // Nace donde está el dedo AHORA, no donde se apoyó: apoyarse fue elegir
      // la pieza, y el punto de nacimiento es el primer sitio al que se la
      // llevó. Si naciera en el botón, aparecería debajo de la vitrina.
      if (q.m) onCrear(e, q.m);
      else onPegar(e);
      return;
    }
    onArrastrar(e);
  }

  function levantar() {
    const q = pend.current;
    pend.current = null;
    if (!q) return;
    if (!q.nacido) { if (q.m) onArmar(q.m); else onArmarPegado(); return; }
    onSoltar();
  }
  // La casilla de pegar se arma del molde guardado + la ficha del catálogo, que
  // es de donde salen el símbolo y el nombre. Las medidas son las VIVAS —lo que
  // se copió es la pieza ya ajustada— y se intercambian si está girada, para que
  // la miniatura muestre la huella que va a caer y no la de fábrica.
  const copia = copiado
    ? catalogo.find((m) => m.clave === copiado.tipo)
    : undefined;
  const vertical = copiado?.giro === 90 || copiado?.giro === 270;
  const fichaCopia: ModeloMueble | null = copia && copiado
    ? {
      ...copia,
      w: vertical ? copiado.h : copiado.w,
      h: vertical ? copiado.w : copiado.h,
      nombre: `${copia.nombre} (copia)`,
    }
    : null;

  return (
    <div className="w-[16.5rem] p-1" data-catalogo=""
         data-reemplazando={reemplazando ? '' : undefined}>
      {reemplazando && (
        <p className="flex items-center gap-1.5 border-b border-stone-200 px-1.5 pb-1.5
                      pt-1 text-xs font-medium text-[#5f6f80]">
          <Swap size={14} weight="bold" />
          Tocá con qué lo reemplazás
        </p>
      )}
      {/* Los tabs. Se ENVUELVEN en varias filas en vez de desplazarse: Daniel
          lo pidió el 11-oct-2026 —«que sean dos filas de tabs en los tipos de
          objetos porque actualmente en el celular me obligaba hacer scroll»—.
          Y la fila corrida era peor que incómoda: los grupos que quedaban fuera
          del borde no se veían, así que un catálogo entero podía existir sin
          que nadie supiera. Envueltos entran todos a la vista de una.

          `shrink-0` sigue siendo lo que impide que las secciones se aplasten en
          16 rem hasta volverse ilegibles; es justo lo que las empuja a bajar de
          renglón. Con el catálogo de hoy caen en dos filas. */}
      <div className="flex flex-wrap gap-0.5 border-b border-stone-200 pb-1"
           data-tabs-objetos="">
        {grupos.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setSeccion(g)}
            aria-pressed={g === activa}
            data-tab-objeto={g}
            className={'shrink-0 rounded px-2 py-1 text-[0.68rem] font-medium '
              + (g === activa
                ? 'bg-[#5f6f80] text-white'
                : 'text-stone-500 hover:bg-stone-100 active:bg-stone-200')}
          >
            {g}
          </button>
        ))}
      </div>
      {/* El portapapeles no sale reemplazando: pegar PONE una pieza nueva y acá
          lo que se hace es cambiarle el módulo a la que ya está. */}
      {!reemplazando && fichaCopia && (
        <div className="mb-1">
          <p className="px-1 pb-0.5 pt-1 text-[0.68rem] font-medium uppercase
                        tracking-wide text-stone-400">
            Portapapeles
          </p>
          <div className="grid grid-cols-3 gap-0.5">
            <button
              type="button"
              className="flex touch-none select-none flex-col items-center gap-0.5
                         rounded border border-dashed border-[#5f6f80] px-1 py-1.5
                         text-center hover:bg-stone-100 active:bg-stone-200"
              onPointerDown={(e) => apoyar(e, null)}
              onPointerMove={mover}
              onPointerUp={levantar}
              onPointerCancel={levantar}
              data-pegar-mueble=""
              title={`Pegar ${fichaCopia.nombre} — ${cm(fichaCopia.w)} × ${cm(fichaCopia.h)} cm`}
            >
              <MiniaturaMueble modelo={fichaCopia} />
              <span className="text-[0.63rem] leading-tight text-stone-600">Pegar</span>
              <span className="text-[0.58rem] leading-none tabular-nums text-stone-400">
                {cm(fichaCopia.w)}×{cm(fichaCopia.h)}
              </span>
            </button>
          </div>
        </div>
      )}
      {/* Una sola sección a la vez. El `max-h-[46vh] overflow-y-auto` que tenía
          esta rejilla se fue el 29-oct-2026: nunca llegaba a disparar —la
          `Franja` recortaba 100 px antes— y lo único que lograba era prometer
          un segundo desplazador anidado que no existía. Con la franja ya
          desplazable, el recorte vive en un solo sitio. */}
      {/* En «Mis objetos» la grilla de arriba es la del grupo propio: las piezas
          que uno guardó sin mandarlas a otro grupo se arrastran y se arman desde
          acá, como cualquier otra. Abajo va el mantenimiento. */}
      {[activa === TAB_MIS_OBJETOS ? GRUPO_PROPIO : activa].map((g) => {
        const items = catalogo.filter((m) => m.grupo === g);
        if (!items.length) return null;
        return (
          <div key={g} data-grupo={g} className="mb-1 last:mb-0">
            <div className="grid grid-cols-3 gap-0.5">
              {items.map((m) => {
                return (
                  // El tachito que vivía encima de cada pieza propia se fue a la
                  // pestaña «Mis objetos» (12-oct-2026): un botón de 5 mm sobre
                  // la esquina de otro botón se tocaba sin querer, y borrar es
                  // justo lo que no se quiere hacer sin querer.
                  <div key={m.clave} className="relative">
                    <button
                      type="button"
                      // `touch-none` es lo que deja arrastrar con el dedo desde
                      // acá: sin eso el navegador se queda el gesto para
                      // desplazar la lista y el mueble no sale nunca del
                      // catálogo.
                      //
                      // REEMPLAZANDO sí vale el condicional, y antes no: desde
                      // que la franja se puede recorrer, `touch-none` sobre tres
                      // columnas de botones tapa casi toda la superficie de
                      // agarre y deja la lista quieta. Ahí el gesto es un TOQUE
                      // —los `onPointer*` ni siquiera se montan—, así que no hay
                      // arrastre que proteger y el dedo tiene que poder
                      // desplazar desde cualquier parte.
                      className={'flex w-full select-none flex-col items-center'
                        + (reemplazando ? ' ' : ' touch-none')
                        + ' gap-0.5 rounded px-1 py-1.5 text-center hover:bg-stone-100'
                        + ' active:bg-stone-200'
                        + (m.clave === armada
                          ? ' ring-2 ring-[#5f6f80] ring-offset-0 bg-stone-100'
                          : '')}
                      // Reemplazar es un TOQUE y poner tiene DOS salidas en el
                      // mismo gesto: arrastrar saca el módulo pegado al dedo,
                      // un toque suelto lo arma para ponerlo con un toque en el
                      // plano. Las reparte `mover`/`levantar`, por el umbral de
                      // arrastre; con `onClick` no se podrían distinguir.
                      onClick={reemplazando ? () => onReemplazar(m) : undefined}
                      onPointerDown={reemplazando ? undefined : (e) => apoyar(e, m)}
                      onPointerMove={reemplazando ? undefined : mover}
                      onPointerUp={reemplazando ? undefined : levantar}
                      onPointerCancel={reemplazando ? undefined : levantar}
                      data-mueble-nuevo={m.clave}
                      data-armada={m.clave === armada ? '' : undefined}
                      title={m.electrico
                        ? `${m.nombre}${m.alturaDefecto == null ? ''
                             : ` — h = ${m2(m.alturaDefecto)} m`}`
                        : `${m.nombre} — ${cm(m.w)} × ${cm(m.h)} cm`
                          + (m.propuesta ? ' (medida propuesta, editable)' : '')}
                    >
                      <MiniaturaMueble modelo={m} />
                      <span className="text-[0.63rem] leading-tight text-stone-600">
                        {m.nombre}
                      </span>
                      {/* El eléctrico canta su ALTURA de montaje, no su medida.
                          Los 20 × 20 cm son el tamaño convencional del SÍMBOLO
                          —un tomacorriente real mide 7 cm, ver `TAM_ELECTRICO`—
                          y escribirlos acá se leería como que la pieza mide eso.
                          Lo que de verdad define a una salida es a qué altura
                          va, que es lo que se rotula en una lámina eléctrica. */}
                      <span className="text-[0.58rem] leading-none tabular-nums text-stone-400">
                        {m.electrico
                          ? (m.alturaDefecto == null ? '' : `h ${m2(m.alturaDefecto)}`)
                          : `${cm(m.w)}×${cm(m.h)}`}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {activa === TAB_MIS_OBJETOS && (
        <MisObjetos onCambio={onCambioPropio} onPublicar={onPublicarPropio} />
      )}
    </div>
  );
}

/**
 * La tabla de áreas, en una hoja encima del editor.
 *
 * Era una lista clavada abajo del lienzo y Daniel la pidió como modal
 * (4-oct-2026): «un botón que sea como mostrar áreas y lo que abre es como un
 * modal en el que se puedan ver y que uno lo pueda cerrar». El motivo es de
 * espacio y es el mismo de siempre en este editor: en un celular la lista se
 * comía media pantalla justo mientras se diseña, y los números no se leen todo
 * el tiempo —se consultan—.
 *
 * Las filas siguen seleccionando el área, que es para lo que servía la lista
 * vieja: en un celular un clóset de 1,20 m se toca más fácil acá por su nombre
 * que adentro del dibujo. Al elegir una, la hoja se cierra sola: lo que uno
 * quiere después de elegir es verla en el plano.
 *
 * Y cada fila trae su selector de clase, al lado de su número. La paleta es el
 * camino del dedo sobre el dibujo; esto es el camino de quien está leyendo la
 * suma y se da cuenta de que la cochera está contando adentro.
 */
function TablaAreas({
  medicion, recinto, libreSel, omitidas, vista, final,
  onRegion, onLibre, onClaseRegion, onClaseLibre, onDevolver, onCerrar,
}: {
  medicion: Medicion;
  recinto: string | null;
  libreSel: number | null;
  /** Las que el usuario sacó del cómputo: no están en `medicion`, por eso van aparte. */
  omitidas: string[];
  /**
   * Qué vista de obra está puesta. La tabla LA SIGUE —es la decisión de Daniel
   * del 10-oct-2026— porque una tabla que no sigue a la vista deja al usuario
   * mirando «Estado actual» con los números del final y sin modo de saberlo.
   */
  vista: VistaObra;
  /**
   * Lo que va a quedar construido, medido aparte, o `null` cuando la vista YA
   * es el estado final y los dos números son el mismo.
   *
   * La otra mitad de esa decisión: «el PRECIO siempre al estado final,
   * rotulando de cuál habla». El delta contra el modelo es lo que cobra el
   * extra, y cobrarlo contra lo que uno tenga puesto en pantalla sería que el
   * precio cambie según cómo se mire el plano.
   */
  final: Medicion | null;
  onRegion: (nombre: string) => void;
  onLibre: (id: number) => void;
  onClaseRegion: (nombre: string, c: Clase) => void;
  onClaseLibre: (id: number, c: Clase) => void;
  onDevolver: (nombre: string) => void;
  onCerrar: () => void;
}) {
  const filas = [
    ...medicion.regiones.map((g) => ({
      llave: `r:${g.nombre}`,
      nombre: g.nombre,
      area: g.area,
      // El delta es sólo de las regiones: una libre no tiene reposo contra el
      // que compararse.
      delta: g.area - g.referencia,
      clase: g.clase,
      activa: g.nombre === recinto,
      elegir: () => onRegion(g.nombre),
      clasificar: (c: Clase) => onClaseRegion(g.nombre, c),
    })),
    ...medicion.libres.map((l) => ({
      llave: `l:${l.id}`,
      nombre: l.nombre,
      area: l.area,
      delta: 0,
      clase: l.clase,
      activa: l.id === libreSel,
      elegir: () => onLibre(l.id),
      clasificar: (c: Clase) => onClaseLibre(l.id, c),
    })),
  ];

  /**
   * Las filas AGRUPADAS por tipo, con su subtotal.
   *
   * Lo pidió Daniel el 8-oct-2026: «si hay varias de exterior, las agrupe por
   * exterior y quede un subtotal en la parte de abajo, como si fueran tipo
   * tabla». La lista plana obligaba a ir leyendo el selector de cada fila para
   * saber de qué lado sumaba cada una, que es justo el trabajo que una tabla
   * tiene que hacer sola.
   *
   * El subtotal se calcula de las filas que están A LA VISTA y no se lee de
   * `medicion[clase]`, aunque los dos números tengan que dar igual —las columnas
   * de arriba son exactamente esta misma suma, ver `porClase` en `medirPlanta`—.
   * Si algún día se desalinean, lo honesto es que el subtotal siga coincidiendo
   * con lo que uno puede sumar a mano en la pantalla, y que la diferencia se vea
   * contra la columna de arriba en vez de esconderse.
   *
   * El orden es `CLASES`, el mismo de las columnas y de la barra. Los grupos
   * vacíos no se dibujan: una casa sin azotea no tiene por qué mostrar un
   * encabezado de azotea con cero filas.
   */
  const grupos = CLASES.map((c) => {
    const suyas = filas.filter((f) => f.clase === c);
    return { clase: c, filas: suyas, suma: suyas.reduce((s, f) => s + f.area, 0) };
  }).filter((g) => g.filas.length > 0);

  /**
   * El área total de la casa: interna + externa.
   *
   * «Es un dato, pues importante, que es el área total de la casa como tal. Esa
   * no contempla el área de azotea» —Daniel, 8-oct-2026—. Y tampoco el lote, por
   * lo mismo: el terreno no es casa. Las dos que suman son las dos que uno
   * camina bajo techo propio, estén adentro del modelo o aparte.
   *
   * Va en su propia banda y no como quinta columna arriba porque no es una
   * clase: es una cuenta DERIVADA de dos de ellas. Mezclarla con las cuatro
   * haría creer que un área se puede marcar como «total».
   */
  const casa = medicion.interna + medicion.externa;

  return (
    <div className="absolute inset-0 z-30 flex items-end justify-center sm:items-center">
      {/* El velo cierra al tocarlo. Es el gesto que todo el mundo prueba
          primero, y acá importa el doble: con el teléfono en la mano, la X
          queda arriba y el pulgar está abajo. */}
      <div
        className="absolute inset-0 bg-stone-900/40"
        data-velo-tabla=""
        onClick={onCerrar}
      />
      {/* `max-h-[94%]` en el teléfono: Daniel pidió «pueda hacerse más alto ese
          modal que sale para que la información quede más legible», y con los
          totales arriba más el encabezado y el subtotal de cada grupo, el 85 %
          de antes dejaba dos filas de la lista asomando. Casi toda la pantalla,
          que es lo correcto para algo que se abre a LEER; la franja de velo que
          queda arriba sigue diciendo que hay un editor debajo y que tocando ahí
          se cierra. En pantalla grande se queda en 90 %, donde ya no falta nada. */}
      <div
        data-tabla-areas=""
        className="relative flex max-h-[94%] w-full max-w-md flex-col rounded-t-2xl
                   border border-stone-200 bg-white shadow-xl sm:max-h-[90%] sm:rounded-2xl"
      >
        <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-2">
          <h3 className="text-sm font-semibold">Áreas</h3>
          {/* De cuál planta habla esta tabla. Sólo sale cuando los números NO
              son los del estado final: puesta siempre sería ruido en los 50
              modelos que no tienen una sola cosa marcada, y ausente cuando hace
              falta es justamente el malentendido que esta pastilla evita.
              Se pregunta por la geometría porque «Planteado» y «Estado final»
              miden lo mismo —difieren en la tinta, y la tinta no suma—. */}
          {!mismaGeometria(vista, 'final') && (
            <span
              data-tabla-vista={vista}
              className="rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
              style={{ background: '#78716c' }}
            >
              {ROTULO_VISTA[vista]}
            </span>
          )}
          <button
            type="button"
            data-cerrar-tabla=""
            onClick={onCerrar}
            className={`${HERRAMIENTA} ml-auto -mr-1`}
            aria-label="Cerrar la tabla de áreas"
          >
            <X size={17} weight="bold" />
          </button>
        </div>

        {/* Los cuatro totales arriba y en grande: es lo que se viene a ver.
            La INTERNA es la que se compara contra el modelo —y su diferencia es
            la que fija el precio del extra—; las otras tres se miden y se
            muestran, cada una en su columna, pero ninguna entra ahí.

            Dos columnas en el teléfono y cuatro de ahí para arriba: la tarjeta
            es `max-w-md`, y cuatro cifras de cinco dígitos con su rótulo en 448
            px dejan los números partidos en dos líneas. */}
        <div className="grid grid-cols-2 gap-2 border-b border-stone-200 px-3 py-2 sm:grid-cols-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-stone-500">Área interna</p>
            {/* También va de su color, no en negro: es la primera de las cuatro
                y dejarla neutra era parte de la incongruencia que Daniel
                señaló —la cara azul de la barra abría un número gris—. */}
            <p className="text-lg font-semibold tabular-nums"
               style={{ color: TINTA_CLASE.interna }}>{m2(medicion.interna)} m²</p>
            {/* EL NÚMERO DE ARRIBA SIGUE A LA VISTA; ESTA LÍNEA, NO. Es la
                línea del precio: el delta contra el modelo es lo que se cobra
                de extra, y tiene que salir del estado final aunque uno esté
                mirando «Demoliciones». Cuando los dos no coinciden se dice cuál
                es cuál con todas las letras —no alcanza con que sea correcto,
                hay que poder verlo— y esa es la mitad «rotulando de cuál
                habla» de la decisión. */}
            <p className="text-xs text-stone-600">
              {Math.abs((final ?? medicion).delta) < 0.005
                ? 'igual al modelo original'
                : `${(final ?? medicion).delta > 0 ? '+' : '−'}`
                  + `${m2(Math.abs((final ?? medicion).delta))} m² `
                  + `sobre los ${m2((final ?? medicion).referencia)} m² del modelo`}
              {final && (
                <span className="block text-[11px] text-stone-500">
                  del estado final: {m2(final.interna)} m²
                </span>
              )}
            </p>
          </div>
          {(['externa', 'lote', 'azotea'] as const).map((c) => (
            <div key={c} data-total={c}>
              <p className="text-[11px] uppercase tracking-wide text-stone-500">
                {ROTULO_CLASE[c]}
              </p>
              <p className="text-lg font-semibold tabular-nums"
                 style={{ color: TINTA_CLASE[c] }}>
                {m2(medicion[c])} m²
              </p>
              <p className="text-xs text-stone-600">aparte del área interna</p>
            </div>
          ))}
        </div>

        {/* EL TOTAL DE LA CASA. Va en una banda propia, a lo ancho y con el
            rótulo a la izquierda y la cifra a la derecha: es una línea de SUMA,
            no una quinta columna, y leerla como suma es lo que la distingue de
            las cuatro de arriba.

            Se escribe de qué está hecha —«interna + externa»— porque es lo
            primero que uno pregunta de un número que no puede tocar, y sobre
            todo porque dice por omisión lo que NO entra. */}
        <div className="flex items-baseline gap-2 border-b border-stone-200 px-3 py-2"
             data-total-casa="">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-stone-500">
              Área total de la casa
            </p>
            <p className="text-xs text-stone-600">
              interna + externa · sin lote ni azotea
            </p>
          </div>
          <p className="ml-auto shrink-0 text-lg font-semibold tabular-nums">
            {m2(casa)} m²
          </p>
        </div>

        {medicion.sueltos.length > 0 && (
          <p className="border-b border-stone-200 px-3 py-1.5 text-xs text-amber-700">
            {medicion.sueltos.join(', ')} quedó abierto al exterior.
          </p>
        )}

        {/* Las quitadas del cómputo viven ACÁ y en ningún otro lado. Es la
            única vía de vuelta: una región quitada desaparece del dibujo, así
            que no hay nada que tocar para traerla, y sin esta lista quedaría
            fuera para siempre sin siquiera constar en pantalla. */}
        {omitidas.length > 0 && (
          <div className="border-b border-stone-200 px-3 py-1.5" data-omitidas="">
            <p className="text-[11px] uppercase tracking-wide text-stone-500">
              Fuera del cómputo
            </p>
            {omitidas.map((n) => (
              <div key={n} className="flex items-center gap-2 py-0.5 text-xs">
                <span className="truncate text-stone-500 line-through">{n}</span>
                <button
                  type="button"
                  onClick={() => onDevolver(n)}
                  data-devolver={n}
                  className="ml-auto rounded border border-stone-200 px-1.5 py-0.5"
                  title="Volver a contar esta área"
                >
                  Devolver
                </button>
              </div>
            ))}
          </div>
        )}

        <ul className="min-h-0 flex-1 overflow-y-auto px-2 py-1">
          {grupos.map((g) => (
            <li key={g.clase} data-grupo-area={g.clase} className="mb-1.5">
              {/* El encabezado del grupo, del color de su clase y pegado a su
                  borde: hace de regla horizontal y de rótulo al mismo tiempo,
                  que es lo que separa un grupo del siguiente sin gastar una
                  línea en blanco —en un teléfono cada línea es una fila menos—. */}
              <p className="flex items-baseline gap-2 border-b px-1 pb-0.5 pt-1
                            text-[11px] font-semibold uppercase tracking-wide"
                 style={{ color: TINTA_ROTULO[g.clase], borderColor: TINTA_CLASE[g.clase] }}>
                {ROTULO_CLASE[g.clase]}
                <span className="font-normal normal-case tracking-normal text-stone-500">
                  {g.filas.length} {g.filas.length === 1 ? 'área' : 'áreas'}
                </span>
              </p>
              <ul>
                {g.filas.map((f) => (
                  <li key={f.llave} className="flex items-center gap-2 py-0.5">
                    <button
                      type="button"
                      data-fila-area={f.nombre}
                      onClick={() => { f.elegir(); onCerrar(); }}
                      className={`flex min-w-0 flex-1 items-baseline justify-between gap-2 rounded
                                  px-1 py-1 text-left text-xs ${f.activa ? 'bg-stone-100 font-medium' : ''}`}
                    >
                      <span className="truncate">{f.nombre}</span>
                      {/* El metraje de cada fila va del color de su clase: así la
                          tabla detallada habla los mismos cuatro colores que el
                          plano y la barra. Agrupadas ya se sabe de qué lado suma
                          cada una, pero el color se queda igual: es lo que ata la
                          fila a su área pintada cuando uno vuelve al dibujo. */}
                      <span className="shrink-0 tabular-nums"
                            style={{ color: TINTA_CLASE[f.clase] }}>
                        {m2(f.area)} m²
                        {Math.abs(f.delta) >= 0.005 && (
                          <span className="ml-1 text-[#8a5a3b]">
                            ({f.delta > 0 ? '+' : '−'}{m2(Math.abs(f.delta))})
                          </span>
                        )}
                      </span>
                    </button>
                    <MenuClase clase={f.clase} onClase={f.clasificar} nombre={f.nombre} />
                  </li>
                ))}
              </ul>
              {/* El subtotal, alineado con los metrajes de sus filas para que la
                  columna de números se pueda sumar con el dedo. Con una sola
                  área no se dibuja: repetiría el número de la línea de arriba. */}
              {g.filas.length > 1 && (
                <p className="flex items-baseline gap-2 px-1 pb-0.5 text-[11px]"
                   data-subtotal={g.clase}>
                  <span className="text-stone-500">Subtotal {ROTULO_CLASE[g.clase].toLowerCase()}</span>
                  <span className="ml-auto font-semibold tabular-nums"
                        style={{ color: TINTA_ROTULO[g.clase] }}>
                    {m2(g.suma)} m²
                  </span>
                  {/* Este hueco es el ancho del control de tipo de la fila: sin
                      él el subtotal se iría al borde y dejaría de estar debajo
                      de los metrajes, que es todo el punto de alinearlo. */}
                  <span className="w-[5.5rem] shrink-0" aria-hidden="true" />
                </p>
              )}
            </li>
          ))}
          {!filas.length && (
            <li className="px-2 py-3 text-xs text-stone-500">
              Todavía no hay áreas cerradas que medir.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

export function EditorPlanta({
  planoUrl, modelo, onAreas, titulo, onCerrar, onGuardar, onExportar, acciones,
}: Props) {
  // El modelo se clona una vez: el editor lo muta y «Volver al plano» restaura.
  const original = useMemo(() => JSON.stringify(modelo.plantas), [modelo]);
  const [plantas, setPlantas] = useState<Planta[]>(() => JSON.parse(original));
  const [iPlanta, setIPlanta] = useState(() => Math.max(0, modelo.plantas.length - 1));
  // La selección es una LISTA: con una pared la paleta trae la medida y las
  // operaciones sueltas; con varias cambia de cara. `multi` es el modo en el
  // que un toque SUMA en vez de reemplazar —lo que en el celular se enciende
  // con un doble toque, y en el teclado con Shift—.
  const [sel, setSel] = useState<number[]>([]);
  const [multi, setMulti] = useState(false);
  // Un vano se toma SOLO, nunca junto con paredes: lo que se le hace es otra
  // cosa. Se identifica por pared + id y no por índice en la lista porque
  // partir, unir o recortar una pared rehacen esa lista.
  const [vanoSel, setVanoSel] = useState<VanoSel | null>(null);
  // Las áreas tomadas se recuerdan por NOMBRE, que es lo único que sobrevive a
  // una medición nueva: los índices los reparte la inundación y renumeran en
  // cuanto alguien mueve una pared.
  //
  // Es una LISTA por el mismo motivo que la de paredes: unir dos áreas se hace
  // tomándolas, no eligiéndolas de un desplegable. Daniel lo pidió así
  // (4-oct-2026): «dejo hold un área y eso se pone en modo seleccionar varias, y
  // al seleccionar varias me sale la paleta flotante con el ícono para unir».
  // Antes de eso había un «Unir con…» que yo defendí porque dos áreas chicas son
  // difíciles de tomar con el dedo; el argumento de Daniel es mejor y es el que
  // manda: ese gesto YA existe para paredes, y repetirlo no se aprende. (El
  // gesto que lo enciende dejó de ser el hold el 6-oct; ver `DOBLE_MS`. Lo que
  // sigue valiendo del pedido es que sea el MISMO que el de las paredes.)
  const [recintos, setRecintos] = useState<string[]>([]);
  /** El modo en que un toque sobre un área SUMA en vez de reemplazar. */
  const [multiAreas, setMultiAreas] = useState(false);
  /** El área tomada cuando hay exactamente una. */
  const recinto = recintos.length === 1 ? recintos[0] : null;
  const [medicion, setMedicion] = useState<Medicion | null>(null);
  /**
   * Si los rótulos de área se pintan sobre el plano. Es el ojo de la barra.
   *
   * Separado de la tabla por pedido de Daniel (4-oct-2026): «que exista, por
   * así decirlo, como un ojito, otro botón que sea un ojo para mostrar las
   * áreas o ocultarlas […] con esto estaríamos dividiendo la función de mostrar
   * la tabla de áreas, que ahora sería un modal, y la opción de ver las áreas
   * propiamente para editarlas». Son dos necesidades distintas: la tabla es
   * para leer los números, el ojo es para trabajar encima del dibujo.
   */
  /**
   * Qué se VE de cada capa. El ojo de las áreas se volvió uno por capa el
   * 5-oct-2026, a pedido de Daniel: «no sé si más bien poner también el ojo,
   * que en este momento lo único que sirve es para apagar o prender áreas, es
   * que el ojo esté dentro de cada uno de esos botones, por si quiero ocultar
   * las paredes o si quiero ocultar los objetos».
   *
   * Paredes y muebles nacen encendidos —son el dibujo— y las áreas apagadas,
   * que es como venía el ojo único: la medición es una capa que se prende para
   * trabajarla y se apaga para ver el plano limpio.
   *
   * Las cotas también nacen APAGADAS, y eso resuelve la pregunta que Daniel
   * dejó abierta el 5-oct-2026 —«si quiero ver la planta acotada, con un botón
   * la genera o ya quedan generadas»—: no hay botón de generar porque no hay
   * nada que generar; las cotas de pared se calculan de la geometría en cada
   * repintado y el ojo decide si se dibujan. Prenderlo es «ver la planta
   * acotada» y apagarlo la deja limpia, sin basura acumulada en el modelo.
   */
  const [ver, setVer] = useState<Record<Capa, boolean>>({
    pared: true, area: false, mueble: true, cota: false,
  });
  /**
   * Qué momento de la obra se está mirando. Ver `VistaObra` en tipos.ts.
   *
   * Es un SEGUNDO EJE, que cruza al de capas y no lo reemplaza: el ojo filtra
   * por tipo de objeto —paredes, áreas, mobiliario, cotas— y esto filtra por
   * estado de rehabilitación. Una pared demolida se oculta en «Estado final»
   * aunque la capa de paredes esté encendida, y una pared nueva se oculta con la
   * capa apagada aunque la vista la muestre. Los dos filtros se aplican, y por
   * eso son dos controles y no uno.
   *
   * Arranca en `planteado` porque es lo que pidió Daniel el 10-oct-2026 —«por
   * defecto, si le doy a un objeto a demoler, se oculta»— y porque en una planta
   * sin nada marcado es idéntica a mostrar todo. Es la vista TEÑIDA de lo que va
   * a quedar: la de entrada tiene que ser la de trabajar, y marcar algo sin ver
   * la marca sería trabajar a ciegas. `final` enseña lo mismo pero en negro, y
   * para eso hay que pedirla —ver `VISTAS_SIN_TINTA`—.
   */
  const [vistaObra, setVistaObra] = useState<VistaObra>('planteado');
  /**
   * La vista, leída por las ~26 mediciones repartidas por el editor.
   *
   * Es un ref y no el estado porque esas mediciones viven dentro de handlers de
   * puntero y de `useEffect` que capturan el valor del render en que se
   * crearon: con el estado pelado, cambiar de vista y arrastrar una pared medía
   * contra la ANTERIOR. Mismo motivo por el que ya existe `marcadoRef`.
   *
   * Se llama `vistaObra` y no `vista` porque el editor YA tiene una `vista`: el
   * encuadre de la cámara. Son cosas distintas y el nombre corto estaba tomado.
   */
  const vistaObraRef = useRef<VistaObra>(vistaObra);
  vistaObraRef.current = vistaObra;
  /** Si el menú de vistas está desplegado. */
  const [menuVista, setMenuVista] = useState(false);
  /**
   * La capa ACTIVA: la única manipulable mientras esté encendida.
   *
   * Es la regla que pidió Daniel el 5-oct-2026 y la razón es de dedo gordo:
   * «si estoy en el botón de objetos, mobiliario, no puedo editar paredes para
   * no moverlas por accidente». En `null` —el estado de arranque— todo se
   * manipula, que es como funcionó siempre: «al menos de que tenga todos […]
   * prendidos o apagados y ahí sí puedo manipular cualquiera».
   *
   * De paso arregla el agarre remoto del mueble en el celular: con la capa de
   * mobiliario activa las paredes dejan de recibir el dedo, así que apoyar
   * encima de una ya no toma la pared en vez de mover el mueble. Era el
   * «hace moverse, pero no se mueve» del reporte.
   */
  const [capa, setCapa] = useState<Capa | null>(null);
  /** El ojo viejo, ahora derivado: siete sitios del dibujo lo leen por nombre. */
  const verAreas = ver.area;
  /** Enciende la capa de áreas. Lo hacen medir, crear un área y la tabla. */
  const encenderAreas = () => setVer((v) => ({ ...v, area: true }));
  /**
   * ¿Se puede TOCAR lo de esta capa?
   *
   * Dos condiciones y las dos importan: que esté a la vista —lo que no se ve no
   * se toca, si no el editor responde a dedos sobre cosas invisibles— y que no
   * haya OTRA capa mandando.
   */
  const puede = (c: Capa) => ver[c] && (capa == null || capa === c);
  /**
   * El estilo que le quita el dedo a una capa que no manda.
   *
   * `pointer-events: none` y no un `if` en cada handler: así el toque no se
   * «come» ni se ignora, SIGUE DE LARGO hasta el lienzo. Eso es lo que deja que
   * el agarre remoto del mueble funcione encima de una pared —y que el paneo
   * del plano vuelva a funcionar apoyando sobre lo que está protegido— sin
   * tocar una sola línea de `tomarRemoto` ni de la lista negra.
   */
  const dedo = (c: Capa) => (puede(c) ? undefined : ({ pointerEvents: 'none' } as const));
  /** Si la tabla de áreas está abierta. */
  const [tabla, setTabla] = useState(false);
  /**
   * Si el resumen de cantidades está desplegado.
   *
   * Lo pidió Daniel el 9-oct-2026: «al lado derecho de donde está el botón para
   * cambiar de nivel, que haya un botón que sea un símbolo de info y que lo que
   * despliega al darle clic es que dé un resumen de cantidades de
   * tomacorrientes, luces, etcétera. Que etcétera me refiero a que se le vayan a
   * ir agregando más detalles de este tipo» — o sea que la caja es el lugar de
   * los conteos de la casa, y hoy arranca con el eléctrico adentro.
   */
  const [resumen, setResumen] = useState(false);
  const [opacidad, setOpacidad] = useState(0.55);
  /**
   * Si el plano de fondo —la REFERENCIA— se pinta.
   *
   * Es booleano aparte de la opacidad a propósito: bajar el deslizador a 0
   * también esconde el plano, pero después no hay cómo volver sin acordarse de
   * en cuánto estaba. El doble toque lo apaga y lo prende dejando el porcentaje
   * donde quedó, que es lo que hace el ojo de cualquier capa.
   */
  const [verFondo, setVerFondo] = useState(true);
  /** Si el deslizador de porcentaje de la referencia está desplegado. */
  const [reglaFondo, setReglaFondo] = useState(false);
  const [fondo, setFondo] = useState<string | null>(null);
  const [paginaPt, setPaginaPt] = useState<{ w: number; h: number } | null>(null);
  const [errorPdf, setErrorPdf] = useState<string | null>(null);
  const [tick, setTick] = useState(0);        // repinta tras mutar en sitio
  const [modo, setModo] = useState<Modo | null>(null);
  /**
   * La pieza elegida en la vitrina que todavía no se puso en ningún lado.
   *
   * Va junto con `modo === 'objeto'` y nunca sin él: el modo es lo que hace que
   * el toque en el lienzo signifique «ponelo acá» en vez de «tomá lo que haya
   * ahí», y este es el qué.
   */
  const [armado, setArmado] = useState<{
    modelo: ModeloMueble; extra?: Partial<Mueble>;
  } | null>(null);
  const [trazo, setTrazo] = useState<Trazo | null>(null);
  const [paleta, setPaleta] = useState<Anclaje | null>(null);
  // Lo que el usuario corrió la paleta a mano. Se guarda acá y no adentro de
  // `Flotante` porque la paleta se remonta sola —cambiar de pared cambia su
  // `key`— y el corrimiento tiene que sobrevivir a eso.
  const [corrimiento, setCorrimiento] = useState<{ dx: number; dy: number } | null>(null);
  /** Si la paleta de agregar área está abierta. */
  const [herramientas, setHerramientas] = useState(false);
  /** Su corrimiento es APARTE: es la única paleta que no se ancla a un objeto
   *  del dibujo, así que no comparte el «vuelve a donde cae» de las otras. */
  const [corrHerr, setCorrHerr] = useState<{ dx: number; dy: number } | null>(null);
  /** Si la paleta de agregar pared está abierta, y con qué método. */
  const [herrPared, setHerrPared] = useState(false);
  const [metodoPared, setMetodoPared] = useState<MetodoPared>('una');
  const [corrHerrP, setCorrHerrP] = useState<{ dx: number; dy: number } | null>(null);
  /** El tamaño del lienzo, para plantar ahí la paleta de herramientas. */
  const [marcoLienzo, setMarcoLienzo] = useState({ w: 0, h: 0 });
  /**
   * Lo que mide la barra de abajo AHORA MISMO, para no plantar las paletas
   * debajo de ella.
   *
   * Medido y no la constante `ALTO_BARRA_PIE`: la barra crece hacia arriba con
   * cada aviso, y con uno puesto la paleta nacía 28 px más abajo de lo que
   * debía y salía cortada. Se vio en la foto del 8-oct-2026, no en el código.
   */
  const pieRef = useRef<HTMLDivElement>(null);
  const [altoPie, setAltoPie] = useState(ALTO_BARRA_PIE);
  /** Si la paleta va clavada abajo (teléfono) o flotando junto al elemento. */
  const angosta = useAngosta();
  /**
   * El hueco del pill donde la `Franja` portea la paleta, arriba de las capas.
   *
   * Estado y no `ref` a propósito: el portal necesita que el componente vuelva a
   * pintar cuando el nodo existe, y un `ref` no dispara repintado —la franja se
   * quedaría sin destino para siempre y la paleta no saldría nunca—.
   */
  const [ranura, setRanura] = useState<HTMLDivElement | null>(null);
  /** Dónde va a caer el gesto en curso, con el imán ya resuelto. */
  const [mira, setMira] = useState<Mira | null>(null);
  /**
   * Dónde está parada la pieza armada AHORA, antes de soltarla.
   *
   * Es el punto del fantasma: el módulo elegido dibujado en el plano, con su
   * medida real y su símbolo, siguiendo al puntero hasta que se suelta. Daniel
   * lo pidió con todas las letras el 11-oct-2026 —«pensé que el sello era
   * previsualizar el objeto en el plano antes de colocarlo»—, y es lo que un
   * sello de verdad hace: se ve la huella antes de estampar.
   *
   * Guarda el punto y no la pieza porque la pieza se deriva: el fantasma tiene
   * que pasar por el MISMO imán a pared que `nacerMueble`, o mentiría —se vería
   * en medio del cuarto y nacería pegado—.
   */
  const [fantasma, setFantasma] = useState<{ x: number; y: number } | null>(null);
  /** La marca del corte de una pared, mientras se apunta. */
  const [corte, setCorte] = useState<Corte | null>(null);
  /**
   * Si hay puntería en pantalla AHORA MISMO, para que el zoom la pueda repintar.
   *
   * La mira se redibuja sola mientras el dedo pasea —cada toque la vuelve a
   * poner—, pero queda clavada esperando el segundo apoyo, y ahí uno hace zoom
   * para ubicar el final: sin esto el aro se quedaría del tamaño del zoom
   * anterior. Es una ref y no un estado porque la lee `onTransformed`, que
   * dispara en CADA cuadro del pinch; repintar el plano entero 60 veces por
   * segundo cuando no hay nada que apuntar —que es casi siempre— sería pagar la
   * fluidez del gesto más usado del editor por un caso que no está ocurriendo.
   */
  const hayPunteria = useRef(false);
  hayPunteria.current = mira != null || corte != null;
  /** El divisor tomado, por id. */
  const [divSel, setDivSel] = useState<number | null>(null);
  /** La cota PROPIA tomada, por id. Las automáticas no se toman: ver `PaletaCota`. */
  const [cotaSel, setCotaSel] = useState<number | null>(null);
  /**
   * La pared VECINA cuya cota está en edición, por id. Null = ninguna.
   *
   * No es una selección: la pared tomada sigue siendo la de `sel`, y ésta es
   * nada más el número que se está escribiendo. Por eso vive aparte y no entra
   * en `sel` —si entrara, la paleta pasaría a ser la de dos paredes y el campo
   * desaparecería justo al abrirlo—.
   */
  const [vecinaSel, setVecinaSel] = useState<number | null>(null);
  /** El área libre tomada, por id. */
  const [libreSel, setLibreSel] = useState<number | null>(null);
  /**
   * El LADO del polígono que está tomado, y de qué área.
   *
   * Es el equivalente de `sel` para un área creada, y existe desde el
   * 8-oct-2026 por el mismo pedido que trajo las cotas a las áreas: «al igual
   * como se maneja con el tema de seleccionar un lado, poder permitir editar
   * alguno de los otros lados y que cambie la dimensión». Sin un lado tomado no
   * hay contra qué medir: las cotas se ven, pero ninguna se escribe.
   */
  const [ladoSel, setLadoSel] = useState<{ id: number; lado: number } | null>(null);
  /**
   * El lado VECINO cuya cota está en edición, por índice. Null = ninguno.
   *
   * Gemelo exacto de `vecinaSel` y por la misma razón: no es una selección —el
   * tomado sigue siendo `ladoSel`— sino el número que se está escribiendo.
   */
  const [ladoVecinoSel, setLadoVecinoSel] = useState<number | null>(null);
  /**
   * Qué se le está haciendo a los NODOS del área tomada, o null si nada.
   *
   * Son los dos verbos que Daniel pidió el 8-oct-2026 en vez de los tres
   * botones de método: «un botón de dividir y ese botón de dividir lo que hace
   * es que puede dividir uno los lados para generar más puntos de anclaje» y
   * «otro botón más, que sería también el de borrar un punto de anclaje que
   * actualmente no existe».
   *
   * Es un MODO y no un botón que actúa solo, porque la acción necesita un
   * segundo dato que está en el dibujo —cuál lado, cuál nodo—: se aprieta el
   * botón, se toca en el plano, y se puede repetir sin volver a la paleta.
   *
   * Vive aparte de `modo` a propósito. `modo` esconde la paleta flotante (ver el
   * efecto que la planta), así que meter esto ahí haría desaparecer el botón que
   * se acaba de apretar, y con él la única forma de salir.
   */
  const [nodo, setNodo] = useState<'partir' | 'quitar' | null>(null);
  /**
   * El eje al que el imán ortogonal pegó el vértice que se está arrastrando.
   *
   * Existe sólo para DIBUJARLO. Un imán que corrige en silencio se siente como
   * un plano que no obedece: el usuario ve que el punto no fue donde lo llevó y
   * no tiene cómo saber si es una ayuda o un defecto. La guía punteada lo
   * convierte en una promesa visible —«estás alineado con ese vértice»— y de
   * paso enseña que existe. Ver `imantarVertice`.
   */
  const [iman, setIman] = useState<Iman | null>(null);
  /** El mueble tomado, por id. */
  const [muebleSel, setMuebleSel] = useState<number | null>(null);
  /**
   * Qué esquina del mueble tomado está en edición, como índice de
   * `Mueble.radios`. Null = la paleta muestra las medidas.
   *
   * Es un índice y no un lado del plano porque lo que se edita es el radio, que
   * vive en coordenadas del dibujo: guardar `'ne'` obligaría a deshacer el giro
   * de nuevo en cada sitio que lo lea.
   */
  const [esquinaSel, setEsquinaSel] = useState<number | null>(null);
  /**
   * Si el mueble tomado tiene el candado ABIERTO: sus agarres del plano vuelven.
   *
   * Nace cerrado en cada selección —`useEffect` sobre `muebleSel`— y es
   * exactamente lo que pidió Daniel el 5-oct-2026: «la función que se omitió
   * antes es hacerla disponible en caso de que se requiera, pero que por defecto
   * venga limitada a como está actualmente».
   *
   * Que sea UN booleano y no uno por mueble es a propósito: el candado no es una
   * propiedad de la pieza —no se guarda en el modelo, no viaja al PDF— sino el
   * permiso de este momento. Soltar el mueble y volver a tomarlo lo cierra, que
   * es lo que protege del clic accidental que lo motivó.
   */
  const [candado, setCandado] = useState(false);
  /**
   * El portapapeles de mobiliario: el molde del último mueble copiado.
   *
   * Guarda la medida VIVA y el giro, no los del catálogo: lo que uno copia es la
   * pieza que ya ajustó. Y guarda un molde en vez del mueble entero porque un
   * `id` y una posición copiados no significan nada en el destino.
   */
  const [copiado, setCopiado] = useState<Copia | null>(null);
  /** Si el catálogo de mobiliario está abierto. */
  const [catalogo, setCatalogo] = useState(false);
  /**
   * Si el catálogo abierto está eligiendo el REEMPLAZO de la pieza tomada.
   *
   * Es la misma vitrina con otro significado, y por eso es una bandera y no un
   * segundo componente: lo que cambia es el gesto —un toque en vez de apoyar y
   * arrastrar— y qué hace con el módulo elegido.
   */
  const [reemplazando, setReemplazando] = useState(false);
  /** Su corrimiento, aparte como el de la paleta de herramientas: tampoco se
   *  ancla a un objeto del dibujo. */
  const [corrCat, setCorrCat] = useState<{ dx: number; dy: number } | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  /** El botón de descartar ya pidió confirmación y espera el segundo toque. */
  const [confirmaDescarte, setConfirmaDescarte] = useState(false);
  /** El menú de los tres puntos de la cabecera (exportar, descartar). */
  const [menu, setMenu] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // Lo que Daniel ya leyó y mandó a callar. Guarda el TEXTO y no un booleano a
  // propósito: así el aviso de recinto abierto vuelve solo cuando cambia —si se
  // abre OTRO cuarto, el mensaje es otro y se tiene que ver— pero no reaparece
  // en cada remedición diciendo exactamente lo mismo. Nace del pedido del
  // 19-oct-2026: «esos textos ni siquiera puedo quitarlos».
  const [callado, setCallado] = useState<string | null>(null);
  /**
   * La oferta de APRENDER la huella que se acaba de corregir a mano.
   *
   * Es el paso 2 de lo que pidió Daniel el 14-oct-2026 —«debería existir la
   * manera de entrenar […] al final es un inventario de máximo 20 objetos»—, y
   * va deliberadamente SIN pantalla aparte: entrenar es el trabajo que ya está
   * haciendo, no un trámite nuevo. Sale cuando reemplaza una pieza que vino del
   * plano, y se contesta con un toque o se ignora.
   */
  const [oferta, setOferta] = useState<{ w: number; h: number; clave: string; nombre: string } | null>(null);
  /**
   * El render de venta: `null` = nadie lo pidió, `'…'` = está corriendo.
   *
   * La URL es un `blob:` y hay que soltarla a mano; vive en un ref aparte para
   * que revocarla no dependa de que el efecto de limpieza lea el estado viejo.
   */
  const [render, setRender] = useState<{ url: string } | 'eligiendo' | 'generando' | null>(null);
  const renderUrlRef = useRef<string | null>(null);
  const renderAbortRef = useRef<AbortController | null>(null);
  /**
   * Qué referencia de estilo se le manda al motor, y la que el usuario subió.
   *
   * Viven en el componente y no en `localStorage` a propósito: la elección es
   * de esta sesión de venta, y una preferencia pegajosa escondida es justo lo
   * que hace que meses después nadie entienda por qué sus láminas salen
   * distintas a las del compañero de al lado.
   */
  const [estiloId, setEstiloId] = useState<string>(ESTILO_POR_DEFECTO);
  const [subida, setSubida] = useState<{ nombre: string; url: string; blob: Blob } | null>(null);
  const subidaUrlRef = useRef<string | null>(null);

  // Cerrar el editor con un render a medias no debe dejar la petición viva: son
  // 40-90 s de un motor que se cobra. Y el `blob:` del resultado se suelta acá
  // porque el navegador no lo libera solo mientras el documento exista.
  useEffect(() => () => {
    renderAbortRef.current?.abort();
    if (renderUrlRef.current) URL.revokeObjectURL(renderUrlRef.current);
    if (subidaUrlRef.current) URL.revokeObjectURL(subidaUrlRef.current);
  }, []);

  /** La referencia que el usuario subió sustituye a la del catálogo. */
  const tomarSubida = (f: File | undefined) => {
    if (!f) return;
    if (subidaUrlRef.current) URL.revokeObjectURL(subidaUrlRef.current);
    const url = URL.createObjectURL(f);
    subidaUrlRef.current = url;
    setSubida({ nombre: f.name, url, blob: f });
    setEstiloId('subida');
  };

  /**
   * El material de cada superficie, para la imagen que va al motor.
   *
   * NO es la paleta de la pantalla (`TINTA_CLASE`), que habla de selección. Acá
   * cada tono nombra un MATERIAL de los que el prompt maestro ya describe en su
   * sección 3, así que la mancha y el párrafo dicen lo mismo y no compiten.
   */
  const MATERIAL: Record<Clase, string> = {
    interna: '#dcd8d2',   // porcelanato gris claro cálido
    externa: '#b7b9b6',   // concreto de terraza y sendero
    lote: '#9fae86',      // césped
    azotea: '#c3c5c3',    // losa
  };

  /**
   * Las superficies que se pintan DEBAJO del dibujo antes de mandarlo a
   * renderizar: el suelo de la casa y el del lote.
   *
   * Existe por una medición del 14-oct-2026: sin suelo, la base es un dibujo de
   * líneas sobre blanco y el motor completa el terreno copiándolo de la imagen
   * de referencia —apareció una cochera con dos autos que no estaban en ninguna
   * parte—. Con suelo, el motor pasa de inventar a texturizar.
   *
   * Se mide acá y no se lee de la pantalla a propósito: las áreas sólo están en
   * el DOM cuando el ojo las enciende, y la lámina no puede depender de qué
   * tenía prendido el usuario.
   */
  const suelo = (): Mancha[] => {
    // La geometría de las áreas libres vive en la planta; `Medicion.libres` sólo
    // trae su cómputo (id, nombre, área, clase). Por eso se leen de las dos
    // partes, y la planta se toma SEGÚN LA VISTA para que las dos coincidan.
    const vista = plantaSegunVista(planta, vistaObraRef.current);
    const med = medirSegunVista(planta, vistaObraRef.current);
    const libres = vista.libres ?? [];
    const forma = (l: Libre): [number, number, number, number][] =>
      l.celdas ?? [[l.x, l.y, l.w, l.h]];
    return [
      // El lote primero: es la plataforma y todo lo demás se apoya encima.
      ...libres
        .filter((l) => l.clase === 'lote')
        .map((l) => ({ celdas: forma(l), color: MATERIAL.lote })),
      ...libres
        .filter((l) => l.clase !== 'lote')
        // Sin clase se trata como externa: un área dibujada afuera del casco es
        // terraza o acceso mucho más seguido que cualquier otra cosa.
        .map((l) => ({ celdas: forma(l), color: MATERIAL[l.clase ?? 'externa'] })),
      // Y los recintos al final: el piso de adentro tapa cualquier área suelta
      // que los solape.
      ...med.regiones.map((g) => ({ celdas: g.celdas, color: MATERIAL[g.clase] })),
    ];
  };

  /**
   * «Generar planta render»: un toque y vuelve la lámina.
   *
   * Todo lo que decide el resultado —prompt, modelo, fidelidad, tamaño— vive en
   * el backend a propósito. Quien vende un sábado no tiene que elegir nada, y
   * un parámetro suelto en el navegador es un parámetro que alguien cambia sin
   * saber qué rompe.
   *
   * Lo único que SÍ elige el usuario es la **referencia de estilo**, porque es
   * lo que más cambia el resultado y porque «ninguna» es hoy la opción que mejor
   * respeta la planta (ver `estilos-render.ts`). Se elige antes de generar, en
   * el paso `'eligiendo'`; el botón «Otra» repite con la misma sin volver a
   * preguntar.
   */
  const generarRender = useCallback(async () => {
    if (render === 'generando') return;

    // Lo anterior se suelta ANTES de pedir lo nuevo: dos láminas de varios MB
    // retenidas por un `blob:` vivo es memoria que no vuelve.
    if (renderUrlRef.current) {
      URL.revokeObjectURL(renderUrlRef.current);
      renderUrlRef.current = null;
    }

    const ctrl = new AbortController();
    renderAbortRef.current = ctrl;
    setAviso(null);
    setRender('generando');

    try {
      const { png, ancho, alto } = await rasterizarLienzo(suelo());

      // `catch(() => undefined)` y no un `if`: que la referencia falte es un
      // caso normal (la opción «sin referencia» lo es a propósito), no un error
      // que deba frenar el render.
      const elegida = ESTILOS_RENDER.find((e) => e.id === estiloId);
      const estilo = estiloId === 'subida'
        ? subida?.blob
        : elegida?.url
          ? await fetch(elegida.url).then((r) => (r.ok ? r.blob() : undefined)).catch(() => undefined)
          : undefined;

      const salida = await api.planta.render(png, ancho, alto, estilo, ctrl.signal);
      const url = URL.createObjectURL(salida);
      renderUrlRef.current = url;
      setRender({ url });
    } catch (e) {
      // Abortar es una salida legítima (el usuario cerró), no un fallo que
      // merezca un cartel.
      if ((e as Error).name === 'AbortError') { setRender(null); return; }
      setRender(null);
      setAviso((e as Error).message || 'No se pudo generar la lámina.');
    }
  }, [render, estiloId, subida]);
  /**
   * Si este usuario está en `ObjetosPlantaPublicador`.
   *
   * Arranca apagado y sólo lo enciende el servidor: sin tabla, sin sesión o sin
   * red el botón de publicar no aparece. Es el lado correcto para equivocarse —
   * un botón que promete lo que el backend va a rechazar con 403 enseña que la
   * función no sirve, y después nadie vuelve a tocarlo cuando sí sirve.
   */
  const [puedePublicar, setPuedePublicar] = useState(false);
  // El historial son FOTOS del modelo en texto, no una lista de operaciones
  // inversas. Hay ocho verbos que mutan las paredes en sitio; escribirle una
  // inversa a cada uno son ocho oportunidades de que una quede mal, y una foto
  // no tiene cómo quedar mal. El tope es para que la memoria no crezca sin fin
  // en un celular: treinta pasos atrás es más de lo que nadie deshace seguido.
  const [atras, setAtras] = useState<string[]>([]);
  const [adelante, setAdelante] = useState<string[]>([]);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const lienzoRef = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef<ReactZoomPanPinchRef | null>(null);
  const agarreRef = useRef<Agarre | null>(null);
  /**
   * De qué puntero es el agarre vivo.
   *
   * Hace falta para reconocer un agarre COLGADO: si llega un apoyo de un dedo
   * distinto al que armó el que está puesto, el gesto anterior ya terminó —un
   * dedo nuevo es prueba de que el viejo se levantó— aunque su `pointerup`
   * nunca haya vuelto. Sin esto, un solo gesto perdido deja el arrastre muerto
   * hasta recargar la página.
   */
  const dedoRef = useRef<number | null>(null);
  // La guía del imán se lee durante el repintado desde un ref y no desde el
  // estado: el arrastre ya bumpea `tick` en cada cuadro, así que no hace falta
  // un segundo repintado por cuadro para dibujar una línea de puntos.
  const guiaRef = useRef<Guia | null>(null);
  /**
   * Dónde se apoyó el puntero y si desde entonces se corrió lo suficiente para
   * contar como arrastre. Va en un ref y no en el estado porque se consulta
   * dentro del mismo gesto: un `setState` acá llegaría tarde al `pointerup`.
   */
  const bajoRef = useRef<{ x: number; y: number; corrido: boolean; dedo: boolean } | null>(null);
  /**
   * El primer toque de un doble toque todavía sin pareja: sobre QUÉ cayó,
   * cuándo y dónde. Va en un ref por lo mismo que `bajoRef`: se consulta en el
   * toque siguiente, y un `setState` llegaría tarde.
   */
  const dobleRef = useRef<{ que: string; t: number; x: number; y: number } | null>(null);
  /**
   * Sobre qué pared se apoyó el dedo, mientras no se levante. El doble toque de
   * las paredes se resuelve en el `pointerup` —ver `soltar`— y para entonces ya
   * no hay evento que diga a quién se tocó.
   */
  const pendienteRef = useRef<{ que: string; id: number; x: number; y: number } | null>(null);
  // Si el gesto en curso ya dejó su foto en el historial. Un arrastre entra a
  // `arrastrar` decenas de veces y todas son UN paso para el que deshace.
  const marcadoRef = useRef(false);
  /**
   * El UMBRAL del gesto en curso: dónde apoyó el dedo y si ya se corrió lo
   * suficiente para que esto cuente como arrastre.
   *
   * Pedido de Daniel el 8-oct-2026: «muchas veces por error muevo un objeto que
   * no quería […] me gustaría buscar alguna estrategia para que no sea tan
   * sensible». Hasta acá el agarre se armaba en el `pointerdown` y el PRIMER
   * `pointermove` ya movía, así que el temblor de apoyar el pulgar bastaba.
   */
  const umbralRef = useRef<
    { x: number; y: number; ok: boolean; soloToque: boolean } | null
  >(null);
  // Dónde apoyó el dedo sobre un área. Las áreas NO se excluyen del pan —son el
  // interior entero de la casa, y excluirlas dejaría el plano sin poder
  // arrastrarse—, así que hay que distinguir a mano el toque del arrastre: si el
  // dedo se corrió, eso fue un pan y no una selección.
  const tocoRef = useRef<{ x: number; y: number } | null>(null);
  /**
   * ¿MANDA EL DEDO? Es el interruptor del gesto de DOS TIEMPOS.
   *
   * Daniel, 8-oct-2026, después de probar una maqueta con los tres modelos
   * posibles: «hacé el B para todo». El B dice que con el dedo, arrastrar sobre
   * algo que NO está tomado PANEA el plano —el objeto ni se entera—; un toque
   * corto lo toma; y recién entonces arrastrarlo lo mueve. El motivo es su
   * queja, que no era sobre paredes: «si me estoy moviendo y de casualidad le di
   * tap a donde estaba un mobiliario, mueve el mobiliario […] pasa lo mismo al
   * pasar por cualquier objeto».
   *
   * Con MOUSE el modelo viejo se queda tal cual —apoyar y arrastrar mueve— y eso
   * no es una concesión: con mouse el puntero se ve, el clic es preciso y el
   * paneo tiene la rueda y el botón del medio. El peaje del segundo gesto sólo
   * se paga donde el problema existe.
   *
   * Cómo se sabe cuál de los dos manda, sin preguntarle nada a nadie: **un mouse
   * se anuncia pasando por encima; un dedo nunca.** Se arranca creyéndole a la
   * pantalla (`pointer: coarse` = teléfono) y el primer `pointermove` de un
   * mouse —que llega ANTES de cualquier apoyo— lo corrige. Mirar sólo el
   * `pointerdown` no alcanzaría: para cuando llega, la clase de agarre ya se
   * pintó y `react-zoom-pan-pinch` ya decidió si panea.
   */
  const [dedoManda, setDedoManda] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  );
  useEffect(() => {
    const mirar = (e: PointerEvent) => setDedoManda(e.pointerType !== 'mouse');
    window.addEventListener('pointermove', mirar, { passive: true });
    window.addEventListener('pointerdown', mirar, { passive: true, capture: true });
    return () => {
      window.removeEventListener('pointermove', mirar);
      window.removeEventListener('pointerdown', mirar, true);
    };
  }, []);
  /**
   * El catálogo compartido y el permiso de publicar, una vez al abrir.
   *
   * No se reintenta ni se refresca solo: si el servidor no contestó, el editor
   * funciona igual con lo de fábrica y lo propio, que es lo que hizo siempre.
   */
  useEffect(() => {
    let vivo = true;
    void traerObjetos().then((puede) => {
      if (!vivo) return;
      setPuedePublicar(puede);
      setTick((k) => k + 1);
    });
    return () => { vivo = false; };
  }, []);
  /**
   * ¿Hay un primer tiempo en curso? O sea: el dedo está apoyado sobre algo que
   * no estaba tomado, y todavía no se sabe si esto va a ser toque o paneo.
   *
   * Hace falta leerlo DURANTE el render, y por eso sale de un ref y no de un
   * estado: el render que importa es el que dispara el propio `pointerdown` al
   * seleccionar, y un `setState` no habría llegado a tiempo.
   */
  function enPrimerTiempo() {
    const u = umbralRef.current;
    return u != null && u.soloToque && !u.ok;
  }
  /**
   * La clase que decide si el dedo EDITA o PANEA, para el CUERPO de un objeto.
   *
   * Con `tomado` en false y el dedo mandando devuelve vacío: sin `AGARRE`, el
   * `pointerdown` sigue de largo hasta `react-zoom-pan-pinch` y el gesto es un
   * paneo. Los agarres chicos —puntas, lados, vértices, esquinas— NO pasan por
   * acá y no hace falta: sólo se dibujan cuando su dueño ya está tomado, así que
   * ya son el segundo tiempo por construcción.
   *
   * El primer tiempo manda por encima de `tomado`, y esto NO es un detalle:
   * `react-zoom-pan-pinch` engancha `touchstart`, que el navegador dispara
   * DESPUÉS del `pointerdown`. Para cuando la librería mira la clase, React ya
   * repintó con la selección que acaba de hacer ese mismo toque, y el cuerpo
   * habría recuperado `AGARRE` justo a tiempo de matar el paneo. Medido: sin
   * esta línea el primer tiempo no paneaba —sólo dejaba de mover—, que es media
   * función.
   */
  function cuerpoAgarrable(tomado: boolean) {
    if (enPrimerTiempo()) return '';
    return dedoManda && !tomado ? '' : AGARRE;
  }
  const planta = plantas[iPlanta];
  /**
   * «Planta en PDF»: la lámina en VECTORES, que es otra entrega que el render.
   *
   * El render es una imagen para vender; esto es un plano para construir, y la
   * diferencia que importa es que se puede MEDIR con la regla del visor. Por eso
   * no pasa por `rasterizarLienzo`: el lienzo ya es un `<svg>` y bajar a píxeles
   * sólo perdería la escala, que es lo único que convierte un dibujo en plano.
   *
   * Sale lo que se está VIENDO —la planta abierta y la vista de obra elegida—,
   * que es lo que pidió Daniel el 16-oct-2026: «al generar una edición de uno de
   * esos planos, poder exportarlo en PDF». Si la vista es «Demoliciones», el PDF
   * es el plano de demolición; no hay una segunda decisión escondida acá.
   *
   * Todavía NO lleva cajetín: ver `exportar-lamina.ts`.
   */
  const exportarPDF = useCallback(() => {
    if (!planta) { setAviso('No hay planta que exportar.'); return; }
    const v = vistaObraRef.current;
    try {
      bajarLamina(plantaSegunVista(planta, v), medirSegunVista(planta, v), {
        titulo: `Planta Arquitectónica · ${planta.nombre}`,
        subtitulo: [titulo, ROTULO_VISTA[v]].filter(Boolean).join(' · '),
      });
    } catch (e) {
      setAviso((e as Error).message || 'No se pudo escribir el PDF.');
    }
    // `tick` entra a propósito: las operaciones mutan las paredes EN SITIO y sin
    // él se exportaría la casa como estaba antes del último gesto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planta, titulo, tick, iPlanta]);
  /**
   * Si en esta planta alguien marcó algo con un estado de rehabilitación.
   *
   * Es lo que decide si el botón de tipos de vista existe: mientras todo sea
   * `existente` las siete vistas dibujan exactamente lo mismo, y un control que
   * no cambia nada es ruido en una barra que en un teléfono ya va justa.
   *
   * Va contra `tick` como los demás memos del editor: la planta se muta EN SITIO
   * —marcar una pared le escribe el campo al objeto— así que la referencia no
   * cambia y sin el `tick` el botón no aparecería hasta el siguiente repintado
   * por otro motivo.
   */
  const hayEstados = useMemo(() => {
    const marcado = (x: { estado?: Estado }) => !!x.estado && x.estado !== 'existente';
    return planta.paredes.some(marcado)
      || (planta.libres ?? []).some(marcado)
      || (planta.muebles ?? []).some(marcado)
      || (planta.divisores ?? []).some(marcado)
      || (planta.cotas ?? []).some(marcado)
      || Object.values(planta.estados ?? {}).some((e) => e !== 'existente');
  }, [planta, tick, iPlanta]);
  /**
   * Lo que va a quedar construido, medido aparte, para la línea del precio de
   * la tabla. `null` cuando la vista ya mide como el estado final —los dos
   * números son el mismo— o cuando la tabla está cerrada.
   *
   * Lo de `tabla` no es tacañería: esto es una inundación entera y se
   * recalcularía con cada `tick`, o sea con cada centímetro que se arrastra una
   * pared, para alimentar una línea que nadie está mirando.
   *
   * Y va por `medirPlanta(plantaSegunVista(…))` y NO por `medirSegunVista`, que
   * sería lo natural: ese escribe el ancla sobre la planta cuando todavía no
   * está anclada, y un `useMemo` que muta el modelo mientras React pinta es una
   * trampa que un día se cobra. Acá no hace falta —cuando la tabla está abierta
   * ya se midió, o sea ya se ancló— así que se pide la versión que sólo lee.
   */
  const medicionFinal = useMemo(
    () => (tabla && !mismaGeometria(vistaObra, 'final')
      ? medirPlanta(plantaSegunVista(planta, 'final'))
      : null),
    [planta, tick, iPlanta, tabla, vistaObra],
  );
  /**
   * Cambia de vista y vuelve a medir en el mismo golpe.
   *
   * El ref se adelanta al estado a propósito: `setVistaObra` no actualiza
   * `vistaObraRef` hasta el próximo render, y la medición de la línea siguiente
   * leería la vista vieja. Es el mismo motivo por el que el ref existe.
   */
  const cambiarVista = (v: VistaObra) => {
    vistaObraRef.current = v;
    setVistaObra(v);
    if (medicion) setMedicion(medirSegunVista(planta, v));
  };
  /** La única pared tomada, o null si no hay exactamente una. */
  const uno = sel.length === 1 ? sel[0] : null;
  /**
   * Qué se pinta de `TINTA_MULTI`: el MODO, no la cuenta.
   *
   * Antes era `sel.length > 1`, y Daniel lo mandó cambiar (6-oct-2026): «que al
   * en modo selección múltiple en seguida el primer elemento seleccionado se
   * ponga del color morado ya que actualmente se pone morado hasta que se
   * selecciona el segundo y eso confunde si en saber si está o no en modo
   * selección múltiple». Tenía razón: el morado es el acuse de que el gesto
   * cambió de significado, y llega tarde si espera a la segunda pared —cuando
   * ya no hace falta, porque la selección doble se ve sola—. Con `multi` el
   * color es el estado del editor, que es lo que el morado siempre quiso decir.
   */
  const variasParedes = multi || sel.length > 1;
  /** El vano tomado con su pared, o null. Se busca en cada repintado y no se
   *  memoriza: las operaciones mutan las paredes en sitio, así que una lista
   *  guardada de un repintado atrás puede no tener ya ni la pared ni el vano. */
  const paredDelVano = vanoSel ? planta?.paredes.find((x) => x.id === vanoSel.pared) : undefined;
  const vanoTomado = vanoSel ? paredDelVano?.vanos.find((x) => x.id === vanoSel.vano) : undefined;
  const vano = paredDelVano && vanoTomado ? { w: paredDelVano, v: vanoTomado } : null;
  /** El área tomada, o null. Un nombre que ya no existe —se volvió a medir y la
   *  región se partió— no es un error: simplemente no hay nada tomado. */
  const region = medicion?.regiones.find((g) => g.nombre === recinto) ?? null;
  /** Todas las áreas tomadas. Se filtra contra la medición viva por lo mismo:
   *  un nombre que ya no existe sale solo de la lista. */
  const regiones = medicion?.regiones.filter((g) => recintos.includes(g.nombre)) ?? [];
  /** El divisor tomado, o null. */
  const divisor = planta?.divisores?.find((d) => d.id === divSel) ?? null;
  /** La cota propia tomada, o null. */
  const cota = planta?.cotas?.find((k) => k.id === cotaSel) ?? null;
  /**
   * LAS COTAS DE LAS PAREDES, calculadas de la geometría viva.
   *
   * Acá está la respuesta a la pregunta que Daniel dejó abierta —«si quiero ver
   * la planta acotada, con un botón la genera o ya quedan generadas»—: no hay
   * botón de generar porque no hay nada que guardar. Cada pared entrega su cota
   * en cada repintado, así que estirar una pared mueve su cota en el mismo
   * cuadro. Una cota guardada sería una FOTO: diría 3,40 donde el plano ya mide
   * 3,85, y una cota que miente es peor que ninguna.
   *
   * Va A CENTRO DE PARED, que es lo que pidió: la línea se apoya en el eje `c`
   * de la pared —no en una de sus caras— y corre de `a` a `b`. Ese es el mismo
   * eje contra el que se miden las áreas desde la ronda del 4-oct-2026, así que
   * el número de la cota y el del área nunca se contradicen.
   *
   * Sólo las paredes QUE SE VEN: con el ojo de paredes apagado no hay plano que
   * acotar. Es textual —«todas las paredes que se vean existentes»—.
   */
  const cotasAuto = useMemo<Cota[]>(() => {
    if (!planta || !ver.pared) return [];
    return planta.paredes.map((w) => (w.o === 'v'
      ? { id: w.id, x1: w.c, y1: w.a, x2: w.c, y2: w.b }
      : { id: w.id, x1: w.a, y1: w.c, x2: w.b, y2: w.c }));
    // `tick` entra a propósito: las operaciones mutan las paredes EN SITIO y sin
    // él las cotas se quedarían dibujadas donde estaba la pared antes del gesto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planta, ver.pared, tick, iPlanta]);
  /**
   * LAS PAREDES QUE CUELGAN DE LA TOMADA: las candidatas a que se les escriba la
   * medida.
   *
   * Son exactamente las de `pegadas` —las perpendiculares que mueren contra
   * ella—, y no es una coincidencia que sean las mismas que el arrastre se lleva:
   * escribirle la medida a una de éstas ES correr la pared tomada, así que si la
   * lista fuera otra, el número y el movimiento no hablarían de lo mismo.
   *
   * Las PARALELAS quedan afuera a propósito. La cota de una paralela mide su
   * propio largo, y para cambiarlo no hace falta este gesto: se la toma y se
   * escribe el número en su propia paleta.
   */
  const vecinas = useMemo<Pegada[]>(() => {
    const w = uno == null ? null : planta?.paredes.find((x) => x.id === uno);
    if (!w || !planta) return [];
    const out: Pegada[] = [];
    for (const g of pegadas(w)) {
      const q = planta.paredes.find((x) => x.id === g.id);
      if (!q) continue;
      // Una pared que toca a la tomada por sus DOS puntas —pasa con un tabique
      // más corto que la holgura de 45 cm que `pegadas` tolera— daría dos
      // entradas para un solo número en pantalla. Manda la punta más cercana.
      const ya = out.findIndex((p) => p.id === g.id);
      if (ya < 0) { out.push(g); continue; }
      const antes = Math.abs((out[ya].fin === 0 ? q.a : q.b) - w.c);
      if (Math.abs((g.fin === 0 ? q.a : q.b) - w.c) < antes) out[ya] = g;
    }
    return out;
    // `tick` por lo mismo que en `cotasAuto`: las paredes se mutan en sitio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planta, uno, tick, iPlanta]);
  /**
   * LAS COTAS DE LOS LADOS DE LAS ÁREAS CREADAS.
   *
   * Son el gemelo de `cotasAuto` para lo que no tiene paredes. Un área dibujada
   * a mano no sale de `planta.paredes`, así que hasta el 8-oct-2026 encender las
   * cotas acotaba la casa y dejaba el área muda —que es justo lo que Daniel
   * trajo en la foto del Burgos: cotas encendidas, el «Área 2» de 18,57 m² sin
   * una sola medida propia—.
   *
   * Se derivan igual que las de pared: en cada repintado, de la geometría viva.
   * Una cota guardada sería una foto, y una cota que miente es peor que ninguna.
   *
   * Sólo las áreas QUE SE VEN, por lo mismo que `cotasAuto` mira `ver.pared`:
   * con la capa apagada no hay nada que acotar.
   */
  const cotasLibres = useMemo(() => {
    if (!planta || !ver.area) return [];
    const out: Array<{ id: number; lado: number; x1: number; y1: number; x2: number; y2: number }> = [];
    for (const l of planta.libres ?? []) {
      const vs = poliDe(l);
      if (!vs) continue;
      for (let i = 0; i < vs.length; i++) {
        const [ax, ay] = vs[i];
        const [bx, by] = vs[(i + 1) % vs.length];
        // El lado de largo cero que deja una partición no se acota: rotularía un
        // «0,00 m» encima de los dos que sí miden.
        if (Math.hypot(bx - ax, by - ay) < 0.01) continue;
        out.push({ id: l.id, lado: i, x1: ax, y1: ay, x2: bx, y2: by });
      }
    }
    return out;
    // `tick` por lo mismo que en `cotasAuto`: los vértices se mutan en sitio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planta, ver.area, tick, iPlanta]);
  /**
   * LOS LADOS QUE CUELGAN DEL TOMADO: los candidatos a que se les escriba la
   * medida.
   *
   * Traducción exacta de `vecinas` al polígono, y la equivalencia se sostiene
   * sola: son los dos CONTIGUOS, y sólo los perpendiculares. Escribirle la
   * medida a uno de éstos ES empujar el lado tomado, igual que allá escribirle
   * a una vecina es correr la pared tomada.
   *
   * Quedan afuera a propósito:
   *
   * - Los PARALELOS y los lejanos, por lo mismo que las paredes paralelas: su
   *   cota mide su propio largo y moverlos no es este gesto.
   * - Las DIAGONALES —decisión de Daniel, 8-oct-2026: «rotula pero no se
   *   escribe»—. Una diagonal no tiene perpendicular contra la cual medir, y
   *   darle un número obligaría a inventar hacia dónde corre el vértice. Para
   *   cambiarla está el arrastre del vértice, que ya hace exactamente eso.
   * - El RECTÁNGULO, que no necesita este camino: su paleta ya trae ancho y
   *   alto. Por eso se exige `vertices`, no `poliDe`.
   */
  const ladosVecinos = useMemo<number[]>(() => {
    // El lado tiene que ser del área que está tomada AHORA: tomar otra no pasa
    // por `tomarLadoPoli`, así que sin esto un `ladoSel` viejo seguiría pintando
    // las cotas del área anterior.
    const l = ladoSel == null || ladoSel.id !== libreSel ? null
      : planta?.libres?.find((x) => x.id === ladoSel.id);
    const vs = l && !l.fijo ? l.vertices : null;
    if (!ladoSel || !vs?.length) return [];
    const dir = orientaLado(vs, ladoSel.lado);
    if (!dir) return [];                        // el tomado es diagonal: no empuja
    const n = vs.length;
    return [(ladoSel.lado + n - 1) % n, (ladoSel.lado + 1) % n]
      .filter((i) => orientaLado(vs, i) != null && orientaLado(vs, i) !== dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planta, ladoSel, libreSel, tick, iPlanta]);
  /** La cota de lado en edición, ya validada contra los candidatos de ahora. */
  const ladoVecino = ladoVecinoSel != null && ladosVecinos.includes(ladoVecinoSel)
    ? ladoVecinoSel : null;
  /** Su geometría viva, que es donde se planta la paleta y de dónde sale el largo. */
  const cotaLadoVecino = ladoVecino == null || !ladoSel ? null
    : cotasLibres.find((k) => k.id === ladoSel.id && k.lado === ladoVecino) ?? null;
  /** La cota vecina en edición, ya validada contra las candidatas de ahora. */
  const vecina = vecinaSel == null ? null
    : vecinas.find((g) => g.id === vecinaSel) ?? null;
  /** La pared de esa cota vecina. */
  const paredVecina = vecina == null ? null
    : planta?.paredes.find((x) => x.id === vecina.id) ?? null;
  /** El área libre tomada, o null. */
  const libre = planta?.libres?.find((l) => l.id === libreSel) ?? null;
  /** El mueble tomado, o null. */
  const mueble = planta?.muebles?.find((m) => m.id === muebleSel) ?? null;

  // El candado se cierra en cada cambio de selección: es permiso del momento, no
  // propiedad de la pieza. Va como efecto y no repartido por los sitios que
  // mueven `muebleSel` porque son seis, y uno que se olvide deja los agarres
  // abiertos en un mueble que nadie destrabó —justo el accidente que evita—.
  useEffect(() => { setCandado(false); }, [muebleSel]);

  /**
   * QUÉ paleta manda. Una sola, siempre.
   *
   * Existe porque las paletas se montaban cada una por su cuenta —`paleta &&
   * region`, `paleta && libre`, …— y cualquier selección que quedara viva de
   * más montaba la suya encima de la otra, en el mismo ancla. Daniel lo reportó
   * con la foto el 4-oct-2026: «quedan como dos paletas flotantes activas, la
   * cual no debería de ser así». Limpiar cada selección donde corresponde es
   * necesario y se hizo, pero no alcanza: mientras el render permita dos, el
   * defecto vuelve con la próxima combinación que nadie pensó.
   *
   * El orden es el mismo con el que se ANCLA la paleta (ver el efecto de abajo),
   * y eso tampoco es casualidad: si el ancla eligiera una y el render otra, la
   * paleta saldría puesta sobre el objeto equivocado.
   */
  const quePaleta:
    'vano' | 'region' | 'areas' | 'divisor' | 'cota' | 'libre' | 'mueble'
    | 'vecina' | 'ladoVecino' | 'pared' | 'paredes' | null =
    vano ? 'vano'
    : region ? 'region'
    : regiones.length > 1 ? 'areas'
    : divisor ? 'divisor'
    : cota ? 'cota'
    : mueble ? 'mueble'
    // La cota de un lado va ANTES que el área y en su lugar, por lo mismo que la
    // cota vecina gana sobre la pared: el área sigue tomada —es la que se va a
    // deformar— pero el campo que importa es el del número que se acaba de
    // tocar, y dos paletas a la vez es justo lo que `quePaleta` impide.
    : cotaLadoVecino ? 'ladoVecino'
    : libre ? 'libre'
    // La cota vecina va ANTES que la pared y en su lugar, no además: la pared
    // sigue tomada —es la que se va a correr— pero el campo que importa es el
    // de la cota que se acaba de tocar, y dos paletas a la vez es justo lo que
    // `quePaleta` existe para impedir.
    : vecina ? 'vecina'
    : uno != null ? 'pared'
    : sel.length > 1 ? 'paredes'
    : null;

  // El tamaño del lienzo se mide y no se deduce: la paleta de herramientas no
  // cuelga de ninguna pared ni de ningún área, se planta abajo y al centro del
  // dibujo, y eso hay que recalcularlo cuando el modal cambia de tamaño o el
  // teléfono gira.
  useLayoutEffect(() => {
    const el = lienzoRef.current;
    if (!el) return;
    const medir = () => setMarcoLienzo({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // La barra de abajo se mide igual, y por separado: su alto cambia con los
  // avisos, no con el del lienzo.
  useLayoutEffect(() => {
    const el = pieRef.current;
    if (!el) return;
    const medir = () => setAltoPie(el.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * LA RUEDA DEL MOUSE, APARTE DEL TRACKPAD.
   *
   * `react-zoom-pan-pinch` SUMA a la escala `step × |deltaY|` —con `smooth`
   * prendido y `step` 0,015 de fábrica, medido en su `handleWheelZoom`—. Un
   * trackpad manda decenas de eventos de 2 a 10 px y eso sale suave; una muesca
   * de rueda manda UN evento de 100 px (o 3 «líneas», `deltaMode` 1, en
   * Firefox) y la escala salta 1,5 de golpe: de 1× a 2,5× en una muesca, y para
   * abajo pega en el piso de 0,4×. Daniel, 5-oct-2026: «el zoom desde la
   * computadora con el mouse utilizando el scroll se amplía demasiado y se
   * aleja mucho, es difícil llegar al punto que uno desea versus el zoom desde
   * el trackpad y el zoom desde el celular que sí los hacen bien».
   *
   * Entonces se le saca a la librería SÓLO el evento que es inequívocamente una
   * rueda —paso en líneas o páginas, o un salto de 50 px o más de un tirón— y se
   * le aplica un zoom MULTIPLICATIVO de 1,1 por muesca anclado al cursor. El
   * trackpad (eventos chicos) y el pinch —del celular, y el del trackpad, que
   * llega como `ctrlKey`— no entran acá: siguen exactamente como hoy, que es lo
   * que Daniel pidió conservar.
   *
   * Multiplicativo y no aditivo porque es lo que hace que acercarse y alejarse
   * cuesten lo mismo: 0,2 de escala es un salto enorme en 0,4× y un suspiro en
   * 8×, y ahí nacía el «es difícil llegar al punto que uno desea».
   *
   * Va como listener NATIVO en fase de CAPTURA sobre el lienzo —que contiene al
   * `wrapper` de la librería, donde ella engancha su `wheel` en burbuja—: así
   * `stopPropagation` llega antes y el evento no se procesa dos veces.
   */
  useEffect(() => {
    const el = lienzoRef.current;
    if (!el) return;
    const MUESCA = 1.1;
    const rueda = (e: WheelEvent) => {
      // `ctrlKey` es el pinch del trackpad; el delta chico en píxeles es el
      // trackpad scrolleando. Ninguno de los dos es la rueda: que pasen.
      if (e.ctrlKey || (e.deltaMode === 0 && Math.abs(e.deltaY) < 50)) return;
      const z = zoomRef.current;
      const marco = z?.instance.wrapperComponent;
      if (!z || !marco) return;
      e.preventDefault();
      e.stopPropagation();
      const { scale, positionX, positionY } = z.instance.state;
      const { minScale, maxScale } = z.instance.setup;
      // Una muesca de rueda son 100 px en píxeles y 3 líneas en modo línea; el
      // tope de 3 existe para que un barrido de rueda inercial no se escape.
      const muescas = Math.sign(e.deltaY)
        * Math.min(Math.abs(e.deltaY) / (e.deltaMode === 0 ? 100 : 3), 3);
      const nueva = Math.min(maxScale, Math.max(minScale, scale * MUESCA ** -muescas));
      if (nueva === scale) return;
      const r = marco.getBoundingClientRect();
      const cx = e.clientX - r.left;
      const cy = e.clientY - r.top;
      const k = nueva / scale;
      z.setTransform(cx - (cx - positionX) * k, cy - (cy - positionY) * k, nueva, 0);
    };
    el.addEventListener('wheel', rueda, { capture: true, passive: false });
    return () => el.removeEventListener('wheel', rueda, { capture: true });
  }, []);

  // ── el PDF de fondo ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!planoUrl) return;
    let vivo = true;
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ url: planoUrl }).promise;
        const page = await pdf.getPage(1);
        const pts = page.getViewport({ scale: 1 });
        // 2× para que al acercar el zoom el plano no se vea pastoso.
        const vp = page.getViewport({ scale: 2 });
        const cv = document.createElement('canvas');
        cv.width = vp.width;
        cv.height = vp.height;
        const ctx = cv.getContext('2d');
        if (!ctx) { pdf.destroy(); return; }
        await page.render({ canvasContext: ctx, canvas: cv, viewport: vp }).promise;
        pdf.destroy();
        if (!vivo) return;
        setPaginaPt({ w: pts.width, h: pts.height });
        setFondo(cv.toDataURL('image/png'));
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (vivo && !msg.includes('cancelled')) setErrorPdf(msg);
      }
    })();
    return () => { vivo = false; };
  }, [planoUrl]);

  // ── el encuadre ──────────────────────────────────────────────────────────
  // Se calcula sobre la casa ORIGINAL de cada planta y no se vuelve a tocar.
  // Recalcularlo en cada repintado hacía zoom mientras se arrastraba una
  // fachada, y el punto agarrado se corría bajo el dedo: pedir 0,60 m movía 2,60.
  const vista = useMemo(() => {
    const base: Planta[] = JSON.parse(original);
    const pl = base[iPlanta];
    const ws = pl?.paredes ?? [];
    // Sin paredes, 10 × 10 m: es el lienzo de una planta que arranca en blanco.
    if (!ws.length && !pl?.marco) return { x0: 0, y0: 0, x1: 10, y1: 10 };
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const w of ws) {
      const k = caja(w);
      x0 = Math.min(x0, k[0]); y0 = Math.min(y0, k[1]);
      x1 = Math.max(x1, k[2]); y1 = Math.max(y1, k[3]);
    }
    // El marco es hasta dónde llega el DIBUJO del edificio, que puede pasarse
    // de las paredes: la cochera no tiene envolvente —sólo piso, losa y
    // columnas— y quedaba fuera del viewBox, inalcanzable incluso con zoom.
    // Ver `Planta.marco`. La casa se ve más chica en reposo; eso lo arregla el
    // zoom, y lo otro no se arreglaba con nada.
    if (pl?.marco) {
      const [mx0, mx1, my0, my1] = pl.marco;
      x0 = Math.min(x0, mx0); x1 = Math.max(x1, mx1);
      y0 = Math.min(y0, my0); y1 = Math.max(y1, my1);
    }
    return { x0: x0 - AIRE, y0: y0 - AIRE, x1: x1 + AIRE, y1: y1 + AIRE };
  }, [original, iPlanta]);

  // Dónde cae la página del PDF, en metros, respecto al origen de esta planta.
  const fondoCaja = useMemo(() => {
    if (!paginaPt || !planta) return null;
    const k = 1 / modelo.ptPorMetro;
    const [x0, , y0] = planta.caja;
    return { x: -x0 * k, y: -y0 * k, w: paginaPt.w * k, h: paginaPt.h * k };
  }, [paginaPt, planta, modelo.ptPorMetro]);

  // ── el dedo ──────────────────────────────────────────────────────────────
  /**
   * La lente: cuántos píxeles de pantalla mide un metro AHORA y dónde cae el
   * origen del viewBox, todo en el mismo sistema de coordenadas que traen los
   * eventos (`clientX`/`clientY`).
   *
   * Acá NO se usa `getScreenCTM()`, y es a propósito. Esa matriz mapea al
   * espacio «screen», que en Safari de iPhone incluye el zoom NATIVO de la
   * página —el que hace el pinch de dos dedos del sistema, que iOS concede
   * siempre: `user-scalable=no` lo ignora desde iOS 10—. `clientX`/`clientY`,
   * en cambio, nunca lo incluyen: van referidos al viewport de layout. Mezclar
   * los dos sistemas desplaza el punto EXACTAMENTE lo que haya de escala y
   * corrimiento visual: cero mientras no se hizo pinch, y cientos de píxeles
   * una vez acercado y paneado.
   *
   * Es el defecto que Daniel trajo tres veces y que ningún banco reprodujo:
   * «sigue sin verse, se ve fuera del área de zoom visible y lo tengo que jalar
   * hasta que se vea en la pantalla». Sin zoom nativo no pasa nada —por eso con
   * el plano completo siempre funcionó—; con zoom el arranque se clava fuera de
   * cuadro. Playwright no sabe hacer pinch nativo, así que el banco jamás lo vio.
   *
   * `getBoundingClientRect()` vive en el MISMO sistema que `clientX`, así que
   * cualquier escala o corrimiento visual se cancela al restar. La caja ya trae
   * aplicado el `transform` CSS de react-zoom-pan-pinch, con lo cual el pan/zoom
   * de la librería sigue contemplado sin recalcular nada a mano.
   *
   * El `preserveAspectRatio` del lienzo es el de fábrica (`xMidYMid meet`): la
   * escala es la MENOR de las dos y el sobrante se reparte a los lados, que es
   * lo que recentra el `(w - vb.w * k) / 2`.
   */
  const lente = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    const vb = svg.viewBox.baseVal;
    if (!r.width || !r.height || !vb.width || !vb.height) return null;
    const k = Math.min(r.width / vb.width, r.height / vb.height);
    if (!k || !Number.isFinite(k)) return null;
    return {
      k,
      ox: r.x + (r.width - vb.width * k) / 2,
      oy: r.y + (r.height - vb.height * k) / 2,
      x0: vb.x, y0: vb.y,
    };
  }, []);

  /**
   * Punto del evento en metros.
   *
   * Con `alzar` el punto sube `ALZADO_PX` ANTES de dividir por la escala: por
   * eso el desplazamiento es constante en pantalla y sobrevive al zoom. Lo piden
   * los gestos de PUNTERÍA —trazar una pared, trazar la línea que divide,
   * dibujar un área—, donde el dedo tapa justo lo que hay que mirar y la mira
   * dibujada es la única referencia.
   */
  const enMetros = useCallback((
    e: React.PointerEvent, alzar = false,
  ): { x: number; y: number } | null => {
    const l = lente();
    if (!l) return null;
    const y = e.clientY - (alzar && e.pointerType !== 'mouse' ? ALZADO_PX : 0);
    return { x: l.x0 + (e.clientX - l.ox) / l.k, y: l.y0 + (y - l.oy) / l.k };
  }, [lente]);

  /** Píxeles de pantalla pasados a metros al zoom vivo. */
  const enMetrosPx = useCallback((px: number) => {
    const k = lente()?.k ?? 0;
    return k ? px / k : PASO;
  }, [lente]);

  /**
   * Cuánto mide UN píxel de pantalla, en metros, al zoom vivo.
   *
   * Lo usa el dibujo de la mira para quedar del mismo tamaño a cualquier zoom
   * (ver `MIRA_PX`). Se lee una sola vez por pintada y de ahí salen todos sus
   * radios y grosores: medir la caja obliga al navegador a resolver el layout, y
   * hacerlo diez veces por cuadro mientras el dedo pasea no se paga solo.
   *
   * El `0.01` de respaldo es para el primer render, cuando el SVG todavía no
   * está montado y no hay mira que dibujar; nunca se ve.
   */
  const unPx = useCallback(() => {
    const k = lente()?.k ?? 0;
    return k ? 1 / k : 0.01;
  }, [lente]);

  /** El alcance del imán AHORA, en metros: los `IMAN_PX` traducidos al zoom
   *  vivo. Nunca por debajo del paso de la rejilla —si no, la rejilla gana y el
   *  imán no existiría— ni tan ancho que cruce media casa. */
  const alcanceIman = useCallback(
    () => Math.min(0.5, Math.max(PASO, enMetrosPx(IMAN_PX))),
    [enMetrosPx],
  );

  /**
   * Dónde cae un punto del lienzo de verdad: pegado al nodo de una pared vecina
   * si hay uno a tiro, y si no en la rejilla. Devuelve además a qué se pegó,
   * para poder dibujar la guía.
   *
   * Los dos ejes se resuelven POR SEPARADO, y eso es lo que hace que se pueda
   * arrancar una pared nueva exactamente en la esquina de otra: ahí hay un nodo
   * en x y otro en y, y pegarse a los dos a la vez es caer en el vértice. Es el
   * caso que Daniel reportó como «si quiero agregarla donde está un nodo de una
   * pared, no me permite».
   */
  const imanar = useCallback((p: { x: number; y: number }): Mira => {
    const r = alcanceIman();
    const ix = nodoCerca(p.x, nodos(planta?.paredes ?? [], 'x', []), r);
    const iy = nodoCerca(p.y, nodos(planta?.paredes ?? [], 'y', []), r);
    const guias: Guia[] = [];
    if (ix != null) guias.push({ eje: 'x', v: ix });
    if (iy != null) guias.push({ eje: 'y', v: iy });
    return { x: ix ?? aPaso(p.x), y: iy ?? aPaso(p.y), guias };
  }, [alcanceIman, planta]);

  /**
   * Las paredes que mueren CONTRA la que se agarra. Al correrla, sus puntas la
   * siguen con el MISMO delta — no se pegan a su eje. Pegarlas al eje sería más
   * simple pero destruye la esquina: en estas plantas las fachadas se cruzan, no
   * se topan, y la lateral arranca por encima del eje de la fachada.
   */
  function pegadas(w: Pared): Pegada[] {
    const out: Pegada[] = [];
    for (const q of planta.paredes) {
      if (q.id === w.id || q.o === w.o) continue;
      if (q.c < w.a - 0.35 || q.c > w.b + 0.35) continue;
      if (Math.abs(q.a - w.c) <= 0.45) out.push({ id: q.id, fin: 0, v0: q.a });
      if (Math.abs(q.b - w.c) <= 0.45) out.push({ id: q.id, fin: 1, v0: q.b });
    }
    return out;
  }

  /**
   * DÓNDE PONER EL EJE DE UNA PARED QUE SE CORRE, PARA QUE LA COTA CIERRE EN 5 cm.
   *
   * Hasta hoy se redondeaba la COORDENADA (`aPaso(c + d)`) y no alcanza: lo que
   * se lee en pantalla no es la coordenada, es el LARGO de las paredes
   * perpendiculares que ésta se lleva pegadas, y ese largo es una DIFERENCIA
   * contra la otra punta —que viene del PDF y casi nunca cae en la rejilla—.
   *
   * El caso del video, reproducido con la aritmética: punta fija en 0,013 y
   * pared en 3,113 dan un largo de 3,100 → se lee «3,10»; una flecha deja la
   * pared en `aPaso(3,163)` = 3,15 y el largo pasa a 3,137 → «3,14», y la
   * siguiente a 3,187 → «3,19». Daniel, 5-oct-2026: «la medida de 3.10 al
   * empezar a aumentar con la flecha pasa a 3.14, 3.19 … cuando debería siempre
   * cerrar en medidas de 5 cm 3.15, 3.20».
   *
   * Así que lo que se redondea es el LARGO, y de ahí sale la coordenada —el
   * mismo criterio que ya usaba el gesto de estirar una punta—. `ref` es la
   * coordenada que dejaría esa pegada en largo cero, y absorbe la holgura de
   * hasta 45 cm que `pegadas` tolera entre la punta y el eje.
   *
   * Manda LA PEGADA MÁS LARGA porque es la cota que se está leyendo. Con dos
   * pegadas de referencias distintas no hay número que cierre las dos a la vez:
   * en un recinto las dos paredes arrancan de la misma pared de enfrente y caen
   * juntas, y cuando no, cierra la grande. Sin pegadas no hay cota que cerrar y
   * se vuelve a la rejilla absoluta.
   */
  function ejeDeCota(peg: Pegada[], antes: number, crudo: number): number {
    let manda: { ref: number; largo: number } | null = null;
    for (const g of peg) {
      const q = planta.paredes.find((x) => x.id === g.id);
      if (!q) continue;
      const fijo = g.fin === 0 ? q.b : q.a;   // la punta que NO viaja con la pared
      const ref = fijo - (g.v0 - antes);
      const largo = Math.abs(crudo - ref);
      if (!manda || largo > manda.largo) manda = { ref, largo };
    }
    if (!manda) return aRejilla(antes, crudo);
    const largo = Math.max(PASO, aRejilla(Math.abs(antes - manda.ref), manda.largo));
    return mm(crudo >= manda.ref ? manda.ref + largo : manda.ref - largo);
  }

  /**
   * Agarra el puntero para el resto del gesto y anota de quién es.
   *
   * La captura puede FALLAR —el puntero ya se fue, o el motor la rechaza sobre
   * un nodo SVG, que en Safari pasa más que en Chrome— y eso no debe matar el
   * gesto: los `pointermove` siguen cayendo sobre el lienzo, que cubre toda el
   * área. Por eso va en `try` y por eso `dedoRef` se anota IGUAL, prenda la
   * captura o no: el dueño del agarre es el dedo, no la captura.
   */
  function capturar(e: React.PointerEvent, tomado = true) {
    dedoRef.current = e.pointerId;
    // El umbral se arma acá y no en cada `tomar*`: capturar es el único paso
    // por el que pasan todos los agarres que el dedo arrastra.
    //
    // `soloToque` es el primer tiempo del gesto de dos tiempos: el agarre se
    // arma igual —para que el toque corto tome, y para que el doble toque siga
    // funcionando— pero NADA se mueve. Si el dedo se corre, el que se movió fue
    // el plano, y `soltar` deshace la selección.
    umbralRef.current = {
      x: e.clientX, y: e.clientY, ok: false,
      soloToque: dedoManda && !tomado && e.pointerType !== 'mouse',
    };
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); }
    catch { /* el puntero ya se fue: el lienzo igual recibe los move */ }
  }

  /**
   * ¿Este toque es el SEGUNDO de un doble toque sobre lo mismo?
   *
   * `que` identifica el blanco —`w3`, `aCocina`— porque dos toques sueltos en
   * dos paredes distintas no son un doble toque. Al acertar se olvida el
   * primero, para que tres toques seguidos no cuenten como dos dobles.
   *
   * Se llama SIEMPRE al levantar el dedo, nunca al apoyarlo: mientras el dedo
   * esté abajo el gesto todavía puede volverse un arrastre, y decidirlo en el
   * `pointerdown` repetía el choque del hold —tocar una pared y empujarla
   * enseguida entraba en selección múltiple—. Por lo mismo no sirven
   * `e.detail` ni `onDoubleClick`: el que llama ya filtró los arrastres.
   */
  function esDoble(que: string, x: number, y: number) {
    const g = dobleRef.current;
    const ahora = Date.now();
    const pareja = g != null && g.que === que && ahora - g.t < DOBLE_MS
      && Math.hypot(x - g.x, y - g.y) < DOBLE_PX;
    dobleRef.current = pareja ? null : { que, t: ahora, x, y };
    return pareja;
  }

  function tomar(e: React.PointerEvent, w: Pared, punta: 0 | 1 | null) {
    const p = enMetros(e);
    if (!p) return;
    // La pared tomada es la que ya está en `sel`: sobre una que no lo está, el
    // dedo panea. La punta nunca entra acá —sólo se dibuja con la pared tomada—
    // así que va directo al segundo tiempo.
    capturar(e, punta != null || sel.includes(w.id));
    setAviso(null);
    setVanoSel(null);
    setRecintos([]);
    setMultiAreas(false);
    marcadoRef.current = false;

    // En modo múltiple —o con Shift— el toque ELIGE, no arrastra: suma la pared
    // a la selección o la saca. Es el gesto de seleccionar fotos, que es el que
    // todo el mundo ya tiene en el dedo. Mover el grupo entero no entra acá a
    // propósito: con dos paredes tomadas lo que se quiere es una operación
    // sobre las dos (unir), no empujarlas juntas.
    if (punta == null && (e.shiftKey || multi)) {
      pendienteRef.current = null;
      setMulti(true);
      setSel((s) => (s.includes(w.id) ? s.filter((i) => i !== w.id) : [...s, w.id]));
      return;
    }

    // Queda anotado por si este toque resulta ser la mitad de un doble toque.
    // No se decide nada acá: el gesto todavía puede ser un arrastre, y eso se
    // sabe recién al levantar el dedo (`soltar`).
    pendienteRef.current = punta == null
      ? { que: `w${w.id}`, id: w.id, x: e.clientX, y: e.clientY }
      : null;

    setSel([w.id]);
    // Se marca una sola vez por gesto —no por cuadro— para esconder la paleta
    // mientras la pared se mueve bajo el dedo.
    setArrastrando(true);
    agarreRef.current = punta == null
      ? { tipo: 'mover', id: w.id, c0: w.c, m0: w.o === 'v' ? p.x : p.y, pegadas: pegadas(w) }
      : { tipo: 'punta', id: w.id, fin: punta, v0: punta ? w.b : w.a,
          m0: w.o === 'v' ? p.y : p.x };

  }

  /**
   * Toma un vano. No hereda nada del gesto de la pared: no entra a selección
   * múltiple —dos puertas tomadas no tienen ninguna operación en común— y no
   * borra la medición, porque mover una puerta no cambia un área (la inundación
   * tapa los vanos) y perder los números de pantalla por eso sería mentir al
   * revés: decir «esto ya no vale» cuando sí vale.
   */
  function tomarVano(e: React.PointerEvent, w: Pared, v: Vano) {
    const p = enMetros(e);
    if (!p) return;
    capturar(e, vanoSel?.pared === w.id && vanoSel.vano === v.id);
    e.stopPropagation();
    setAviso(null);
    setSel([]);
    setMulti(false);
    setRecintos([]);
    setMultiAreas(false);
    setVanoSel({ pared: w.id, vano: v.id });
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'vano', id: w.id, vano: v.id, a0: v.a, m0: w.o === 'v' ? p.y : p.x,
    };
  }

  /**
   * Toma un área libre, del cuerpo o de uno de sus bordes.
   *
   * No borra la medición, y eso es a propósito: un área libre no participa de la
   * inundación —no encierra nada, se mide sola— así que correr la terraza no
   * puede invalidar las áreas de la casa. Borrarlas sería decir «esto ya no
   * vale» de los números que sí siguen valiendo. Con una pared es lo contrario y
   * ahí `tomar` sí las borra.
   */
  function tomarLibre(e: React.PointerEvent, l: Libre, lado: Lado) {
    const p = enMetros(e);
    if (!p) return;
    const yaSel = libreSel === l.id;
    // Los bordes sólo se dibujan con el área tomada, así que llegar acá por un
    // borde ya es el segundo tiempo.
    capturar(e, yaSel || lado !== 'cuerpo');
    e.stopPropagation();
    setAviso(null);
    setSel([]);
    setMulti(false);
    setVanoSel(null);
    setRecintos([]);
    setMultiAreas(false);
    setDivSel(null);
    setCorrimiento(null);
    setLibreSel(l.id);
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'libre', id: l.id, lado, yaSel,
      x0: l.x, y0: l.y, w0: l.w, h0: l.h, mx: p.x, my: p.y,
    };
  }

  /**
   * Toma un LADO de un área hecha de vértices.
   *
   * UN gesto, UN verbo: el lado se EMPUJA. Nada más.
   *
   * Hasta el 8-oct-2026 este mismo gesto hacía dos cosas y las separaba por si
   * el dedo se había movido: quieto partía el lado, movido lo empujaba. Daniel
   * lo mandó a sacar —«por defecto te crea vértices cuando tal vez lo que uno
   * quiere es mover el lado»— y el motivo de fondo es que la distinción era
   * falsa: en un teléfono un empujón corto y un toque son el mismo gesto, así
   * que el verbo raro se colaba encima del verbo de todos los días.
   *
   * Meter un nodo ahora exige la TIJERA puesta, que es el único caso que sigue
   * acá arriba: con el modo declarado no hay ambigüedad que resolver.
   */
  function tomarLadoPoli(e: React.PointerEvent, l: Libre, i: number) {
    const p = enMetros(e);
    if (!p || !l.vertices?.length) return;
    capturar(e);
    e.stopPropagation();
    // CON EL MODO DIVIDIR PUESTO el lado no se empuja: el toque mete el nodo
    // donde cayó el dedo y se queda en el modo para poder meter varios. Es el
    // botón que pidió Daniel, y acá la ambigüedad toque-vs-arrastre ya no
    // existe: el usuario declaró antes qué venía a hacer.
    if (nodo === 'partir' && !l.fijo) {
      const vs = partirLado(l.vertices, i, p.x, p.y, l.anguloLibre ? 1 : 2);
      if (vs === l.vertices) {
        setAviso('El corte quedó pegado a una esquina. Tocá más al medio del lado.');
        return;
      }
      marcar();
      l.vertices = vs;
      recajar(l);
      if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
      setTick((t) => t + 1);
      return;
    }
    setAviso(null);
    setLibreSel(l.id);
    // Tomar el lado es lo que enciende sus cotas vecinas, igual que tomar una
    // pared enciende las suyas. Va acá y no al soltar el dedo a propósito: el
    // gesto es el mismo tome o no arrastre, y esperar al final dejaría el lado
    // sin cotas durante todo el arrastre, que es cuando más sirven.
    setLadoSel({ id: l.id, lado: i });
    setLadoVecinoSel(null);
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'poli', id: l.id, lado: i,
      vs0: l.vertices.map(([x, y]) => [x, y] as [number, number]),
      mx: p.x, my: p.y,
    };
  }

  /**
   * Toma un VÉRTICE del polígono irregular y lo lleva a donde vaya el dedo.
   *
   * Es el gesto propio del tercer método: acá no hay perpendicular que respetar
   * ni lado vecino que arreglar, el punto va donde se lo lleve y los dos lados
   * que salen de él lo siguen en diagonal. Un toque que no mueve nada no hace
   * NADA —a diferencia del lado, que se parte— porque el vértice ya existe y no
   * hay un segundo verbo que darle.
   */
  function tomarVertice(e: React.PointerEvent, l: Libre, i: number) {
    const p = enMetros(e, true);
    if (!p || !l.vertices?.length) return;
    capturar(e);
    e.stopPropagation();
    // CON EL MODO QUITAR NODO puesto, el toque lo borra en vez de arrastrarlo.
    // Es el segundo verbo que el vértice no tenía.
    if (nodo === 'quitar' && !l.fijo) {
      const vs = quitarVertice(l.vertices, i);
      if (vs === l.vertices) {
        setAviso('Con tres nodos ya es un triángulo: sacarle uno más lo deja sin área.');
        return;
      }
      marcar();
      l.vertices = vs;
      recajar(l);
      if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
      setTick((t) => t + 1);
      return;
    }
    setAviso(null);
    setLibreSel(l.id);
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'vertice', id: l.id, indice: i,
      vs0: l.vertices.map(([x, y]) => [x, y] as [number, number]),
      mx: p.x, my: p.y,
    };
  }

  /**
   * Recalcula la caja de un objeto a partir de su forma.
   *
   * `x/y/w/h` dejan de ser la geometría en cuanto hay vértices, pero siguen
   * siendo lo que ancla la paleta, el rótulo y el imán de arrimar: si no se
   * recalculan, mover un vértice deja la paleta flotando donde el objeto ya no
   * está. Mismo papel que `recajar` en el área libre.
   */
  function recajarMueble(m: Mueble, vs: Array<[number, number]>) {
    m.vertices = vs;
    // Los radios y la forma no conviven: ver `Mueble.radios`.
    delete m.radios;
    const [x, y, w, h] = cajaDeVertices(vs);
    Object.assign(m, { x: mm(x), y: mm(y), w: mm(w), h: mm(h) });
  }

  /**
   * Toma un LADO de la forma de un objeto «a medida»: lo empuja, o lo parte.
   *
   * Gemelo de `tomarLadoPoli`, y a propósito: Daniel pidió el 14-oct-2026 que
   * el objeto a medida tuviera «las mismas funciones que tiene el área», así
   * que es el mismo gesto con la misma tijera y los mismos avisos. Lo único
   * distinto es que un objeto NO MIDE: acá no se rehace la medición.
   *
   * Si el objeto todavía es un rectángulo, partir un lado le siembra los cuatro
   * vértices y recién ahí empieza a tener forma. Ver `verticesMueble`.
   */
  function tomarLadoObjeto(e: React.PointerEvent, m: Mueble, i: number) {
    const p = enMetros(e);
    if (!p) return;
    capturar(e);
    e.stopPropagation();
    const vs0 = verticesMueble(m);
    if (nodo === 'partir') {
      // UN solo nodo, siempre: la forma del objeto es de ángulo libre, como el
      // polígono irregular del área. No hace falta un modo ortogonal aparte
      // porque de eso se encarga el imán al mover el vértice —que es lo que
      // Daniel pidió: «que por defecto trate siempre de buscar una línea
      // vertical u horizontal», no que lo prohíba—.
      const vs = partirLado(vs0, i, p.x, p.y, 1);
      if (vs === vs0) {
        setAviso('El corte quedó pegado a una esquina. Tocá más al medio del lado.');
        return;
      }
      marcar();
      recajarMueble(m, vs);
      setTick((t) => t + 1);
      return;
    }
    setAviso(null);
    setMuebleSel(m.id);
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = { tipo: 'mpoli', id: m.id, lado: i, vs0, mx: p.x, my: p.y };
  }

  /** Toma un VÉRTICE de la forma de un objeto: lo mueve, o lo borra. */
  function tomarVerticeObjeto(e: React.PointerEvent, m: Mueble, i: number) {
    const p = enMetros(e, true);
    if (!p || !m.vertices?.length) return;
    capturar(e);
    e.stopPropagation();
    if (nodo === 'quitar') {
      const vs = quitarVertice(m.vertices, i);
      if (vs === m.vertices) {
        setAviso('Con tres nodos ya es un triángulo: sacarle uno más lo deja sin forma.');
        return;
      }
      marcar();
      recajarMueble(m, vs);
      setTick((t) => t + 1);
      return;
    }
    setAviso(null);
    setMuebleSel(m.id);
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'mvertice', id: m.id, indice: i,
      vs0: m.vertices.map(([x, y]) => [x, y] as [number, number]),
      mx: p.x, my: p.y,
    };
  }

  /**
   * Toma un mueble, del cuerpo o de uno de sus bordes.
   *
   * Igual que el área libre no borra la medición, y acá la razón es todavía más
   * fuerte: un mueble NO MIDE. No entra en la inundación, no suma a ningún
   * total, no lo lee `medirPlanta`. Mover el clóset no puede cambiar un número.
   *
   * El dedo se lee ALZADO (`enMetros(e, true)`): un módulo de 60 × 60 cm es más
   * chico que la yema, y sin alzar el dibujo queda tapado por la mano justo
   * cuando se lo está acomodando contra la pared.
   */
  function tomarMueble(e: React.PointerEvent, m: Mueble, lado: Lado) {
    const p = enMetros(e, true);
    if (!p) return;
    const yaSel = muebleSel === m.id;
    capturar(e, yaSel || lado !== 'cuerpo');
    e.stopPropagation();
    setAviso(null);
    setSel([]);
    setMulti(false);
    setVanoSel(null);
    setRecintos([]);
    setMultiAreas(false);
    setDivSel(null);
    setLibreSel(null);
    setCorrimiento(null);
    setMuebleSel(m.id);
    // Agarrar el cuerpo o un borde cierra la edición de esquina: la paleta
    // vuelve a mostrar las medidas, que es lo que corresponde a ese gesto.
    setEsquinaSel(null);
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'mueble', id: m.id, lado, yaSel,
      x0: m.x, y0: m.y, w0: m.w, h0: m.h, mx: p.x, my: p.y,
    };
  }

  /**
   * El AGARRE REMOTO del mueble: con uno tomado, apoyar el dedo en cualquier
   * parte del lienzo y arrastrar lo mueve, sin tener que acertarle encima.
   *
   * Lo pidió Daniel el 5-oct-2026: «si tengo seleccionado algún objeto de esos
   * de mobiliario y quiero moverlo, que no sea únicamente dándole tap hold al
   * objeto, sino que me permita hacerlo fuera en cualquier parte de la pantalla
   * y ya yo empiezo a moverlo sin soltar el dedo […] para hacer el mismo efecto
   * de la pared, que puedo yo ver el objeto sin tener que tener el dedo encima».
   *
   * Es directo y NO un tap-hold, porque eso es lo que dice el pedido: el dedo
   * se apoya y ya se está moviendo. El costo —dicho y aceptado— es que mientras
   * haya un mueble tomado el arrastre en el vacío mueve el mueble en vez de
   * panear el plano; soltarlo con un toque corto devuelve el paneo.
   *
   * Y por eso corta la propagación: el `pointerdown` que sigue hasta el oyente
   * de «tocar afuera» soltaría el mueble que acabamos de agarrar, y el que
   * llega a `react-zoom-pan-pinch` arrancaría el paneo encima del arrastre.
   */
  function tomarRemoto(e: React.PointerEvent) {
    // Un agarre COLGADO —de un gesto cuyo `pointerup` nunca volvió— dejaba el
    // arrastre remoto muerto hasta recargar la página, porque el `return` de
    // abajo lo rechazaba para siempre. Colgarse es fácil: si la captura del
    // puntero no prende (Safari la rechaza sobre SVG más seguido que Chrome) y
    // el dedo se levanta sobre la paleta —que es HTML y vive FUERA del SVG— el
    // `onPointerUp` del lienzo no corre nunca. Un dedo NUEVO es prueba de que
    // el anterior terminó, así que el agarre viejo se descarta.
    if (agarreRef.current && dedoRef.current !== e.pointerId) soltar();
    if (muebleSel == null || agarreRef.current || modo) return;
    const t = e.target as Element | null;
    // Lo que ya tiene su propio gesto no se toca: paredes, vanos, áreas libres,
    // divisores, el mueble mismo y sus agarres.
    if (t?.closest('[data-paleta], [data-pared], [data-punta], [data-vano], [data-libre],'
                    + ' [data-divisor], [data-mueble], [data-lado], [data-esquina],'
                    + ' [data-catalogo]')) return;
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    const p = enMetros(e, true);
    if (!p) return;
    // Un cuarto medido SÍ entra —cubren casi toda la casa con el ojo
    // encendido—, pero su `pointerdown` ya anotó dónde apoyó el dedo antes de
    // que el evento llegara hasta acá, y su `pointerup` leería ese arrastre
    // como un toque sobre el cuarto. Se consume.
    tocoRef.current = null;
    e.stopPropagation();
    marcadoRef.current = false;
    setArrastrando(true);
    // `yaSel` en true es lo que deja que el toque corto —apoyar y levantar sin
    // mover— suelte el mueble, que es cómo se sale del modo sin un botón más.
    agarreRef.current = {
      tipo: 'mueble', id: m.id, lado: 'cuerpo', yaSel: true,
      x0: m.x, y0: m.y, w0: m.w, h0: m.h, mx: p.x, my: p.y,
    };
    // La captura va DESPUÉS de armar el agarre y sin poder tumbarlo: si falla,
    // el arrastre igual funciona porque el lienzo cubre toda el área y los
    // `pointermove` caen sobre él de todos modos. Antes iba primero, así que un
    // rechazo de la captura mataba el gesto entero sin dejar rastro.
    capturar(e);
  }

  function arrastrar(e: React.PointerEvent) {
    const ag = agarreRef.current;
    if (!ag) return;
    const p = enMetros(e);
    if (!p) return;

    // EL UMBRAL. Hasta que el dedo no se corre `ARRASTRE_PX` no se mueve nada:
    // apoyar y temblar es un toque, no un arrastre.
    //
    // Cruzado el umbral el elemento salta al dedo y de ahí lo sigue 1:1, y eso
    // es a propósito: la alternativa —descontar los 12 px para que no salte—
    // deja el elemento clavado ESE desfase detrás del dedo para todo el resto
    // del gesto, y a un zoom corriente 12 px son 28 cm. En un editor de planta
    // eso es peor que el salto: lo que se suelta no queda donde se puso el
    // dedo, y el imán y la rejilla de 5 cm dejan de coincidir con lo que se ve.
    const u = umbralRef.current;
    if (u && !u.ok) {
      if (Math.hypot(e.clientX - u.x, e.clientY - u.y) < ARRASTRE_PX) return;
      u.ok = true;
      // La medición se borra ACÁ y no al apoyar el dedo. Mover una pared sí
      // invalida las áreas —la inundación cambia— pero TOCARLA no, y borrarlas
      // en el `pointerdown` significaba que rozar una pared para panear dejaba
      // la planta sin números. Es el mismo descuido que el de mover un mueble
      // sin querer, y se arregla con la misma regla: el gesto se define cuando
      // el dedo se corre, no cuando se apoya.
      if (ag.tipo === 'mover' || ag.tipo === 'punta') setMedicion(null);
    }
    // EL PRIMER TIEMPO. Con el dedo, lo que no estaba tomado no se arrastra: el
    // apoyo lo deja elegido y el movimiento se lo lleva el paneo del plano (el
    // cuerpo no lleva `AGARRE`, ver `cuerpoAgarrable`).
    if (u?.soloToque) return;

    // ── LA COTA ───────────────────────────────────────────────────────────
    // Las dos van antes del `find` de pared por lo mismo que el área libre: una
    // cota no es una pared y la búsqueda saldría vacía, matando el gesto sin
    // decir nada.
    if (ag.tipo === 'cotaoff') {
      const k = (planta.cotas ?? []).find((q) => q.id === ag.id);
      if (!k) return;
      // Sólo cuenta lo que el dedo se corrió EN LA PERPENDICULAR: el componente
      // a lo largo se descarta, y por eso arrastrar en diagonal no desalinea la
      // cota. Es lo que hace que el gesto sea imposible de hacer mal.
      const dx = k.x2 - k.x1, dy = k.y2 - k.y1;
      const largo = Math.max(Math.hypot(dx, dy), 1e-6);
      const nx = -dy / largo, ny = dx / largo;
      const d = (p.x - ag.mx) * nx + (p.y - ag.my) * ny;
      // Al paso de 5 cm como todo lo demás: así dos cotas arrastradas a ojo
      // quedan alineadas entre sí en vez de a 3 mm una de otra.
      const off = mm(Math.round((ag.off0 + d) / PASO) * PASO);
      if (off !== (k.off ?? 0)) {
        marcarGesto();
        // El cero se BORRA en vez de guardarse: una cota pegada tiene que leerse
        // igual que las de antes del 22-oct-2026, en el modelo y en el JSON.
        if (off === 0) delete k.off; else k.off = off;
        setTick((t) => t + 1);
      }
      return;
    }
    if (ag.tipo === 'cotanodo') {
      const k = (planta.cotas ?? []).find((q) => q.id === ag.id);
      if (!k) return;
      // Con imán y con mira: mover un nodo es volver a apuntar, así que merece
      // la misma puntería que ponerlo la primera vez —guías, pegado y lupa—.
      const q = imanar(p);
      setMira(q);
      const x = mm(q.x), y = mm(q.y);
      const [ax, ay] = ag.fin === 0 ? [k.x1, k.y1] : [k.x2, k.y2];
      if (x !== ax || y !== ay) {
        marcarGesto();
        if (ag.fin === 0) { k.x1 = x; k.y1 = y; } else { k.x2 = x; k.y2 = y; }
        setTick((t) => t + 1);
      }
      return;
    }

    // El área libre va ANTES de buscar la pared: no es una pared y el `find`
    // de abajo saldría vacío, matando el gesto en silencio.
    if (ag.tipo === 'libre') {
      const l = (planta.libres ?? []).find((q) => q.id === ag.id);
      if (!l) return;
      // El paso de 5 cm se cuenta desde donde estaba el rectángulo, no desde el
      // origen del lote: así apoyar el dedo no lo corre solo.
      const dx = Math.round((p.x - ag.mx) / PASO) * PASO;
      const dy = Math.round((p.y - ag.my) / PASO) * PASO;
      const c = estirar({ x: ag.x0, y: ag.y0, w: ag.w0, h: ag.h0 }, ag.lado, dx, dy);
      const nuevo = { x: mm(c.x), y: mm(c.y), w: mm(c.w), h: mm(c.h) };
      if (nuevo.x !== l.x || nuevo.y !== l.y || nuevo.w !== l.w || nuevo.h !== l.h) {
        marcarGesto();
        // La que tiene forma se mueve con todo su contenido: la caja sola
        // dejaría el dibujo donde estaba y el área caminando sin él. No se la
        // puede redimensionar (no tiene agarres), así que acá sólo hay que
        // correr las celdas lo mismo que se corrió la caja.
        if (l.celdas?.length) {
          const ddx = nuevo.x - l.x, ddy = nuevo.y - l.y;
          l.celdas = l.celdas.map(([cx, cy, cw, ch]) => [mm(cx + ddx), mm(cy + ddy), cw, ch]);
        }
        // La de vértices viaja igual que la de celdas, y por la misma razón: la
        // caja es su ancla, no su forma.
        if (l.vertices?.length) {
          const ddx = nuevo.x - l.x, ddy = nuevo.y - l.y;
          l.vertices = l.vertices.map(([vx, vy]) => [mm(vx + ddx), mm(vy + ddy)]);
        }
        Object.assign(l, nuevo);
        setTick((t) => t + 1);
      }
      return;
    }

    // El lado de un polígono: empuja en perpendicular y recalcula la caja, que
    // es lo que ancla el rótulo y la paleta.
    if (ag.tipo === 'poli') {
      const l = (planta.libres ?? []).find((q) => q.id === ag.id);
      if (!l?.vertices?.length) return;
      const dx = Math.round((p.x - ag.mx) / PASO) * PASO;
      const dy = Math.round((p.y - ag.my) / PASO) * PASO;
      const vs = empujarLado(ag.vs0, ag.lado, dx, dy);
      // El área no puede colapsar: un polígono de 0 m² se queda sin forma y sin
      // vuelta, igual que el ancla del rectángulo no lo deja ir a negativo.
      if (areaPoligono(vs) < MINIMA_LIBRE) return;
      if (JSON.stringify(vs) === JSON.stringify(l.vertices)) return;
      marcarGesto();
      l.vertices = vs;
      const [x, y, w, h] = cajaDeVertices(vs);
      Object.assign(l, { x: mm(x), y: mm(y), w: mm(w), h: mm(h) });
      setTick((t) => t + 1);
      return;
    }

    // Un vértice suelto: va a donde va el dedo, pegado a la rejilla de 5 cm en
    // las dos direcciones. El dedo se lee ALZADO igual que el del mueble: el
    // punto es más chico que la yema y se lo está acomodando mirándolo.
    if (ag.tipo === 'vertice') {
      const l = (planta.libres ?? []).find((q) => q.id === ag.id);
      if (!l?.vertices?.length) return;
      const q = enMetros(e, true) ?? p;
      const dx = Math.round((q.x - ag.mx) / PASO) * PASO;
      const dy = Math.round((q.y - ag.my) / PASO) * PASO;
      const vs = ag.vs0.map(([x, y]) => [x, y] as [number, number]);
      const [x0, y0] = ag.vs0[ag.indice];
      // El imán ORTOGONAL corre acá y no dentro de `enMetros` porque necesita
      // ver a los vecinos del vértice: no pega a una retícula, pega a la
      // columna o al renglón del punto de al lado. Ver `imantarVertice`.
      const t = imantarVertice(vs, ag.indice, mm(x0 + dx), mm(y0 + dy));
      vs[ag.indice] = [mm(t.x), mm(t.y)];
      setIman(t.iman.x == null && t.iman.y == null ? null : t.iman);
      // MUDA: la lupa sale, el retículo no. Acomodar un nodo es puntería fina
      // —es lo que Daniel está haciendo con el dedo encima del punto— así que
      // merece la lupa; pero el nodo ya se dibuja como una mira, y una segunda
      // encima sólo taparía la primera. Ver `Mira.muda`.
      setMira({ x: t.x, y: t.y, guias: [], muda: true });
      // No puede colapsar, igual que el lado: un polígono de 0 m² se queda sin
      // forma y sin vuelta. Esto frena de paso el caso en que el vértice cruza
      // al otro lado del anillo y lo dobla sobre sí mismo, que es donde el
      // área firmada se va a cero al pasar.
      if (areaPoligono(vs) < MINIMA_LIBRE) return;
      if (JSON.stringify(vs) === JSON.stringify(l.vertices)) return;
      marcarGesto();
      l.vertices = vs;
      const [bx, by, bw, bh] = cajaDeVertices(vs);
      Object.assign(l, { x: mm(bx), y: mm(by), w: mm(bw), h: mm(bh) });
      setTick((t) => t + 1);
      return;
    }

    // El lado y el vértice de un objeto «a medida» con forma propia. Son los dos
    // gemelos de `poli`/`vertice`, con dos diferencias que valen la duplicación:
    // el objeto NO MIDE (no se rehace la medición al soltarlo) y su piso de
    // colapso es el del mueble, no el del área.
    if (ag.tipo === 'mpoli') {
      const m = (planta.muebles ?? []).find((q) => q.id === ag.id);
      if (!m) return;
      const dx = Math.round((p.x - ag.mx) / PASO) * PASO;
      const dy = Math.round((p.y - ag.my) / PASO) * PASO;
      const vs = empujarLado(ag.vs0, ag.lado, dx, dy);
      if (areaPoligono(vs) < MIN_MUEBLE * MIN_MUEBLE) return;
      if (JSON.stringify(vs) === JSON.stringify(m.vertices)) return;
      marcarGesto();
      recajarMueble(m, vs);
      setTick((t) => t + 1);
      return;
    }

    if (ag.tipo === 'mvertice') {
      const m = (planta.muebles ?? []).find((q) => q.id === ag.id);
      if (!m) return;
      const q = enMetros(e, true) ?? p;
      const dx = Math.round((q.x - ag.mx) / PASO) * PASO;
      const dy = Math.round((q.y - ag.my) / PASO) * PASO;
      const vs = ag.vs0.map(([x, y]) => [x, y] as [number, number]);
      const [x0, y0] = ag.vs0[ag.indice];
      // El MISMO imán que el área: era la segunda mitad del pedido de Daniel
      // —«en ambos, tanto áreas como objeto a medida»—, así que es la misma
      // función y no una copia que se vaya a desviar con el tiempo.
      const t = imantarVertice(vs, ag.indice, mm(x0 + dx), mm(y0 + dy));
      vs[ag.indice] = [mm(t.x), mm(t.y)];
      setIman(t.iman.x == null && t.iman.y == null ? null : t.iman);
      // Misma mira muda que el vértice del área, por lo mismo que el imán es el
      // mismo: para el dedo son el mismo gesto, y no hay razón para que uno
      // tenga lupa y el otro no.
      setMira({ x: t.x, y: t.y, guias: [], muda: true });
      if (areaPoligono(vs) < MIN_MUEBLE * MIN_MUEBLE) return;
      if (JSON.stringify(vs) === JSON.stringify(m.vertices)) return;
      marcarGesto();
      recajarMueble(m, vs);
      setTick((t) => t + 1);
      return;
    }

    // El mueble, por lo mismo que el área libre: no es una pared.
    if (ag.tipo === 'mueble') {
      const ms = planta.muebles ?? [];
      const m = ms.find((q) => q.id === ag.id);
      if (!m) return;
      // El dedo se lee ALZADO igual que al crearlo. Tiene que ser el mismo
      // criterio en los dos lados o el mueble salta 44 px al primer movimiento.
      const q = enMetros(e, true) ?? p;
      const dx = q.x - ag.mx;
      const dy = q.y - ag.my;
      const c = estirar({ x: ag.x0, y: ag.y0, w: ag.w0, h: ag.h0 }, ag.lado, dx, dy, MIN_MUEBLE);

      // LO QUE PEGA A PARED no usa el imán de muebles: tiene el suyo, que no
      // arrima un borde contra otro sino que CUELGA la pieza de una cara. Son
      // dos imanes distintos a propósito —el del mobiliario alinea cajas, éste
      // elige pared y cara— y mezclarlos daba un tomacorriente alineado con el
      // borde del muro en vez de apoyado contra él.
      //
      // Se busca desde el CENTRO de la pieza y no desde el dedo: agarrada de un
      // borde, el dedo puede estar sobre otra pared que la que uno está
      // señalando con la pieza.
      if (ag.lado === 'cuerpo' && fichaMueble(m.tipo)?.pegaPared) {
        const a = anclaCerca(planta.paredes, c.x + c.w / 2, c.y + c.h / 2);
        const antes = JSON.stringify([m.x, m.y, m.giro, m.ancla ?? null]);
        if (a) sentar(m, a);
        else {
          // Lejos de toda pared se suelta y se queda donde el dedo la dejó, en
          // la rejilla de 5 cm como cualquier pieza. Es el «o en algún punto
          // que uno defina que no sea la pared» del pedido.
          delete m.ancla;
          m.x = aPaso(c.x); m.y = aPaso(c.y);
        }
        setMira({ x: m.x, y: m.y, guias: [] });
        if (JSON.stringify([m.x, m.y, m.giro, m.ancla ?? null]) !== antes) {
          marcarGesto();
          setTick((t) => t + 1);
        }
        return;
      }

      // El imán manda y la rejilla es el respaldo, igual que al mover una pared.
      // Acá los candidatos son las CARAS de las paredes y los bordes de los
      // otros muebles (ver `nodosMueble`), y se prueban los dos bordes de la
      // caja (ver `arrimar`): eso es lo que deja apilar módulos de cocina
      // viniendo desde cualquiera de los dos lados.
      const r = alcanceIman();
      const cx = nodosMueble(planta.paredes, ms, 'x', m.id);
      const cy = nodosMueble(planta.paredes, ms, 'y', m.id);
      const guias: Guia[] = [];
      // Moviendo entera se arrima la caja; estirando, el borde que se agarró —y
      // ahí el «largo» es 0 porque lo que topa es ese borde y no el opuesto.
      const mueve = ag.lado === 'cuerpo';
      const ix = arrimar(c.x, mueve ? c.w : 0, cx, r);
      const iy = arrimar(c.y, mueve ? c.h : 0, cy, r);
      const fx = arrimar(c.x + c.w, 0, cx, r);
      const fy = arrimar(c.y + c.h, 0, cy, r);
      let { x, y, w: aw, h: ah } = c;
      if (mueve) {
        if (ix != null) { x = ix; guias.push({ eje: 'x', v: x }); }
        else x = aPaso(x);
        if (iy != null) { y = iy; guias.push({ eje: 'y', v: y }); }
        else y = aPaso(y);
      } else {
        // Estirando, cada borde se arrima por su cuenta: el oeste y el norte
        // mueven la esquina, el este y el sur sólo el tamaño.
        if (ag.lado.includes('o') && ix != null) { aw = mm(aw + x - ix); x = ix; guias.push({ eje: 'x', v: x }); }
        if (ag.lado.includes('e') && fx != null) { aw = mm(fx - x); guias.push({ eje: 'x', v: fx }); }
        if (ag.lado.includes('n') && iy != null) { ah = mm(ah + y - iy); y = iy; guias.push({ eje: 'y', v: y }); }
        if (ag.lado.includes('s') && fy != null) { ah = mm(fy - y); guias.push({ eje: 'y', v: fy }); }
        aw = Math.max(aw, MIN_MUEBLE);
        ah = Math.max(ah, MIN_MUEBLE);
      }
      const nuevo = { x: mm(x), y: mm(y), w: mm(aw), h: mm(ah) };
      setMira({ x: nuevo.x, y: nuevo.y, guias });
      if (nuevo.x !== m.x || nuevo.y !== m.y || nuevo.w !== m.w || nuevo.h !== m.h) {
        marcarGesto();
        // La FORMA viaja con la caja, por lo mismo que la del área libre: los
        // vértices están en coordenadas de mundo, así que mover sólo `x`/`y`
        // dejaría el objeto dibujado donde estaba y la caja en otra parte.
        // Acá es siempre una traslación pura —un objeto con forma no muestra
        // los agarres de estirar, ver `vsm`—, así que basta con correrlos.
        if (m.vertices?.length) {
          const ddx = nuevo.x - m.x, ddy = nuevo.y - m.y;
          m.vertices = m.vertices.map(([vx, vy]) => [mm(vx + ddx), mm(vy + ddy)]);
        }
        Object.assign(m, nuevo);
        setTick((t) => t + 1);
      }
      return;
    }

    const w = planta.paredes.find((x) => x.id === ag.id);
    if (!w) return;

    if (ag.tipo === 'vano') {
      // El vano corre A LO LARGO de su pared y nada más: conserva el ancho y se
      // topa contra las dos puntas. Sin el tope, arrastrarlo fuera de la pared
      // lo dejaba existiendo en el aire —`tramos` lo ignora— y la puerta
      // desaparecía del dibujo sin que nada dijera por qué.
      const v = w.vanos.find((x) => x.id === ag.vano);
      if (!v) return;
      const ancho = v.b - v.a;
      const dedo = w.o === 'v' ? p.y : p.x;
      const puesta = mm(Math.min(
        Math.max(ag.a0 + Math.round((dedo - ag.m0) / PASO) * PASO, w.a),
        w.b - ancho,
      ));
      if (puesta !== v.a) marcarGesto();
      v.a = puesta;
      v.b = mm(puesta + ancho);
      setTick((t) => t + 1);
      return;
    }

    if (ag.tipo === 'mover') {
      // Dos candidatos para la posición nueva, y el imán tiene prioridad:
      //  · el nodo vecino más cercano, si el dedo pasó a tiro;
      //  · la rejilla ABSOLUTA de 5 cm (ver `aRejilla`), que es la que deja la
      //    pared en 1,40 y 1,45 en vez de arrastrar el decimal del plano.
      const eje = w.o === 'v' ? 'x' : 'y';
      const crudo = ag.c0 + (eje === 'x' ? p.x : p.y) - ag.m0;
      // Las puntas que viajan con la pared quedan FUERA de la lista del imán:
      // si no, la pared se imanta a su propia pegada y nunca cae en la rejilla.
      const iman = nodoCerca(crudo,
        nodosLibres(planta.paredes, eje, [w.id], ag.pegadas), alcanceIman());
      // La rejilla de 5 cm se aplica a la COTA que se lee, no a la coordenada:
      // ver `ejeDeCota`. Sin pegadas cae de vuelta en la rejilla absoluta, que
      // es lo que deja la pared en 1,40 y 1,45 en vez de arrastrar el decimal
      // del plano.
      const nueva = iman ?? ejeDeCota(ag.pegadas, ag.c0, crudo);
      guiaRef.current = iman == null ? null : { eje, v: iman };
      const corrio = nueva - ag.c0;
      // Una pared que YA se movió no puede ser el primer toque de un doble
      // toque: si no, arrastrarla y volver a tocarla entraría en selección
      // múltiple sin que nadie la haya tocado dos veces.
      if (corrio !== 0) dobleRef.current = null;
      const puesta = Math.round(nueva * 1000) / 1000;
      // La foto se toma en el primer cuadro que CAMBIA algo, no al apoyar el
      // dedo: tocar una pared para tomarla no es un paso que haya que deshacer.
      if (puesta !== w.c) marcarGesto();
      w.c = puesta;
      for (const g of ag.pegadas) {
        const q = planta.paredes.find((x) => x.id === g.id);
        if (!q) continue;
        if (g.fin === 0) q.a = Math.min(g.v0 + corrio, q.b - MIN_LARGO);
        else q.b = Math.max(g.v0 + corrio, q.a + MIN_LARGO);
      }
    } else {
      // La punta se pega al mismo juego de nodos, pero del OTRO eje: alargar
      // una pared vertical hasta topar con el eje de una horizontal es cerrar
      // una esquina, que es justo el gesto que más cuesta a ojo.
      const eje = w.o === 'v' ? 'y' : 'x';
      const dedo = eje === 'x' ? p.x : p.y;
      const crudo = ag.v0 + dedo - ag.m0;
      const iman = nodoCerca(crudo, nodos(planta.paredes, eje, [w.id]), alcanceIman());
      // Acá lo que se lee en la cota —y lo que Daniel pidió en múltiplos de 5—
      // es el LARGO, no la coordenada de la punta: cuantizar la posición dejaría
      // «1,43 m» en pantalla cada vez que el otro extremo no cae en rejilla. Se
      // redondea entonces la distancia al extremo FIJO, que no se mueve en todo
      // el gesto. El imán sigue mandando: topar con una pared vecina vale más
      // que un número redondo.
      const fijo = ag.fin === 0 ? w.b : w.a;
      const largo = aRejilla(Math.abs(ag.v0 - fijo), Math.abs(crudo - fijo));
      const v = iman ?? (ag.fin === 0 ? fijo - largo : fijo + largo);
      guiaRef.current = iman == null ? null : { eje, v: iman };
      const puesta = ag.fin === 0 ? Math.min(v, w.b - MIN_LARGO) : Math.max(v, w.a + MIN_LARGO);
      if (puesta !== (ag.fin === 0 ? w.a : w.b)) marcarGesto();
      if (ag.fin === 0) w.a = puesta;
      else w.b = puesta;
      w.vanos = w.vanos.filter((vn) => vn.a > w.a && vn.b < w.b);
    }
    // La pared se movió: lo que cuelga de ella va con ella. Éste es el único
    // punto de salida de los dos gestos de pared —correrla y estirarle una
    // punta—, así que alcanza con reasentar acá.
    reasentarAnclados(planta);
    setTick((t) => t + 1);
  }

  const soltar = () => {
    const ag = agarreRef.current;
    const u = umbralRef.current;
    agarreRef.current = null;
    dedoRef.current = null;
    guiaRef.current = null;
    umbralRef.current = null;
    setArrastrando(false);
    // La guía del imán vive sólo mientras el dedo está abajo: es una señal del
    // gesto, no un rasgo del plano. Dejarla puesta sería dibujar una línea que
    // no existe sobre un dibujo que sí se entrega.
    setIman(null);
    // EL SEGUNDO TIEMPO, resuelto. Si el gesto era `soloToque` y el dedo se
    // corrió, esto fue un PANEO que arrancó encima de algo: se suelta lo que el
    // apoyo había elegido y no queda nada tomado ni ninguna paleta en el
    // camino. Si no se corrió, fue el toque corto que TOMA, y la selección que
    // ya hizo el `pointerdown` es justo lo que se quería.
    if (u?.soloToque && u.ok) {
      pendienteRef.current = null;
      soltarTodo();
      return;
    }
    // Dos toques seguidos sobre la misma pared ENTRAN a selección múltiple con
    // esa pared ya tomada. Se mira acá, al levantar el dedo, y sólo si el
    // gesto no movió nada: un arrastre —aunque arranque medio segundo después
    // de haber tocado la pared— es un arrastre y no la mitad de un doble
    // toque. Eso es lo que el hold no podía distinguir.
    const pend = pendienteRef.current;
    pendienteRef.current = null;
    if (pend && !marcadoRef.current && esDoble(pend.que, pend.x, pend.y)) {
      setMulti(true);
      setSel([pend.id]);
      return;
    }
    // Mover un nodo apaga su mira por lo mismo que el mueble: la mira —y con
    // ella la lupa— es del gesto, no del objeto. Los tres nodos que la montan
    // la apagan acá: el de una cota y los dos vértices (área y objeto a
    // medida), estos últimos con la mira muda que sólo existe para la lupa.
    if (ag?.tipo === 'cotanodo' || ag?.tipo === 'vertice' || ag?.tipo === 'mvertice') {
      setMira(null);
    }
    // El mueble deja de dibujar su mira al soltar: la mira es del gesto, no del
    // objeto, y dejarla puesta señalaría un imán que ya no está midiendo nada.
    if (ag?.tipo === 'mueble') {
      setMira(null);
      // El catálogo se cierra ACÁ y no al crear: el botón del catálogo es el que
      // tiene la captura del puntero mientras el mueble viene pegado al dedo, y
      // desmontarlo antes de soltar mataría el arrastre en el primer cuadro.
      // Cerrado deja UNA sola paleta a la vista —la del mueble recién puesto—,
      // que es la regla que pidió Daniel.
      if (ag.nacido) { setCatalogo(false); setCorrCat(null); }
      // Mismo toggle que el área libre: un toque que no movió nada lo suelta.
      // Vale también para el agarre remoto, y es lo que devuelve el paneo del
      // plano: mientras haya un mueble tomado, arrastrar en el vacío lo mueve.
      if (ag.lado === 'cuerpo' && ag.yaSel && !marcadoRef.current) {
        setMuebleSel(null);
        setEsquinaSel(null);
      }
      return;
    }
    // Acá vivía el segundo verbo del lado: un toque que no empujaba nada lo
    // PARTÍA en dos, sin pedir permiso. Lo sacó Daniel el 8-oct-2026 y tenía
    // razón: «está activo siempre y cuesta mucho, porque por defecto te crea
    // vértices cuando tal vez lo que uno quiere es mover el lado». El gesto que
    // uno hace cien veces —empujar— y el que hace una —meter un nodo— estaban
    // en el mismo dedo, separados sólo por si el pulgar tembló tres píxeles, y
    // con un dedo en un teléfono eso se resuelve siempre a favor del raro.
    //
    // Partir quedó SÓLO bajo la tijera (`nodo === 'partir'`, arriba en el
    // `pointerdown`), que es exactamente para lo que Daniel pidió ese botón:
    // «la intención es que exista el botón para cuando uno desee hacerlo».
    // El lado se sigue empujando sin modo, como cualquier arrastre.
    if (ag?.tipo === 'poli') {
      if (marcadoRef.current && medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
      return;
    }
    // El vértice no tiene segundo verbo —un toque que no lo movió no hace
    // nada— pero sí hay que volver a medir si se movió: el total aparte de su
    // clase cambió con la forma.
    if (ag?.tipo === 'vertice') {
      if (marcadoRef.current && medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
      return;
    }
    if (ag?.tipo !== 'libre') return;
    // Un toque en el cuerpo que no movió nada suelta el área: es el toggle de
    // siempre, y hace falta acá porque al apoyar no se puede saber todavía si
    // esto va a ser un toque o un arrastre.
    if (ag.lado === 'cuerpo' && ag.yaSel && !marcadoRef.current) {
      setLibreSel(null);
      return;
    }
    // La lista de abajo tiene que quedar con el número nuevo. Sólo las libres se
    // vuelven a medir al soltar: las de la casa no cambiaron —el área libre no
    // entra en la inundación— pero su total aparte sí.
    if (marcadoRef.current && medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
  };

  // ── la paleta de la pared tomada ─────────────────────────────────────────
  /**
   * Planta la paleta encima de la cinta de cota de la pared tomada.
   *
   * Es HTML y vive FUERA del SVG a propósito: adentro habría que meterla en un
   * `foreignObject` y entonces el zoom del lienzo le escalaría los iconos, la
   * letra y el teclado del celular. Por eso se ancla midiendo en píxeles dónde
   * quedó la cinta —que sí está dentro del SVG y se mueve con la pared— contra
   * la caja del contenedor del lienzo.
   *
   * Mientras se arrastra se esconde: taparía justo la pared que se está
   * moviendo, y la cinta ya hace de cota viva durante el gesto.
   */
  useLayoutEffect(() => {
    const marco = lienzoRef.current?.getBoundingClientRect();
    if (arrastrando || modo || !marco) { setPaleta(null); return; }

    /** Un punto en metros, pasado a píxeles del contenedor.
     *
     *  Va por `lente()` y no por la matriz del SVG por lo mismo que `enMetros`:
     *  `marco` sale de `getBoundingClientRect()` y la matriz mapea al espacio
     *  «screen», que en iPhone incluye el zoom nativo de la página. Restar uno
     *  del otro desplazaba la paleta flotante en cuanto se hacía pinch. */
    const enPixeles = (x: number, y: number) => {
      const l = lente();
      if (!l) return null;
      return { x: l.ox + (x - l.x0) * l.k - marco.left,
               y: l.oy + (y - l.y0) * l.k - marco.top };
    };

    if (!quePaleta) { setPaleta(null); return; }

    // El vano y el área se anclan a su propio centro y no a la cinta de cota:
    // no tienen cinta, y el centro es justo donde está el dedo que los tomó.
    if (quePaleta === 'vano' && vano) {
      const k = caja(vano.w, vano.v.a, vano.v.b);
      const en = enPixeles((k[0] + k[2]) / 2, k[1]);
      setPaleta(en);
      return;
    }
    if (quePaleta === 'region' && region) {
      setPaleta(enPixeles(region.x, region.y));
      return;
    }
    // Con varias áreas tomadas la paleta se planta sobre el borde de arriba del
    // conjunto, igual que con varias paredes: puesta sobre una de ellas parece
    // que es la paleta de esa, y lo que ofrece es sobre todas.
    if (quePaleta === 'areas') {
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity;
      for (const g of regiones) {
        for (const [cx, cy, cw] of g.celdas) {
          x0 = Math.min(x0, cx); x1 = Math.max(x1, cx + cw); y0 = Math.min(y0, cy);
        }
      }
      if (Number.isFinite(y0)) { setPaleta(enPixeles((x0 + x1) / 2, y0)); return; }
    }
    if (quePaleta === 'divisor' && divisor) {
      setPaleta(enPixeles((divisor.x1 + divisor.x2) / 2, (divisor.y1 + divisor.y2) / 2));
      return;
    }
    // La cota se ancla a su punto medio, igual que el divisor: es una línea, no
    // tiene «arriba», y el medio es donde está escrito su número.
    if (quePaleta === 'cota' && cota) {
      setPaleta(enPixeles((cota.x1 + cota.x2) / 2, (cota.y1 + cota.y2) / 2));
      return;
    }
    // La cota de un lado se ancla al MEDIO DE ESE LADO, que es donde está
    // escrito el número que se tocó —mismo criterio que la cota vecina.
    if (quePaleta === 'ladoVecino' && cotaLadoVecino) {
      const k = cotaLadoVecino;
      setPaleta(enPixeles((k.x1 + k.x2) / 2, (k.y1 + k.y2) / 2));
      return;
    }
    // El área libre se ancla al borde de ARRIBA de su rectángulo, no al centro:
    // la paleta trae un campo de nombre y, puesta en el medio, tapa justo el
    // área que se está nombrando.
    if (quePaleta === 'libre' && libre) {
      setPaleta(enPixeles(libre.x + libre.w / 2, libre.y));
      return;
    }
    // El mueble también por el borde de arriba, por lo mismo: su paleta trae las
    // dos medidas y puesta encima taparía el módulo que se está midiendo.
    if (quePaleta === 'mueble' && mueble) {
      setPaleta(enPixeles(mueble.x + mueble.w / 2, mueble.y));
      return;
    }
    // La cota vecina se ancla al MEDIO DE SU PARED, que es donde está escrito el
    // número que se tocó. Ponerla sobre la pared tomada —que es la que se
    // mueve— dejaría el campo lejos del dedo que acaba de apuntar.
    if (quePaleta === 'vecina' && paredVecina) {
      const q = paredVecina;
      const medio = (q.a + q.b) / 2;
      const en = q.o === 'v' ? enPixeles(q.c, medio) : enPixeles(medio, q.c);
      setPaleta(en);
      return;
    }
    if (!sel.length) { setPaleta(null); return; }

    if (quePaleta === 'pared' && uno != null) {
      const cinta = lienzoRef.current?.querySelector('[data-cota] rect');
      if (!cinta) { setPaleta(null); return; }
      const k = cinta.getBoundingClientRect();
      setPaleta({ x: k.left - marco.left + k.width / 2, y: k.top - marco.top });
      return;
    }

    // Con varias tomadas no hay una cinta que medir —tampoco un largo que
    // escribir—, así que la paleta se planta sobre el borde de arriba del
    // conjunto, pasando la caja en metros por la lente del lienzo.
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity;
    for (const w of planta.paredes) {
      if (!sel.includes(w.id)) continue;
      const k = caja(w);
      x0 = Math.min(x0, k[0]); y0 = Math.min(y0, k[1]); x1 = Math.max(x1, k[2]);
    }
    if (!Number.isFinite(y0)) { setPaleta(null); return; }
    const s = enPixeles((x0 + x1) / 2, y0);
    if (!s) { setPaleta(null); return; }
    setPaleta(s);
  }, [sel, uno, arrastrando, modo, tick, iPlanta, planta, vanoSel, recintos, divisor, cota,
      libre, mueble, paredVecina, cotaLadoVecino, medicion, quePaleta, lente]);

  /**
   * Tocar fuera de la paleta suelta la pared. Reemplaza al botón de cerrar: en
   * un panel que ya tiene cuatro iconos, el quinto para «no hacer nada» es el
   * que menos se gana su lugar, y el gesto de tocar afuera ya se usa en
   * cualquier menú del teléfono.
   *
   * Se escucha en `document` y no en el lienzo porque el dedo puede caer en la
   * barra de arriba o en la de abajo, y ahí también se suelta. Los toques que
   * NO sueltan son tres: adentro de la paleta, encima de una pared y encima de
   * un punto de punta —esos dos últimos los maneja `tomar`, que ya decidió a
   * quién tomar; el punto se nombra aparte porque vive en su propia capa, fuera
   * del grupo de la pared—.
   *
   * Mientras hay un modo encendido no escucha nada. Trazar y dividir tapan el
   * lienzo con una capa propia que NO es una pared, así que el toque caería
   * acá: `dividirEn` corre primero y toma el trozo nuevo, y un instante después
   * este oyente lo soltaba. El corte se hacía bien y la selección se perdía.
   */
  /**
   * Corre con la flecha las paredes tomadas, arrastrando las que mueren contra
   * ellas.
   *
   * Hace exactamente lo mismo que el dedo —misma rejilla absoluta, mismas
   * pegadas— porque si no, mover con la flecha y mover arrastrando dejarían
   * plantas distintas. Lo único que no hace es imán: el imán compensa el pulso,
   * y acá el pulso ya es exacto.
   *
   * Las pegadas se calculan ANTES de correr la pared: `pegadas` busca las puntas
   * que caen sobre el eje viejo, y pedírselas después no encontraría ninguna.
   */
  function correrParedes(ids: number[], dx: number, dy: number) {
    // Una pared sólo se mueve en su perpendicular: empujarla a lo largo de sí
    // misma no mueve nada.
    const mueve = planta.paredes.filter((w) => ids.includes(w.id)
      && (w.o === 'v' ? dx : dy) !== 0);
    if (!mueve.length) return;
    marcarGesto();
    for (const w of mueve) {
      const peg = pegadas(w);
      const antes = w.c;
      // La flecha mueve 5 cm y DEJA LA COTA EN MÚLTIPLO DE 5: lo que se redondea
      // es el largo de las pegadas, no la coordenada. Ver `ejeDeCota`.
      w.c = ejeDeCota(peg, antes, antes + (w.o === 'v' ? dx : dy));
      const corrio = w.c - antes;
      for (const g of peg) {
        const q = planta.paredes.find((x) => x.id === g.id);
        if (!q) continue;
        if (g.fin === 0) q.a = mm(Math.min(g.v0 + corrio, q.b - MIN_LARGO));
        else q.b = mm(Math.max(g.v0 + corrio, q.a + MIN_LARGO));
      }
    }
    setTick((t) => t + 1);
  }

  /**
   * Tap con DOS dedos = deshacer · con TRES = rehacer.
   *
   * Pedido de Daniel el 5-oct-2026 para el celular, donde la barra de abajo
   * queda lejos del pulgar mientras se trabaja arriba del plano.
   *
   * Lo difícil no es el gesto, es distinguirlo: el pellizco de zoom y el paneo
   * de dos dedos entran por los MISMOS eventos. El filtro es el de siempre
   * —poco tiempo y poco recorrido del centro— más uno propio del pellizco: si
   * la separación entre los dedos cambió, era zoom y no un toque.
   *
   * Se cuenta el MÁXIMO de dedos que llegaron a estar apoyados, no los que
   * quedan al final: los tres dedos nunca aterrizan en el mismo milisegundo, y
   * leer el último evento daría casi siempre «uno».
   */
  const tapRef = useRef({ n: 0, x: 0, y: 0, d: 0, t: 0, movido: false });

  /**
   * El segundo dedo CANCELA el arrastre en curso y devuelve lo que movió.
   *
   * Es el otro pedido de Daniel del 8-oct-2026: «si hago zoom y me muevo,
   * muchas veces intentando hacer el zoom se mueven las paredes o se mueven los
   * objetos». El umbral no alcanza para esto —en un pellizco el primer dedo se
   * corre muchísimo más de 12 px—, y la señal que lo delata es inequívoca: un
   * segundo dedo aterrizando significa zoom o paneo, nunca un arrastre.
   *
   * Si el gesto ya había movido algo se deshace. Entró al historial como UN
   * paso (`marcarGesto`), así que un `viajar('atras')` lo borra exacto.
   */
  function pellizcoCancela() {
    if (!agarreRef.current) return;
    const movio = marcadoRef.current;
    agarreRef.current = null;
    dedoRef.current = null;
    guiaRef.current = null;
    umbralRef.current = null;
    pendienteRef.current = null;
    marcadoRef.current = false;
    setArrastrando(false);
    if (movio) viajar('atras');
  }

  function dedosAbajo(e: React.TouchEvent) {
    const g = tapRef.current;
    if (e.touches.length === 1) { g.n = 0; g.t = Date.now(); g.movido = false; }
    if (e.touches.length > g.n) {
      g.n = e.touches.length;
      Object.assign(g, centroDedos(e.touches), { d: aperturaDedos(e.touches) });
    }
    // El `movido` se fuerza para que este mismo apoyo no cuente además como el
    // tap de dos dedos: deshacer el arrastre y deshacer OTRA VEZ al levantar
    // sería un paso de historia perdido sin que nadie lo pidiera.
    if (e.touches.length > 1 && agarreRef.current) { g.movido = true; pellizcoCancela(); }
  }

  function dedosMueven(e: React.TouchEvent) {
    const g = tapRef.current;
    if (g.n < 2 || g.movido) return;
    const c = centroDedos(e.touches);
    if (Math.hypot(c.x - g.x, c.y - g.y) > TAP_PX
        || Math.abs(aperturaDedos(e.touches) - g.d) > TAP_PX) g.movido = true;
  }

  function dedosArriba(e: React.TouchEvent) {
    const g = tapRef.current;
    if (e.touches.length) return;              // todavía queda alguno apoyado
    const { n, movido, t } = g;
    g.n = 0;
    if (movido || Date.now() - t > TAP_MS) return;
    if (n === 2 && atras.length) viajar('atras');
    else if (n === 3 && adelante.length) viajar('adelante');
  }

  /** Lo mismo para el mueble tomado: 5 cm por flecha, a la rejilla absoluta. */
  function correrMueble(id: number, dx: number, dy: number) {
    const m = (planta.muebles ?? []).find((q) => q.id === id);
    if (!m) return;
    marcarGesto();
    m.x = mm(aPaso(m.x + dx));
    m.y = mm(aPaso(m.y + dy));
    setTick((t) => t + 1);
  }

  useEffect(() => {
    if ((!sel.length && !vanoSel && !recintos.length && divSel == null && cotaSel == null
         && libreSel == null && muebleSel == null) || modo) return;
    // El gesto que suelta es un TAP CORTO sobre el vacío, y se decide al
    // LEVANTAR el dedo, no al apoyarlo (pedido de Daniel, 18-oct-2026):
    // «selecciona el elemento, sale esa barra y puede seguir moviéndose; y ya si
    // se quiere salir, hace un tap rápido en un lugar donde no haya nada».
    // Antes esto vivía en el `pointerdown` y por eso el PRIMER cuadro de un
    // paneo sobre el lienzo —con el elemento tomado— le vaciaba la mano y se
    // llevaba la paleta, dejando la pieza inalcanzable. Mismo discriminador que
    // ya usa el gesto de varios dedos: `ARRASTRE_PX` de corrimiento y `TAP_MS`
    // de duración.
    let tap: { id: number; x: number; y: number; t: number } | null = null;
    let dedos = 0;
    const soltar = () => {
      setSel([]);
      setMulti(false);
      setVanoSel(null);
      setRecintos([]);
      setMultiAreas(false);
      setDivSel(null);
      setCotaSel(null);
      setVecinaSel(null);
      setLibreSel(null);
      setLadoSel(null);
      setLadoVecinoSel(null);
      setMuebleSel(null);
      setEsquinaSel(null);
      // La paleta vuelve a donde cae. Es el camino de regreso si se la arrastró
      // fuera de la pantalla: tocar afuera y volver a tomar siempre la trae.
      setCorrimiento(null);
    };
    const abajo = (e: PointerEvent) => {
      dedos++;
      // Un segundo dedo nunca es un tap: es pinza o zoom. Desarma y se va.
      if (dedos > 1) { tap = null; return; }
      const t = e.target as Element | null;
      // Los botones de capa quedan afuera desde el 6-oct-2026: son los únicos
      // que deciden por sí mismos qué soltar. El toque simple y el doble ya
      // llaman a `soltarTodo`, y el SOSTENIDO conserva a propósito lo de su
      // propia capa —proteger el módulo que se está acomodando—, así que si
      // este oyente llegara primero le vaciaría la mano.
      // `data-fondo-modo` es el rectángulo que recibe los toques mientras hay
      // una herramienta viva, y está en la lista por un caso de borde fino: al
      // poner un objeto, el `pointerdown` apaga el modo, React re-renderiza
      // SÍNCRONAMENTE —es un evento discreto— y este oyente queda registrado a
      // tiempo de recibir ese mismo toque. Sin esta línea, la pieza nacía y el
      // mismo dedo que la puso la soltaba, dejándola sin su paleta.
      if (t?.closest('[data-capa], [data-paleta], [data-pared], [data-punta], [data-vano],'
                      + ' [data-region], [data-divisor], [data-acote], [data-cota-pared],'
                      + ' [data-cota-libre], [data-libre], [data-mueble], [data-esquina],'
                      + ' [data-catalogo], [data-fondo-modo]')) return;
      tap = { id: e.pointerId, x: e.clientX, y: e.clientY, t: Date.now() };
    };
    const moviendo = (e: PointerEvent) => {
      if (!tap || e.pointerId !== tap.id) return;
      // Pasó el umbral: el dedo está paneando el plano, no tocando el vacío.
      if (Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > ARRASTRE_PX) tap = null;
    };
    const arriba = (e: PointerEvent) => {
      dedos = Math.max(0, dedos - 1);
      const g = tap;
      tap = null;
      if (!g || e.pointerId !== g.id) return;
      if (Date.now() - g.t > TAP_MS) return;
      soltar();
    };
    // `pointercancel` solo desarma: cuando el navegador se queda el gesto es
    // porque lo leyó como paneo o pinza, y eso nunca debe soltar.
    const cancelar = () => { dedos = Math.max(0, dedos - 1); tap = null; };
    document.addEventListener('pointerdown', abajo);
    document.addEventListener('pointermove', moviendo);
    document.addEventListener('pointerup', arriba);
    document.addEventListener('pointercancel', cancelar);
    return () => {
      document.removeEventListener('pointerdown', abajo);
      document.removeEventListener('pointermove', moviendo);
      document.removeEventListener('pointerup', arriba);
      document.removeEventListener('pointercancel', cancelar);
    };
  }, [sel.length, vanoSel, recintos.length, divSel, cotaSel, libreSel, muebleSel, modo]);

  // Al soltar la herramienta de dividir, la línea deja de dibujarse: si siguiera
  // tomada quedaría una paleta flotando sobre algo invisible, y la tecla Borrar
  // partiría un área que ya nadie ve.
  useEffect(() => {
    if (modo !== 'area') setDivSel(null);
  }, [modo]);

  /**
   * La tecla Borrar borra lo que esté tomado. (Pedido de Daniel, 4-oct-2026:
   * «si estoy en la computadora y le doy al botón de borrar, que pues borre el
   * elemento seleccionado».)
   *
   * El basurero de la paleta sigue siendo el camino del dedo; esto es el del
   * teclado, y ahí la tecla es lo que la mano busca primero. Cada cosa borra con
   * su propia función porque borrar una puerta, un divisor y un área no son la
   * misma operación —una saca un hueco de una pared, otra vuelve a juntar dos
   * regiones, la tercera quita un rectángulo—.
   *
   * ⚠️ `Backspace` también, pero NUNCA si el foco está en un campo: la paleta
   * tiene campos de texto —el nombre del área, el largo de la pared— y ahí la
   * tecla tiene que borrar letras. Sin esta guarda, corregir «Terraza» a
   * «Terrazas» borraba el área entera a la primera tecla.
   *
   * Un recinto medido no entra: no es un objeto del modelo, es el resultado de
   * la inundación. Lo que se borra para que desaparezca es la pared que lo cierra.
   */
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      // Con la tabla abierta la tecla no borra nada: lo que está adelante es la
      // tabla, y ahí Borrar no tiene a quién borrar.
      if (e.key === 'Escape' && tabla) { setTabla(false); e.preventDefault(); return; }
      // Esc baja el fantasma sin poner nada. Es el «Cancelar» de la pastilla
      // desde el teclado, y hace falta: con un módulo armado el plano no
      // selecciona nada, así que sin esto habría que ir hasta la pastilla.
      if (e.key === 'Escape' && armado) { desarmar(); e.preventDefault(); return; }
      const t = e.target as HTMLElement | null;
      const escribiendo = !!t
        && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      // Los atajos de siempre, los que pidió Daniel el 5-oct-2026: «el de
      // deshacer, que sería control Z, o rehacer, no sé si podría ser shift
      // control Z […] de copiar con control C». Van por el MISMO camino que los
      // botones de la barra —`viajar`, `copiarMueble`— y no por uno paralelo.
      //
      // Dentro de un campo de texto no se tocan: ahí Ctrl+Z deshace lo que se
      // acaba de escribir, y robárselo para deshacer una pared sería peor que
      // no tener atajo.
      if ((e.ctrlKey || e.metaKey) && !escribiendo && !tabla) {
        const k = e.key.toLowerCase();
        if (k === 'z') {
          // Shift+Ctrl+Z rehace, y Ctrl+Y también: es el atajo de Windows y la
          // compu del papá es una Windows.
          const hacia = e.shiftKey ? 'adelante' : 'atras';
          if ((hacia === 'atras' ? atras : adelante).length) viajar(hacia);
          e.preventDefault();
          return;
        }
        if (k === 'y') {
          if (adelante.length) viajar('adelante');
          e.preventDefault();
          return;
        }
        if (k === 'c' && muebleSel != null) {
          // Sólo con una pieza tomada. Sin eso, Ctrl+C tiene que seguir
          // copiando el texto que el usuario haya seleccionado en la pantalla.
          copiarMueble();
          e.preventDefault();
          return;
        }
        return;
      }
      // Las flechas mueven lo que está tomado, de 5 en 5 cm —el mismo paso que
      // el dedo—. Daniel, 5-oct-2026: «si lo hago desde la computadora y utilizo
      // las flechas, que lo mueva igual en esos múltiplos de 5 cm».
      //
      // Una pared SÓLO se mueve en su perpendicular: empujar una pared a lo
      // largo de sí misma no es mover nada, y dejar que lo intente haría que dos
      // de las cuatro flechas no hicieran nada visible sin decir por qué.
      const FLECHAS: Record<string, [number, number]> = {
        ArrowLeft: [-PASO, 0], ArrowRight: [PASO, 0],
        ArrowUp: [0, -PASO], ArrowDown: [0, PASO],
      };
      const paso = FLECHAS[e.key];
      if (paso && !escribiendo && !tabla) {
        const [dx, dy] = paso;
        if (muebleSel != null) { correrMueble(muebleSel, dx, dy); e.preventDefault(); }
        else if (sel.length) { correrParedes(sel, dx, dy); e.preventDefault(); }
        return;
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (tabla) return;
      if (escribiendo) return;
      if (sel.length) borrar();
      else if (vanoSel) borrarVano();
      else if (divSel != null) borrarDivisor();
      else if (cotaSel != null) borrarCota();
      else if (libreSel != null) borrarLibre();
      else if (muebleSel != null) borrarMueble();
      else return;
      e.preventDefault();
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
    // `plantas` entra en las dependencias aunque no se lea acá: deshacer la
    // reemplaza por un objeto nuevo, y sin eso el oyente seguiría borrando
    // sobre la planta vieja. No alcanza con `tick`, que no cambia la identidad.
  }, [sel.length, vanoSel, divSel, cotaSel, libreSel, muebleSel, iPlanta, plantas,
      tabla, armado, atras.length, adelante.length]);

  /**
   * Vuelve a medir cuando hace falta, y sólo cuando hace falta.
   *
   * Esto es lo que reemplazó al botón «Recalcular áreas», y contesta la pregunta
   * de Daniel —«no sé si el tema de recalcular áreas es obligatorio que sea de
   * esa manera»—: obligatorio es NO medir en cada cuadro del arrastre, porque
   * ahí el número empieza a mandar sobre el diseño. Un botón aparte para pedirlo
   * a mano no era obligatorio nunca.
   *
   * Entonces la medición se vuelve consecuencia de haber terminado un gesto.
   * Cada operación del editor ya deja la medición en null —así quedaba claro que
   * el número era viejo—, y eso es justo la señal que este efecto necesita.
   *
   * Medía SÓLO con el ojo de áreas encendido o la tabla abierta, y eso se cayó
   * el 6-oct-2026: los dos totales viven ahora en la pastilla de arriba, que
   * está siempre a la vista. Un número que se mira de reojo mientras se diseña
   * no puede depender de haber prendido otra cosa antes.
   *
   * `arrastrando` es la guarda que conserva la regla vieja: mientras el dedo
   * está apoyado no se mide, por lejos que se haya corrido la pared.
   */
  useEffect(() => {
    if (medicion || arrastrando) return;
    recalcular();
    // `recalcular` se recrea en cada repintado, así que no entra en la lista:
    // lee todo de `planta`/`plantas`, que sí están.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verAreas, tabla, medicion, arrastrando, iPlanta, plantas, tick]);

  // ── historial ────────────────────────────────────────────────────────────
  /**
   * Guarda una foto del modelo ANTES de cambiarlo. Va al principio de cada
   * operación que muta, y de un arrastre va UNA sola vez (ver `marcarGesto`).
   *
   * Rehacer se tira acá: desde este punto la historia va por otro lado, y un
   * «adelante» que lleva a un modelo que ya nadie pidió es peor que ninguno.
   */
  function marcar() {
    // La foto se saca ACÁ y no adentro del updater. Parece lo mismo y no lo es:
    // React corre el updater en el repintado, que es DESPUÉS de que la operación
    // ya mutó las paredes, así que la foto salía con el cambio adentro. En una
    // operación suelta no se notaba —React evalúa el updater al vuelo cuando no
    // hay nada pendiente— pero en un arrastre, donde cada cuadro deja un
    // `setTick` pendiente, la foto quedaba un cuadro corrida: deshacer un
    // arrastre devolvía la pared 5 cm —un paso de rejilla— más allá de donde
    // estaba. Medido con el arrastre de un vano: salía de 4,625 y deshacer lo
    // dejaba en 4,775.
    const foto = JSON.stringify(plantas);
    setAtras((s) => [...s, foto].slice(-TOPE_HISTORIAL));
    setAdelante([]);
  }

  /** Marca una sola vez por gesto: `arrastrar` corre en cada cuadro. */
  function marcarGesto() {
    if (marcadoRef.current) return;
    marcadoRef.current = true;
    marcar();
  }

  /**
   * Deshacer y rehacer son el mismo movimiento con las pilas al revés, así que
   * son la misma función: saca la foto de una pila, mete la de ahora en la otra
   * y restaura.
   *
   * La selección se suelta siempre. La pared tomada puede no existir en la foto
   * que vuelve —deshacer un trazo borra la pared que estaba tomada— y una
   * paleta flotando sobre una pared que ya no está es un fantasma.
   */
  function viajar(hacia: 'atras' | 'adelante') {
    const pila = hacia === 'atras' ? atras : adelante;
    if (!pila.length) return;
    const foto = pila[pila.length - 1];
    const ahora = JSON.stringify(plantas);
    if (hacia === 'atras') {
      setAtras(pila.slice(0, -1));
      setAdelante((s) => [...s, ahora].slice(-TOPE_HISTORIAL));
    } else {
      setAdelante(pila.slice(0, -1));
      setAtras((s) => [...s, ahora].slice(-TOPE_HISTORIAL));
    }
    setPlantas(JSON.parse(foto));
    soltarTodo();
    setMedicion(null);
    setModo(null);
    setTrazo(null);
    setCorte(null);
    setMira(null);
    setAviso(null);
  }

  /** Suelta lo que haya tomado, sea pared, vano, área, división o cota. */
  function soltarTodo() {
    setSel([]);
    setMulti(false);
    setVanoSel(null);
    setRecintos([]);
    setMultiAreas(false);
    setDivSel(null);
    setCotaSel(null);
    setVecinaSel(null);
    setLibreSel(null);
    setLadoSel(null);
    setLadoVecinoSel(null);
    setMuebleSel(null);
    setEsquinaSel(null);
    setCorrimiento(null);
  }

  /**
   * Aplica el largo escrito. La punta `a` queda CLAVADA y se mueve `b`: así el
   * número que se escribe se ve crecer desde donde arranca la pared, que es
   * donde el ojo la tiene anclada —normalmente contra otra pared.
   */
  function aplicarCota(texto: string, id: number) {
    // El campo vacío no es un cero: es «todavía no escribí». `Number('')` da 0,
    // que es finito, y sin este corte borrar el número encogía la pared al
    // mínimo sola.
    if (!texto.trim()) return;
    const v = Number(texto.replace(',', '.'));
    const w = planta.paredes.find((x) => x.id === id);
    if (!w || !Number.isFinite(v)) return;
    marcar();
    w.b = mm(w.a + Math.max(MIN_LARGO, v));
    w.vanos = vanosEn(w.vanos, w.a, w.b);
    setMedicion(null);
    setTick((t) => t + 1);
  }

  /** Abre —o cierra— el campo de la cota de un lado vecino del polígono. */
  function tomarCotaLadoVecino(e: React.PointerEvent, lado: number) {
    e.stopPropagation();
    setAviso(null);
    setLadoVecinoSel((a) => (a === lado ? null : lado));
  }

  /**
   * APLICA LA MEDIDA ESCRITA EN UN LADO VECINO EMPUJANDO EL LADO TOMADO.
   *
   * La cuenta es la misma de `medidaVecina` contada con vértices: de las dos
   * puntas del vecino, una muere contra el lado tomado y VIAJA con él; la otra
   * se queda quieta y es el ANCLA contra la que se mide. Entonces el largo que
   * se escribe fija dónde tiene que terminar la punta que viaja, y la diferencia
   * con donde está hoy es exactamente cuánto hay que empujar el lado tomado.
   *
   * Cuál punta viaja sale del orden del polígono y no de la geometría: si el
   * vecino es el SIGUIENTE, comparte con el tomado su vértice de arranque; si es
   * el ANTERIOR, su vértice de fin. El signo conserva el sentido que la forma ya
   * tenía, así que escribir un número nunca la voltea.
   *
   * Empuja con `empujarLado`, el mismo que usa el arrastre del dedo: si fueran
   * dos cuentas distintas, el número y el movimiento podrían no coincidir.
   */
  function medidaLadoVecino(texto: string, lado: number) {
    // El campo vacío no es un cero, igual que en `aplicarCota`.
    if (!texto.trim()) return;
    const v = Number(texto.replace(',', '.'));
    const l = ladoSel == null ? null : planta.libres?.find((x) => x.id === ladoSel.id);
    const vs = l?.vertices;
    if (!ladoSel || !l || !vs?.length || l.fijo || !Number.isFinite(v)) return;
    if (!ladosVecinos.includes(lado)) return;
    const n = vs.length;
    const siguiente = lado === (ladoSel.lado + 1) % n;
    const ancla = siguiente ? vs[(lado + 1) % n] : vs[lado];
    const viaja = siguiente ? vs[lado] : vs[(lado + 1) % n];
    // Un lado horizontal mide en x, uno vertical en y. Y el tomado, que es el
    // perpendicular, se empuja justo por ese mismo eje.
    const eje = orientaLado(vs, lado) === 'h' ? 0 : 1;
    const destino = ancla[eje]
      + (Math.sign(viaja[eje] - ancla[eje]) || 1) * Math.max(MIN_LARGO, v);
    const corrio = destino - viaja[eje];
    // Medio milímetro no es un movimiento: sería una foto de deshacer por cada
    // tecla que no cambió nada.
    if (Math.abs(corrio) < 0.0005) return;
    marcar();
    l.vertices = empujarLado(vs, ladoSel.lado, eje === 0 ? corrio : 0, eje === 1 ? corrio : 0);
    recajar(l);
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((t) => t + 1);
  }

  /** Abre —o cierra— el campo de la cota de una pared vecina. */
  function tomarCotaVecina(e: React.PointerEvent, id: number) {
    // Sin esto el toque sigue hasta el oyente de «tocar afuera», que suelta la
    // pared tomada: el gesto se mataría a sí mismo al abrirse.
    e.stopPropagation();
    setAviso(null);
    // Un vano tomado gana a la pared en `quePaleta`, así que su paleta taparía
    // el campo que se acaba de pedir. Soltarlo es lo mismo que hace tomar
    // cualquier otra cosa.
    setVanoSel(null);
    setVecinaSel((a) => (a === id ? null : id));
  }

  /**
   * APLICA LA MEDIDA ESCRITA EN LA COTA DE UNA PARED VECINA CORRIENDO LA TOMADA.
   *
   * La cuenta es corta y conviene verla escrita, porque es lo único que hace
   * falta entender del gesto: la punta de la vecina que muere contra la pared
   * tomada viaja con ella —eso ya lo hacía el arrastre, vía `pegadas`—, así que
   * el largo de la vecina cambia EXACTAMENTE lo que se corra la tomada. Si la
   * punta que viaja es `a`, crecer quiere decir correr hacia atrás; si es `b`,
   * hacia adelante. De ahí el signo, y nada más.
   *
   * No pasa por `ejeDeCota`: ése redondea el largo a la rejilla de 5 cm para que
   * la cota cierre cuando el eje se mueve a ciegas —arrastrando o con la
   * flecha—. Acá el largo lo acaba de escribir Daniel, y redondearle un 2,63 a
   * 2,65 sería desobedecerlo con cara de prolijidad.
   *
   * Lo que se escribe es el largo de ESA pared, no la distancia entre dos: es la
   * misma cota que ya se veía en pantalla, que mide de `a` a `b` sobre el eje.
   */
  function medidaVecina(texto: string, id: number) {
    // El campo vacío no es un cero, igual que en `aplicarCota`.
    if (!texto.trim()) return;
    const v = Number(texto.replace(',', '.'));
    const w = uno == null ? null : planta.paredes.find((x) => x.id === uno);
    const g = vecinas.find((p) => p.id === id);
    const q = planta.paredes.find((x) => x.id === id);
    if (!w || !g || !q || !Number.isFinite(v)) return;
    const corrio = (g.fin === 0 ? -1 : 1) * (Math.max(MIN_LARGO, v) - (q.b - q.a));
    // Medio milímetro no es un movimiento: sería una foto de deshacer por cada
    // tecla que no cambió nada.
    if (Math.abs(corrio) < 0.0005) return;
    marcar();
    // Las pegadas se piden ANTES de correr la pared, por lo mismo que en
    // `correrParedes`: buscan las puntas que caen sobre el eje VIEJO.
    const peg = pegadas(w);
    w.c = mm(w.c + corrio);
    for (const p of peg) {
      const x = planta.paredes.find((y) => y.id === p.id);
      if (!x) continue;
      if (p.fin === 0) x.a = mm(Math.min(p.v0 + corrio, x.b - MIN_LARGO));
      else x.b = mm(Math.max(p.v0 + corrio, x.a + MIN_LARGO));
    }
    setMedicion(null);
    setTick((t) => t + 1);
  }

  // ── puertas y ventanas ───────────────────────────────────────────────────
  /**
   * Abre una puerta o una ventana en la pared tomada.
   *
   * Cae en el centro del tramo MACIZO más largo y no en el centro de la pared:
   * una pared que ya tiene una puerta al medio tiene el centro ocupado, y nacer
   * encima de otro vano los funde en uno solo —`tramos` junta los que se
   * solapan— así que la segunda puerta se veía como un hueco más ancho y no
   * como dos.
   *
   * Nace con el ancho de catálogo y queda TOMADA: el gesto siguiente es casi
   * siempre correrla a su sitio o escribirle el ancho, y las dos cosas piden que
   * ya esté agarrada.
   */
  function agregarVano(tipo: 'puerta' | 'ventana') {
    const w = planta.paredes.find((x) => x.id === uno);
    if (!w) return;
    const ancho = ANCHO[tipo];
    const libres = tramos(w);
    const hueco = libres.reduce((m, t) => (t[1] - t[0] > m[1] - m[0] ? t : m), libres[0]);
    if (!hueco || hueco[1] - hueco[0] < ancho) {
      setAviso(
        `No cabe una ${tipo} de ${m2(ancho)} m: el tramo de pared más largo que queda libre mide `
        + `${m2(hueco ? hueco[1] - hueco[0] : 0)} m.`,
      );
      return;
    }
    marcar();
    const medio = (hueco[0] + hueco[1]) / 2;
    const id = idVano(w.vanos);
    w.vanos = [...w.vanos, { id, a: mm(medio - ancho / 2), b: mm(medio + ancho / 2), tipo }];
    setSel([]);
    setMulti(false);
    setAviso(null);
    setVanoSel({ pared: w.id, vano: id });
    setTick((t) => t + 1);
  }

  /**
   * Aplica el ancho escrito al vano tomado. Crece y se encoge desde su CENTRO —no
   * desde una punta como la pared— porque una puerta se corre a su sitio y lo que
   * uno quiere es que siga centrada donde la dejó; y si al crecer se sale de la
   * pared, se recuesta contra la punta en vez de quedarse por fuera.
   */
  function aplicarAncho(texto: string) {
    if (!texto.trim() || !vano) return;
    const { w, v } = vano;
    const pedido = Number(texto.replace(',', '.'));
    if (!Number.isFinite(pedido)) return;
    marcar();
    const ancho = Math.min(Math.max(MIN_VANO, pedido), w.b - w.a);
    const centro = (v.a + v.b) / 2;
    let a = centro - ancho / 2;
    if (a < w.a) a = w.a;
    if (a + ancho > w.b) a = w.b - ancho;
    v.a = mm(a);
    v.b = mm(a + ancho);
    setTick((t) => t + 1);
  }

  function cambiarTipoVano(tipo: TipoVano) {
    if (!vano) return;
    marcar();
    vano.v.tipo = tipo;
    setTick((t) => t + 1);
  }

  /** Cierra el vano: la pared vuelve a ser maciza en ese tramo. */
  function borrarVano() {
    if (!vano) return;
    marcar();
    vano.w.vanos = vano.w.vanos.filter((x) => x.id !== vano.v.id);
    setVanoSel(null);
    setTick((t) => t + 1);
  }

  // ── las áreas ────────────────────────────────────────────────────────────
  /**
   * Renombra el área tomada.
   *
   * El nombre de una región no se guarda en la región —la región la inventa la
   * inundación en cada medición— sino en los RÓTULOS que le caen adentro, así
   * que renombrar es reescribir esos rótulos. Cuando son varios («S.S + Pasillo»
   * son dos rótulos en un mismo espacio continuo) se reescriben todos: en el
   * modelo del editor eso ES un solo recinto, y dejar uno viejo haría reaparecer
   * el nombre anterior pegado al nuevo.
   *
   * Y después hay que reescribir el reposo, que es lo delicado. `planta.reposo`
   * —el área con la que se compara para decir cuánto ganó o perdió— se indexa
   * POR NOMBRE, y renombrar no corre sólo la llave de esta región: corre también
   * la numeración de las que compartían el nombre viejo. Con dos «Sin rótulo»,
   * bautizar la primera deja a la otra sin número («Sin rótulo 2» → «Sin
   * rótulo»), su llave deja de existir y aparece un delta inventado en un cuarto
   * que nadie tocó. Por eso el reposo no se parchea por nombre: se vuelve a
   * medir y se reescribe por POSICIÓN, que es el orden de la inundación y no se
   * movió porque renombrar no mueve ninguna pared.
   */
  function renombrar(texto: string) {
    const nuevo = texto.trim();
    const g = region;
    if (!g || !medicion || !nuevo || nuevo === g.nombre) return;
    marcar();

    const reposoAntes = medicion.regiones.map(
      (r) => planta.reposo?.[r.nombre] ?? r.referencia,
    );
    const dentro = planta.recintos.filter((r) => r.region === g.indice);
    if (dentro.length) {
      for (const r of dentro) r.nombre = nuevo;
    } else {
      // Región sin un solo rótulo: hay que crearle uno. Nace con `plano: 0` —no
      // declara área— y eso no mueve ningún número: desde que se mide a ejes el
      // área de cada región es su geometría y los rótulos sólo le ponen nombre.
      planta.recintos.push({
        nombre: nuevo, plano: 0, x: g.x, y: g.y, region: g.indice,
      });
    }

    const med = medirSegunVista(planta, vistaObraRef.current);
    if (med.regiones.length === reposoAntes.length) {
      planta.reposo = Object.fromEntries(
        med.regiones.map((r, i) => [r.nombre, reposoAntes[i]]),
      );
    }
    // Se vuelve a medir para que `referencia` lea el reposo ya reescrito.
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setRecintos([nuevo]);
    setTick((t) => t + 1);
  }

  /**
   * Gira la pared 90° sobre su CENTRO.
   *
   * Las plantas del catálogo son ortogonales, así que girar es cambiar de eje:
   * lo que era vertical queda horizontal con el mismo largo y el mismo centro.
   * Sobre el centro y no sobre una punta porque así la pared rota EN SU LUGAR —
   * no se dispara al otro extremo del cuarto— y desde ahí se arrastra a donde
   * vaya. Los vanos viajan con ella guardando su distancia al arranque: una
   * puerta a 80 cm de la punta sigue a 80 cm después de girar.
   *
   * Las paredes que morían contra ella quedan sueltas, igual que al borrarla:
   * arrastrarlas de vuelta es un gesto, adivinar a cuál se pegan no.
   *
   * Con varias tomadas gira CADA UNA sobre su propio centro, no el grupo sobre
   * el centro del conjunto: así dos tabiques paralelos siguen paralelos y en su
   * sitio, que es lo que uno espera al girar «estas dos».
   */
  function girar() {
    if (!sel.length) return;
    marcar();
    for (const w of planta.paredes) {
      if (!sel.includes(w.id)) continue;
      const largo = w.b - w.a;
      const medio = (w.a + w.b) / 2;
      const eje = w.c;
      const rel = w.vanos.map((v) => ({ ...v, a: v.a - w.a, b: v.b - w.a }));
      w.o = w.o === 'v' ? 'h' : 'v';
      w.c = aPaso(medio);
      w.a = aPaso(eje - largo / 2);
      w.b = mm(w.a + largo);
      w.vanos = rel.map((v) => ({ ...v, a: mm(w.a + v.a), b: mm(w.a + v.b) }));
    }
    setMedicion(null);
    setTick((t) => t + 1);
  }

  /**
   * Por qué NO se pueden unir las paredes tomadas, o null si sí se puede.
   *
   * Se devuelve el motivo y no un booleano para que el botón apagado pueda
   * decir en su tooltip qué le falta: un icono gris sin explicación se lee como
   * «esto está roto».
   */
  function motivoUnir(): string | null {
    if (sel.length !== 2) return 'Unir trabaja con dos paredes a la vez.';
    const [p, q] = sel.map((i) => planta.paredes.find((w) => w.id === i));
    if (!p || !q) return 'Unir trabaja con dos paredes a la vez.';
    if (p.o !== q.o) return 'Esas dos paredes se cruzan: unir junta las que van en la misma línea.';
    if (Math.abs(p.c - q.c) > 0.1) return 'Esas dos paredes van paralelas pero no en la misma línea.';
    return null;
  }

  /**
   * Une dos paredes que van en la MISMA LÍNEA y deja una sola.
   *
   * El hueco que quedaba entre las dos no se rellena: se convierte en un VANO.
   * Es lo que arquitectónicamente es —dos tramos sobre la misma línea con un
   * paso en medio son una pared con una puerta— y hace que unir sea el reverso
   * exacto de partir y correr un trozo: el área vuelve a cerrar igual y el paso
   * sigue dibujado. Si se solapan no hay vano que inventar.
   *
   * El espesor que queda es el MAYOR de los dos: unir un tabique de 10 con una
   * mampostería de 15 no debe adelgazar la mampostería en silencio.
   */
  function unir() {
    const motivo = motivoUnir();
    if (motivo) { setAviso(motivo); return; }
    const [p, q] = sel.map((i) => planta.paredes.find((w) => w.id === i));
    if (!p || !q) return;
    marcar();
    const a = Math.min(p.a, q.a);
    const b = Math.max(p.b, q.b);
    // Los vanos de las dos paredes pasan a convivir en una sola lista, así que
    // los de la que se va se renumeran: los `id` son locales a su pared y dos
    // vanos con el mismo número dejan la selección apuntando a cualquiera.
    const juntos = [...p.vanos];
    for (const v of q.vanos) juntos.push({ ...v, id: idVano(juntos) });
    const corte = Math.min(p.b, q.b);
    const sigue = Math.max(p.a, q.a);
    if (sigue - corte > 0.05) {
      // El hueco entre las dos entra como PASO y no como puerta: es un vacío
      // que ya estaba en el dibujo, no una carpintería que alguien eligió.
      juntos.push({ id: idVano(juntos), a: corte, b: sigue, tipo: 'paso' });
    }
    p.a = a;
    p.b = b;
    p.e = Math.max(p.e, q.e);
    p.vanos = vanosEn(juntos, a, b);
    planta.paredes = planta.paredes.filter((w) => w.id !== q.id);
    // La pared absorbida desapareció: lo que colgaba de ella queda suelto donde
    // estaba, igual que al borrarla.
    reasentarAnclados(planta);
    setSel([p.id]);
    setMulti(false);
    setMedicion(null);
    setTick((t) => t + 1);
  }

  /** Por qué NO se puede intersecar lo tomado, o null si sí se puede. */
  function motivoInterseccion(): string | null {
    if (sel.length !== 2) return 'La intersección trabaja con dos paredes a la vez.';
    const [p, q] = sel.map((i) => planta.paredes.find((w) => w.id === i));
    if (!p || !q) return 'La intersección trabaja con dos paredes a la vez.';
    if (p.o === q.o) return 'Esas dos paredes van en la misma dirección: para juntarlas en una, usá unir.';
    return null;
  }

  /**
   * Cierra la esquina entre dos paredes que se cruzan: lleva la punta más
   * cercana de CADA UNA hasta el punto donde sus líneas se encuentran.
   *
   * Es la «Intersección» de Archicad —«conecta dos elementos en el punto más
   * cercano de sus extremos»— y es el gesto que arregla la esquina que quedó
   * abierta o pasada de largo. Las dos se mueven, que es lo que la distingue de
   * ajustar: acá no hay una pared que manda, el punto lo ponen entre las dos.
   *
   * El cruce es inmediato porque estas plantas son ortogonales: la vertical
   * aporta su eje `x` y la horizontal su eje `y`, y cada una estira o recorta
   * hasta el eje de la otra.
   */
  function interseccion() {
    const motivo = motivoInterseccion();
    if (motivo) { setAviso(motivo); return; }
    const [p, q] = sel.map((i) => planta.paredes.find((w) => w.id === i));
    if (!p || !q) return;
    marcar();
    // `llevarPunta` sólo toca `a`/`b`, nunca el eje: los dos ejes que forman el
    // cruce siguen valiendo lo mismo después de mover la primera punta.
    llevarPunta(p, q.c);
    llevarPunta(q, p.c);
    setMedicion(null);
    setTick((t) => t + 1);
  }

  /**
   * La pared que hace de REGLA: la última que se tocó.
   *
   * La selección múltiple va empujando al final, así que la última del arreglo
   * es la última que entró. Que la regla sea la última y no la primera es para
   * que el gesto se lea solo: se marcan las paredes que van a moverse y se
   * remata tocando aquella contra la que se alinean.
   */
  function regla(): Pared | null {
    if (sel.length < 2) return null;
    return planta.paredes.find((w) => w.id === sel[sel.length - 1]) ?? null;
  }

  /** Por qué NO se puede ajustar lo tomado, o null si sí se puede. */
  function motivoAjustar(): string | null {
    const r = regla();
    if (!r) return 'Ajustar necesita la regla y al menos una pared: tomá las paredes y la regla de último.';
    const otras = planta.paredes.filter((w) => sel.includes(w.id) && w.id !== r.id);
    if (!otras.some((w) => w.o !== r.o)) {
      return 'Esas paredes van paralelas a la regla: ajustar estira o recorta contra una línea que las cruce.';
    }
    return null;
  }

  /**
   * Estira o recorta las paredes tomadas hasta la ÚLTIMA que se tocó, que hace
   * de regla y no se mueve.
   *
   * Es el «Ajustar» de Archicad —«extiende o recorta los extremos de los
   * elementos seleccionados a un borde de elemento»— y es lo que ordena de un
   * tiro una fila de tabiques que quedaron todos un poco cortos o un poco
   * largos contra la misma fachada.
   *
   * Las que van paralelas a la regla se quedan quietas y se cuentan: una línea
   * paralela no tiene dónde cruzar, y mover esas sería inventar. Se avisa
   * cuántas se quedaron afuera en vez de dejar que parezca que no pasó nada.
   */
  function ajustar() {
    const motivo = motivoAjustar();
    if (motivo) { setAviso(motivo); return; }
    const r = regla();
    if (!r) return;
    marcar();
    let afuera = 0;
    for (const w of planta.paredes) {
      if (!sel.includes(w.id) || w.id === r.id) continue;
      if (w.o === r.o) { afuera++; continue; }
      llevarPunta(w, r.c);
    }
    setSel([r.id]);
    setMulti(false);
    setMedicion(null);
    setAviso(afuera
      ? `${afuera} ${afuera === 1 ? 'pared quedó' : 'paredes quedaron'} sin ajustar: van paralelas a la regla.`
      : null);
    setTick((t) => t + 1);
  }

  // ── paredes nuevas y paredes partidas ────────────────────────────────────
  /**
   * Arranca el trazo, o lo CIERRA si venía esperando el segundo apoyo.
   *
   * Los dos caminos conviven a propósito, y el que decide es el PUNTERO que
   * disparó el evento —`pointerType`—, no una suposición sobre la máquina. Daniel
   * midió la diferencia entre sus dos aparatos: «en la computadora no hay
   * problema y está perfecto» —ahí el mouse aprieta, arrastra y suelta— pero con
   * el dedo lo que pidió es otra cosa, y lo dijo paso por paso:
   *
   *   «el primer movimiento al darle a agregar paredes, lo dejo apretado para
   *   ver el punto y lo ubico donde quiero. Ya cuando tengo definido dónde
   *   quiero iniciar esa pared, suelto el tap y lo vuelvo a presionar para ahora
   *   sí definir el largo y la dirección».
   *
   * O sea: con el dedo, soltar NO crea nada nunca. El primer apoyo pasea el
   * punto de arranque todo lo que haga falta y al soltar lo deja clavado; el
   * segundo apoyo pasea la punta libre y al soltar nace la pared. Dos apoyos
   * siempre, por lejos que se haya corrido el dedo.
   *
   * Con el mouse se conserva el arrastre de una pasada: corrido más de
   * `ARRASTRE_PX`, la pared nace al soltar. Un clic suelto clava igual, así que
   * en el teclado también se puede hacer de dos tiempos si se prefiere.
   */
  function trazarDesde(e: React.PointerEvent) {
    // La varita es la única que NO alza el punto: no dibuja mira, así que
    // correrle el toque 44 px sería levantar un cuarto que el dedo nunca señaló.
    // Ahí la puntería no manda —el punto sólo dice en qué espacio estamos—.
    const p = enMetros(e, !esVarita(modo));
    if (!p) return;
    capturar(e);
    if (modo === 'dividir') { apuntar(p, true); return; }
    // La varita es UN toque y no hay nada que trazar: resuelve al apoyar, sin
    // imán —el punto solo dice en qué cuarto estamos, no dónde cae un borde—.
    if (modo === 'varita') { varitear(p); return; }
    if (modo === 'varita-pared') { varitearPared(p); return; }
    // Poner un objeto NO nace al apoyar: el apoyo para el fantasma ahí y lo
    // suelta `trazarFin`. Es lo que pidió Daniel el 11-oct-2026 —ver el objeto
    // en el plano ANTES de colocarlo—, y sólo funciona si entre el apoyo y el
    // levantar hay una pieza dibujada que se pueda corregir arrastrando.
    //
    // El punto va alzado como el resto: en un teléfono el dedo tapa justo lo
    // que hay que mirar, y el fantasma se dibuja arriba, a la vista.
    if (modo === 'objeto') { setFantasma(p); return; }
    const q = imanar(p);
    setMira(q);
    bajoRef.current = {
      x: e.clientX, y: e.clientY, corrido: false,
      dedo: e.pointerType !== 'mouse',
    };
    // Segundo apoyo de un trazo que quedó esperando: cierra ACÁ.
    if (trazo?.clavado) {
      const t2: Trazo = { ...trazo, x1: q.x, y1: q.y };
      setTrazo(modo === 'rect-pared' ? enRejilla(t2) : t2);
      return;
    }
    setTrazo({ x0: q.x, y0: q.y, x1: q.x, y1: q.y, clavado: false });
  }

  /**
   * Mueve la mira y estira el trazo.
   *
   * Corre aunque el puntero no esté apoyado: con mouse pasea sin apretar nada, y
   * es ahí donde la mira gana —se ve a qué se va a pegar ANTES de decidir—. Con
   * el dedo no hay paseo, pero una vez clavado el primer punto el segundo toque
   * muestra la línea viva mientras el dedo sigue apoyado.
   */
  function trazarHasta(e: React.PointerEvent) {
    const p = enMetros(e, !esVarita(modo));
    if (!p) return;
    if (bajoRef.current && !bajoRef.current.corrido) {
      const d = Math.hypot(e.clientX - bajoRef.current.x, e.clientY - bajoRef.current.y);
      if (d > ARRASTRE_PX) bajoRef.current.corrido = true;
    }
    if (modo === 'dividir') { apuntar(p, false); return; }
    if (esVarita(modo)) return;
    // El fantasma sigue al puntero. Con el mouse eso pasa SIN apretar —este
    // `pointermove` llega igual—, así que la pieza se pasea por el plano y uno
    // elige mirando; con el dedo empieza en el apoyo y se corrige arrastrando.
    if (modo === 'objeto') { setFantasma(p); return; }
    const q = imanar(p);
    setMira(q);
    if (!trazo) return;
    // Con el arranque todavía sin clavar, el puntero apoyado lo CORRE: así se
    // afina la puntería viendo el punto, que es justo lo que pidió Daniel.
    //
    // Esto es cosa del DEDO y SÓLO del dedo: el primer apoyo es siempre para
    // ubicar el arranque, se haya corrido 3 px o media pantalla.
    //
    // Con el mouse el arranque se clava donde se apretó y no se mueve más. Antes
    // también se corría mientras el recorrido no pasara los 12 px, y eso le comía
    // el arranque a toda pared dibujada de una pasada: el trazo nacía donde el
    // puntero cruzaba la raya, no donde se había apretado. Medido el 6-oct-2026
    // con el banco de gestos —2 m dibujados daban una pared de 1,75 m, los 12 px
    // redondeados al paso de 5 cm—. Con el mouse eso no hacía falta nunca: la mira
    // sigue al puntero SIN apretar, así que la puntería se afina antes de apoyar.
    if (!trazo.clavado && bajoRef.current?.dedo) {
      setTrazo({ x0: q.x, y0: q.y, x1: q.x, y1: q.y, clavado: false });
      return;
    }
    // La línea que divide se endereza EN VIVO, no al soltar: el imán tiene que
    // verse mientras se apunta, o uno sigue peleando con un ángulo que ya no va
    // a quedar.
    const t2: Trazo = { ...trazo, x1: q.x, y1: q.y };
    setTrazo(modo === 'area' || modo === 'cota' ? enderezar(t2)
             : modo === 'rect-pared' ? enRejilla(t2) : t2);
  }

  function trazarFin(e?: React.PointerEvent) {
    const t = trazo;
    const bajo = bajoRef.current;
    // Dividiendo no hay trazo que cerrar: el gesto son dos toques sobre la
    // pared y la marca tiene que SOBREVIVIR al primero —soltar no puede apagar
    // la guía del imán justo cuando hay que leerla, antes de cortar—. Acá se
    // reparten los dos: el primer toque firma la marca, el segundo la ejecuta.
    if (modo === 'dividir') {
      bajoRef.current = null;
      if (!corte) return;
      if (corte.firme) {
        const w = planta.paredes.find((q) => q.id === corte.pared);
        if (w) { partir(w, corte.v); return; }
      }
      setCorte({ ...corte, firme: true });
      return;
    }
    if (esVarita(modo)) { bajoRef.current = null; return; }
    // El objeto nace al LEVANTAR, donde quedó parado el fantasma. Entre el
    // apoyo y el levantar está toda la previsualización: se ve la pieza, se
    // corrige sin soltar, y recién el levantar la deja puesta.
    //
    // El punto sale del evento de LEVANTAR y el fantasma queda de respaldo: un
    // toque tan corto que no alcanzó a repintarse igual tiene que poner la
    // pieza, y ahí el estado todavía trae el punto del apoyo.
    if (modo === 'objeto') {
      bajoRef.current = null;
      const p = (e && enMetros(e, true)) || fantasma;
      if (p) ponerArmado(p);
      return;
    }
    // Con el dedo, soltar NUNCA cierra el trazo: el primer apoyo deja el
    // arranque clavado y recién el segundo da largo y dirección. El arrastre de
    // una pasada queda como privilegio del mouse, que es donde Daniel lo dio por
    // bueno y donde el puntero no tapa el punto que está poniendo.
    const arrastro = (bajo?.corrido ?? false) && !bajo?.dedo;
    bajoRef.current = null;
    if (!t) { setMira(null); return; }
    // Apoyo que no arrastró y sin arranque clavado: no cierra nada todavía. Deja
    // el punto puesto, visible, esperando el segundo apoyo.
    if (!arrastro && !t.clavado) {
      // Acá iba un aviso que narraba el paso siguiente («Arranque puesto. Apoyá
      // de nuevo y estirá…»). Se fue por pedido de Daniel del 19-oct-2026: esos
      // textos le tapan el plano y no los puede cerrar. El arranque clavado ya
      // se VE —la mira queda puesta en el punto—, así que el cartel repetía en
      // palabras lo que el dibujo dice solo.
      setTrazo({ ...t, clavado: true });
      return;
    }
    setTrazo(null);
    setMira(null);
    setAviso(null);
    if (modo === 'area') { dividirArea(t); return; }
    if (modo === 'cota') { crearCota(t); return; }
    // La caja de PAREDES se bifurca acá, antes de `crearLibre`: comparte el
    // gesto con el rectángulo de áreas pero no lo que nace de él.
    if (modo === 'rect-pared') { paredesDeCaja(t); return; }
    if (dibujaCaja(modo)) { crearLibre(t, undefined, modo === 'irregular'); return; }
    const id = planta.paredes.reduce((m, w) => Math.max(m, w.id), 0) + 1;
    const nueva = paredDelTrazo(t, espesorTipico(planta.paredes), id);
    if (!nueva) { setAviso(`Trazo muy corto: la pared mínima es de ${m2(MIN_LARGO)} m.`); return; }
    marcar();
    planta.paredes.push(nueva);
    setMedicion(null);
    setTick((t2) => t2 + 1);
    // SEGUIDAS: la pared nace y el gesto NO se apaga, con el arranque de la
    // próxima ya clavado en la punta donde terminó esta. Es el método que pidió
    // Daniel el 8-oct-2026 —«crear varias paredes de manera seguida»— y es el
    // camino de dibujar un cuarto: cuatro paredes en cuatro apoyos en vez de
    // ocho, sin volver a buscar la esquina anterior con el dedo.
    //
    // Y NO se toma la pared recién hecha: tomarla abre su paleta flotante, que
    // es justo encima de donde va a caer el apoyo siguiente. En «una» sí se
    // toma, porque ahí el gesto terminó y la paleta es lo que se quiere.
    if (metodoPared === 'seguidas') {
      // La punta final es la del trazo, pegada a la rejilla igual que nació la
      // pared: arrancar de `t.x1` crudo dejaría la cadena corrida medio paso.
      const fin = nueva.o === 'v'
        ? { x: nueva.c, y: t.y1 >= t.y0 ? nueva.b : nueva.a }
        : { x: t.x1 >= t.x0 ? nueva.b : nueva.a, y: nueva.c };
      setSel([]);
      setTrazo({ x0: fin.x, y0: fin.y, x1: fin.x, y1: fin.y, clavado: true });
      // Sin cartel: la pared recién puesta y la mira clavada en su punta ya
      // cuentan la historia. Ver la nota del otro aviso que se fue, más arriba.
      setMira({ ...fin, guias: [] });
      return;
    }
    setSel([nueva.id]);
    setModo(null);
    // Y la paleta se cierra sola, igual que la de áreas al crear un área: dejarla
    // abierta con la herramienta ya apagada deja el botón de la capa marcado como
    // «creando» cuando no está creando nada, y el toque siguiente sobre él —el de
    // alguien que quiere dibujar OTRA pared— lo que hace es cerrarlo.
    setHerrPared(false);
    setCorrHerrP(null);
  }

  /**
   * La varita de paredes: un toque sobre un área y nace una pared en cada lado.
   *
   * Lo pidió Daniel el 8-oct-2026 junto con los otros dos métodos: «una varita
   * mágica, que en este caso serviría más que todo para tema de áreas, que al
   * darle clic a la varita que reconozca los lados y generen pues las paredes».
   *
   * Es la varita de áreas al revés. Aquella mira paredes y levanta un área;
   * ésta mira un área y levanta las paredes. Sirve para el camino que hoy es
   * puro trabajo a mano: dibujar la terraza o la cochera como rectángulo —que
   * es rápido, son dos apoyos— y recién después cerrarla con sus muros.
   *
   * El eje de cada pared cae EN EL LADO del área, no por fuera ni por dentro, y
   * eso no es una comodidad: el editor mide a ejes, así que el área dibujada y
   * la que después midan esas paredes son el mismo número. Correrlas media
   * pared para afuera dejaría dos medidas distintas del mismo cuarto.
   *
   * Los lados DIAGONALES se saltan y se avisan: una pared del editor es `o`
   * horizontal o vertical, no hay manera de representar una inclinada. Un
   * polígono irregular queda entonces a medio cerrar, y el aviso dice cuántos
   * lados faltaron para que no parezca que la varita falló en silencio.
   */
  /**
   * Una pared por cada lado de un contorno cerrado.
   *
   * Es el corazón que comparten los dos métodos que levantan varias paredes de
   * un saque: la varita —que saca el contorno de un área ya dibujada— y el
   * rectángulo —que lo saca de la caja que uno acaba de estirar—. Está aparte
   * justo para que sean el mismo código: si mañana cambia cómo nace una pared
   * de un lado, cambia para los dos.
   *
   * Devuelve además lo que NO pudo levantar, que es lo que después se avisa:
   * los lados diagonales (una `Pared` es horizontal o vertical, no hay manera
   * de representar una inclinada) y los de largo casi cero (normales en un
   * polígono ortogonal: partir un lado deja un lado de largo cero entre las dos
   * mitades; no es un error, es un lado que no existe).
   */
  function paredesDelAnillo(vs: Array<[number, number]>) {
    const e = espesorTipico(planta.paredes);
    let id = planta.paredes.reduce((m, w) => Math.max(m, w.id), 0);
    const nuevas: Pared[] = [];
    let diagonales = 0;
    let cortos = 0;
    for (let i = 0; i < vs.length; i++) {
      const [ax, ay] = vs[i];
      const [bx, by] = vs[(i + 1) % vs.length];
      if (!orientaLado(vs, i)) { diagonales++; continue; }
      const w = paredDelTrazo({ x0: ax, y0: ay, x1: bx, y1: by, clavado: false }, e, id + 1);
      if (!w) { cortos++; continue; }
      id++;
      nuevas.push(w);
    }
    return { nuevas, diagonales, cortos };
  }

  /**
   * El rectángulo de paredes: dos apoyos y nacen los cuatro muros.
   *
   * El cuarto método, pedido por Daniel el 8-oct-2026: «se me olvidó mencionar
   * que debería haber otra opción más de modelar paredes, que sería por medio
   * de rectángulo; que esté pues esa función que va a ser muy, muy utilizada
   * también». Y tiene razón en que lo va a ser: un cuarto es el 90 % de lo que
   * se dibuja, y hasta hoy costaba cuatro trazos —ocho apoyos— o uno con
   * «seguidas» más acordarse de cerrar.
   *
   * El gesto es EL MISMO que el del rectángulo de áreas, de esquina a esquina,
   * y a propósito: es el que Daniel ya tiene en el dedo. Lo único distinto es
   * qué nace al soltar.
   *
   * No se apaga al terminar y no toma lo que creó, igual que la varita: lo
   * normal es dibujar un cuarto y seguir con el de al lado, y tomar las paredes
   * abriría la paleta flotante justo encima de donde va a caer el apoyo
   * siguiente.
   */
  function paredesDeCaja(t: Trazo) {
    const x0 = Math.min(t.x0, t.x1);
    const x1 = Math.max(t.x0, t.x1);
    const y0 = Math.min(t.y0, t.y1);
    const y1 = Math.max(t.y0, t.y1);
    if (x1 - x0 < MIN_LARGO || y1 - y0 < MIN_LARGO) {
      setAviso(`Caja muy chica: cada lado tiene que medir al menos ${m2(MIN_LARGO)} m.`);
      return;
    }
    // En sentido horario desde la esquina de arriba a la izquierda. El sentido
    // da igual para el resultado —son cuatro lados ortogonales— pero `orientaLado`
    // lee el polígono entero y conviene darle uno consistente.
    const { nuevas } = paredesDelAnillo([[x0, y0], [x1, y0], [x1, y1], [x0, y1]]);
    if (nuevas.length < 4) {
      setAviso('No pude levantar las cuatro paredes de esa caja.');
      return;
    }
    marcar();
    planta.paredes.push(...nuevas);
    setSel([]);
    setMedicion(null);
    setTick((k) => k + 1);
    setAviso(`Cuarto de ${m2(x1 - x0)} × ${m2(y1 - y0)} m levantado con sus cuatro paredes.`);
  }

  function varitearPared(p: { x: number; y: number }) {
    const l = (planta.libres ?? []).find((q) => enCeldas(cuerpoLibre(q), p.x, p.y));
    if (!l) {
      setAviso('Ahí no hay un área. La varita de paredes levanta los muros de un área ya dibujada: tocá adentro de una.');
      return;
    }
    // `poliDe` no sabe de la forma de la varita —no es un polígono simple— así
    // que ahí se le pide el contorno real a `anilloDeCeldas`, el mismo que usa
    // el editor para convertir esa área a polígono.
    const vs = poliDe(l) ?? (l.celdas?.length ? anilloDeCeldas(l.celdas) : null);
    if (!vs || vs.length < 3) {
      setAviso(`No pude leer el contorno de «${l.nombre}».`);
      return;
    }
    const { nuevas, diagonales, cortos } = paredesDelAnillo(vs);
    if (!nuevas.length) {
      setAviso(`«${l.nombre}» no tiene ningún lado recto al que ponerle pared.`);
      return;
    }
    marcar();
    planta.paredes.push(...nuevas);
    // La varita NO se apaga al levantar unas paredes, igual que la de áreas: lo
    // normal es cerrar la terraza y seguir con la cochera. Y no toma lo que
    // creó —sería abrir la paleta flotante justo encima del área siguiente—; lo
    // que se creó lo canta el aviso.
    setSel([]);
    setMedicion(null);
    setTick((t) => t + 1);
    setAviso(
      `${nuevas.length === 1 ? '1 pared levantada' : `${nuevas.length} paredes levantadas`}`
      + ` sobre los lados de «${l.nombre}».`
      + (diagonales
        ? ` ⚠️ ${diagonales === 1 ? 'Un lado quedó' : `${diagonales} lados quedaron`} sin pared por ir en diagonal: el editor sólo tiene paredes rectas.`
        : '')
      + (cortos ? ` (${cortos} ${cortos === 1 ? 'lado' : 'lados'} de largo casi cero, saltados.)` : ''),
    );
  }

  /**
   * La pared cuyo cuerpo está más cerca del punto, o null si no hay ninguna.
   *
   * Si hay una pared tomada, sólo cuenta esa: la tijera sale de SU paleta, así
   * que el corte tiene que caer en ella y no en la vecina que quedó un
   * centímetro más cerca del dedo.
   */
  function paredEn(p: { x: number; y: number }): Pared | null {
    const tomada = uno == null ? null : planta.paredes.find((w) => w.id === uno);
    let mejor: Pared | null = null;
    // El alcance iba clavado en 0,35 m y por eso en el celular el editor
    // contestaba «tocá encima de la pared» con el dedo encima: a plano entero
    // en una pantalla de teléfono 0,35 m son 21 px, menos de la mitad de lo que
    // mide la yema. Va en píxeles, como el imán, y el piso en metros queda por
    // si alguien acerca tanto que 44 px no alcanzan ni para el espesor.
    let dist = Math.max(0.35, enMetrosPx(DEDO_PX));
    for (const w of tomada ? [tomada] : planta.paredes) {
      const k = caja(w);
      const dx = Math.max(k[0] - p.x, 0, p.x - k[2]);
      const dy = Math.max(k[1] - p.y, 0, p.y - k[3]);
      const d = Math.hypot(dx, dy);
      if (d < dist) { dist = d; mejor = w; }
    }
    return mejor;
  }

  /**
   * Dónde caería el corte de un punto sobre una pared: su coordenada a lo largo,
   * ya pasada por el IMÁN y respetando el mínimo de las dos puntas.
   *
   * El imán acá es el pedido de Daniel para partir, y el caso que lo justifica es
   * concreto: cortar una fachada EXACTAMENTE donde muere el tabique que la toca.
   * A pulso no sale —la rejilla de 5 cm cae al lado del eje real y queda un
   * escalón de centímetros que después hay que corregir a mano—.
   *
   * Se consulta el MISMO juego de nodos que `imanar`, pero del eje A LO LARGO de
   * la pared: para una pared vertical, los ejes de las horizontales y las puntas
   * de las otras verticales. Eso es precisamente donde uno quiere cortar.
   *
   * La propia pared se excluye: sus nodos son sus dos extremos, y pegarse ahí es
   * pedir un trozo de largo cero que el mínimo rechaza igual.
   *
   * `pegado` devuelve el nodo al que se pegó, o null si el valor salió de la
   * rejilla — es lo que permite dibujar la guía. Si el mínimo tuvo que correr el
   * valor, ya no está pegado a nada y lo dice, en vez de mostrar una guía que
   * miente.
   */
  function corteEn(
    w: Pared,
    p: { x: number; y: number },
  ): { v: number; pegado: number | null } {
    const eje = w.o === 'v' ? 'y' : 'x';
    const crudo = w.o === 'v' ? p.y : p.x;
    const iman = nodoCerca(crudo, nodos(planta.paredes, eje, [w.id]), alcanceIman());
    const v = Math.min(
      Math.max(iman ?? aPaso(crudo), w.a + MIN_LARGO),
      w.b - MIN_LARGO,
    );
    return { v, pegado: iman != null && v === iman ? iman : null };
  }

  /**
   * Apunta el corte de una pared, y corta con el SEGUNDO toque.
   *
   * El doble toque lo pidió Daniel con todas las letras —«que el gesto sea darle
   * doble tap, una para definir el punto y el segundo ya para cortar»— y resuelve
   * un problema real del gesto anterior, que partía en el primer toque: en el
   * celular el dedo tapa justo el punto donde va a caer el corte, así que no
   * había forma de ver dónde se estaba cortando hasta que ya estaba cortado.
   *
   * Los DOS toques van encima de la pared, que es el camino natural: el primero
   * pone la marca —y el dedo apoyado la corre para afinar— y el segundo corta
   * ahí. Cortar tocando FUERA de la pared es un atajo que se conserva, no el
   * único camino: exigirlo obligaba a hacer zoom out para encontrar vacío donde
   * tocar, y eso Daniel lo cazó en el celular el 6-oct-2026.
   *
   * Quién corta y quién apunta lo dice el ESTADO de la marca, no la distancia:
   *
   * - sin marca firme en esa pared → apunta (la marca se firma al soltar);
   * - con marca firme en esa pared → el toque vuelve a apuntar mientras está
   *   apoyado y CORTA al soltar, así que tocar el mismo punto corta ahí mismo y
   *   tocar un poco más allá corrige sin tener que deshacer;
   * - fuera de la pared, con la marca puesta → CORTA (el atajo).
   *
   * Cambiar de pared borra la firma: una pared recién tocada siempre arranca
   * apuntando, nunca cortando de una.
   *
   * La versión más vieja medía la distancia del segundo toque a la marca y por
   * eso era indistinguible en la mano: tocar «cerca» cortaba y tocar un poco más
   * lejos volvía a apuntar, sin nada en pantalla que dijera cuál de las dos iba
   * a pasar. El estado de la marca sí se ve: está dibujada.
   */
  function apuntar(p: { x: number; y: number }, toque: boolean) {
    const w = paredEn(p);
    if (!w) {
      // Fuera de la pared no hay nada a qué pegarse: la guía se apaga para no
      // dejar una línea viva que ya no corresponde a ningún corte.
      setMira(null);
      // Toque fuera de la pared: si ya hay marca, ese es el corte.
      if (toque && corte?.firme) {
        const marcada = planta.paredes.find((q) => q.id === corte.pared);
        if (marcada) { partir(marcada, corte.v); return; }
      }
      if (toque) setAviso('Tocá encima de la pared, donde la querés partir.');
      return;
    }
    if (w.b - w.a < 2 * MIN_LARGO) {
      if (toque) setAviso(`Esa pared es muy corta para partirla (mínimo ${m2(2 * MIN_LARGO)} m).`);
      return;
    }
    // Apoyado se apunta —nunca se corta—, y el dedo corre la marca: así se
    // afina la puntería sin levantar la mano. El corte lo hace `trazarFin` al
    // soltar, y sólo si la marca ya venía firmada por un toque anterior.
    const yaFirme = corte?.pared === w.id && corte.firme;
    const { v, pegado } = corteEn(w, p);
    setCorte({ pared: w.id, v, firme: yaFirme });
    // La guía sólo aparece cuando el imán AGARRÓ, y su cruz cae sobre la marca
    // del corte: así se ve a qué pared se pegó ANTES de cortar, que es la misma
    // razón por la que existe la mira al crear.
    setMira(pegado == null ? null : {
      x: w.o === 'v' ? w.c : v,
      y: w.o === 'v' ? v : w.c,
      guias: [{ eje: w.o === 'v' ? 'y' : 'x', v: pegado }],
    });
    if (toque && !yaFirme) {
      setAviso(pegado == null
        ? 'Marcado. Tocá otra vez para cortar ahí.'
        : 'Marcado sobre la pared vecina. Tocá otra vez para cortar ahí.');
    }
  }

  /**
   * Parte la pared EN EL PUNTO APUNTADO, no por la mitad. Dividir sirve para
   * mover sólo un trozo —correr medio tabique, abrir un paso—, y para eso el
   * corte tiene que caer donde el dedo dice.
   */
  function partir(w: Pared, v: number) {
    marcar();
    const id = planta.paredes.reduce((m, q) => Math.max(m, q.id), 0) + 1;
    const resto: Pared = { ...w, id, a: v, b: w.b, vanos: vanosEn(w.vanos, v, w.b) };
    w.b = v;
    w.vanos = vanosEn(w.vanos, w.a, v);
    planta.paredes.push(resto);
    setSel([id]);
    setCorte(null);
    setMira(null);
    setModo(null);
    setMedicion(null);
    // El aviso de «marcado» no se apaga solo y quedaba vivo DESPUÉS del corte,
    // pidiendo un segundo toque que ya no corresponde a nada.
    setAviso(null);
    setTick((t) => t + 1);
  }

  // ── áreas partidas sin pared ─────────────────────────────────────────────
  /**
   * Parte el área tomada en dos con una línea que NO es pared.
   *
   * Es el pedido de Daniel —«que las áreas se puedan dividir donde uno desee […]
   * aunque sea dentro de las mismas paredes»— y existe porque un cuarto y su
   * nombre no siempre coinciden con lo que las paredes encierran: la planta baja
   * de casi todos los modelos es un solo espacio continuo, y «Sala» y «Comedor»
   * son dos cuartos para quien vende aunque sean uno para la geometría.
   *
   * No mete un tabique falso, que sería mentir sobre la casa, ni le quita área a
   * nadie: la línea frena la inundación por la arista (ver `medirPlanta`), así
   * que las dos mitades suman EXACTO lo que sumaba el área entera.
   *
   * Se tantea sobre una copia antes de tocar nada. Una línea que no llega a
   * cerrar contra las paredes no parte nada —la inundación se escapa por la
   * punta—, y sin el tanteo eso dejaba un divisor invisible, sin efecto, y un
   * paso de deshacer que no deshace nada visible.
   */
  function dividirArea(trazado: Trazo) {
    setModo(null);
    const madre = region;
    if (!madre || !medicion) return;
    // Se vuelve a enderezar acá y no sólo al dibujar: con el mouse el trazo se
    // cierra de una pasada, sin pasar por el camino que lo endereza en vivo.
    const t = enderezar(trazado);
    if (Math.hypot(t.x1 - t.x0, t.y1 - t.y0) < MIN_LARGO) {
      setAviso(`La línea quedó muy corta: el mínimo es ${m2(MIN_LARGO)} m.`);
      return;
    }
    const id = (planta.divisores ?? []).reduce((m, d) => Math.max(m, d.id), 0) + 1;
    const linea: Divisor = { id, x1: t.x0, y1: t.y0, x2: t.x1, y2: t.y1 };

    const tanteo: Planta = JSON.parse(JSON.stringify(planta));
    tanteo.divisores = [...(tanteo.divisores ?? []), linea];
    const med = medirSegunVista(tanteo, vistaObraRef.current);
    const hijas = med.regiones.filter((g) => enCeldas(madre.celdas, g.x, g.y));
    if (hijas.length < 2) {
      setAviso('La línea no partió el área: tiene que cruzarla entera, de pared a pared.');
      return;
    }

    marcar();
    planta.divisores = [...(planta.divisores ?? []), linea];
    planta.reposo = repartirReposo(medicion.regiones, planta.reposo, med);
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setRecintos([hijas[0].nombre]);
    setTick((k) => k + 1);
  }

  /** Quita una división: las dos mitades vuelven a ser un área, y su reposo
   *  vuelve a sumarse en una (ver `repartirReposo`). */
  function borrarDivisor() {
    if (divSel == null) return;
    marcar();
    planta.divisores = (planta.divisores ?? []).filter((d) => d.id !== divSel);
    if (medicion) {
      const med = medirSegunVista(planta, vistaObraRef.current);
      planta.reposo = repartirReposo(medicion.regiones, planta.reposo, med);
      setMedicion(medirSegunVista(planta, vistaObraRef.current));
    }
    setDivSel(null);
    setCorrimiento(null);
    setTick((t) => t + 1);
  }

  // ── cotas puestas a mano ─────────────────────────────────────────────────
  /**
   * Pone una cota del trazo: la otra mitad del pedido de acotación.
   *
   * Las automáticas cubren las paredes; esto cubre lo que ninguna pared mide
   * —el ancho de un vano, la distancia entre el clóset y la ventana, el frente
   * de la cochera—. Es textual: «que esté la función, pues de agregar nuevas
   * cotas, nuevas dimensiones».
   *
   * Se endereza como la línea divisoria, por el mismo motivo: en estas casas
   * todo es ortogonal y una cota a 2° de la horizontal mide un número que no es
   * el que se quería leer.
   *
   * Y queda GUARDADA en el modelo —al revés de las de pared— porque no se deriva
   * de nada: nadie la puede recalcular, así que si no se guarda, se pierde.
   */
  function crearCota(trazado: Trazo) {
    setModo(null);
    const t = enderezar(trazado);
    const largo = Math.hypot(t.x1 - t.x0, t.y1 - t.y0);
    if (largo < MIN_COTA) {
      setAviso(`La cota quedó muy corta: el mínimo es ${m2(MIN_COTA)} m.`);
      return;
    }
    marcar();
    const id = (planta.cotas ?? []).reduce((m, k) => Math.max(m, k.id), 0) + 1;
    planta.cotas = [...(planta.cotas ?? []), {
      id, x1: t.x0, y1: t.y0, x2: t.x1, y2: t.y1,
    }];
    // La capa se prende: poner una cota y que no se vea lee como que no pasó
    // nada, y el ojo de cotas nace apagado.
    setVer((v) => ({ ...v, cota: true }));
    setCotaSel(id);
    setTick((k) => k + 1);
  }

  /** Quita una cota puesta a mano. Las automáticas no pasan por acá: no existen
   *  en el modelo, se derivan de las paredes en cada repintado. */
  function borrarCota() {
    if (cotaSel == null) return;
    marcar();
    planta.cotas = (planta.cotas ?? []).filter((k) => k.id !== cotaSel);
    setCotaSel(null);
    setCorrimiento(null);
    setTick((t) => t + 1);
  }

  /**
   * Levanta un área que las paredes no encierran, del rectángulo trazado.
   *
   * No pasa por `medirPlanta`: no hay nada que inundar ni nada que anclar, su
   * área es la del rectángulo y punto. Nace con nombre genérico porque el
   * nombre se escribe en su paleta, igual que el de un recinto.
   *
   * `celdas` llega sólo desde la varita y es la forma REAL del espacio. Cuando
   * viene, el rectángulo del trazo pasa a ser su caja y el área la dan las
   * celdas (ver `Libre.celdas`).
   */
  function crearLibre(t: Trazo, celdas?: Rect[], irregular?: boolean) {
    setModo(null);
    const x = Math.min(t.x0, t.x1), y = Math.min(t.y0, t.y1);
    const w = Math.abs(t.x1 - t.x0), h = Math.abs(t.y1 - t.y0);
    // El mínimo se le exige al rectángulo dibujado a mano, no a la forma: un
    // espacio real puede tener un brazo de 40 cm y seguir siendo el cuarto.
    if (!celdas && (w < MIN_LARGO || h < MIN_LARGO)) {
      setAviso(`El área quedó muy chica: cada lado tiene que pasar de ${m2(MIN_LARGO)} m.`);
      return;
    }
    marcar();
    const id = (planta.libres ?? []).reduce((m, l) => Math.max(m, l.id), 0) + 1;
    // El polígono irregular NACE de la caja que se acaba de dibujar: sus cuatro
    // esquinas son los primeros cuatro vértices y de ahí en adelante cada uno se
    // arrastra suelto. Es el mismo anillo que armaba `cambiarMetodo` al
    // convertir un rectángulo, sólo que acá se arma al crear —que es donde
    // Daniel puso el método— y la caja queda igual, así que no hay que
    // recalcularla.
    const vertices: Array<[number, number]> | undefined = irregular
      ? [[x, y], [x + w, y], [x + w, y + h], [x, y + h]]
      : undefined;
    planta.libres = [...(planta.libres ?? []), {
      id, nombre: `Área ${id}`, x, y, w, h, celdas,
      vertices, anguloLibre: irregular ? true : undefined,
    }];
    // El área ya está, así que la pantalla queda con UNA sola paleta: la del
    // área nueva, que es donde se le pone el nombre. Se suelta todo lo que
    // estuviera tomado —un cuarto medido, una pared, una división— porque si no
    // su paleta queda montada en el mismo ancla y se ven las dos encimadas, que
    // es lo que reportó Daniel (4-oct-2026).
    soltarTodo();
    setHerramientas(false);
    setCorrHerr(null);
    setLibreSel(id);
    // Y se enciende el ojo: desde que las áreas dibujadas se ocultan con él,
    // crear una con el ojo apagado la dejaba invisible al nacer.
    encenderAreas();
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * La varita: un toque adentro de un espacio y el área sale sola.
   *
   * Existe por los cuartos que la inundación NO ve. La inundación necesita el
   * recinto CERRADO, y los que importan para el precio —cochera sin frente,
   * terraza bajo el alero, pilas— están abiertos de un lado a propósito: la
   * planta baja de Salamanca mide 0 regiones y 8 rótulos sueltos. Ahí la varita
   * tira un rayo a cada lado desde el punto tocado, se topa con el EJE de la
   * primera pared que encuentra (el mismo criterio que todo lo demás desde que
   * medimos a ejes, así el número es comparable con el de un cuarto cerrado) y
   * cuando de un lado no hay nada cae a la envolvente de la planta.
   *
   * Pero si el dedo cayó adentro de una región que la inundación YA midió, la
   * varita se aparta y la muestra: lo cerrado ya tiene dueño y duplicarlo sería
   * contar la casa dos veces.
   *
   * Y las áreas YA DIBUJADAS frenan la inundación igual que una pared: sus
   * bordes son la línea divisoria. Sin eso, levantar el pedazo que sobra al lado
   * de una terraza devolvía el espacio entero entre paredes y la terraza se
   * contaba dos veces.
   *
   * ⚠️ Lo que entrega es una FORMA, no un rectángulo. Ver `zonaEncerrada` para
   * por qué: el rectángulo de los cuatro rayos se tragaba los cuartos vecinos
   * cuyo tabique no tapaba el punto tocado —20,01 m² con el W.C., el S.S. y la
   * escalera adentro— y eso es lo que Daniel marcó como «definitivamente no es
   * correcto» el 4-oct-2026.
   */
  function varitear(p: { x: number; y: number }) {
    const med = medicion ?? medirSegunVista(planta, vistaObraRef.current);
    if (!medicion) setMedicion(med);
    const dentro = med.regiones.find((g) => enCeldas(g.celdas, p.x, p.y));
    if (dentro) {
      setModo(null);
      setAviso(
        `Ahí ya hay un área medida: «${dentro.nombre}», ${m2(dentro.area)} m². `
        + 'La varita es para los espacios que las paredes no cierran.',
      );
      return;
    }
    // EL LOTE NO CUENTA PARA LA VARITA, y no es una excepción cómoda: el lote
    // contiene la casa entera. Como «ahí ya hay un área» contestaría eso en
    // cualquier punto de la planta, y como barrera de `zonaEncerrada` —que
    // devuelve null cuando el punto cae dentro de un bloqueo— contestaría «no
    // pude cerrar un espacio» en cualquier punto. O sea, con un lote puesto la
    // varita dejaría de existir.
    //
    // La regla va contra `clase === 'lote'` y NO contra `fijo`: bloquear un área
    // es que no se mueva de un toque, no que regale su espacio. Un cuarto
    // bloqueado sigue frenando la varita, como debe.
    //
    // Y no hay doble conteo: el lote suma en su propia columna y nunca en la
    // interna ni en la externa, así que un cuarto levantado adentro del lote no
    // cuenta dos veces nada.
    const libres = (planta.libres ?? []).filter((l) => l.clase !== 'lote');
    // Se pregunta por el cuerpo y no por la caja —ver `cuerpoLibre`—, y así la
    // pregunta es la misma para un rectángulo, para la forma de la varita y para
    // un polígono de vértices.
    const encima = libres.find((l) => enCeldas(cuerpoLibre(l), p.x, p.y));
    if (encima) {
      setModo(null);
      setAviso(
        `Ahí ya hay un área dibujada: «${encima.nombre}», ${m2(areaLibre(encima))} m². `
        + 'Tocá del otro lado de su borde para levantar lo que queda.',
      );
      return;
    }
    // Las divisiones de uso entran como barrera igual que las paredes: una línea
    // que parte «Sala» de «Comedor» también parte lo que la varita levanta, o la
    // mitad que no se tocó vendría de regalo.
    const hallado = zonaEncerrada(
      planta.paredes, p,
      libres.flatMap(cuerpoLibre),
      planta.divisores ?? [],
    );
    if (!hallado) {
      setAviso('No pude cerrar un espacio ahí. Probá tocando más adentro del cuarto, no sobre una pared.');
      return;
    }
    if (hallado.area < MINIMA_LIBRE) {
      setAviso(`El espacio quedó muy chico: ${m2(hallado.area)} m². Probá tocando más adentro del cuarto.`);
      return;
    }
    const [x, y, w, h] = cajaDe(hallado.celdas);
    // La forma se guarda SÓLO cuando no es un rectángulo. Casi todos los
    // espacios lo son, y guardarle celdas a un rectángulo le quitaría los
    // agarres y los dos campos de medida sin ganar nada.
    const rectangular = Math.abs(hallado.area - w * h) < 0.005;
    crearLibre(
      { x0: x, y0: y, x1: x + w, y1: y + h, clavado: false },
      rectangular ? undefined : hallado.celdas,
    );
    // Los lados sin pared se avisan: ahí el borde lo puso la envolvente de la
    // planta, no una pared, y el número hay que mirarlo con el plano al lado.
    setAviso(
      `Área de ${m2(hallado.area)} m²`
      + (rectangular ? ` (${m2(w)} × ${m2(h)}).` : `, con la forma del espacio (caja de ${m2(w)} × ${m2(h)}).`)
      + (hallado.abiertos
        ? ` ⚠️ ${hallado.abiertos === 1 ? 'Un lado' : `${hallado.abiertos} lados`} sin pared: ahí llegué hasta el borde de la planta. Corregí la medida si no era eso.`
        : '')
      + ' Nace como externa; mandala para adentro desde la tabla si suma.',
    );
  }

  function renombrarLibre(nombre: string) {
    const l = (planta.libres ?? []).find((q) => q.id === libreSel);
    if (!l) return;
    marcar();
    l.nombre = nombre.trim() || l.nombre;
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * Aplica una medida escrita en la paleta del área.
   *
   * La esquina de arriba a la izquierda queda CLAVADA y crece el lado opuesto,
   * igual que el largo de una pared mueve `b` y no `a`: así escribir un número
   * no corre el área de lugar, que es lo último que uno espera al corregir una
   * medida.
   */
  function medidaLibre(cual: 'w' | 'h', v: string) {
    // Un campo vacío no es un cero: es un campo vacío.
    if (!v.trim()) return;
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n) || n < MIN_LARGO) {
      setAviso(`Cada lado del área tiene que pasar de ${m2(MIN_LARGO)} m.`);
      return;
    }
    const l = (planta.libres ?? []).find((q) => q.id === libreSel);
    // Con forma no hay medida que aplicar —ni la de la varita (`celdas`) ni el
    // polígono de vértices—: la paleta no muestra los campos, y si llegara acá
    // igual, escribir un ancho sobre una L le estiraría la caja y dejaría el
    // dibujo diciendo otra cosa que el número.
    if (!l || l.celdas?.length || l.vertices?.length || mm(n) === l[cual]) return;
    marcar();
    l[cual] = mm(n);
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * Manda una región cerrada para adentro o para afuera del área interna.
   *
   * Se guarda por NOMBRE en `planta.clases`, no por índice: los índices los
   * reparte la inundación y renumeran al primer movimiento de pared, así que una
   * cochera marcada como externa volvería a contar adentro sola.
   *
   * Pasa por `marcar()` porque es una decisión que vale plata —cambia el lado en
   * que suma un cuarto entero— y tiene que poder deshacerse con ↶ como todo lo
   * demás.
   */
  function clasificarRegion(nombre: string, clase: Clase) {
    if ((planta.clases?.[nombre] ?? 'interna') === clase) return;
    marcar();
    planta.clases = { ...(planta.clases ?? {}), [nombre]: clase };
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * Marca el estado de rehabilitación de una PARED.
   *
   * Hermano exacto de `clasificarLibre` y con el mismo molde a propósito: son
   * dos ejes del mismo objeto. Lo que cambia es el peso —acá la medición se
   * rehace de verdad, porque una pared que la vista no muestra deja de frenar la
   * inundación— y por eso el `setMedicion` va sin el `if (medicion)`: marcar un
   * muro puede fundir dos cuartos, y eso hay que verlo.
   */
  function ponerEstadoPared(id: number, estado: Estado) {
    const w = planta.paredes.find((q) => q.id === id);
    if (!w || (w.estado ?? 'existente') === estado) return;
    marcar();
    w.estado = estado;
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * La pared con los vanos que la vista deja ver.
   *
   * Hermana de `plantaSegunVista`: la medición filtra allá y el dibujo acá, con
   * la misma regla. Devuelve la MISMA pared cuando no hay nada que sacar, así
   * una planta sin vanos marcados no genera un objeto por repintado.
   */
  function vistaDePared(w: Pared): Pared {
    const vs = w.vanos.filter((v) => seVe(v.estado, vistaObra));
    return vs.length === w.vanos.length ? w : { ...w, vanos: vs };
  }

  /** Marca el estado de un VANO. Es dibujo puro: ningún área se mueve. */
  function ponerEstadoVano(idPared: number, idVano: number, estado: Estado) {
    const w = planta.paredes.find((q) => q.id === idPared);
    const v = w?.vanos.find((q) => q.id === idVano);
    if (!v || (v.estado ?? 'existente') === estado) return;
    marcar();
    v.estado = estado;
    setTick((k) => k + 1);
  }

  /** Lo mismo para un área dibujada a mano. */
  function ponerEstadoLibre(id: number, estado: Estado) {
    const l = (planta.libres ?? []).find((q) => q.id === id);
    if (!l || (l.estado ?? 'existente') === estado) return;
    marcar();
    l.estado = estado;
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * El divisor va con `setMedicion` incondicional, como la pared y a diferencia
   * del área libre: quitarlo funde los dos cuartos que separaba, así que la
   * tabla cambia aunque todavía no estuviera abierta.
   */
  function ponerEstadoDivisor(id: number, estado: Estado) {
    const d = (planta.divisores ?? []).find((q) => q.id === id);
    if (!d || (d.estado ?? 'existente') === estado) return;
    marcar();
    d.estado = estado;
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * Y para una REGIÓN, que va por nombre porque no es un objeto: ver
   * `Planta.estados`. Mismo mecanismo que `clasificarRegion`.
   */
  function ponerEstadoRegion(nombre: string, estado: Estado) {
    if ((planta.estados?.[nombre] ?? 'existente') === estado) return;
    marcar();
    planta.estados = { ...(planta.estados ?? {}), [nombre]: estado };
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /** Y para un mueble, que es el único donde el estado no mueve ningún número. */
  function ponerEstadoMueble(id: number, estado: Estado) {
    const m = (planta.muebles ?? []).find((q) => q.id === id);
    if (!m || (m.estado ?? 'existente') === estado) return;
    marcar();
    m.estado = estado;
    setTick((k) => k + 1);
  }

  /**
   * Las partes que hoy componen la región que se muestra con ese nombre.
   *
   * Una región sin unir es una sola parte: ella misma. Una ya unida no está en
   * `grupos` —su nombre ES el del grupo—, así que sus partes son las llaves que
   * apuntan a él.
   */
  function partesDe(nombre: string): string[] {
    const partes = Object.entries(planta.grupos ?? {})
      .filter(([, g]) => g === nombre)
      .map(([k]) => k);
    return partes.length ? partes : [nombre];
  }

  /**
   * Las regiones que TOCAN a la tomada: las únicas con las que tiene sentido
   * unirse.
   *
   * Se ofrece sólo lo pegado porque unir dos cuartos de puntas opuestas de la
   * casa no describe nada —sería un número sin forma— y porque la lista corta es
   * la que se puede tocar con el dedo. Que se detecte funciona gracias a la
   * medición a ejes: dos regiones vecinas terminan EXACTAMENTE en el eje de la
   * pared que las separa, así que sus celdas se tocan sin huelgo.
   */
  function vecinasDe(g: Medicion['regiones'][number]): string[] {
    // 1 cm de tolerancia. No se toma de la retícula de `medirPlanta` —que es
    // privada suya— a propósito: acá lo que se pregunta es si dos bordes son el
    // MISMO borde, y un centímetro es la holgura que eso admite en una planta.
    const t = 0.01;
    const toca = (a: Rect, b: Rect) => {
      const [ax, ay, aw, ah] = a, [bx, by, bw, bh] = b;
      const xSolapa = ax < bx + bw - t && bx < ax + aw - t;
      const ySolapa = ay < by + bh - t && by < ay + ah - t;
      const xPega = Math.abs(ax + aw - bx) < t || Math.abs(bx + bw - ax) < t;
      const yPega = Math.abs(ay + ah - by) < t || Math.abs(by + bh - ay) < t;
      return (ySolapa && xPega) || (xSolapa && yPega);
    };
    return (medicion?.regiones ?? [])
      .filter((o) => o.nombre !== g.nombre
        && o.celdas.some((cb) => g.celdas.some((ca) => toca(ca, cb))))
      .map((o) => o.nombre);
  }

  /**
   * Manda dos regiones a contar como un solo cuarto.
   *
   * El área no cambia —el grupo suma lo que sumaban sus partes— y el reposo del
   * grupo se escribe como la suma de los dos reposos. Esa segunda parte es la
   * que importa: sin ella el grupo nacería sin referencia, su delta saldría
   * igual a su área entera, y unir dos cuartos que nadie tocó aparecería como
   * +15 m² de extra a cobrar. Unir es una forma de LEER la planta, no una venta.
   */
  function unirAreas() {
    if (regiones.length < 2) return;
    marcar();
    const grupo = regiones.map((g) => g.nombre).join(' + ');
    const partes = regiones.flatMap((g) => partesDe(g.nombre));
    planta.grupos = { ...(planta.grupos ?? {}) };
    for (const p of partes) planta.grupos[p] = grupo;
    planta.reposo = {
      ...(planta.reposo ?? {}),
      [grupo]: regiones.reduce((s, g) => s + g.referencia, 0),
    };
    // La clase la hereda de la primera tomada: es con la que el usuario arrancó
    // la selección, o sea el cuarto que estaba mirando cuando decidió unir.
    const clase = planta.clases?.[regiones[0].nombre];
    if (clase) planta.clases = { ...planta.clases, [grupo]: clase };
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setRecintos([grupo]);
    setMultiAreas(false);
    setTick((k) => k + 1);
  }

  /**
   * Por qué no se pueden unir las tomadas, o null si sí se puede.
   *
   * Tienen que formar UNA sola mancha: que cada una toque a alguna otra del
   * grupo, no que todas toquen a la primera. Se camina el grupo desde una
   * cualquiera y si queda alguna sin alcanzar, no es un cuarto —sería un número
   * sin forma, que es justo lo que no se puede mostrar en una venta—.
   */
  function motivoUnirAreas(): string | null {
    if (regiones.length < 2) return 'Tomá al menos dos áreas';
    const alcanzadas = new Set([regiones[0].nombre]);
    for (let crecio = true; crecio;) {
      crecio = false;
      for (const g of regiones) {
        if (alcanzadas.has(g.nombre)) continue;
        if (!vecinasDe(g).some((v) => alcanzadas.has(v))) continue;
        alcanzadas.add(g.nombre);
        crecio = true;
      }
    }
    return alcanzadas.size === regiones.length
      ? null
      : 'Las áreas tienen que estar pegadas entre sí';
  }

  /** Deshace la unión: cada parte vuelve a su nombre, su reposo y su delta. */
  function separarAreas() {
    const g = region;
    if (!g || !planta.grupos) return;
    marcar();
    const grupos = { ...planta.grupos };
    for (const p of partesDe(g.nombre)) delete grupos[p];
    planta.grupos = Object.keys(grupos).length ? grupos : undefined;
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setRecintos([]);
    setTick((k) => k + 1);
  }

  /**
   * Saca una región del cómputo. Es lo más parecido a borrarla que hay.
   *
   * Borrarla de veras no se puede: una región no es un objeto del modelo, la
   * inventa la inundación en cada medición y volvería a aparecer en el mismo
   * cuadro. Así que lo que se guarda es la decisión —esta no cuenta— y se
   * respeta en cada medición. Se deshace desde la tabla de áreas.
   */
  function quitarArea() {
    if (!regiones.length) return;
    marcar();
    planta.omitidas = [...(planta.omitidas ?? []), ...regiones.map((g) => g.nombre)];
    const total = regiones.reduce((s, g) => s + g.area, 0);
    const que = regiones.length === 1
      ? `«${regiones[0].nombre}» salió`
      : `${regiones.length} áreas salieron`;
    setRecintos([]);
    setMultiAreas(false);
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setAviso(`${que} del cómputo (${m2(total)} m²). Volvé a traerlas desde la tabla de áreas.`);
    setTick((k) => k + 1);
  }

  /** La devuelve al cómputo, desde la lista de omitidas de la tabla. */
  function devolverArea(nombre: string) {
    marcar();
    const quedan = (planta.omitidas ?? []).filter((n) => n !== nombre);
    planta.omitidas = quedan.length ? quedan : undefined;
    setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setAviso(null);
    setTick((k) => k + 1);
  }

  /** Lo mismo para un área dibujada a mano. Acá la clase vive en el objeto
   *  porque el objeto existe: una libre no la inventa ninguna inundación. */
  function clasificarLibre(id: number, clase: Clase) {
    const l = (planta.libres ?? []).find((q) => q.id === id);
    if (!l || (l.clase ?? 'externa') === clase) return;
    marcar();
    l.clase = clase;
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * Vuelve a encajar el área en la caja de sus vértices.
   *
   * La caja viaja SIEMPRE junto a la forma y no es decorativa: es la que publica
   * `data-geo`, la que frena la inundación de la varita y la que mueve el
   * arrastre del cuerpo. Agregar o sacar un nodo le cambia el contorno, así que
   * dejar la caja vieja deja el área agarrable donde ya no está.
   */
  function recajar(l: Libre) {
    if (!l.vertices?.length) return;
    const [x, y, w, h] = cajaDeVertices(l.vertices);
    Object.assign(l, { x: mm(x), y: mm(y), w: mm(w), h: mm(h) });
  }

  /**
   * Prende o apaga uno de los dos modos de NODO sobre el área tomada.
   *
   * Reemplaza a los tres botones de método en la paleta del área, y la razón es
   * de Daniel (8-oct-2026): «que no exista ninguno de esos tres botones del
   * cuadrado, del polígono y del polígono irregular, ya que realmente con una
   * sola se podrían hacer todos».
   *
   * Y eso es literal: por eso acá, antes de entrar al modo, el área se pasa a
   * polígono irregular si todavía no lo es. Un rectángulo o la forma de la
   * varita no tienen nodos que partir ni que borrar; convertirla en el momento
   * en que se pide el primer nodo es lo que hace que un solo botón alcance para
   * llegar a cualquier forma, sin una vitrina de métodos que elegir antes.
   *
   * La conversión pasa por `cambiarMetodo`, que ya sabe sacarle el anillo a cada
   * una de las tres formas de origen y se niega cuando no puede —una forma con
   * un agujero—. Si se negó, el área sigue sin vértices y el modo no entra: no
   * hay nada que tocar en el plano.
   */
  function cambiarNodo(destino: 'partir' | 'quitar') {
    if (nodo === destino) { setNodo(null); setAviso(null); return; }
    const l = (planta.libres ?? []).find((q) => q.id === libreSel);
    if (!l || l.fijo) return;
    if (!l.vertices?.length) {
      cambiarMetodo('irregular');
      if (!l.vertices?.length) return;
    }
    setNodo(destino);
    setAviso(destino === 'partir'
      ? 'Tocá un lado donde quieras el nodo nuevo. Podés meter varios; tocá el botón otra vez para salir.'
      : 'Tocá el nodo que querés borrar. Tocá el botón otra vez para salir.');
  }

  /**
   * El modo de nodo es del ÁREA TOMADA y muere con ella: soltarla, bloquearla o
   * tomar otra lo apaga. Sin esto quedaba un editor que borra nodos de un área
   * que el usuario ya no cree tener seleccionada.
   */
  useEffect(() => {
    setNodo(null);
  }, [libreSel]);

  /**
   * Los mismos dos verbos, sobre el objeto «a medida» tomado.
   *
   * A diferencia del área NO hay nada que convertir antes: un objeto todavía
   * rectangular ya muestra los cuatro lados de su caja (ver `verticesMueble`) y
   * el primer corte le siembra las esquinas. Por eso `partir` entra siempre y
   * `quitar` sólo cuando ya hay forma: borrar un nodo de algo que no tiene
   * nodos no es una operación, es un botón que miente.
   */
  function cambiarNodoObjeto(destino: 'partir' | 'quitar') {
    if (nodo === destino) { setNodo(null); setAviso(null); return; }
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m || !muebleFlexible(m)) return;
    if (destino === 'quitar' && !m.vertices?.length) {
      setAviso('Todavía no tiene nodos: partile un lado primero con la tijera.');
      return;
    }
    setNodo(destino);
    setAviso(destino === 'partir'
      ? 'Tocá un lado donde quieras el nodo nuevo. Podés meter varios; tocá el botón otra vez para salir.'
      : 'Tocá el nodo que querés borrar. Tocá el botón otra vez para salir.');
  }

  /** Y muere con el objeto tomado, por lo mismo que muere con el área. */
  useEffect(() => {
    setNodo(null);
  }, [muebleSel]);

  /**
   * Cambia con qué MÉTODO está modelada el área tomada. Es la única puerta
   * entre los tres que Daniel puede elegir.
   *
   * Los tres, tal como él los nombró el 7-oct-2026:
   *
   * - **Rectángulo** — el básico. Dos medidas escribibles y ocho agarres.
   * - **Polígono** (ortogonal) — tocar un lado le mete un vértice y cada mitad
   *   se empuja. Todos los ángulos quedan rectos. Es el que ya existía.
   * - **Polígono irregular** — los vértices se mueven sueltos, así que admite
   *   diagonales: «desde un triángulo o polígonos con ciertas formas».
   *
   * Hay un cuarto que no se elige, sólo se recibe: la forma que levanta la
   * varita del plano (`celdas`). Desde ahí se sale a cualquiera de los tres.
   *
   * Antes esto eran dos funciones (`rectificarLibre` y `poligonarLibre`) con la
   * misma mitad repetida. Con el tercer método habrían sido tres, así que lo
   * que cambia por destino quedó en un `switch` y lo común —buscar el área,
   * marcar para deshacer, recalcular la caja, medir y avisar— se escribe una
   * vez. Pasa por `marcar`, así que ↶ devuelve cualquiera de las conversiones.
   *
   * DESDE EL 8-OCT-2026 EL ÚNICO DESTINO QUE SE USA ES `irregular`, y lo pide
   * `cambiarNodo`: Daniel sacó el selector de método de la paleta («que no
   * exista ninguno de esos tres botones») y el botón de dividir convierte solo.
   * `rect` y `poli` quedaron sin botón que los dispare —no hay vuelta al
   * rectángulo, y el ortogonal sólo entra por dato viejo—. No se borran porque
   * son la mitad escrita de devolverle la vuelta a la paleta si se pide.
   */
  function cambiarMetodo(destino: 'rect' | 'poli' | 'irregular') {
    const l = (planta.libres ?? []).find((q) => q.id === libreSel);
    if (!l || l.fijo) return;
    const actual = metodoLibre(l);
    if (actual === destino) return;
    const antes = areaLibre(l);

    /**
     * El anillo de la que todavía no es polígono. De un rectángulo salen sus
     * cuatro esquinas; de la forma de la varita, su contorno real, y si ese
     * contorno no cierra de una sola vuelta —una forma con un agujero— no se
     * toca nada: convertirla a medias le cambiaría el área, que es el número
     * que se está cobrando.
     */
    const anillo = (): Array<[number, number]> | null => {
      if (l.vertices?.length) return l.vertices;
      if (l.celdas?.length) {
        const vs = anilloDeCeldas(l.celdas);
        if (!vs) {
          setAviso(
            `No pude sacarle un contorno de una sola vuelta a «${l.nombre}». `
            + 'Borrala y dibujala de nuevo con el polígono irregular para poder '
            + 'modelarla por nodos.',
          );
          return null;
        }
        return vs;
      }
      return [[l.x, l.y], [l.x + l.w, l.y], [l.x + l.w, l.y + l.h], [l.x, l.y + l.h]];
    };

    if (destino === 'rect') {
      if (actual === 'rect') return;
      marcar();
      const [x, y, w, h] = l.vertices?.length ? cajaDeVertices(l.vertices) : cajaDe(l.celdas!);
      Object.assign(l, {
        x: mm(x), y: mm(y), w: mm(w), h: mm(h),
        celdas: undefined, vertices: undefined, anguloLibre: undefined,
      });
      setAviso(
        `«${l.nombre}» quedó como rectángulo de ${m2(w)} × ${m2(h)} = ${m2(w * h)} m². `
        + 'Ya se le pueden escribir las medidas y estirar los lados; deshacé con ↶ si no era eso.',
      );
    } else if (destino === 'irregular') {
      const vs = anillo();
      if (!vs) return;
      marcar();
      l.vertices = vs.map(([x, y]) => [mm(x), mm(y)] as [number, number]);
      l.celdas = undefined;
      l.anguloLibre = true;
      setAviso(
        `«${l.nombre}» es un polígono irregular de ${l.vertices.length} vértices `
        + `y ${m2(antes)} m². Arrastrá un vértice para moverlo en cualquier dirección; `
        + 'tocá el punto del medio de un lado para agregarle otro.',
      );
    } else {
      // A ORTOGONAL. Desde el irregular hay que ENDEREZARLO, y eso puede no
      // poder hacerse: un triángulo o un chaflán no tienen una versión
      // ortogonal que conserve la forma. `anilloOrtogonal` contesta `null` en
      // ese caso y ahí lo correcto es no tocar nada y decirlo — devolver la
      // caja en silencio le cambiaría el área a algo que el usuario dibujó a
      // propósito. La tolerancia va en METROS porque acá los vértices ya son
      // de obra; 2 cm es lo que alcanza para pegar un lado casi recto sin
      // tragarse un ochavo de verdad.
      const base = anillo();
      if (!base) return;
      const recto = l.anguloLibre ? anilloOrtogonal(base, 0.02) : base;
      if (!recto) {
        setAviso(
          `«${l.nombre}» tiene lados en diagonal, así que no hay un polígono `
          + 'ortogonal que conserve su forma. Dejala irregular, o pasala a '
          + 'rectángulo si querés empezar de nuevo con ángulos rectos.',
        );
        return;
      }
      marcar();
      l.vertices = recto.map(([x, y]) => [mm(x), mm(y)] as [number, number]);
      l.celdas = undefined;
      l.anguloLibre = undefined;
      setAviso(
        `«${l.nombre}» es un polígono de ${l.vertices.length} vértices y ${m2(areaLibre(l))} m². `
        + 'Arrastrá un lado para empujarlo; tocalo sin arrastrar y se parte en dos.',
      );
    }

    recajar(l);
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  /**
   * Pone o suelta el candado del área.
   *
   * Es DATO y se guarda con la planta: el lote nace bloqueado desde el
   * importador, y un cuarto que alguien bloquea a mano sigue bloqueado al volver
   * a abrir el modelo. Lo pidió Daniel el 7-oct-2026 («añadir posibilidad de
   * bloquear áreas») y no es lo mismo que el candado del mueble, que es estado
   * de pantalla.
   *
   * Entra al deshacer como cualquier otro cambio: bloquear el lote por error y
   * perder el Ctrl+Z sería peor que el error.
   */
  function fijarLibre() {
    const l = (planta.libres ?? []).find((q) => q.id === libreSel);
    if (!l) return;
    marcar();
    // Bloquearla saca de pantalla los botones de nodo, así que el modo tiene que
    // morir con ellos: si no, queda puesto y sin manera de apagarlo.
    setNodo(null);
    l.fijo = !l.fijo;
    setAviso(l.fijo
      ? `«${l.nombre}» quedó bloqueada: no se mueve, no se redimensiona y no se borra. Su tipo sí se puede cambiar.`
      : `«${l.nombre}» quedó suelta: ya se mueve y se puede borrar.`);
    setTick((k) => k + 1);
  }

  /**
   * Manda el área al frente o al fondo del montón.
   *
   * El orden del array ES el orden de pintado y también el de los toques: lo
   * último que se dibuja queda encima, y `elementFromPoint` devuelve lo de
   * encima. Hasta el 8-oct-2026 eso no se podía cambiar, y Daniel se topó con
   * la consecuencia exacta: «creé de manera posterior el área del lote y le
   * cayó encima a las demás, así que ya no puedo manipular las otras». Un área
   * grande dibujada al final tapa a todas las que encierra, para siempre.
   *
   * El candado tapa el mismo agujero por otro lado —bloqueada no recibe el
   * dedo— pero no sirve acá: bloquear el lote para poder tocar los cuartos
   * obliga a desbloquearlo cada vez que hay que tocar el lote. El orden es el
   * verbo que faltaba.
   *
   * Vale también estando bloqueada: cambiar el orden no le cambia el área, que
   * es lo único que el candado protege.
   */
  function ordenarLibre(a: 'frente' | 'atras') {
    const ls = planta.libres ?? [];
    const l = ls.find((q) => q.id === libreSel);
    if (!l) return;
    // Con una sola área libre no hay contra quién reordenar, y hay que DECIRLO.
    // Callarse es lo que hizo que Daniel reportara el botón como roto el
    // 14-oct-2026: lo apretaba con el lote tomado —el único libre de esa
    // planta— y no pasaba nada, ni un aviso. El lote ya se pinta debajo de los
    // cuartos por `libresAlFondo`, así que no hay nada que arreglar: hay que
    // contar que ya está donde quiere.
    if (ls.length < 2) {
      setAviso(l.clase === 'lote'
        ? `«${l.nombre}» ya va por debajo de todo: el lote es el suelo del plano.`
        : `«${l.nombre}» es la única área libre: no hay a quién tapar ni por quién pasar.`);
      return;
    }
    const resto = ls.filter((q) => q.id !== l.id);
    marcar();
    planta.libres = a === 'frente' ? [...resto, l] : [l, ...resto];
    setAviso(l.clase === 'lote'
      // El lote vive en su propia pasada, debajo de las áreas medidas, así que
      // «adelante» no lo sube por encima de los cuartos y prometerlo sería
      // mentir. Ver `libresAlFondo`.
      ? (a === 'frente'
        ? `«${l.nombre}» quedó adelante de las otras áreas libres, pero sigue debajo de los cuartos medidos: el lote es el suelo.`
        : `«${l.nombre}» quedó al fondo de todo.`)
      : a === 'frente'
        ? `«${l.nombre}» quedó adelante: se dibuja encima y recibe el dedo primero.`
        : `«${l.nombre}» quedó atrás: ya no tapa a las demás.`);
    setTick((k) => k + 1);
  }

  function borrarLibre() {
    if (libreSel == null) return;
    const l = (planta.libres ?? []).find((q) => q.id === libreSel);
    // Bloqueada no se borra, y se DICE. Callarse dejaría la tecla Supr sin
    // efecto visible y eso se lee como que el editor se trabó.
    if (l?.fijo) {
      setAviso(`«${l.nombre}» está bloqueada: no se borra. Soltale el candado en su paleta si de verdad la querés sacar.`);
      return;
    }
    marcar();
    planta.libres = (planta.libres ?? []).filter((l) => l.id !== libreSel);
    setLibreSel(null);
    setCorrimiento(null);
    if (medicion) setMedicion(medirSegunVista(planta, vistaObraRef.current));
    setTick((k) => k + 1);
  }

  // ── el mobiliario ────────────────────────────────────────────────────────
  /**
   * Mete un módulo del catálogo y lo deja PEGADO AL DEDO.
   *
   * Es lo que eligió Daniel (4-oct-2026, «pegado al dedo»): se apoya sobre el
   * clóset del catálogo y el clóset ya viene en la mano, con el imán midiendo
   * en vivo, hasta que se suelta contra la pared. El camino alternativo —nace
   * en el centro y después se lo arrastra— son dos gestos para una sola
   * intención, y en un teléfono el segundo empieza buscando dónde cayó.
   *
   * Nace con la medida DICTADA del catálogo, centrado en el dedo. No pasa por
   * `medirPlanta` ni enciende el ojo de áreas: un mueble no es un área, no suma
   * a ningún total y se ve siempre —es parte del diseño, no de la medición—.
   *
   * La captura del puntero queda en el BOTÓN del catálogo, que está fuera del
   * SVG. Eso funciona porque `enMetros` lee `clientX/clientY` contra la matriz
   * del lienzo y no le importa sobre qué elemento cayó el evento; lo que hace
   * falta es que el botón reenvíe sus `pointermove`/`pointerup` al mismo
   * `arrastrar`/`soltar` del lienzo, y eso lo hace `PaletaCatalogo`.
   *
   * El tercer parámetro es lo que agrega la casilla de PEGAR: el giro y los
   * radios de la pieza copiada. Va como añadido y no como un segundo camino
   * porque pegar es poner un módulo —el mismo nacimiento, el mismo imán, el
   * mismo arrastre— sólo que el molde viene del portapapeles y no del catálogo.
   */
  /**
   * Deja la pieza colgada del ancla que se le pase, o suelta si es `null`.
   *
   * Es el único lugar que escribe `Mueble.ancla`, y por eso el único que puede
   * garantizar la invariante: con ancla, `x`/`y`/`giro` los manda la pared; sin
   * ancla, la pieza se queda EXACTAMENTE donde estaba. Esa segunda mitad es lo
   * que hace que despegar una pieza no la teletransporte.
   */
  function sentar(m: Mueble, a: AnclaPared | null) {
    if (!a) { delete m.ancla; return; }
    const p = planta.paredes.find((q) => q.id === a.pared);
    if (!p) { delete m.ancla; return; }
    const s = asientoDeAncla(p, a, m);
    m.ancla = { ...a, t: s.t };
    m.x = s.x; m.y = s.y; m.giro = s.giro;
  }

  /**
   * Pone la pieza centrada en un punto del plano y la deja tomada.
   *
   * Está separado de `crearMueble` porque ahora hay DOS caminos que la hacen
   * nacer y sólo uno de ellos tiene un evento de puntero en la mano: el
   * arrastre desde la vitrina (que sigue vivo) y el toque en el lienzo con una
   * pieza armada. Lo que comparten —el id, el imán a pared, soltar todas las
   * otras selecciones— es exactamente esto.
   */
  function nacerMueble(
    px: number, py: number, modelo: ModeloMueble, extra?: Partial<Mueble>,
  ): { id: number; x: number; y: number } {
    marcar();
    const ms = planta.muebles ?? [];
    const id = ms.reduce((k, m) => Math.max(k, m.id), 0) + 1;
    const x = mm(px - modelo.w / 2);
    const y = mm(py - modelo.h / 2);
    // Los radios del MODELO van primero y los del `extra` después: así una
    // pieza guardada nace con el recorte de esquinas que el usuario le dejó, y
    // el portapapeles —que trae los de la pieza copiada— los pisa igual.
    const nuevo: Mueble = {
      ...(modelo.radios ? { radios: [...modelo.radios] as Mueble['radios'] } : null),
      ...extra, id, tipo: modelo.clave, x, y, w: modelo.w, h: modelo.h,
    };
    // Lo que PEGA A PARED nace pegado, si hay una a tiro del dedo. Soltarlo en
    // medio del cuarto lo deja suelto, que es la otra mitad del pedido y no
    // necesita modo: la ausencia de pared cerca YA es la decisión.
    if (modelo.pegaPared) sentar(nuevo, anclaCerca(planta.paredes, px, py));
    planta.muebles = [...ms, nuevo];
    setAviso(null);
    setSel([]);
    setMulti(false);
    setVanoSel(null);
    setRecintos([]);
    setMultiAreas(false);
    setDivSel(null);
    setLibreSel(null);
    setCorrimiento(null);
    setMuebleSel(id);
    setEsquinaSel(null);
    return { id, x, y };
  }

  function crearMueble(
    e: React.PointerEvent, modelo: ModeloMueble, extra?: Partial<Mueble>,
  ) {
    const p = enMetros(e, true);
    if (!p) return;
    capturar(e);
    e.stopPropagation();
    const { id, x, y } = nacerMueble(p.x, p.y, modelo, extra);
    // `marcadoRef` queda en false aunque `marcar()` ya tomó la foto: lo que ese
    // ref cuenta es si el GESTO movió algo, y el toggle de soltar lo lee.
    marcadoRef.current = false;
    setArrastrando(true);
    agarreRef.current = {
      tipo: 'mueble', id, lado: 'cuerpo', yaSel: false,
      x0: x, y0: y, w0: modelo.w, h0: modelo.h, mx: p.x, my: p.y,
      nacido: true,
    };
    setTick((k) => k + 1);
  }

  /**
   * Arma una pieza del catálogo y espera a que se toque dónde va.
   *
   * Es el pedido de Daniel del 10-oct-2026: «que la manera de insertar uno de
   * esos objetos funcione a como funcionan las paredes y las áreas, que primero
   * hay que definir el punto donde se va a colocar, ya que actualmente al darle
   * uno clic a uno de los objetos lo agrega automáticamente y uno no sabe a
   * dónde los agrega».
   *
   * Tenía razón y el motivo es geométrico: la vitrina está FUERA del lienzo, y
   * `crearMueble` nace donde cayó el dedo. Con un arrastre eso es exactamente
   * lo que uno quiere; con un toque suelto, el dedo nunca salió del botón, así
   * que la pieza nacía abajo y al centro —donde está la vitrina— y había que ir
   * a buscarla.
   */
  function armarObjeto(modelo: ModeloMueble, extra?: Partial<Mueble>) {
    cerrarHerramientas();
    cerrarHerrPared();
    // Elegir una casilla BAJA la vitrina. Daniel, 11-oct-2026: «que al elegir
    // cualquiera se cierre el modal de objetos porque si elijo uno es porque es
    // el que quiero». Y el que elige necesita ver justo lo que la vitrina
    // tapaba: la mitad de abajo del plano, que es donde va a soltar la pieza.
    //
    // No se puede llamar a `cerrarCatalogo` acá: ese también desarma, y mataría
    // la pieza en el mismo gesto que la eligió.
    setCatalogo(false);
    setCorrCat(null);
    setArmado({ modelo, extra });
    setModo('objeto');
    setTrazo(null);
    setCorte(null);
    setMira(null);
  }

  /**
   * La pieza que se vería si se soltara AHÍ: el fantasma del sello.
   *
   * Es `nacerMueble` sin el nacimiento. Repite su geometría a propósito —el
   * centrado en el punto y el imán a pared— porque una previsualización que no
   * pase por exactamente lo mismo que la creación miente, y una que miente es
   * peor que no tenerla: se vería suelta en medio del cuarto y nacería pegada a
   * la pared de al lado.
   *
   * El `id: -1` nunca entra a la planta. Está porque `SimboloMueble` pide un
   * `Mueble` entero y porque un id imposible deja dicho que esto no existe.
   */
  function fantasmaDe(p: { x: number; y: number }): Mueble | null {
    if (!armado) return null;
    const mo = armado.modelo;
    const g: Mueble = {
      ...(mo.radios ? { radios: [...mo.radios] as Mueble['radios'] } : null),
      ...armado.extra, id: -1, tipo: mo.clave,
      x: mm(p.x - mo.w / 2), y: mm(p.y - mo.h / 2), w: mo.w, h: mo.h,
    };
    if (mo.pegaPared) sentar(g, anclaCerca(planta.paredes, p.x, p.y));
    return g;
  }

  /**
   * Suelta la pieza armada donde quedó el fantasma, y apaga el modo.
   *
   * Una elección, una pieza. El modo sello del 11-oct-2026 llegó a quedarse
   * cargado para repetir sin volver a la vitrina, y Daniel lo rechazó el mismo
   * día: «pensé que el sello era previsualizar el objeto en el plano antes de
   * colocarlo, no que se quedaba con el loop de agregar el mismo objeto, eso no
   * me sirve». El sello es la HUELLA que se ve antes de estampar —eso lo dibuja
   * `fantasmaDe`—, no la repetición.
   *
   * Y es la mejor salida del riesgo que el modo cargado tenía encima: con el
   * modo apagado al soltar, el toque siguiente vuelve a ser mirar y elegir, no
   * otra pieza que uno no pidió.
   */
  function ponerArmado(p: { x: number; y: number }) {
    if (!armado) { setModo(null); return; }
    nacerMueble(p.x, p.y, armado.modelo, armado.extra);
    desarmar();
    setTick((k) => k + 1);
  }

  /** Baja el fantasma y apaga el modo, sin poner nada. */
  function desarmar() {
    setArmado(null);
    setFantasma(null);
    setModo(null);
  }

  /**
   * Copia el mueble tomado: deja una copia al lado Y la guarda para pegar.
   *
   * Lo pidió Daniel el 5-oct-2026: «que aparezca la opción de copiar y pegar
   * cualquier objeto de mobiliario». Hace las DOS cosas en un toque a propósito,
   * porque son dos usos distintos del mismo molde:
   *
   * - La copia que cae al lado resuelve el caso de todos los días —poner la
   *   segunda silla, el tercer módulo de cocina— sin salir del dibujo. En un
   *   celular, obligar a abrir el catálogo entremedio son tres gestos para una
   *   intención.
   * - El portapapeles resuelve el otro: pegar en OTRA PARTE, o en otra planta,
   *   arrastrando la casilla «Pegar» que aparece en el catálogo.
   *
   * Lo que se copia es la pieza VIVA con su medida ajustada, su giro y sus
   * radios; lo único que no se copia es el `id` y la posición, que no significan
   * nada en el destino. El corrimiento de 20 cm es para que la copia se vea: una
   * encima de la otra se lee como que no pasó nada.
   */
  function copiarMueble() {
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    marcar();
    const ms = planta.muebles ?? [];
    const id = ms.reduce((k, q) => Math.max(k, q.id), 0) + 1;
    const copia: Mueble = { ...m, id, x: mm(m.x + 0.2), y: mm(m.y + 0.2) };
    // La FORMA se copia punto por punto, ya corrida. El `...m` copiaría la
    // referencia al mismo arreglo y mover un vértice del original movería el de
    // la copia: dos objetos con una sola forma compartida.
    if (m.vertices?.length) {
      copia.vertices = m.vertices.map(([x, y]) => (
        [mm(x + 0.2), mm(y + 0.2)] as [number, number]
      ));
    }
    // La copia de algo COLGADO se corre a lo largo de su misma pared, no en
    // diagonal: 20 cm al sudeste la sacarían del muro y el ancla diría una cosa
    // mientras el dibujo dice otra. Correrla por la pared es además lo que uno
    // quiere al copiar un tomacorriente — poner el segundo al lado del primero.
    if (m.ancla) {
      copia.ancla = { ...m.ancla };
      sentar(copia, { ...m.ancla, t: mm(m.ancla.t + 0.4) });
    }
    planta.muebles = [...ms, copia];
    setCopiado({
      tipo: m.tipo, w: m.w, h: m.h, giro: m.giro, radios: m.radios, altura: m.altura,
      espejo: m.espejo,
    });
    setMuebleSel(id);
    setEsquinaSel(null);
    setCorrimiento(null);
    setTick((k) => k + 1);
  }

  /**
   * Pega el molde del portapapeles, pegado al dedo como cualquier módulo.
   *
   * No tiene camino propio: arma la ficha del catálogo con la medida copiada y
   * llama al mismo `crearMueble`, pasándole el giro y los radios como añadido.
   * Así pegar hereda gratis el imán, el arrastre, el deshacer y el nacimiento
   * centrado en el dedo, en vez de ser una segunda implementación que se
   * desactualiza.
   */
  function pegarMueble(e: React.PointerEvent) {
    const m = moldePegado();
    if (m) crearMueble(e, m.modelo, m.extra);
  }

  /** El molde del portapapeles como ficha + añadidos, o null si no hay nada. */
  function moldePegado(): { modelo: ModeloMueble; extra: Partial<Mueble> } | null {
    if (!copiado) return null;
    const ficha = fichaMueble(copiado.tipo);
    if (!ficha) return null;
    return {
      modelo: { ...ficha, w: copiado.w, h: copiado.h },
      extra: { giro: copiado.giro, radios: copiado.radios, altura: copiado.altura,
               espejo: copiado.espejo },
    };
  }

  /** La casilla «Pegar», tocada sin arrastrar: arma el molde copiado. */
  function armarPegado() {
    const m = moldePegado();
    if (m) armarObjeto(m.modelo, m.extra);
  }

  /**
   * Abre el catálogo para CAMBIAR el módulo de la pieza tomada.
   *
   * No llama a `abrirCreador('mueble')` a propósito: ese camino suelta la
   * selección —es para PONER una pieza nueva— y acá la selección es el objeto
   * mismo del gesto. Así que abre la vitrina a mano y deja la paleta del mueble
   * a la vista, que es lo que hace entender qué se está reemplazando.
   *
   * Las dos paletas conviven en pantalla, y esta es la excepción deliberada a
   * «una sola paleta flotante a la vez»: esa regla nació porque dos paletas
   * caían sobre el MISMO ancla y se tapaban. La vitrina se planta abajo y al
   * centro; la del mueble cuelga de la pieza.
   */
  function abrirReemplazo() {
    if (muebleSel == null) return;
    cerrarHerramientas();
    setAviso(null);
    setOferta(null);
    setModo(null);
    setTrazo(null);
    setCorte(null);
    setMira(null);
    setEsquinaSel(null);
    setReemplazando(true);
    setCatalogo(true);
  }

  /**
   * Cambia el módulo de la pieza tomada por otro del catálogo.
   *
   * **El que entra manda su medida.** Daniel lo corrigió el 11-oct-2026: «el
   * objeto nuevo lleve las medidas que trae por defecto, que no use las del
   * origen». Y tiene razón contra lo que decía antes este comentario: una
   * refrigeradora que hereda los 0,50 × 1,00 de la ducha que reemplazó NO es la
   * medida de una refrigeradora, es una ducha con otro dibujo adentro. El
   * catálogo existe para traer la medida real de cada pieza; conservar la ajena
   * convierte el reemplazo en un sello y deja números falsos en el plano.
   *
   * **La única excepción es «A medida»**, y por definición: es la pieza que NO
   * trae medida propia, así que lo único sensato que puede hacer al entrar es
   * quedarse con la huella que ya había. Es también el camino para «dejame esta
   * caja como está pero sin el símbolo». Ojo con `flexible`: una pieza GUARDADA
   * que salió de «A medida» lo hereda, pero sí tiene medida propia —es la que
   * Daniel moldeó— así que entra con la suya. Por eso la prueba es la clave del
   * módulo y no la bandera (ver `CLAVE_A_MEDIDA`).
   *
   * Dos detalles de colocación al cambiar la huella: el CENTRO se queda quieto
   * —crecer desde la esquina de arriba-izquierda correría la pieza fuera del
   * recinto en el que estaba encajada— y, si quedó colgada de una pared, se la
   * vuelve a sentar para que la profundidad nueva quede a ras y no flotando ni
   * metida dentro del muro.
   */
  function reemplazarMueble(nuevo: ModeloMueble) {
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    marcar();
    // La huella del PLANO, antes de que el reemplazo pise `w`/`h`. Es lo que se
    // ofrece aprender, y por eso se lee de `m.huella` y no de la medida actual:
    // corregir dos veces la misma pieza tiene que seguir enseñando el bloque que
    // el plano traía, no el tamaño de la pieza que entró en el intento anterior.
    const hue = m.huella;
    m.tipo = nuevo.clave;
    if (nuevo.clave !== CLAVE_A_MEDIDA) {
      // La medida del catálogo es con giro 0; la de la pieza, en la orientación
      // en que está puesta. Acostada, hay que cruzarlas.
      const vertical = m.giro === 90 || m.giro === 270;
      const w = vertical ? nuevo.h : nuevo.w;
      const h = vertical ? nuevo.w : nuevo.h;
      m.x = mm(m.x + (m.w - w) / 2);
      m.y = mm(m.y + (m.h - h) / 2);
      m.w = w;
      m.h = h;
    }
    // Los radios son del objeto «a medida» y de las piezas guardadas que salen
    // de él. En cualquier otro módulo no significan nada, y dejarlos puestos le
    // redondearía las esquinas a una refrigeradora.
    if (nuevo.radios) m.radios = [...nuevo.radios] as Mueble['radios'];
    else delete m.radios;
    // Lo mismo con lo que es del ELÉCTRICO. Un ancla que sobrevive a un cambio
    // de módulo es una trampa en diferido: el sofá que reemplazó al toma se
    // quedaría quieto hasta que alguien mueva esa pared, y ahí saltaría a
    // pegarse a ella sin que nadie lo haya tocado.
    if (!nuevo.pegaPared) delete m.ancla;
    if (!nuevo.electrico) delete m.altura;
    // Con la huella nueva, el asiento contra la pared cambió: volver a sentarla
    // es lo que la deja a ras en vez de flotando o metida en el muro.
    if (m.ancla) sentar(m, m.ancla);
    setEsquinaSel(null);
    cerrarCatalogo();
    setAviso(`Quedó ${nuevo.nombre} de ${m2(m.w)} × ${m2(m.h)} m.`);
    // Y se ofrece aprender, salvo que no haya nada que aprender: una pieza que
    // no vino del plano no tiene huella, «A medida» y el eléctrico no son piezas
    // reconocibles, y una huella que YA dice esto no necesita confirmarse.
    const yaSabida = hue && huellaDe(hue[0], hue[1])?.clave === nuevo.clave;
    setOferta(hue && !yaSabida && nuevo.clave !== CLAVE_A_MEDIDA && !nuevo.electrico
      ? { w: hue[0], h: hue[1], clave: nuevo.clave, nombre: nuevo.nombre }
      : null);
    setTick((k) => k + 1);
  }

  /**
   * Acepta la oferta: de acá en adelante, esa medida ES esa pieza.
   *
   * No rehace el plano abierto a propósito —la pieza que acaba de corregir ya
   * quedó bien, y recalcular el resto movería cosas que Daniel no tocó—. Lo que
   * cambia es lo que viene: la próxima importación, y las otras plantas del
   * mismo modelo cuando se vuelvan a leer.
   */
  function aprenderOferta() {
    if (!oferta) return;
    const problema = aprenderHuella(oferta.w, oferta.h, oferta.clave);
    setOferta(null);
    setAviso(problema
      ?? `Aprendido: ${m2(oferta.w)} × ${m2(oferta.h)} m es ${oferta.nombre}.`);
  }

  /**
   * Guarda la pieza tomada en el catálogo, con nombre y categoría.
   *
   * Es el pedido de Daniel del 5-oct-2026: «le cambio las medidas o hago con el
   * módulo variable alguna medida en específico y en base a eso lo puedo guardar
   * con un nombre y lo meto a la categoría que desee».
   *
   * Guarda la medida SIN GIRO —la del catálogo es la huella con giro 0, y el
   * giro se aplica al poner la pieza—, así que una pieza guardada de costado
   * vuelve a nacer derecha y se gira con su botón, como cualquier módulo. Y
   * guarda los radios: en el módulo variable la forma de las esquinas es parte
   * de lo que se moldeó.
   *
   * Lo guardado vive en `localStorage`: en ESTE navegador. No viaja del celular
   * a la computadora, y eso hace falta decirlo —ver `leerPropios`—.
   */
  function guardarMueblePropio(comoSeLlama: string, grupo: string) {
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    const n = comoSeLlama.trim();
    if (!n) { setAviso('Ponele un nombre a la pieza para poder guardarla.'); return; }
    const ficha = fichaMueble(m.tipo);
    const vertical = m.giro === 90 || m.giro === 270;
    const flexible = muebleFlexible(m);
    guardarPropio({
      nombre: n,
      grupo: grupo.trim() || GRUPO_PROPIO,
      w: vertical ? m.h : m.w,
      h: vertical ? m.w : m.h,
      simbolo: ficha?.simbolo ?? 'mueble',
      ...(flexible ? { flexible: true as const } : null),
      ...(m.radios ? { radios: [...m.radios] as [number, number, number, number] } : null),
    });
    setAviso(`«${n}» quedó en el catálogo, bajo ${grupo}. Sólo en este dispositivo.`);
    setTick((k) => k + 1);
  }

  /**
   * El catálogo propio cambió desde la pestaña «Mis objetos».
   *
   * El repintado no es cosmético: las piezas ya puestas sacan su nombre y su
   * símbolo del índice por clave, así que corregir una medida o un nombre tiene
   * que verse en el plano y no sólo en la lista.
   */
  function avisarCambioPropio(texto: string) {
    setAviso(texto);
    setTick((k) => k + 1);
  }

  /**
   * Pasa una pieza propia al catálogo que ve TODO el equipo.
   *
   * Sube y suelta el dueño en dos llamadas —`publicarObjeto` las encadena—
   * porque hasta ahora la pieza sólo existía en este navegador. La copia local
   * se queda: sigue siendo mía, y además la lista compartida que vuelve del
   * servidor se une a las propias sin repetir clave.
   */
  async function publicarPropio(clave: string) {
    const ficha = fichaMueble(clave);
    if (!ficha) return;
    setAviso(`Publicando «${ficha.nombre}»…`);
    try {
      await publicarObjeto(ficha);
      setAviso(`«${ficha.nombre}» ya está en el catálogo de todo el equipo.`);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo publicar la pieza.');
    }
    setTick((k) => k + 1);
  }

  /**
   * Gira el mueble tomado, de 90 en 90.
   *
   * Girar un rectángulo 90° es INTERCAMBIAR sus medidas, y además hay que
   * guardar el ángulo: el símbolo no es simétrico —el inodoro tiene tanque de un
   * lado, la cama cabecera— y sin el ángulo quedaría mirando siempre al sur
   * aunque la caja ya estuviera de costado.
   *
   * Sólo múltiplos de 90 a propósito: en estas casas todo es ortogonal, y un
   * ángulo libre rompería el imán a caras de pared —que trabaja por eje— sin
   * servirle a nadie.
   *
   * El CENTRO queda clavado: girar desde la esquina de arriba a la izquierda
   * manda el módulo a caminar media medida cada vez que se toca el botón.
   */
  /**
   * La ALTURA DE MONTAJE de una salida eléctrica, en metros sobre piso.
   *
   * Daniel la pidió «con altura por defecto según el tipo y editable en la
   * paleta», así que la pieza NO guarda la altura de fábrica: sólo guarda la
   * que alguien cambió (ver `Mueble.altura`). Escribir el mismo valor del
   * catálogo la vuelve a dejar sin guardar nada, que es lo correcto — así,
   * cuando el departamento decida que los tomas van a 0,45, las piezas que
   * nadie tocó se mueven solas con el catálogo.
   *
   * El tope de 3 m no es una regla de obra: es el filtro de un dedo que escribió
   * 40 en vez de 0,40. Un cero de más en un plano eléctrico es la clase de error
   * que nadie vuelve a mirar.
   */
  function alturaMueble(v: string) {
    if (!v.trim()) return;
    const n = Number(v.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0 || n > 3) {
      setAviso('La altura de montaje va entre 0 y 3 m.');
      return;
    }
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    const defecto = fichaMueble(m.tipo)?.alturaDefecto;
    const puesta = mm(n);
    if (puesta === (m.altura ?? defecto)) return;
    marcar();
    if (puesta === defecto) delete m.altura;
    else m.altura = puesta;
    setTick((k) => k + 1);
  }

  /** Pasa la pieza a la otra cara de su pared. Sin ancla no hay dos caras. */
  function voltearMueble() {
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m?.ancla) return;
    marcar();
    sentar(m, { ...m.ancla, cara: m.ancla.cara === 1 ? -1 : 1 });
    setTick((k) => k + 1);
  }

  function girarMueble() {
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    marcar();
    const cx = m.x + m.w / 2, cy = m.y + m.h / 2;
    // CON FORMA PROPIA se giran los VÉRTICES, no el `giro`: ese campo orienta
    // un dibujo fijo y acá `SimboloMueble` lo ignora, así que tocarlo dejaría
    // un botón que no hace nada. Giro de 90° en el sentido del reloj alrededor
    // del centro de la caja: (x,y) → (cx − (y − cy), cy + (x − cx)).
    if (m.vertices?.length) {
      recajarMueble(m, m.vertices.map(([x, y]) => (
        [mm(cx - (y - cy)), mm(cy + (x - cx))] as [number, number]
      )));
      setTick((k) => k + 1);
      return;
    }
    m.giro = (((m.giro ?? 0) + 90) % 360) as 0 | 90 | 180 | 270;
    const w = m.h, h = m.w;
    m.w = w;
    m.h = h;
    m.x = mm(cx - w / 2);
    m.y = mm(cy - h / 2);
    setTick((k) => k + 1);
  }

  /**
   * Voltea el símbolo de la pieza sobre su eje propio.
   *
   * No toca `x`/`y`/`w`/`h` a propósito: el espejo es DIBUJO, no geometría. Por
   * eso no hay que recentrar nada, el imán a caras de pared sigue valiendo y
   * una pieza anclada no se despega. Lo hace todo `SimboloMueble`, con un
   * `scale(-1 1)` metido adentro del giro.
   */
  function espejarMueble() {
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    marcar();
    // Y con forma propia, por lo mismo que el giro: se voltean los vértices
    // sobre el eje vertical de la caja. Acá sí cambia la geometría —no hay un
    // dibujo aparte que voltear— pero la caja no se mueve, así que el objeto
    // se queda exactamente donde estaba.
    if (m.vertices?.length) {
      const cx = m.x + m.w / 2;
      recajarMueble(m, m.vertices.map(([x, y]) => (
        [mm(2 * cx - x), y] as [number, number]
      )));
      setTick((k) => k + 1);
      return;
    }
    if (m.espejo) delete m.espejo; else m.espejo = true;
    setTick((k) => k + 1);
  }

  /**
   * Aplica una medida escrita en la paleta del mueble.
   *
   * Mismo molde que `medidaLibre` —la esquina de arriba a la izquierda queda
   * clavada y crece el lado opuesto— porque es el mismo gesto: corregir un
   * número no debe correr el objeto de lugar.
   *
   * Que las DOS medidas se puedan escribir es el pedido textual de Daniel:
   * «closets de 60 por 1.50 y que se puedan editar, si uno desea, las dos
   * dimensiones». La medida del catálogo es el arranque, no una jaula.
   */
  function medidaMueble(cual: 'w' | 'h', v: string) {
    if (!v.trim()) return;
    const n = Number(v.replace(',', '.'));
    // El mínimo del mueble es más chico que el de una pared: una silla de 45 cm
    // es un mueble legítimo y `MIN_LARGO` la dejaría afuera por 15 cm.
    if (!Number.isFinite(n) || n < MIN_MUEBLE) {
      setAviso(`Cada medida del mueble tiene que pasar de ${m2(MIN_MUEBLE)} m.`);
      return;
    }
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m || mm(n) === m[cual]) return;
    marcar();
    m[cual] = mm(n);
    setTick((k) => k + 1);
  }

  /**
   * Toca una esquina del objeto «a medida»: la pone en edición para que la
   * paleta muestre su radio. NO arrastra nada.
   *
   * Es el pedido literal de Daniel —«que al darle clic uno a la esquina, salga
   * el panel flotante en el que uno pueda ponerle el radio que uno desea»— y es
   * también lo que saca de las esquinas el gesto de estirar, que era el que se
   * disparaba sin intención.
   */
  function tomarEsquina(e: React.PointerEvent, m: Mueble, i: number) {
    if (i < 0) return;
    // Corta acá: sin esto el `pointerdown` sigue hasta el oyente de «tocar
    // afuera», que no reconoce este círculo y suelta el mueble en el acto.
    e.stopPropagation();
    setAviso(null);
    setMuebleSel(m.id);
    // Volver a tocar la misma esquina cierra el campo. Es el toggle de siempre
    // en este editor —tocar lo tomado lo suelta— y evita un botón de cerrar.
    setEsquinaSel((k) => (k === i ? null : i));
  }

  /**
   * Aplica el radio escrito para la esquina en edición.
   *
   * Vacío o 0 la devuelve a escuadra, que es cómo se deshace sin un botón más.
   * Y cuando las cuatro quedan en cero el campo entero se borra del modelo: un
   * mueble sin esquinas redondeadas no tiene por qué arrastrar `[0,0,0,0]` en el
   * JSON del historial ni en lo que se guarde.
   */
  function radioEsquina(v: string) {
    if (esquinaSel == null) return;
    const m = (planta.muebles ?? []).find((q) => q.id === muebleSel);
    if (!m) return;
    const t = v.trim();
    const n = t ? Number(t.replace(',', '.')) : 0;
    if (!Number.isFinite(n) || n < 0) {
      setAviso('El radio va de 0 para arriba.');
      return;
    }
    // El tope es la mitad del lado corto: pasado de ahí los arcos de dos
    // esquinas vecinas se comen entre sí y el contorno sale deforme.
    const tope = topeRadio(m.w, m.h);
    if (n > tope) setAviso(`Acá el radio llega hasta ${m2(tope)} m: la mitad del lado corto.`);
    const r = mm(Math.min(n, tope));
    const rs = [...(m.radios ?? [0, 0, 0, 0])] as [number, number, number, number];
    if (rs[esquinaSel] === r) return;
    marcar();
    rs[esquinaSel] = r;
    m.radios = rs.some((k) => k > 0) ? rs : undefined;
    setTick((k) => k + 1);
  }

  /**
   * Saca el mueble tomado. No vuelve a medir: nunca sumó.
   */
  function borrarMueble() {
    if (muebleSel == null) return;
    marcar();
    planta.muebles = (planta.muebles ?? []).filter((m) => m.id !== muebleSel);
    setMuebleSel(null);
    setEsquinaSel(null);
    setCorrimiento(null);
    setTick((k) => k + 1);
  }

  /** Cierra la vitrina de objetos. */
  function cerrarCatalogo() {
    setCatalogo(false);
    setCorrCat(null);
    // El modo reemplazar muere con la vitrina: dejarlo vivo haría que el
    // catálogo siguiente —abierto para PONER un módulo— lo cambiara en vez.
    setReemplazando(false);
    // Y la pieza armada también: sin vitrina a la vista no hay nada que diga
    // cuál quedó cargada, así que el toque siguiente en el plano soltaría un
    // objeto que el usuario ya no recuerda haber elegido.
    setArmado(null);
    setFantasma(null);
    setModo((m) => (m === 'objeto' ? null : m));
  }

  /**
   * Tocar afuera cierra el catálogo. Misma regla que la paleta de agregar área:
   * «el de salir de la paleta de control, para mí básicamente es darle clic en
   * otro lado que no sea la paleta de control».
   *
   * Sin la guarda de `modo` que tiene la otra —el catálogo no enciende ninguna
   * herramienta— pero SÍ con la de `arrastrando`: mientras un módulo viene
   * pegado al dedo, el `pointerdown` que lo arrancó cae sobre el botón del
   * catálogo y cerrarlo ahí desmontaría al que tiene la captura del puntero.
   */
  useEffect(() => {
    if (!catalogo) return;
    const afuera = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-paleta], [data-agregar-mueble]')) return;
      // Con una pieza ARMADA, el toque de afuera es justamente el que la pone:
      // la vitrina se cierra —ya estorba— pero desarmar acá mataría el gesto en
      // el instante en que se estaba completando. De desarmar se encarga
      // `ponerArmado`, que corre un suspiro después con el mismo toque.
      if (modo === 'objeto') { setCatalogo(false); setCorrCat(null); return; }
      cerrarCatalogo();
    };
    document.addEventListener('pointerdown', afuera, true);
    return () => document.removeEventListener('pointerdown', afuera, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogo, modo]);

  function cambiarModo(m: Modo) {
    setModo(modo === m ? null : m);
    setTrazo(null);
    setCorte(null);
    setMira(null);
    setAviso(null);
  }

  /**
   * Elegir método de pared desde la paleta.
   *
   * Los tres son excluyentes y uno está siempre puesto: a diferencia de las
   * herramientas de área —donde apagar las dos deja la paleta sin modo— acá
   * volver a tocar el método encendido no lo apaga. La salida de la capa es el
   * botón de la capa o el toque afuera, no dejar la paleta en un estado mudo.
   *
   * El trazo a medio hacer se tira: cambiar de método con un arranque clavado
   * dejaría un punto esperando un segundo apoyo que ahora significa otra cosa.
   */
  function elegirMetodoPared(m: MetodoPared) {
    setMetodoPared(m);
    setModo(m === 'varita' ? 'varita-pared' : m === 'rect' ? 'rect-pared' : 'pared');
    setTrazo(null);
    setCorte(null);
    setMira(null);
    setAviso(null);
  }

  // ── las cuatro capas ─────────────────────────────────────────────────────
  /** ¿Está abierta la paleta de AGREGAR de esta capa? */
  function creandoEn(c: Capa) {
    if (c === 'pared') return herrPared;
    if (c === 'cota') return modo === 'cota';
    if (c === 'area') return herramientas;
    return catalogo;
  }

  /** Abre la paleta de agregar de una capa y cierra las de las otras tres. */
  function abrirCreador(c: Capa) {
    cerrarHerramientas();
    cerrarHerrPared();
    cerrarCatalogo();
    soltarTodo();
    // La cota «crea» sin paleta: no hay tres maneras de poner una cota, hay
    // una, así que el botón enciende el gesto de dos apoyos y ya.
    if (c === 'cota') {
      setModo(c); setTrazo(null); setCorte(null); setMira(null); return;
    }
    // La pared nace con «individual» ya elegida, por el mismo motivo que el
    // área nace con el rectángulo: es el método de los veinte usos y obligar a
    // elegirlo cada vez sería el doble paso que Daniel ya mandó a sacar.
    if (c === 'pared') {
      setMetodoPared('una'); setModo('pared');
      setTrazo(null); setCorte(null); setMira(null); setHerrPared(true); return;
    }
    setTrazo(null);
    setCorte(null);
    setMira(null);
    // El área nace con el RECTÁNGULO ya elegido. Pedido de Daniel (6-oct-2026):
    // «cuando agrego un área, que por defecto sea el método de rectángulo, ya
    // que me obliga a hacer doble paso actualmente, tener que darle al área y
    // después elegir el rectángulo; ya si deseo cambiarlo lo hago por la
    // varita». Casi todas las áreas se dibujan a mano, así que el default es el
    // camino de los veinte usos y la varita el del caso raro.
    if (c === 'area') { setModo('libre'); setHerramientas(true); return; }
    setModo(null);
    setCatalogo(true);
  }

  /** Cierra la paleta de agregar de una capa SIN apagar la capa. */
  function cerrarCreador(c: Capa) {
    if (c === 'cota') {
      setModo(null); setTrazo(null); setCorte(null); setMira(null); return;
    }
    if (c === 'pared') { cerrarHerrPared(); return; }
    if (c === 'area') { cerrarHerramientas(); return; }
    cerrarCatalogo();
  }

  /**
   * El toque en el cuerpo de un botón de capa: alterna su paleta de AGREGAR, y
   * de paso la capa pasa a mandar sobre lo manipulable.
   *
   * La regla de la que cuelga todo lo demás: la paleta que CIERRA EL BOTÓN
   * apaga la capa —se terminó de trabajar con eso—, pero la que se cierra SOLA
   * (poner un mueble cierra el catálogo) deja la capa viva. Eso es lo que hace
   * falta para acomodar el módulo recién puesto sin rozar una pared, que es
   * justo el momento en que el dedo las mueve sin querer.
   *
   * El ciclo salió así y no al revés —primero la capa, el creador en un segundo
   * toque— porque poner muebles es lo que se hace veinte veces seguidas: con el
   * creador en el segundo toque, cada módulo costaba dos.
   */
  function tocarCapa(c: Capa) {
    if (capa === c && creandoEn(c)) { cerrarCreador(c); setCapa(null); return; }
    setCapa(c);
    // Entrar a una capa la prende: una capa activa e invisible no deja
    // manipular nada, y el toque siguiente parecería no hacer nada.
    setVer((v) => ({ ...v, [c]: true }));
    abrirCreador(c);
  }

  /**
   * Mostrar u ocultar una capa. Sólo eso —no elige la capa activa— y apagarla
   * la saca de juego: lo que no se ve no se toca (ver `puede`). Si la apagada
   * ERA la capa activa, se desactiva: si no, el editor quedaría sin nada
   * manipulable y leería como colgado.
   */
  function tocarOjo(c: Capa) {
    const prendido = ver[c];
    setVer((v) => ({ ...v, [c]: !prendido }));
    if (!prendido) return;
    soltarTodo();
    if (capa === c) { setCapa(null); cerrarCreador(c); }
  }

  /**
   * El doble toque sobre el ícono de una capa, que es el gesto que pidió Daniel
   * el 6-oct-2026: «para ocultar o mostrar sea con doble tap en el ícono […] ese
   * doble tap va a hacer de que se vea el círculo en verde o se vea en rojo si
   * está apagado».
   *
   * Reemplaza al ojo, que era un segundo botón pegado a cada capa y se comía la
   * mitad del ancho de la barra. El costo es que el toque simple tiene que
   * ESPERAR a ver si llega el segundo: sin esa espera, un doble toque primero
   * activaría la capa y después la apagaría, o sea haría las dos cosas. Se
   * espera `DOBLE_MS`, el mismo umbral del doble toque de paredes y áreas, así
   * que es un gesto que la mano ya tiene.
   *
   * El temporizador vive en un ref por capa y no en estado: cambiarlo no tiene
   * que repintar nada, y un repintado en medio del gesto lo perdería.
   */
  const espera = useRef<Record<string, number>>({});
  useEffect(() => () => {
    for (const t of Object.values(espera.current)) window.clearTimeout(t);
  }, []);

  function golpe(clave: string, simple: () => void, doble: () => void) {
    const pendiente = espera.current[clave];
    if (pendiente != null) {
      // Segundo toque: el primero nunca llegó a ejecutarse, así que acá no hay
      // nada que deshacer.
      window.clearTimeout(pendiente);
      delete espera.current[clave];
      doble();
      return;
    }
    espera.current[clave] = window.setTimeout(() => {
      delete espera.current[clave];
      simple();
    }, DOBLE_MS);
  }

  const golpeCapa = (c: Capa) => golpe(c, () => tocarCapa(c), () => tocarOjo(c));

  /**
   * El tercer gesto del botón de capa: sostenerlo la FIJA sin abrir su paleta.
   *
   * Lo que resuelve: hasta ahora, elegir una capa para que mande sobre lo
   * manipulable —«la prioridad de la capa», como lo dijo Daniel— sólo se
   * conseguía de rebote, abriendo el creador y esperando que se cerrara solo.
   * Sostenido se entra a propósito y la capa queda en borde negro sin relleno,
   * que es justo lo que ese estado significa: manda, pero no está poniendo nada.
   *
   * Suelta lo que la capa fijada NO va a poder manipular —su paleta quedaría
   * montada sobre algo intocable—, pero CONSERVA lo de su propia capa: proteger
   * el módulo recién puesto de las paredes sin perderlo de la mano es justo para
   * lo que existe el gesto. Es la diferencia con `abrirCreador`, que suelta todo
   * porque va a montar una paleta nueva y dos encimadas no se leen.
   *
   * Sin aviso a propósito: el párrafo de aviso vive DEBAJO del lienzo y al
   * aparecer lo encoge, y `enMetros` mide contra la caja viva del lienzo —ese es
   * el bug que movía una pared 40 cm—. Un gesto que se hace justo antes de
   * manipular no puede permitirse mover el piso. El borde negro del botón ya lo
   * dice.
   */
  function fijarCapa(c: Capa) {
    if (c !== 'pared') { setSel([]); setMulti(false); setVanoSel(null); setVecinaSel(null); }
    if (c !== 'area') { setRecintos([]); setMultiAreas(false); setLibreSel(null); setDivSel(null); }
    if (c !== 'mueble') { setMuebleSel(null); setEsquinaSel(null); }
    if (c !== 'cota') setCotaSel(null);
    cerrarHerramientas();
    cerrarCatalogo();
    setModo(null);
    setTrazo(null);
    setCorte(null);
    setMira(null);
    setCapa(c);
    setVer((v) => ({ ...v, [c]: true }));
    setAviso(null);
  }

  /**
   * El temporizador del sostenido y la marca de que ya disparó.
   *
   * `holdFirme` existe porque al levantar el dedo llega un `click` igual, y sin
   * la marca ese click entraría a `golpe` y abriría la paleta que el sostenido
   * acababa de NO abrir. Se limpia en cada apoyo nuevo: si el hold disparó y el
   * dedo se fue del botón sin soltar —ahí el click no llega nunca— la marca no
   * puede quedar viva y comerse el toque siguiente.
   */
  const hold = useRef<number | null>(null);
  const holdFirme = useRef<Capa | null>(null);

  function cortarHold() {
    if (hold.current != null) { window.clearTimeout(hold.current); hold.current = null; }
  }

  function arrancarHold(c: Capa) {
    cortarHold();
    holdFirme.current = null;
    hold.current = window.setTimeout(() => {
      hold.current = null;
      holdFirme.current = c;
      fijarCapa(c);
    }, HOLD_MS);
  }

  useEffect(() => cortarHold, []);

  /** Cierra la paleta de agregar área y apaga la herramienta que estuviera
   *  encendida: dejar el modo vivo sin la paleta a la vista es un editor que
   *  responde a toques que nadie pidió. */
  function cerrarHerramientas() {
    setHerramientas(false);
    setCorrHerr(null);
    // Los modos del ÁREA, enumerados y no `dibujaCaja(modo)`: desde que el
    // rectángulo de paredes dibuja la misma caja, esa pregunta también dice que
    // sí con una herramienta que no es de esta paleta, y cerrar la de áreas le
    // apagaba el gesto a la de paredes.
    if (modo === 'libre' || modo === 'irregular' || modo === 'varita') {
      setModo(null);
      setTrazo(null);
      setMira(null);
    }
  }

  /** La misma puerta de salida, para la paleta de agregar pared. */
  function cerrarHerrPared() {
    setHerrPared(false);
    setCorrHerrP(null);
    if (modo === 'pared' || modo === 'varita-pared' || modo === 'rect-pared') {
      setModo(null);
      setTrazo(null);
      setCorte(null);
      setMira(null);
    }
  }

  /**
   * Tocar afuera cierra la paleta de agregar área.
   *
   * Es el reemplazo del botón X que pidió Daniel (4-oct-2026): «el de salir de
   * la paleta de control, para mí básicamente es darle clic en otro lado que no
   * sea la paleta de control». Antes ese camino NO existía —la paleta sólo se
   * cerraba por su X o volviendo a tocar «+ Área»— así que el botón no se podía
   * borrar sin dejar la paleta pegada.
   *
   * Sólo mientras NO haya herramienta elegida. Si ya eligió rectángulo o
   * varita, el toque en el lienzo ES el trabajo: cerrar ahí sería cancelar el
   * gesto justo cuando lo empieza. Con herramienta encendida la paleta se cierra
   * sola al crear el área, o volviendo a tocar el botón de la capa.
   *
   * Desde que el área abre con el rectángulo puesto, este camino es el del que
   * APAGA la herramienta a mano desde la paleta: ahí vuelve a haber paleta sin
   * modo, y sin esto quedaría pegada.
   */
  useEffect(() => {
    if ((!herramientas && !herrPared) || modo) return;
    const afuera = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-paleta], [data-agregar-area], [data-agregar-pared]')) return;
      cerrarHerramientas();
      cerrarHerrPared();
    };
    // En la fase de captura: así se cierra incluso si el elemento tocado
    // detiene la propagación (el lienzo lo hace para no perder el gesto).
    document.addEventListener('pointerdown', afuera, true);
    return () => document.removeEventListener('pointerdown', afuera, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [herramientas, herrPared, modo]);

  /** ¿Está todo suelto? Ningún elemento tomado y ningún gesto de grupo abierto. */
  const nadaTomado = sel.length === 0 && !multi && vanoSel == null
    && recintos.length === 0 && !multiAreas && divSel == null && cotaSel == null
    && vecinaSel == null && libreSel == null && muebleSel == null;

  /**
   * Un toque en el VACÍO del plano suelta la capa activa.
   *
   * Es la puerta de salida que faltaba, y la pidió Daniel el 6-oct-2026 después
   * de quedar atrapado en ella: «probemos que tocar afuera suelte la capa». El
   * estado sin salida era el de borde negro sin relleno —capa mandando, paleta
   * de agregar ya cerrada sola—: `tocarCapa` sólo apaga la capa si el creador
   * está abierto, así que el toque siguiente sobre el mismo botón lo REABRÍA en
   * vez de liberar, y la única salida segura era el doble toque, que además
   * esconde la capa.
   *
   * Las tres condiciones de la guarda son las que hacen que no se coma ningún
   * otro gesto:
   *
   * - `modo` apagado: con el rectángulo, la varita, la pared o la cota
   *   encendidos, el toque en el vacío ES el trabajo.
   * - `nadaTomado`: con algo tomado, el vacío ya tiene dueño —el agarre remoto
   *   del mueble vive justo ahí— y además es el momento de acomodar lo recién
   *   puesto sin rozar una pared, que es para lo que existe la protección.
   * - el toque tiene que caer en el lienzo y FUERA de todo lo `TOMABLE`: tocar
   *   una pared con la capa de áreas mandando no la agarra (pasa de largo por
   *   `pointer-events: none`) y ahí sí libera, que es exactamente el dedo de
   *   alguien queriendo editar otra cosa.
   *
   * Se resuelve al LEVANTAR y midiendo el corrimiento, no al apoyar: el paneo
   * del plano empieza con un apoyo en el vacío igual que este toque, y soltar la
   * capa cada vez que uno corre el plano sería perderla sin haberla tocado.
   */
  useEffect(() => {
    if (capa == null || modo || !nadaTomado) return;
    let desde: { x: number; y: number; id: number } | null = null;
    const abajo = (e: PointerEvent) => {
      const t = e.target as Element | null;
      desde = t?.closest('[data-lienzo]') && !t.closest(TOMABLE)
        ? { x: e.clientX, y: e.clientY, id: e.pointerId }
        : null;
    };
    const arriba = (e: PointerEvent) => {
      const d = desde;
      desde = null;
      if (!d || d.id !== e.pointerId) return;
      // Corrido = fue un paneo, no un toque. Mismo umbral que usan las áreas
      // para distinguir sus dos gestos.
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) return;
      setCapa(null);
      cerrarCreador(capa);
    };
    document.addEventListener('pointerdown', abajo, true);
    document.addEventListener('pointerup', arriba, true);
    return () => {
      document.removeEventListener('pointerdown', abajo, true);
      document.removeEventListener('pointerup', arriba, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capa, modo, nadaTomado]);

  /**
   * El deslizador de la referencia se esconde al tocar en cualquier otro lado.
   *
   * Es textual el pedido de Daniel: «al darle un tap sale visible la barra de
   * porcentaje y ya al darle clic a otra parte de la pantalla se escondería».
   * Se excluye el propio botón para que el segundo toque del gesto de prender y
   * apagar no se coma cerrando el panel antes de llegar a `golpe`.
   */
  useEffect(() => {
    if (!reglaFondo) return;
    const afuera = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-regla-fondo], [data-referencia]')) return;
      setReglaFondo(false);
    };
    document.addEventListener('pointerdown', afuera, true);
    return () => document.removeEventListener('pointerdown', afuera, true);
  }, [reglaFondo]);

  /**
   * El menú de los tres puntos también se cierra al tocar afuera.
   *
   * Y al cerrarse DESARMA la confirmación de descartar: si el menú se fue con
   * «¿Seguro?» puesto, al volver a abrirlo el primer toque estaría borrando la
   * planta sin pedir nada. El `blur` del botón no sirve acá —ya no hay un botón
   * suelto en la barra, está dentro de un panel que se desmonta—.
   */
  useEffect(() => {
    if (!menu) return;
    const afuera = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-menu-planta], [data-menu-opciones]')) return;
      setMenu(false);
      setConfirmaDescarte(false);
    };
    document.addEventListener('pointerdown', afuera, true);
    return () => document.removeEventListener('pointerdown', afuera, true);
  }, [menu]);

  // ── acciones ─────────────────────────────────────────────────────────────
  /**
   * Mide y avisa el área de cada planta hacia afuera (de ahí sale el precio).
   *
   * ⚠️ NO limpia el aviso, y eso importa. Cuando era un botón sí lo limpiaba
   * —el usuario acababa de pedir la medición, el aviso viejo ya no venía al
   * caso—, pero ahora la llama un efecto en cuanto algo cambia, y limpiando el
   * aviso se comía el que la operación acababa de poner: «Volvió la planta como
   * la trajo el plano. Si no era eso, deshacé con ↶» aparecía y desaparecía en
   * el mismo cuadro. Cada operación limpia su aviso donde corresponde.
   */
  function recalcular() {
    const med = medirSegunVista(planta, vistaObraRef.current);
    setMedicion(med);
    const porPlanta: Record<number, number> = {};
    for (const pl of plantas) {
      porPlanta[pl.n] = pl.n === planta.n ? med.total : medirSegunVista(pl, vistaObraRef.current).total;
    }
    onAreas?.(porPlanta);
  }

  function borrar() {
    if (!sel.length) return;
    marcar();
    planta.paredes = planta.paredes.filter((w) => !sel.includes(w.id));
    // Lo que colgaba de esas paredes queda SUELTO donde estaba, no borrado: ver
    // `reasentarAnclados`. Borrar una pared no puede llevarse en silencio los
    // tomacorrientes que alguien marcó encima.
    reasentarAnclados(planta);
    soltarTodo();
    setMedicion(null);
    setTick((t) => t + 1);
  }

  /**
   * Tira TODO lo editado y vuelve a la planta como la trajo el PDF.
   *
   * Se llamaba «Volver al plano» y Daniel lo leyó —con toda razón— como «salir
   * a la pantalla del plano»: dividía un área, le ponía nombre, tocaba el botón
   * y el editor le devolvía las paredes del importador. Un botón que borra el
   * trabajo no puede tener nombre de navegación, así que ahora dice lo que hace
   * y pide confirmación en dos pasos (no hay modales en este editor).
   *
   * Sigue pasando por `marcar()`, así que incluso confirmado se deshace con ↶.
   */
  function descartarCambios() {
    marcar();
    setPlantas(JSON.parse(original));
    soltarTodo();
    setMedicion(null);
    setModo(null);
    setTrazo(null);
    setCorte(null);
    setMira(null);
    setConfirmaDescarte(false);
    setAviso('Volvió la planta como la trajo el plano. Si no era eso, deshacé con ↶.');
  }

  /**
   * Un botón de capa: un toque la elige, dos la muestran u ocultan.
   *
   * Pasó de ser una pastilla con rótulo y ojo a un cuadrado con ícono el
   * 6-oct-2026, y el 18-oct-2026 **volvió a tener su palabra**, esta vez sin
   * caja: «que venga como la referencia, con texto y sin los recuadros que
   * encierran el ícono». La razón de haberla quitado era el OJO —la palabra más
   * el ojo no entraban en una fila de teléfono y la fila se partía justo donde
   * cae el pulgar—, y el ojo se fue hace rato: lo reemplazó el punto de estado,
   * que se dibuja ENCIMA del ícono y no ocupa ancho. Sin él la palabra entra.
   *
   * El ancho en angosta se queda en 52 px, el mismo que medía la caja, para no
   * mover la cuenta que hace que la fila entre en 390: 208 de las cuatro + 48
   * de los tres huecos + 76 de las flechas + 16 de padding + 24 de la rejilla.
   *
   * Sin caja, el estado se dice con la tinta y no con el relleno: gris = capa
   * dormida, negro = capa activa, y negro con una rayita bajo la palabra = la
   * herramienta está PONIENDO cosas. Son los mismos tres escalones que antes
   * pintaban borde gris / borde negro / relleno negro.
   *
   * Es una función que devuelve JSX y no un componente a propósito: como
   * componente declarado acá dentro, React lo trataría como un tipo nuevo en
   * cada repintado y lo desmontaría y volvería a montar —y el botón perdería el
   * toque a la mitad del gesto—.
   */
  function botonCapa(c: Capa, icono: ReactNode, rotulo: string) {
    const activa = capa === c;
    const creando = creandoEn(c);
    // Negro pleno = ESTA es la herramienta en uso, que es lo que dice la
    // maqueta. «Creando» —la paleta de agregar abierta— y «activa» a secas
    // mandan las dos sobre lo manipulable, pero sólo una está poniendo cosas, y
    // el relleno las separa sin inventar un tercer color.
    const pinta = creando
      ? 'border-stone-900 bg-stone-900 text-white'
      : activa
        ? 'border-stone-900 bg-white text-stone-900'
        : 'border-stone-300 bg-white text-stone-700';
    // La tarjeta sin caja de la referencia. En la compu sigue siendo el cuadrado
    // con borde: ahí la barra no es un pill y el botón necesita su propio marco.
    const caja = angosta
      ? 'flex w-[52px] flex-col items-center gap-1 py-0.5 '
        + (creando || activa ? 'text-stone-900' : 'text-stone-400')
      : `${CAJA_BARRA} ${pinta}`;
    const bajo = rotulo.toLowerCase();
    return (
      <div
        className="relative"
        data-capa={c}
        data-capa-activa={activa ? '' : undefined}
        data-capa-creando={creando ? '' : undefined}
      >
        <button
          type="button"
          // El sostenido se mide acá y el click se descarta si ya disparó: ver
          // `arrancarHold`. `pointerleave` y `pointercancel` lo cortan porque un
          // dedo que se fue del botón no es una pulsación sostenida sobre él.
          onPointerDown={() => arrancarHold(c)}
          onPointerUp={cortarHold}
          onPointerLeave={cortarHold}
          onPointerCancel={cortarHold}
          // El menú contextual del navegador sale justo a los ~500 ms de un
          // dedo quieto y taparía el botón con «copiar imagen».
          onContextMenu={(e) => e.preventDefault()}
          onClick={() => {
            if (holdFirme.current === c) { holdFirme.current = null; return; }
            golpeCapa(c);
          }}
          // `aria-pressed` dice si la paleta de AGREGAR está abierta, que es lo
          // que el toque simple alterna. Que la capa esté mandando es otra cosa
          // y viaja en `data-capa-activa`; que se VEA viaja en `data-capa-ve`.
          // Meterlas todas en el mismo atributo deja al lector de pantalla —y al
          // banco de pruebas— creyendo que el catálogo sigue abierto cuando se
          // cerró solo al poner un módulo.
          aria-pressed={creando}
          aria-label={rotulo}
          // `select-none` y `touch-manipulation` son del sostenido: sin ellas
          // el dedo quieto arranca la selección de texto del ícono y el gesto
          // se convierte en un arrastre de nada.
          className={`${caja} relative select-none touch-manipulation`}
          title={`${rotulo} · un toque para agregar ${bajo}`
            + ` · dos toques para ${ver[c] ? 'ocultar' : 'mostrar'} ${bajo}`
            + ` · sostenido para trabajar sólo con ${bajo}`}
          // Los nombres viejos se conservan: son los que usa el banco de
          // pruebas, y renombrarlos sería romper 60 verificaciones por un cambio
          // de barra. `data-capa-ojo` ya no es un botón aparte, pero sigue
          // apuntando a lo que hay que tocar DOS veces para prender y apagar.
          data-agregar-area={c === 'area' ? '' : undefined}
          data-agregar-pared={c === 'pared' ? '' : undefined}
          data-agregar-mueble={c === 'mueble' ? '' : undefined}
          data-capa-cuerpo={c}
          data-capa-ojo={c}
          data-ojo-areas={c === 'area' ? '' : undefined}
          data-capa-ve={ver[c] ? '' : undefined}
        >
          {/* El punto de estado se cuelga del ÍCONO cuando no hay caja: contra
              una caja de 52 px muerde la esquina, pero sobre una tarjeta de 52
              de ancho y 40 de alto esa misma esquina queda a 14 px del glifo,
              flotando sobre nada. Por eso el ícono viaja envuelto en angosta.
              El aro blanco es lo que lo despega del dibujo del plano. */}
          <span className={angosta ? 'relative flex items-center justify-center' : 'contents'}>
            {icono}
            <span
              className={angosta
                ? 'absolute -left-1.5 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white'
                : 'absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white'}
              style={{ background: ver[c] ? VERDE : ROJO }}
            />
          </span>
          {/* La palabra que pidió Daniel. Sólo en angosta: en la compu el rótulo
              vive en el `title`, que es donde se lee con el mouse encima. */}
          {angosta && <span className="text-[10px] leading-[1.15]">{rotulo}</span>}
          {/* La rayita de «esta herramienta está poniendo cosas»: es lo que
              reemplaza al relleno negro que ya no existe sin caja. */}
          {angosta && creando
            && <span className="absolute -bottom-0.5 h-[2px] w-5 rounded-full bg-stone-900" />}
        </button>
      </div>
    );
  }

  if (!planta) return null;
  const ancho = vista.x1 - vista.x0;
  const alto = vista.y1 - vista.y0;

  /**
   * Las caras que la pastilla de totales llega a mostrar: interna y externa van
   * siempre —son las dos que toda planta tiene— y lote y azotea sólo cuando
   * miden. Se calcula acá y no dentro del JSX porque el chip «+N» del teléfono
   * necesita el MISMO conteo que el `map`, y dos filtros separados es la clase
   * de cosa que se desincroniza en el próximo cambio.
   */
  const carasVivas = CLASES.filter(
    (c) => c === 'interna' || c === 'externa' || (medicion?.[c] ?? 0) > 0.005,
  );

  /**
   * El conteo del botón ⓘ. Se recalcula en cada pintado a propósito: recorre
   * los muebles de todas las plantas y en una casa son decenas, mientras que
   * memorizarlo pediría una llave que cambie cuando el editor MUTA `planta` en
   * el sitio —que es como trabaja todo este archivo— y una llave así no existe
   * sin volver a recorrer lo mismo.
   */
  const cuentaElectrica = resumenElectrico(plantas);


  /**
   * Pinta UN área libre. Está extraído de la vista porque el lienzo lo llama
   * DOS VECES, a dos profundidades distintas: ver `libresAlFondo`.
   */
  const pintarLibre = (l: Libre) => {
      // El filtro de la vista de obra, igual que en las paredes: una
      // terraza que se demuele no se dibuja en «Estado final».
      if (!seVe(l.estado, vistaObra)) return null;
      const act = l.id === libreSel;
      // El color dice en qué columna suma, por `TINTA_CLASE`: verde
      // afuera, marrón —el de las paredes y las regiones de la casa—
      // cuando el que vende la mandó al área interna, y su propio tono
      // el lote y la azotea. Así no hay que abrir la tabla para saber
      // si esa terraza está contando adentro.
      const tinta = TINTA_CLASE[l.clase ?? 'externa'];
      /**
       * El CONTORNO, que es por donde habla el estado. Los dos ejes
       * caben a la vez porque se reparten el dibujo: el relleno sigue
       * diciendo en qué columna suma —eso no lo cambia una obra— y el
       * borde dice si va a existir. Una terraza nueva se lee verde por
       * dentro y azul por fuera, que es exactamente lo que es.
       */
      const borde = tintaDe(l.estado, vistaObra) ?? tinta;
      /** El anillo, cuando el área es un polígono editable. */
      const vs = l.vertices?.length ? l.vertices : null;
      /**
       * La forma: su polígono si lo tiene, sus celdas si la levantó la
       * varita, su rectángulo si se dibujó a mano.
       *
       * El polígono entra acá partido en franjas y no como `points`
       * porque esta lista hace DOS cosas más allá de pintar: ancla el
       * rótulo en la franja más grande y es la que `aristas` convierte
       * en contorno. Con un `<polygon>` suelto habría que escribir las
       * dos cosas de nuevo.
       *
       * Las celdas se pintan SIN borde y el contorno se dibuja aparte
       * con `aristas`: una por una con su punteado, las costuras de
       * adentro quedaban a la vista y una L se leía como dos áreas
       * pegadas.
       */
      const forma: Rect[] = cuerpoLibre(l);
      /**
       * El anillo con ÁNGULOS LIBRES, que es el único que no se pinta
       * con la lista de arriba: `rectangulosDePoligono` descarta
       * cualquier lado que no sea vertical, así que de un triángulo
       * saca cero franjas y `cuerpoLibre` cae a su caja. Pintarla
       * sería dibujar un rectángulo donde el usuario dibujó un
       * triángulo, y el número del rótulo —que sí sale del anillo— no
       * coincidiría con la figura. De ahí el camino aparte:
       * `<polygon>` con sus vértices.
       *
       * Lo que sigue viendo la CAJA es la barrera de la varita, y es
       * un de-más consciente: frena el relleno un poco antes de la
       * diagonal en vez de dejarlo pasar por debajo.
       */
      const irr = vs && l.anguloLibre ? vs : null;
      // Donde va el rótulo: el centro de la celda más grande, no el de
      // la caja — en una L el centro de la caja cae en la esquina que
      // le falta, encima del cuarto vecino. Con ángulos libres no hay
      // franjas de donde elegir, así que va el centroide de área, que
      // es lo único que en un triángulo cae adentro.
      const mayor = forma.reduce((m, r) => (r[2] * r[3] > m[2] * m[3] ? r : m), forma[0]);
      const [rx, ry] = irr
        ? centroidePoligono(irr)
        : [mayor[0] + mayor[2] / 2, mayor[1] + mayor[3] / 2];
      // `data-geo` publica su caja VIVA en metros, igual que la de las
      // paredes: es contra esto que mide el banco, no contra píxeles
      // ni contra el relleno. `data-area` lleva el número que cuenta,
      // que con forma NO es el de la caja.
      return (
        <g key={l.id} data-libre={l.id} data-clase={l.clase ?? 'externa'}
           data-estado={l.estado ?? 'existente'}
           data-geo={`${l.x},${l.y},${l.w},${l.h}`}
           data-area={areaLibre(l).toFixed(4)}
           data-forma={irr ? 'irregular'
             : vs ? 'poli' : (l.celdas?.length ? 'libre' : 'rect')}
           data-vertices={vs ? vs.length : undefined}
           data-fijo={l.fijo ? '' : undefined}
           style={dedo('area')}>
          {/* BLOQUEADA NO RECIBE EL DEDO. No es una comodidad: el lote
              es más grande que la casa y está debajo de todo, así que
              si tomara el toque se comería TODOS los de la planta —los
              cuartos medidos dejarían de poder seleccionarse en el
              dibujo—. Y no hay nada que agarrar: bloqueada no se mueve
              ni se redimensiona. Se elige desde la tabla de áreas, que
              es donde se le cambia el tipo y se le suelta el candado. */}
          {irr ? (
            /* El polígono irregular: UNA figura con su propio borde.
               No pasa por `aristas` porque ahí el contorno se deduce
               de los bordes de las franjas, y acá no hay franjas: el
               anillo ya ES el contorno. */
            <polygon
              points={irr.map(([px, py]) => `${px},${py}`).join(' ')}
              fill={tinta} fillOpacity={act ? 0.22 : 0.13}
              stroke={borde} strokeWidth={act ? 0.07 : 0.045}
              strokeDasharray="0.28 0.16"
              className={l.fijo ? undefined : `${cuerpoAgarrable(act)} cursor-move`}
              style={l.fijo ? { pointerEvents: 'none' } : undefined}
              onPointerDown={l.fijo ? undefined : (e) => tomarLibre(e, l, 'cuerpo')}
            />
          ) : (
            <>
              {forma.map(([cx, cy, cw, ch], i) => (
                <rect
                  key={i}
                  x={cx} y={cy} width={cw} height={ch}
                  fill={tinta} fillOpacity={act ? 0.22 : 0.13}
                  className={l.fijo ? undefined : `${cuerpoAgarrable(act)} cursor-move`}
                  style={l.fijo ? { pointerEvents: 'none' } : undefined}
                  onPointerDown={l.fijo ? undefined : (e) => tomarLibre(e, l, 'cuerpo')}
                />
              ))}
              {aristas(forma).map(([ax, ay, bx, by], i) => (
                <line
                  key={i}
                  x1={ax} y1={ay} x2={bx} y2={by}
                  stroke={borde}
                  strokeWidth={act ? 0.07 : 0.045}
                  strokeDasharray="0.28 0.16"
                  style={{ pointerEvents: 'none' }}
                />
              ))}
            </>
          )}
          {/* Los agarres, sólo en la tomada: cuatro bordes y cuatro
              esquinas, que es lo que pidió Daniel —«agarrar un lado y
              extenderlo. O agarrar dos lados y extenderlo»—.

              De cada lado van DOS piezas: una banda invisible de 30 cm
              que es la que recibe el dedo, y una pastilla chica que es
              la que se ve. Separarlas es lo que deja el agarre gordo
              para la mano y fino para el ojo; con una sola pieza, un
              borde de 30 cm de grueso tapaba el dibujo de abajo.

              Las esquinas se dibujan DESPUÉS de las bandas para que
              ganen el toque: en la esquina las dos bandas se cruzan y
              ahí lo que se quiere es estirar los dos lados.

              No van en la que tiene FORMA: un agarre sobre la caja de
              una L no estira la L, la convierte en rectángulo sin
              avisar. Esa conversión existe, pero como botón de su
              paleta y con nombre. Tampoco en la de vértices, que tiene
              los suyos —uno por lado— justo abajo. */}
          {act && !l.fijo && !l.celdas?.length && !vs && (
            <>
              {([
                ['n', l.x, l.y, l.w, 0, 'cursor-ns-resize'],
                ['s', l.x, l.y + l.h, l.w, 0, 'cursor-ns-resize'],
                ['o', l.x, l.y, 0, l.h, 'cursor-ew-resize'],
                ['e', l.x + l.w, l.y, 0, l.h, 'cursor-ew-resize'],
              ] as const).map(([lado, bx, by, bw, bh, cur]) => (
                <g key={lado}>
                  <rect
                    data-lado={`${l.id}:${lado}`}
                    x={bx - (bw ? 0 : 0.15)} y={by - (bh ? 0 : 0.15)}
                    width={bw || 0.3} height={bh || 0.3}
                    fill="transparent"
                    className={`${AGARRE} ${cur}`}
                    onPointerDown={(e) => tomarLibre(e, l, lado)}
                  />
                  {/* Hueca y con borde, igual que las esquinas: una
                      pastilla maciza verde encima de la línea verde
                      punteada no se distinguía del borde mismo. */}
                  <rect
                    x={bw ? bx + bw / 2 - 0.26 : bx - 0.065}
                    y={bh ? by + bh / 2 - 0.26 : by - 0.065}
                    width={bw ? 0.52 : 0.13} height={bh ? 0.52 : 0.13}
                    rx={0.065}
                    fill="#f8fafc" stroke={tinta} strokeWidth={0.05}
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              ))}
              {([
                ['no', l.x, l.y, 'cursor-nwse-resize'],
                ['ne', l.x + l.w, l.y, 'cursor-nesw-resize'],
                ['so', l.x, l.y + l.h, 'cursor-nesw-resize'],
                ['se', l.x + l.w, l.y + l.h, 'cursor-nwse-resize'],
              ] as const).map(([lado, cx, cy, cur]) => (
                <g key={lado}>
                  <circle
                    data-lado={`${l.id}:${lado}`}
                    cx={cx} cy={cy} r={0.3}
                    fill="transparent"
                    className={`${AGARRE} ${cur}`}
                    onPointerDown={(e) => tomarLibre(e, l, lado)}
                  />
                  <circle
                    cx={cx} cy={cy} r={0.14}
                    fill="#f8fafc" stroke={tinta} strokeWidth={0.05}
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              ))}
            </>
          )}
          {/* Los agarres del polígono: UNO POR LADO, y cada uno hace
              las dos cosas que Daniel pidió para las formas
              irregulares —«dividir lados de áreas» y «cambiar la
              geometría del área»—. Arrastrarlo empuja el lado entero
              en perpendicular; tocarlo sin arrastrar le mete un
              vértice y lo parte en dos mitades que ya se arrastran por
              separado. Con esos dos movimientos se llega a cualquier
              forma ortogonal partiendo de un rectángulo.

              Dos piezas por lado, igual que en el rectángulo: una
              banda invisible de 30 cm que recibe el dedo y una línea
              fina encima que es la que se ve. El punto del medio es la
              señal de que ese lado se puede partir ahí.

              En el ORTOGONAL las esquinas no llevan agarre: un vértice
              suelto daría diagonales, que es justamente lo que ese
              método no admite —la forma se arma empujando lados—. En
              el IRREGULAR sí las llevan, y es toda la diferencia
              entre los dos: ahí el vértice se arrastra a donde sea y
              el lado en diagonal ya no se empuja, sólo se parte de un
              toque para tener un punto más con el que modelar. */}
          {act && !l.fijo && vs && vs.map(([ax, ay], i) => {
            const [bx, by] = vs[(i + 1) % vs.length];
            const horiz = Math.abs(ay - by) < 1e-6;
            const vert = Math.abs(ax - bx) < 1e-6;
            // El lado de largo cero que deja una partición no se
            // dibuja: no tiene dirección todavía y su banda caería
            // encima de las dos mitades, robándoles el toque.
            if (horiz && vert) return null;
            // Y en el ortogonal tampoco se dibuja una diagonal: ahí
            // no puede haberla, y si quedó una es basura que no hay
            // que ofrecer para empujar.
            const diagonal = !horiz && !vert;
            if (diagonal && !irr) return null;
            return (
              <g key={`lado-${i}`}>
                <line
                  data-lado-poli={`${l.id}:${i}`}
                  x1={ax} y1={ay} x2={bx} y2={by}
                  stroke="transparent" strokeWidth={0.3}
                  className={`${AGARRE} ${nodo === 'partir' ? 'cursor-crosshair'
                    : diagonal ? 'cursor-crosshair'
                    : horiz ? 'cursor-ns-resize' : 'cursor-ew-resize'}`}
                  onPointerDown={(e) => tomarLadoPoli(e, l, i)}
                />
                <line
                  x1={ax} y1={ay} x2={bx} y2={by}
                  stroke={tinta} strokeWidth={nodo === 'partir' ? 0.09 : 0.06}
                  style={{ pointerEvents: 'none' }}
                />
                {/* El punto del medio existe SÓLO con la tijera puesta.
                    Antes se dibujaba siempre, y Daniel lo mandó esconder
                    el 8-oct-2026: «no deberían de verse esos puntos
                    blancos porque confunden, de que se pueden generar
                    nuevos nodos». Tenía razón, y desde que partir dejó
                    de pasar sin tijera el punto además MENTÍA: anunciaba
                    un nodo donde el toque sólo empuja el lado. */}
                {nodo === 'partir' && (
                  <circle
                    data-nodo-partir={`${l.id}:${i}`}
                    cx={(ax + bx) / 2} cy={(ay + by) / 2} r={0.17}
                    fill="#f8fafc" stroke={tinta} strokeWidth={0.045}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </g>
            );
          })}
          {/* LOS VÉRTICES, sólo en el irregular. Es el agarre que hace
              existir al tercer método: cada punto se arrastra a donde
              sea y los dos lados que salen de él quedan en diagonal.

              Van DESPUÉS de los lados para que ganen el toque en la
              esquina, por lo mismo que las esquinas del rectángulo van
              después de las bandas: ahí las dos bandas se cruzan y lo
              que se quiere es mover el punto, no empujar un lado.

              Dos piezas, igual que todos los demás agarres: un disco
              invisible de 30 cm que recibe el dedo y uno chico que es
              el que se ve. Y el que se ve es MACIZO y no hueco, al
              contrario del punto del medio de un lado: son dos cosas
              distintas —este se mueve, ese parte— y tienen que
              distinguirse de un vistazo. */}
          {act && !l.fijo && irr && irr.map(([px, py], i) => (
            <g key={`vert-${i}`}>
              <circle
                data-vertice={`${l.id}:${i}`}
                cx={px} cy={py} r={0.3}
                fill="transparent"
                className={`${AGARRE} ${nodo === 'quitar'
                  ? 'cursor-crosshair' : 'cursor-move'}`}
                onPointerDown={(e) => tomarVertice(e, l, i)}
              />
              {/* Con BORRAR NODO puesto los puntos crecen y se pintan
                  de rojo: el toque los saca, no los mueve, y eso es
                  algo que conviene ver antes de apoyar el dedo. */}
              <circle
                cx={px} cy={py} r={nodo === 'quitar' ? 0.18 : 0.13}
                fill={nodo === 'quitar' ? '#b91c1c' : tinta}
                stroke="#f8fafc" strokeWidth={0.04}
                style={{ pointerEvents: 'none' }}
              />
            </g>
          ))}
          {/* El rótulo va adentro y sin eventos: si tomara el toque,
              tocar el nombre no seleccionaría el área. */}
          <text
            x={rx} y={ry}
            textAnchor="middle" dominantBaseline="middle"
            fill={TINTA_ROTULO[l.clase ?? 'externa']}
            fontSize={0.22} fontWeight={600}
            style={{ ...SOLO_PLANO, pointerEvents: 'none' }}
          >
            <tspan x={rx} dy="-0.13">{l.nombre}</tspan>
            <tspan x={rx} dy="0.28" fontWeight={400}>
              {m2(areaLibre(l))} m²
            </tspan>
          </text>
        </g>
      );
  };

  /**
   * El LOTE se pinta por debajo de las áreas MEDIDAS; el resto, por encima.
   *
   * Daniel lo reportó el 14-oct-2026: «estoy intentando enviar hacia atrás el
   * área del lote y no hay manera de que se vaya al final, queda siempre
   * delante de las demás áreas dentro de la casa». El botón no estaba roto:
   * `ordenarLibre` reordena bien, pero reordena DENTRO de `planta.libres`, y
   * los cuartos no viven ahí —son `medicion.regiones`, otra pasada del lienzo
   * que se pintaba antes—. Mandar el lote al fondo de su propia lista lo
   * dejaba igual de encima de los cuartos, y en una planta donde el lote es
   * el ÚNICO libre el botón ni siquiera tenía contra quién reordenar.
   *
   * Se parte por CLASE y no por un campo nuevo porque el lote ya es, por
   * definición, el suelo: es más grande que la casa y la contiene. Las demás
   * —terraza, cochera, azotea— se quedan donde estaban, así que una terraza
   * dibujada a mano sobre un cuarto medido se sigue viendo.
   */
  const libresAlFondo = (planta.libres ?? []).filter((l) => l.clase === 'lote');
  const libresEncima = (planta.libres ?? []).filter((l) => l.clase !== 'lote');
  return (
    // `relative` por la hoja de la tabla: se monta con `absolute inset-0` y así
    // tapa el editor entero sin pelearse con los z-index de la app que lo
    // contiene —el editor ya vive dentro de un modal—.
    // Y `grid` de dos filas —cabecera arriba, todo lo demás en UNA SOLA celda—
    // porque las dos barras de botones no son filas: FLOTAN sobre el lienzo.
    // Ver la nota de la zona de trabajo, abajo.
    <div className="relative grid h-full min-h-0 grid-rows-[auto_1fr]">
      {/* ── cabecera ──
          Es del EDITOR y no de quien lo abre, aunque antes fuera al revés: la
          misma fila la dibujaban `PlantaPage` y `EditorPlantaModal` cada uno por
          su lado, y con AdVentas de por medio eran tres copias de lo mismo. El
          dibujo del 6-oct-2026 le agrega guardar y exportar, así que mantener
          tres copias era mantener tres veces cada botón nuevo. */}
      {/* El cinturón del recorte va ACÁ AFUERA y es OSCURO a propósito.
          Daniel, 21-oct-2026, sobre su 17 Pro Max: «ya propiamente la pantalla
          de plantas, lo que es del editor, ahí queda completamente arriba, lo
          que hace imposible poder devolverte con los botones de la barra
          superior». Es el mismo recorte que se come la franja del shell, pero
          acá duele más porque en pantalla completa esta cabecera es la ÚNICA
          salida: con la X debajo de la isla, el editor no se cierra.
          Oscuro y no blanco porque `black-translucent` obliga a iOS a pintar la
          hora en BLANCO: sobre esta cabecera blanca el reloj desaparecería. */}
      <div className="col-start-1 row-start-1 bg-brand-gray-500 pt-[var(--franja-arriba)]">
      <div data-cabecera="" className="flex items-center gap-2 border-b
                      border-stone-200 bg-white px-3 py-2">
        {onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            className={CAJA_CHICA}
            data-cerrar=""
            aria-label="Cerrar"
            title="Cerrar el editor"
          >
            <X size={16} weight="bold" />
          </button>
        )}
        <span className="min-w-0 flex-1 truncate text-center text-sm font-semibold text-stone-700">
          {titulo ?? planta.nombre}
        </span>
        {acciones}
        {/* Guardar es el dibujo del 6-oct-2026 y todavía no tiene función
            detrás. Está igual porque conectarlo después es una línea, y porque
            la cabecera se diseña una vez. Sin `onGuardar` puesto lo DICE en vez
            de quedarse mudo: un botón que no responde se lee como roto, y eso
            cuesta más que no tenerlo. */}
        <button
          type="button"
          onClick={() => (onGuardar
            ? onGuardar()
            : setAviso('Guardar todavía no está conectado.'))}
          className={CAJA_CHICA}
          data-guardar-planta=""
          aria-label="Guardar"
          title="Guardar"
        >
          <FloppyDisk size={16} weight="bold" />
        </button>

        {/* Los tres puntos en lugar del botón de exportar, por pedido de Daniel
            el 6-oct-2026: «el de exportar lo cambiamos por uno que sean tres
            puntos y ahí saldrían varias opciones, que entre ellas estaría el de
            descartar». Resuelve el problema que tenía Descartar, que no entraba
            en una cabecera de cuatro elementos y había quedado abajo con las
            flechas: acá sí tiene lugar, y como el menú hay que abrirlo primero,
            borrar el trabajo sigue costando dos toques.

            Exportar queda DENTRO como opción —no se perdió, cambió de forma—. */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenu((v) => !v)}
            className={CAJA_CHICA}
            data-menu-planta=""
            aria-label="Más opciones"
            aria-expanded={menu}
            title="Más opciones"
          >
            <DotsThreeVertical size={16} weight="bold" />
          </button>
          {menu && (
            <div
              className="absolute right-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-[10px]
                         border border-stone-200 bg-white py-1 shadow-lg"
              data-menu-opciones=""
            >
              {/* Va PRIMERO en el menú, y no detrás de Exportar, porque es lo
                  que se busca cuando hay un cliente enfrente. Sigue adentro del
                  menú por falta de lugar: la cabecera ya estaba llena cuando
                  Descartar tuvo que bajar acá (6-oct-2026). */}
              <button
                type="button"
                onClick={() => { setMenu(false); setRender('eligiendo'); }}
                disabled={render === 'generando'}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs
                           text-stone-700 hover:bg-stone-100 disabled:opacity-50"
                data-render-planta=""
              >
                <Cube size={15} weight="bold" />
                {render === 'generando' ? 'Generando la lámina…' : 'Generar planta render'}
              </button>
              {/* Pegado al render porque son la misma pregunta —«dame la
                  lámina»— con dos respuestas: una imagen para vender y un plano
                  para construir. La escala va EN EL RÓTULO y no escondida: es lo
                  que diferencia un PDF que se mide de uno que sólo se mira. */}
              <button
                type="button"
                onClick={() => { setMenu(false); exportarPDF(); }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs
                           text-stone-700 hover:bg-stone-100"
                data-pdf-planta=""
                title="La planta que se está viendo, en vectores y a escala, con sus cotas"
              >
                <Ruler size={15} weight="bold" />
                {`Planta en PDF (1:${ESCALA_POR_DEFECTO})`}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenu(false);
                  if (onExportar) onExportar();
                  else setAviso('Exportar todavía no está conectado.');
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs
                           text-stone-700 hover:bg-stone-100"
                data-exportar-planta=""
              >
                <Export size={15} weight="bold" />
                Exportar
              </button>
              {/* Descartar sigue en DOS pasos adentro del menú: el ítem cambia
                  de rótulo y de color en vez de abrir un diálogo, porque un
                  ícono rojo no alcanza para decir «se va a borrar todo». */}
              <button
                type="button"
                onClick={() => {
                  if (!confirmaDescarte) { setConfirmaDescarte(true); return; }
                  setConfirmaDescarte(false);
                  setMenu(false);
                  descartarCambios();
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs
                            ${confirmaDescarte
                              ? 'bg-red-600 font-medium text-white'
                              : 'text-red-700 hover:bg-red-50'}`}
                data-descartar=""
                title="Tira lo editado y deja la planta como la trajo el plano"
              >
                <Broom size={15} weight="bold" />
                {confirmaDescarte ? '¿Seguro? Se borra lo editado' : 'Descartar cambios'}
              </button>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* ── áreas, nivel y referencia ──
          FLOTA sobre el lienzo: misma celda de la rejilla, pegada arriba.

          Es la segunda vuelta del pedido del 8-oct-2026. La primera se limitó a
          pintarla del color del lienzo y Daniel la vio igual en el celular: «se
          sigue viendo la barra gris, que de hecho corta el plano, el texto y lo
          que se ve detrás, y se ve todavía una línea divisoria entre esa zona de
          botones y el plano». Tenía razón, y por dos motivos que el color no
          toca: pintada seguía siendo una FILA del flujo, así que le comía alto
          al dibujo —de ahí el plano cortado abajo en su foto— y el `border-b`
          seguía dibujando la raya. Flotando no corta nada: el plano corre entero
          por debajo.

          La barra NO recibe el dedo; sólo sus botones. Así el espacio vacío
          entre ellos sigue siendo plano que se puede panear. */}
      <div className="pointer-events-none z-10 col-start-1 row-start-2 flex items-center
                      gap-2 self-start px-3 py-2">
        {/* La pastilla de los totales. Reemplaza al renglón de abajo Y al
            botón de tabla: tocar cualquiera de las caras abre el desglose,
            que es lo que pidió Daniel —«al darle clic a cualquiera de esos dos
            desplegaría el modal de desglosado de las áreas»—. Es un solo botón
            con varias caras y no varios botones pegados, porque todas van al
            mismo lado y partirlo sólo agregaría bordes en el medio.

            Los números crudos siguen acá, para que el banco de pruebas mida
            lo que el editor calculó y no lo que quedó escrito con coma.

            Desde el 7-oct-2026 las clases son cuatro, pero la pastilla muestra
            SÓLO las que tienen algo: interna y externa van siempre —son las dos
            que toda planta tiene— y lote y azotea aparecen cuando miden. No es
            por estética: cuatro caras de 4,5 rem son 288 px y esta barra lleva
            además el nivel y la referencia, así que en un teléfono la cara
            «Lote 0,00» empujaría afuera a algo que sí se usa. El cero igual se
            lee en el desglose, que siempre tiene las cuatro columnas. */}
        <button
          type="button"
          onClick={() => { encenderAreas(); setTabla(true); }}
          className="pointer-events-auto flex h-11 items-stretch text-left"
          data-abrir-tabla=""
          data-totales={medicion ? '' : undefined}
          data-interna={medicion?.interna.toFixed(4)}
          // CUÁNTAS son, no sólo cuánto suman. Dos recintos que se funden
          // porque se abrió una pared casi no mueven el total —el área fundida
          // es la suma de las dos— así que el total solo no delata una fuga de
          // la inundación. La cuenta sí.
          data-regiones={medicion?.regiones.length}
          data-externa={medicion?.externa.toFixed(4)}
          data-lote={medicion?.lote.toFixed(4)}
          data-azotea={medicion?.azotea.toFixed(4)}
          data-delta={medicion?.delta.toFixed(4)}
          aria-label="Ver el desglose de las áreas"
          title="Ver el desglose de las áreas"
        >
          {carasVivas
            .map((c, i, vivas) => (
              <span
                key={c}
                data-cara={c}
                /* En el teléfono se ve SÓLO la primera cara —Interna, que es la
                   que fija el precio—; las otras se esconden y las reemplaza el
                   chip «+N» de abajo, que lleva al mismo desglose donde ya viven
                   las cuatro con su nombre completo.

                   Daniel lo eligió el 7-oct-2026 con las tres opciones enfrente.
                   El motivo es de espacio medido, no de gusto: con el nivel y la
                   referencia la pastilla se queda con ~176 px de los 366 de un
                   teléfono, así que cuatro caras tocaban a 44 px cada una y la
                   referencia se salía de la pantalla. Y mostrarlas afuera
                   duplicaba el desglose, que es justo lo que el botón abre.

                   Va por CSS puro (`hidden sm:flex`), sin estado ni listener de
                   resize: el único lugar donde esto puede desincronizarse es el
                   redondeo de las puntas, y por eso la última cara pide su
                   `sm:rounded-r` y el chip el suyo sin prefijo. */
                /* `bg-white` igual que el nivel y la referencia: desde que las
                   barras flotan por encima del lienzo, una pastilla sin fondo
                   deja ver las paredes de atrás y el número se pierde. Daniel,
                   9-oct-2026: «donde viene el área se ve transparente por lo
                   que se pierde; eso debería ser fondo blanco como están los
                   demás botones». */
                className={`min-w-[4.5rem] flex-col items-center justify-center border
                            bg-white px-2
                            ${i === 0 ? 'flex rounded-l-[10px]' : 'hidden border-l-0 sm:flex'}
                            ${i === vivas.length - 1 ? 'sm:rounded-r-[10px]' : ''}`}
                style={{ borderColor: TINTA_CLASE[c], color: TINTA_CLASE[c] }}
              >
                {/* Dos renglones en 44 px de alto y 56 px útiles de ancho: el
                    número tiene que entrar en UNA línea o la caja se pasa de
                    alto y el nombre se recorta por arriba —que es exactamente
                    como lo vio Daniel en el celular el 7-oct-2026—.
                    «46,84 m²» en 16 px bold pedía ~70 px y se partía en dos.
                    De ahí las tres cosas de acá abajo: 15 px en vez de 16, la
                    unidad en 9 px pegada al número en la MISMA línea, y
                    `whitespace-nowrap` para que no haya corte posible. */}
                <span className="text-[10px] leading-[1.1]">{ROTULO_CLASE[c]}</span>
                <span className="whitespace-nowrap text-[15px] font-bold leading-[1.15] tabular-nums">
                  {medicion ? m2(medicion[c]) : '—'}
                  {medicion && <span className="ml-0.5 text-[9px] font-semibold">m²</span>}
                </span>
              </span>
            ))}
          {/* El cierre de la pastilla en el teléfono: dice cuántas caras quedaron
              escondidas y es la punta derecha redondeada. Desaparece en `sm`,
              donde la última cara vuelve a ser la punta. */}
          {carasVivas.length > 1 && (
            <span
              data-cara-resto=""
              className="flex items-center justify-center rounded-r-[10px] border border-l-0
                         border-stone-300 bg-white px-2.5 text-xs font-semibold text-stone-500
                         sm:hidden"
            >
              +{carasVivas.length - 1}
            </span>
          )}
        </button>

        {/* El nivel. Va siempre, aunque la planta sea una sola: la maqueta lo
            dibuja así y además dice QUÉ se está editando, que en un modelo de un
            nivel es información igual de útil. */}
        <div className="pointer-events-auto relative">
          <select
            className="h-11 appearance-none rounded-[10px] border border-stone-300 bg-white
                       pl-3 pr-8 text-sm font-medium text-stone-700"
            value={iPlanta}
            data-nivel=""
            aria-label="Nivel"
            onChange={(e) => {
              setIPlanta(Number(e.target.value));
              soltarTodo();
              setMedicion(null);
              setModo(null);
              setTrazo(null);
              setCorte(null);
              setMira(null);
              setAviso(null);
            }}
          >
            {plantas.map((pl, i) => (
              <option key={pl.n} value={i}>{pl.nombre}</option>
            ))}
          </select>
          <CaretDown
            size={14} weight="bold"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-500"
          />
        </div>

        {/* EL RESUMEN DE CANTIDADES, a la derecha del nivel y con el símbolo de
            info, que es donde y cómo lo pidió Daniel.

            Cuenta TODA LA CASA y no el nivel a la vista —«total de la casa, una
            línea por tipo de pieza»—, y por eso está al lado del selector de
            nivel y no adentro: lo que dice vale igual con cualquier nivel
            elegido.

            Nace sin nada que contar y eso es correcto: una planta sin eléctrico
            abre la caja y lee «todavía no hay nada contado», que es información
            —dice que la caja existe y qué va a mostrar— y no un error. */}
        {/* EL BOTÓN DE TIPOS DE VISTA, que es la segunda mitad del pedido de
            Daniel del 10-oct-2026: «habría un botón de tipos de vista en el que
            objetos nuevos se pondrían en azul, objetos a demoler en rojo».

            Va ACÁ ARRIBA y no en la barra de capas de abajo, que es donde
            conceptualmente sería su sitio, por una razón medida: esa barra ya
            ocupa 372 de los 390 px de un teléfono —24 de padding + 24 de la
            rejilla + 68 de las flechas + 208 de las cuatro cajas + 48 de los
            huecos— y una quinta caja manda la última capa a un segundo renglón.

            Se esconde cuando la planta no tiene NADA marcado: mientras todo es
            existente las siete vistas dibujan lo mismo, y un control que no
            cambia nada es ruido. Aparece solo al marcar la primera pieza. */}
        {hayEstados && (
          <div className="pointer-events-auto relative">
            <button
              type="button"
              onClick={() => setMenuVista((v) => !v)}
              className={`flex h-11 w-11 items-center justify-center rounded-[10px] border
                          ${menuVista
                            ? 'border-stone-700 bg-stone-700 text-white'
                            : 'border-stone-300 bg-white text-stone-600'}`}
              aria-label={`Tipos de vista: ${ROTULO_VISTA[vistaObra]}`}
              aria-pressed={menuVista}
              data-abrir-vistas=""
              data-vista-obra={vistaObra}
              title={`${ROTULO_VISTA[vistaObra]} · ${AYUDA_VISTA[vistaObra]}`}
            >
              {/* El martillo de las paletas, y SOLO el icono: lo pidió Daniel el
                  10-oct-2026 —«cambies ese icono del estado por el mismo martillo
                  de la paleta flotante y que sea solo el icono porque ese icono
                  de hecho quiero reservarlo para después agregar concepto de
                  capas»—. El martillo ya significa «estado de obra» en las seis
                  paletas; el de capas queda libre para cuando existan capas.
                  Con el rótulo afuera, cuál vista está puesta se lee en el menú
                  —la fila marcada— y en el `title`. */}
              <Hammer size={19} weight="bold" />
            </button>
            {menuVista && (
              <div
                className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[12rem] rounded-[10px]
                           border border-stone-300 bg-white py-1 shadow-lg"
                data-vistas=""
              >
                {VISTAS_OBRA.map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-vista={v}
                    aria-pressed={vistaObra === v}
                    onClick={() => { setMenuVista(false); cambiarVista(v); }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm
                                ${vistaObra === v ? 'font-medium text-stone-900' : 'text-stone-600'}`}
                    title={AYUDA_VISTA[v]}
                  >
                    {/* Las manchitas dicen de qué está hecha la vista mejor que
                        cualquier rótulo: la de «Estado actual» se lee gris y
                        roja, la de «Planteado» gris y azul. Y leen la tinta de
                        la VISTA, no la del estado: por eso las dos monocromas
                        salen todas grises, que es exactamente lo que son. */}
                    <span className="flex gap-0.5">
                      {ESTADOS.map((e) => (
                        <span
                          key={e}
                          className="h-2.5 w-2.5 rounded-[2px]"
                          style={{
                            background: seVe(e, v) ? (tintaDe(e, v) ?? '#a8a29e') : 'transparent',
                            border: seVe(e, v) ? undefined : '1px solid #e7e5e4',
                          }}
                        />
                      ))}
                    </span>
                    {ROTULO_VISTA[v]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="pointer-events-auto relative">
          <button
            type="button"
            onClick={() => setResumen((v) => !v)}
            className={`flex h-11 w-11 items-center justify-center rounded-[10px] border
                        ${resumen
                          ? 'border-stone-700 bg-stone-700 text-white'
                          : 'border-stone-300 bg-white text-stone-600'}`}
            aria-label="Resumen de cantidades"
            aria-pressed={resumen}
            data-abrir-resumen=""
            title="Resumen de cantidades de toda la casa"
          >
            <Info size={19} weight="bold" />
          </button>
          {resumen && (
            <div
              /* Anclado a la DERECHA y abriendo hacia la izquierda: este botón
                 queda cerca del borde en un teléfono, y colgando de `left-0`
                 la caja se salía de los 390 px con las cantidades —que son lo
                 único que se viene a leer— cortadas afuera de la pantalla. */
              className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[13rem] rounded-[10px]
                         border border-stone-300 bg-white py-1 shadow-lg"
              data-resumen=""
            >
              <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-1.5">
                <Lightning size={14} weight="bold" color={TINTA_ELECTRICO} />
                <span className="text-xs font-medium text-stone-600">Eléctrico</span>
                <span className="ml-auto text-[11px] text-stone-400">toda la casa</span>
              </div>
              {cuentaElectrica.length === 0 ? (
                <p className="px-3 py-2 text-xs text-stone-400">Todavía no hay nada contado.</p>
              ) : (
                cuentaElectrica.map((f) => (
                  /* La altura va en la fila y no escondida tras un desplegable:
                     es la mitad del dato —«seis tomas» no se puede instalar,
                     «seis tomas a 0,30» sí— y una pieza que alguien subió a
                     otra altura abre su propia línea en vez de esconderse
                     dentro del total. La luz de cielo raso no lleva ninguna. */
                  <div key={`${f.tipo}|${f.altura ?? ''}`}
                       data-cuenta={f.tipo} data-cantidad={f.cantidad}
                       data-altura={f.altura ?? ''}
                       className="flex items-baseline gap-2 px-3 py-1 text-sm">
                    <span className="text-stone-700">{f.nombre}</span>
                    {f.altura !== null && (
                      <span className="font-mono text-[11px] text-stone-400">
                        h {m2(f.altura)}
                      </span>
                    )}
                    <span className="ml-auto font-bold tabular-nums text-stone-800">
                      {f.cantidad}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* La REFERENCIA: el plano de fondo. Un toque despliega el porcentaje,
            dos lo prenden y apagan —el mismo par de gestos que las capas de
            abajo, para no tener dos idiomas en la misma pantalla—. */}
        <div className="pointer-events-auto relative ml-auto">
          <button
            type="button"
            onClick={() => golpe('fondo',
              () => setReglaFondo((v) => !v),
              () => { setReglaFondo(false); setVerFondo((v) => !v); })}
            className={`${CAJA} ${reglaFondo
              ? 'border-stone-900 bg-stone-900 text-white'
              : 'border-stone-300 bg-white text-stone-700'}`}
            data-referencia=""
            data-referencia-ve={verFondo ? '' : undefined}
            aria-pressed={reglaFondo}
            aria-label="Referencia"
            title={'Referencia · un toque para el porcentaje'
              + ` · dos toques para ${verFondo ? 'ocultar' : 'mostrar'} el plano de fondo`}
          >
            <SelectionBackground size={20} weight="bold" />
            <span
              className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white"
              style={{ background: verFondo ? VERDE : ROJO }}
            />
          </button>
          {reglaFondo && (
            // `data-paleta` para que el toque adentro no cuente como «afuera»,
            // igual que las paletas flotantes del lienzo.
            <div
              data-paleta=""
              data-regla-fondo=""
              className="absolute right-0 top-full z-20 mt-1.5 flex w-11 flex-col items-center
                         gap-1 rounded-[10px] border border-stone-200 bg-stone-100 py-3"
            >
              {/* El deslizador va rotado y no vertical por CSS: `writing-mode`
                  vertical en un `range` es reciente y todavía se pinta distinto
                  en cada navegador. Rotado es el mismo control de siempre, y el
                  contenedor le reserva el alto que ocupa ya girado. */}
              <div className="relative h-36 w-6">
                <input
                  type="range" min={0} max={1} step={0.05}
                  value={opacidad}
                  onChange={(e) => setOpacidad(Number(e.target.value))}
                  className="absolute left-1/2 top-1/2 w-36 -translate-x-1/2 -translate-y-1/2 -rotate-90"
                  aria-label="Opacidad del plano de referencia"
                  data-opacidad=""
                />
              </div>
              <span className="text-xs font-semibold tabular-nums text-stone-700">
                {Math.round(opacidad * 100)} %
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── lienzo ── */}
      {/* Los gestos de varios dedos van en el CONTENEDOR y no en el `<svg>`: el
          tercer dedo puede caer sobre una pared, sobre un mueble o sobre el
          vacío, y desde acá los tres llegan igual. Ver `dedosAbajo`. */}
      <div ref={lienzoRef}
           className="relative col-start-1 row-start-2 min-h-0 bg-stone-50"
           onTouchStart={dedosAbajo}
           onTouchMove={dedosMueven}
           onTouchEnd={dedosArriba}
           onTouchCancel={dedosArriba}>
        <TransformWrapper
          // Por `onInit` y no por `ref`: la librería declara `ref` como PROP
          // —estilo React 19— y en React 18 eso imprime el aviso «`ref` is not a
          // prop» en cada montaje. `onInit` entrega el mismo objeto, sin ruido.
          onInit={(z) => { zoomRef.current = z; }}
          // Con la mira puesta, el zoom tiene que volver a pintarla: su tamaño
          // sale de la caja del lienzo, que acaba de cambiar. Ver `hayPunteria`.
          onTransform={() => { if (hayPunteria.current) setTick((k) => k + 1); }}
          minScale={0.4} maxScale={8}
          limitToBounds={false}
          doubleClick={{ disabled: true }}
          panning={{ excluded: [AGARRE] }}
        >
          <TransformComponent wrapperClass="!h-full !w-full" contentClass="!h-full !w-full">
            <svg
              ref={svgRef}
              viewBox={`${vista.x0} ${vista.y0} ${ancho} ${alto}`}
              // Con un mueble tomado el lienzo ENTERO queda excluido del paneo,
              // y es la única manera de que el agarre remoto funcione: cortar la
              // propagación del `pointerdown` no alcanza porque
              // `react-zoom-pan-pinch` engancha `mousedown`, que es otro evento.
              // Medido: sin esto el plano se trasladaba JUNTO con el mueble y,
              // como `enMetros` lee contra la caja del lienzo —que ya incluye ese
              // traslado—, el módulo caminaba un solo paso y se congelaba.
              //
              // Es el costo que Daniel aceptó: mientras haya un mueble tomado,
              // arrastrar en el vacío lo mueve; un toque corto lo suelta y el
              // paneo vuelve.
              //
              // El primer tiempo lo desactiva por el mismo motivo que a
              // `cuerpoAgarrable`: el toque que acaba de tomar el mueble repinta
              // antes de que la librería de zoom mire, y sin esto el lienzo
              // entero quedaba excluido justo cuando lo que se quería era panear.
              className={`h-full w-full touch-none${
                muebleSel != null && !enPrimerTiempo() ? ` ${AGARRE}` : ''}`}
              // El lienzo tiene NOMBRE PROPIO porque no es el único `<svg>` de
              // la pantalla: cada ícono de Phosphor también lo es. Mientras los
              // íconos vivieron todos en la barra de abajo, un
              // `querySelector('svg')` caía por orden del DOM en el lienzo y
              // parecía bastar; el día que subió uno a la barra de arriba
              // (5-oct-2026) el banco de pruebas empezó a medir contra la
              // matriz de una escoba de 17 px y los módulos «caían» a 15 m.
              data-lienzo=""
              // El `pointerdown` del lienzo entero existe por una sola cosa: el
              // agarre remoto del mueble. Con nada tomado no hace nada y el
              // toque sigue de largo hasta el paneo, como siempre.
              onPointerDown={tomarRemoto}
              onPointerMove={arrastrar}
              onPointerUp={soltar}
              onPointerCancel={soltar}
              data-tick={tick}
            >
              {/* ╔═══════════════════════════════════════════════════════════╗
                  ║ TODO EL DIBUJO VA DENTRO DE ESTE `<g>`, hasta su cierre   ║
                  ║ al final del lienzo. Los hijos NO están sangrados un      ║
                  ║ nivel más a propósito: son mil líneas y re-sangrarlas     ║
                  ║ convertiría un cambio de dos líneas en un diff ilegible.  ║
                  ╚═══════════════════════════════════════════════════════════╝

                  Existe sólo para que la LUPA pueda clonarlo con un `<use>`.
                  Ésa es la diferencia de fondo con la lupa de Láminas, que
                  amplía una pirámide de tiles de un PDF ya rasterizado y pide
                  su propio nivel para no verse pastosa: acá el plano es SVG
                  vectorial, así que ampliar es sólo mirarlo con otro `viewBox`
                  y sale NÍTIDO a cualquier aumento, sin pedir nada.

                  Y se clona en vez de volver a montar el árbol en React porque
                  son las mismas capas vivas: un `<use>` sigue a su original en
                  el mismo cuadro, así que la lupa no se puede desincronizar del
                  plano ni cuesta un segundo render. */}
              <g id={ESCENA}>
              {verFondo && fondo && fondoCaja && (
                <image
                  href={fondo}
                  x={fondoCaja.x} y={fondoCaja.y}
                  width={fondoCaja.w} height={fondoCaja.h}
                  opacity={opacidad}
                  style={{ pointerEvents: 'none' }}
                />
              )}

              {/* Las áreas medidas, DEBAJO de las paredes.

                  Debajo y no encima por dos razones que van juntas: encima, sus
                  rectángulos se comían el toque de las paredes —que es el gesto
                  principal del editor— y además tapaban el plano de fondo, que
                  es lo que no se puede perder.

                  Van transparentes mientras no están tomadas: el área ya se dice
                  con el rótulo, y pintar los ocho cuartos de colores para
                  «mostrar que se miden» taparía el mobiliario del PDF. Lo que
                  hace falta es poder TOCARLAS, y un relleno transparente recibe
                  el toque igual que uno de color.

                  Las enciende el ojo, igual que los rótulos: apagado, el plano
                  queda limpio y nada de la medición intercepta un toque. Esto
                  es el «ver las áreas propiamente para editarlas» del pedido —
                  verlas y poder tomarlas es la misma cosa—, y por eso elegir
                  una fila de la tabla enciende el ojo solo. */}
              {/* EL LOTE, por debajo de las áreas medidas. Ver `libresAlFondo`. */}
              {verAreas && libresAlFondo.map(pintarLibre)}

              {verAreas && medicion?.regiones.map((g) => {
                const tomada = recintos.includes(g.nombre);
                // El morado dice que el gesto pasó a ser el de grupo, igual que
                // en las paredes, y por eso mira el MODO y no la cuenta: ver
                // `variasParedes` para por qué esperar a la segunda llegaba
                // tarde.
                const varias = multiAreas || recintos.length > 1;
                return (
                <g key={g.nombre} data-region={g.nombre} data-tomada={tomada ? '' : undefined}
                   style={dedo('area')}>
                  {g.celdas.map(([x, y, w, h], i) => (
                    <rect
                      key={i}
                      x={x} y={y} width={w} height={h}
                      // Tomada, el relleno va del color de SU CLASE, no de un
                      // marrón fijo: es el «mismos cuatro colores para todo»
                      // del 8-oct-2026 — el rótulo ya decía azul/naranja y el
                      // cuerpo abajo se pintaba beige, que no decía nada. En
                      // múltiple manda el morado, que habla del gesto (igual
                      // que en las paredes) y no del tipo de área.
                      fill={tomada ? (varias ? TINTA_MULTI : TINTA_CLASE[g.clase]) : 'transparent'}
                      opacity={tomada ? (varias ? 0.2 : 0.16) : 1}
                      onPointerDown={(e) => {
                        tocoRef.current = { x: e.clientX, y: e.clientY };
                      }}
                      onPointerUp={(e) => {
                        const t = tocoRef.current;
                        tocoRef.current = null;
                        // Si el dedo se corrió eso fue un pan del plano, no un
                        // toque: las áreas no se excluyen del pan —son el
                        // interior entero de la casa— y hay que distinguirlos a
                        // mano. Se mide acá y no antes para que un arrastre
                        // tampoco cuente como mitad de un doble toque.
                        if (!t || Math.hypot(e.clientX - t.x, e.clientY - t.y) > 8) return;
                        // Dos toques seguidos sobre el mismo cuarto entran a
                        // selección múltiple con ese cuarto ya tomado, igual
                        // que en las paredes.
                        if (!multiAreas && esDoble(`a${g.nombre}`, t.x, t.y)) {
                          setSel([]);
                          setMulti(false);
                          setVanoSel(null);
                          setDivSel(null);
                          setLibreSel(null);
                          setAviso(null);
                          setMultiAreas(true);
                          setRecintos([g.nombre]);
                          return;
                        }
                        setSel([]);
                        setMulti(false);
                        setVanoSel(null);
                        // Tomar un cuarto medido SUELTA el área dibujada y la
                        // división. Sin esto las dos selecciones quedaban vivas
                        // y se montaban las dos paletas en el mismo ancla: es el
                        // «quedan como dos paletas flotantes activas» que
                        // reportó Daniel con la foto (4-oct-2026).
                        setDivSel(null);
                        setLibreSel(null);
                        setAviso(null);
                        // En múltiple el toque SUMA o SACA; suelto, reemplaza.
                        if (multiAreas) {
                          setRecintos((s) => (s.includes(g.nombre)
                            ? s.filter((n) => n !== g.nombre)
                            : [...s, g.nombre]));
                          return;
                        }
                        setRecintos(tomada ? [] : [g.nombre]);
                      }}
                    />
                  ))}
                </g>
                );
              })}

              {/* Las áreas libres —pilas, terraza, cochera—. Van ANTES de todo
                  lo demás para que queden por debajo: son una mancha de color
                  sobre el plano, no un objeto que deba tapar una pared.

                  Verde y punteadas, lejos del marrón de las paredes y del
                  divisor: lo que se dibuja a mano tiene que distinguirse de un
                  golpe de lo que vino medido del PDF.

                  Las enciende el MISMO ojo que las áreas medidas, que es lo que
                  pidió Daniel el 4-oct-2026: «siempre quedan activos las áreas
                  nuevas o las de Magic, y estas deberían de ocultarse con el
                  mismo botón de prender o apagar». Antes se pintaban siempre y
                  el ojo apagado dejaba el plano con las manchas verdes encima.
                  Crear una enciende el ojo sola (ver `crearLibre`): nacer
                  invisible sería peor que el defecto. */}
              {verAreas && libresEncima.map(pintarLibre)}

              {/* El mobiliario. Va DEBAJO de las paredes —por eso está acá y no
                  al final— para que un módulo arrimado contra el muro no le
                  robe el toque a la pared: la pared es la que mide y la que
                  manda, el mueble se apoya.

                  Y se ve SIEMPRE, con el ojo encendido o apagado. No es una capa
                  de medición como las áreas: es parte del diseño. Daniel lo dijo
                  entero —«esto para mí tiene un montón de potencial, ya que
                  termina de amarrar todo el diseño»—, y un ojo que se llama
                  «ver áreas» no tiene por qué apagar los muebles.

                  La tinta (`TINTA_MUEBLE`) es ajena al marrón de las paredes y
                  al verde de las áreas a propósito: en este editor el color dice
                  QUÉ es cada cosa, y un mueble no es ni pared ni área. */}
              {ver.mueble && planta.muebles?.map((m) => {
                if (!seVe(m.estado, vistaObra)) return null;
                const act = m.id === muebleSel;
                // El estado se dice EN LAS LÍNEAS del símbolo, no con un marco
                // alrededor. Es corrección de Daniel el 10-oct-2026 —«que los
                // estados los que cambien son las líneas del SVG de lo que uno
                // elija, no que le haga un recuadro, ya que es más lógico y
                // queda más limpio»— y tenía razón: un recuadro punteado sobre
                // una planta llena de muebles se lee como una selección, no
                // como un estado, y además compite con el marco de agarre.
                //
                // Ausente = la pieza vuelve a su color de familia, que lo pone
                // `SimboloMueble`: gris el mueble, marrón la salida eléctrica.
                const borde = tintaDe(m.estado, vistaObra);
                // Los nodos del objeto «a medida». Aparecen cuando YA tiene
                // forma, y también con la tijera o la goma puestas aunque
                // todavía sea un rectángulo: así el primer corte le siembra las
                // cuatro esquinas y no hace falta un botón de «convertir en
                // polígono». Ver `verticesMueble`.
                const vsm = act && !esElectrica(m.tipo) && muebleFlexible(m)
                  && (m.vertices?.length || nodo) ? verticesMueble(m) : null;
                // EL BLANCO DEL DEDO SE ESCALA A LA PIEZA, y acá no vale el
                // «el dedo mide lo mismo en los dos lados» que rige en el área.
                // Un objeto de 60 × 120 con discos de 25 cm en cada vértice y
                // bandas de 30 en cada lado queda tapado por sus propios
                // agarres: no sobra un solo punto del cuerpo para agarrarlo y
                // moverlo. Cuando TODO es agarre, nada es el objeto.
                //
                // El tope es el lado MENOR de la caja —el que se queda sin
                // lugar primero— y los pisos están para que en una pieza
                // grande el blanco siga siendo el de siempre.
                const chico = Math.min(m.w, m.h);
                const rNodo = Math.min(0.25, Math.max(0.08, chico / 4));
                const anchoLado = Math.min(0.3, Math.max(0.1, chico / 3));
                // `data-nodos` publica cuántos tiene su forma, ausente si
                // todavía es un rectángulo. Va por lo mismo que `data-geo`:
                // para que el banco pueda AFIRMAR que un corte sembró un nodo
                // sin tener que dejar la pieza tomada para leerle los agarres.
                // Es el gemelo de `data-vertices` del área.
                //
                // `data-geo` publica la caja VIVA en metros, igual que las
                // paredes y las áreas: es contra esto que mide el banco de
                // pruebas, no contra píxeles. `data-tipo` dice qué módulo es.
                //
                // `data-ancla` dice de qué pared y por qué cara cuelga la pieza
                // —ausente = suelta— y `data-altura` la de montaje que rige,
                // propia o de fábrica. Van publicados por lo mismo: para que el
                // banco pueda AFIRMAR que se pegó, y a cuál, en vez de
                // deducirlo de las coordenadas.
                return (
                  <g key={m.id} data-mueble={m.id} data-tipo={m.tipo}
                     data-tomado={act ? '' : undefined}
                     data-estado={m.estado ?? 'existente'}
                     data-geo={`${m.x},${m.y},${m.w},${m.h}`}
                     data-nodos={m.vertices?.length || undefined}
                     data-giro={m.giro ?? 0}
                     data-ancla={m.ancla ? `${m.ancla.pared},${m.ancla.cara}` : undefined}
                     data-altura={alturaDe(m) ?? undefined}
                     style={dedo('mueble')}>
                    {/* El símbolo primero y SIN eventos: el que recibe el dedo es
                        el rectángulo de abajo, que cubre todo el módulo. Si cada
                        trazo del símbolo tomara el toque, los huecos del dibujo
                        —el centro de un inodoro, entre las dos diagonales de la
                        ducha— serían agujeros por los que el toque se cuela. */}
                    <g style={{ pointerEvents: 'none' }} opacity={act ? 1 : 0.85}>
                      <SimboloMueble m={m} tinta={borde ?? undefined} />
                    </g>
                    {/* El blanco de toque de una salida eléctrica se ensancha a
                        30 cm: el símbolo mide 20 y en un teléfono eso es menos
                        de la mitad de una yema. El dibujo no cambia —sigue
                        midiendo lo que dice `data-geo`—, lo que crece es lo que
                        recibe el dedo, que es el mismo truco de las bandas de
                        los agarres. */}
                    {esElectrica(m.tipo) && (
                      <rect
                        x={m.x + m.w / 2 - 0.15} y={m.y + m.h / 2 - 0.15}
                        width={0.3} height={0.3}
                        fill="transparent"
                        className={`${cuerpoAgarrable(act)} cursor-move`}
                        onPointerDown={(e) => tomarMueble(e, m, 'cuerpo')}
                      />
                    )}
                    {/* El blanco del cuerpo sigue a la FORMA, no a la caja:
                        con un objeto en L, el rectángulo que lo envuelve se
                        come media habitación de alrededor y el dedo tomaría el
                        objeto desde donde el objeto no está. */}
                    {m.vertices?.length ? (
                      <polygon
                        points={m.vertices.map(([x, y]) => `${x},${y}`).join(' ')}
                        fill={act ? (esElectrica(m.tipo) ? TINTA_ELECTRICO : TINTA_MUEBLE) : 'transparent'}
                        fillOpacity={act ? 0.12 : 1}
                        className={`${cuerpoAgarrable(act)} cursor-move`}
                        onPointerDown={(e) => tomarMueble(e, m, 'cuerpo')}
                      />
                    ) : (
                      <rect
                        x={m.x} y={m.y} width={m.w} height={m.h}
                        fill={act ? (esElectrica(m.tipo) ? TINTA_ELECTRICO : TINTA_MUEBLE) : 'transparent'}
                        fillOpacity={act ? 0.12 : 1}
                        className={`${cuerpoAgarrable(act)} cursor-move`}
                        onPointerDown={(e) => tomarMueble(e, m, 'cuerpo')}
                      />
                    )}
                    {/* Los agarres, SÓLO en el objeto «a medida» y sólo cuando
                        está tomado. En el resto del catálogo la medida se
                        escribe en la paleta y nada más: Daniel lo pidió el
                        5-oct-2026 —«que no tenga esas opciones de ampliar o
                        disminuir su forma donde está insertado, que únicamente
                        la manera de manipularlo sea a través del panel
                        flotante, ya que es muy fácil darle clic donde uno está
                        moviéndolo y se amplía sin intención»—.

                        En la pieza que sí los conserva van repartidos por
                        función, y eso resuelve el pedido de raíz en vez de
                        taparlo: los cuatro de los LADOS estiran, los cuatro de
                        las ESQUINAS abren el radio. El gesto resbaladizo —la
                        esquina— deja de significar «estirar» en todo el editor.

                        El molde es el del área libre: banda invisible de 30 cm
                        para el dedo + pastilla chica para el ojo, y las
                        esquinas DESPUÉS de las bandas para que ganen el toque
                        donde se cruzan. */}
                    {/* Y desde el 5-oct-2026 también los tiene cualquier mueble
                        con el CANDADO abierto: la llave de escape para el caso
                        puntual, cerrada de fábrica y en cada nueva selección. */}
                    {/* Nunca en el eléctrico, ni con el candado abierto: un
                        tomacorriente no tiene medida que estirar —su símbolo es
                        convencional— y los agarres le taparían el dibujo
                        entero. Lo que sí tiene es altura, y eso vive en la
                        paleta. */}
                    {/* ⚠️ Y NUNCA cuando el objeto tiene forma propia: ahí la
                        caja dejó de ser la geometría, así que estirar un borde
                        de la caja no querría decir nada. Los agarres de ese
                        objeto son los del polígono, más abajo. */}
                    {act && !esElectrica(m.tipo) && !vsm
                      && (muebleFlexible(m) || candado) && (
                      <>
                        {([
                          ['n', m.x, m.y, m.w, 0, 'cursor-ns-resize'],
                          ['s', m.x, m.y + m.h, m.w, 0, 'cursor-ns-resize'],
                          ['o', m.x, m.y, 0, m.h, 'cursor-ew-resize'],
                          ['e', m.x + m.w, m.y, 0, m.h, 'cursor-ew-resize'],
                        ] as const).map(([lado, bx, by, bw, bh, cur]) => (
                          <g key={lado}>
                            <rect
                              data-lado={`m${m.id}:${lado}`}
                              x={bx - (bw ? 0 : 0.15)} y={by - (bh ? 0 : 0.15)}
                              width={bw || 0.3} height={bh || 0.3}
                              fill="transparent"
                              className={`${AGARRE} ${cur}`}
                              onPointerDown={(e) => tomarMueble(e, m, lado)}
                            />
                            {/* Más corta que la del área libre (0,3 contra 0,52):
                                un módulo de cocina mide 60 cm de lado y una
                                pastilla de 52 le tapaba el símbolo entero. */}
                            <rect
                              x={bw ? bx + bw / 2 - 0.15 : bx - 0.055}
                              y={bh ? by + bh / 2 - 0.15 : by - 0.055}
                              width={bw ? 0.3 : 0.11} height={bh ? 0.3 : 0.11}
                              rx={0.055}
                              fill="#f8fafc" stroke={TINTA_MUEBLE} strokeWidth={0.04}
                              style={{ pointerEvents: 'none' }}
                            />
                          </g>
                        ))}
                        {/* Las esquinas NO estiran: abren el campo del radio.
                            «Que al darle clic uno a la esquina, salga el panel
                            flotante en el que uno pueda ponerle el radio que
                            uno desea». Van en el orden de `Mueble.radios` y el
                            giro se deshace con `iEsquina`, porque el radio vive
                            en coordenadas del dibujo y el agarre en las del
                            plano. La pastilla sale rellena cuando esa esquina ya
                            tiene radio: así se ve cuál se redondeó sin tener que
                            abrir una por una. */}
                        {([
                          ['no', m.x, m.y],
                          ['ne', m.x + m.w, m.y],
                          ['se', m.x + m.w, m.y + m.h],
                          ['so', m.x, m.y + m.h],
                        ] as const).map(([lado, qx, qy]) => {
                          const i = iEsquina(lado, m.giro ?? 0);
                          const puesto = (m.radios?.[i] ?? 0) > 0;
                          return (
                            <g key={lado}>
                              <circle
                                data-esquina={`m${m.id}:${lado}`}
                                cx={qx} cy={qy} r={0.28}
                                fill="transparent"
                                className={`${AGARRE} cursor-pointer`}
                                onPointerDown={(e) => tomarEsquina(e, m, i)}
                              />
                              <circle
                                cx={qx} cy={qy} r={0.11}
                                fill={esquinaSel === i || puesto ? TINTA_MUEBLE : '#f8fafc'}
                                stroke={TINTA_MUEBLE} strokeWidth={0.04}
                                style={{ pointerEvents: 'none' }}
                              />
                            </g>
                          );
                        })}
                      </>
                    )}
                    {/* LOS AGARRES DE LA FORMA, el pedido de Daniel del
                        14-oct-2026: «que el objeto a medida tenga las mismas
                        funciones que tiene el área en cuanto a la posibilidad
                        de poder dividirse, poder borrar un vértice, poder mover
                        de una manera con ángulos distintos».

                        Es el molde del área libre, en chico: el objeto mide 60
                        cm donde una sala mide 4 m, así que las pastillas que
                        allá quedan discretas acá taparían el símbolo entero.
                        Lo que NO se achica es la banda invisible —el dedo mide
                        lo mismo en los dos lados—.

                        Los vértices van DESPUÉS de los lados para ganar el
                        toque en la esquina, donde las dos bandas se cruzan. */}
                    {vsm && vsm.map(([ax, ay], i) => {
                      const [bx, by] = vsm[(i + 1) % vsm.length];
                      const horiz = Math.abs(ay - by) < 1e-6;
                      const vert = Math.abs(ax - bx) < 1e-6;
                      if (horiz && vert) return null;
                      return (
                        <g key={`mlado-${i}`}>
                          <line
                            data-lado-objeto={`m${m.id}:${i}`}
                            x1={ax} y1={ay} x2={bx} y2={by}
                            stroke="transparent" strokeWidth={anchoLado}
                            className={`${AGARRE} ${nodo === 'partir' ? 'cursor-crosshair'
                              : horiz ? 'cursor-ns-resize'
                              : vert ? 'cursor-ew-resize' : 'cursor-crosshair'}`}
                            onPointerDown={(e) => tomarLadoObjeto(e, m, i)}
                          />
                          {nodo === 'partir' && (
                            <circle
                              data-nodo-partir-objeto={`m${m.id}:${i}`}
                              cx={(ax + bx) / 2} cy={(ay + by) / 2} r={0.1}
                              fill="#f8fafc" stroke={TINTA_MUEBLE} strokeWidth={0.04}
                              style={{ pointerEvents: 'none' }}
                            />
                          )}
                        </g>
                      );
                    })}
                    {/* Y los vértices SÓLO cuando el objeto ya tiene forma: con
                        la tijera puesta sobre un rectángulo todavía no hay nada
                        que mover ni que borrar, y cuatro discos encima del
                        símbolo serían ruido. */}
                    {vsm && m.vertices?.length ? vsm.map(([px, py], i) => (
                      <g key={`mvert-${i}`}>
                        <circle
                          data-vertice-objeto={`m${m.id}:${i}`}
                          cx={px} cy={py} r={rNodo}
                          fill="transparent"
                          className={`${AGARRE} ${nodo === 'quitar'
                            ? 'cursor-crosshair' : 'cursor-move'}`}
                          onPointerDown={(e) => tomarVerticeObjeto(e, m, i)}
                        />
                        <circle
                          cx={px} cy={py} r={nodo === 'quitar' ? 0.13 : 0.095}
                          fill={nodo === 'quitar' ? '#b91c1c' : TINTA_MUEBLE}
                          stroke="#f8fafc" strokeWidth={0.035}
                          style={{ pointerEvents: 'none' }}
                        />
                      </g>
                    )) : null}
                  </g>
                );
              })}

              {/* Las divisiones de uso. Van DEBAJO de las paredes y punteadas
                  finas a propósito: no son paredes, no miden, no sacan área, y
                  si se dibujaran macizas el plano diría que ahí hay un tabique
                  que la casa no tiene.

                  Y se MUESTRAN SÓLO CON LA HERRAMIENTA DE DIVIDIR PUESTA. Es el
                  cierre de un pedido que Daniel trajo dos veces: el 4-oct-2026
                  («las líneas divisorias quedan visibles siempre y deberían
                  ocultarse. Es únicamente para el momento de hacer la división
                  como tal») se colgaron del ojo de áreas, y el 8-oct volvió con
                  que seguían estorbando —«es simplemente una línea de referencia
                  temporal que no debería por qué existir»—, porque mirar las
                  áreas es justo cuando uno tiene el ojo encendido.

                  ⚠️ OJO CON LA PREMISA: esta línea NO es un rastro del corte, ES
                  el corte. `dividirArea` la mete en `planta.divisores` y
                  `medirPlanta` la usa de BARRERA de la inundación, así que
                  borrarla vuelve a unir las dos mitades —lo dice `borrarDivisor`
                  con todas las letras—. No se puede «eliminar al terminar» sin
                  deshacer la división. Lo que se puede, y es lo que Daniel eligió
                  el 8-oct-2026, es que deje de verse: el corte sigue vivo, el
                  plano queda limpio, y el camino de vuelta es «Unir» desde la
                  paleta de áreas, que ya existe desde la ronda de #186/#192. */}
              {modo === 'area' && planta.divisores?.map((d) => {
                if (!seVe(d.estado, vistaObra)) return null;
                const act = d.id === divSel;
                const puntos = { x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2 };
                return (
                  <g key={d.id} data-divisor={d.id}
                     data-estado={d.estado ?? 'existente'} style={dedo('area')}>
                    <line
                      {...puntos}
                      stroke={tintaDe(d.estado, vistaObra) ?? '#8a5a3b'}
                      strokeWidth={act ? 0.07 : 0.045}
                      strokeDasharray="0.3 0.18"
                      strokeLinecap="round"
                      opacity={act ? 1 : 0.7}
                      style={{ pointerEvents: 'none' }}
                    />
                    {/* Banda de agarre: la línea tiene 4 cm y con el dedo no se
                        acierta, igual que pasa con el espesor real de una pared. */}
                    <line
                      {...puntos}
                      stroke="transparent" strokeWidth={0.3}
                      className={`${AGARRE} cursor-pointer`}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        soltarTodo();
                        setAviso(null);
                        setDivSel(act ? null : d.id);
                      }}
                    />
                  </g>
                );
              })}

              {/* Los cuadrados de esquina van ANTES que las paredes, debajo: si
                  fueran encima taparían con tinta de pared la mitad del codo
                  que está seleccionada y le comerían el color a la selección.
                  Se agrupan por TINTA y sólo se cierra el codo cuando las dos
                  paredes pintan igual —una a demoler contra una existente no se
                  funden en un bloque, que sería inventar obra—. */}
              {ver.pared && Object.entries(planta.paredes
                .filter((w) => seVe(w.estado, vistaObra))
                .reduce<Record<string, typeof planta.paredes>>((acc, w) => {
                  const t = tintaDe(w.estado, vistaObra) ?? '#22211f';
                  (acc[t] ??= []).push(w);
                  return acc;
                }, {})
              ).flatMap(([t, ws]) => cuadradosDeEsquina(ws).map((r, i) => (
                <rect
                  key={`esq-${t}-${i}`}
                  data-esquina={r.map((n) => n.toFixed(3)).join(',')}
                  x={r[0]} y={r[1]} width={r[2] - r[0]} height={r[3] - r[1]}
                  fill={t} pointerEvents="none"
                />
              )))}
              {ver.pared && planta.paredes.map((w) => {
                // El SEGUNDO filtro, el de la vista de obra. No reemplaza al ojo
                // de la capa —ese es el `ver.pared` de arriba— sino que lo cruza:
                // una pared a demoler no se dibuja en «Estado final» aunque la
                // capa esté encendida. Ver `vistaObra`.
                if (!seVe(w.estado, vistaObra)) return null;
                const activa = sel.includes(w.id);
                const k = caja(w);
                // El estado manda sobre el TRAZO y la clase sobre el relleno de
                // las áreas: son dos ejes y los dos tienen que poder decirse a la
                // vez. Lo seleccionado sigue ganando sobre los dos, que es lo que
                // dice qué se está tocando.
                const tinta = tintaDe(w.estado, vistaObra) ?? '#22211f';
                // La pared COMO LA DEJA LA VISTA: con los vanos que esa vista
                // muestra. Un hueco que todavía no se abrió no se dibuja, y la
                // masa de la pared se cierra sola porque `tramos` parte la pared
                // por los vanos que le quedan. Mismo criterio que
                // `plantaSegunVista`, que es quien lo hace para la medición.
                const wv = vistaDePared(w);
                return (
                  // `data-geo` publica la geometría VIVA de la pared (los mismos
                  // metros que el modelo) para que las pruebas midan el efecto de
                  // un gesto sin deducirlo de los píxeles del SVG ni del color
                  // del relleno, que son dos cosas que cambian por motivos de
                  // dibujo y daban falsos negativos.
                  <g key={w.id} data-pared={w.id} data-geo={`${w.o},${w.c},${w.a},${w.b},${w.e}`}
                     data-estado={w.estado ?? 'existente'}
                     style={dedo('pared')}>
                    {tramos(wv).map(([a, b], i) => {
                      const t = caja(w, a, b);
                      return (
                        <rect
                          key={i}
                          x={t[0]} y={t[1]} width={t[2] - t[0]} height={t[3] - t[1]}
                          fill={activa ? (variasParedes ? TINTA_MULTI : '#8a5a3b') : tinta}
                          className={`${cuerpoAgarrable(activa)} cursor-move`}
                          onPointerDown={(e) => tomar(e, w, null)}
                        />
                      );
                    })}
                    {/* Zona de agarre: el espesor real es de 10 cm y con el dedo
                        no se acierta. Invisible, no tapa el plano de abajo. */}
                    <rect
                      x={k[0] - 0.06} y={k[1] - 0.06}
                      width={k[2] - k[0] + 0.12} height={k[3] - k[1] + 0.12}
                      fill="transparent"
                      className={`${cuerpoAgarrable(activa)} cursor-move`}
                      onPointerDown={(e) => tomar(e, w, null)}
                    />
                    {/* Las carpinterías van DESPUÉS de la zona de agarre de su
                        pared: antes quedaban debajo de ella y el toque tomaba la
                        pared entera, así que una puerta no se podía ni agarrar
                        ni correr. */}
                    {/* `wv`, no `w`: la MISMA lista de vanos con la que se
                        partió la masa arriba. Si acá se dibujaran todos, un
                        hueco escondido pintaría su caja de color encima de la
                        pared que la vista acaba de cerrar — se vería abierto y
                        macizo a la vez. */}
                    {wv.vanos.map((v) => {
                      const t = caja(w, v.a, v.b);
                      const color = COLOR_VANO[v.tipo];
                      const activo = vano?.w.id === w.id && vano.v.id === v.id;
                      // Otra vez: el estado manda el TRAZO. Y por eso un paso
                      // marcado también se dibuja aunque no tenga relleno — si
                      // no, un paso nuevo sería indistinguible de uno de
                      // siempre, que es justo lo que la función viene a decir.
                      const borde = tintaDe(v.estado, vistaObra);
                      return (
                        <g key={v.id} data-vano={`${w.id}:${v.id}`} data-tipo={v.tipo}
                           data-estado={v.estado ?? 'existente'}
                           data-geo={`${v.a},${v.b}`}>
                          {(color || borde) && (
                            <rect
                              x={t[0]} y={t[1]} width={t[2] - t[0]} height={t[3] - t[1]}
                              fill={color ?? 'none'}
                              stroke={activo ? '#22211f' : (borde ?? '#f8fafc')}
                              strokeWidth={activo ? 0.04 : (borde ? 0.05 : 0.02)}
                              style={{ pointerEvents: 'none' }}
                            />
                          )}
                          {/* El paso no se pinta pero sí se agarra: es como entra
                              todo vano importado, y es desde acá que se le dice
                              que era una ventana. */}
                          <rect
                            x={w.o === 'v' ? k[0] - 0.06 : v.a}
                            y={w.o === 'v' ? v.a : k[1] - 0.06}
                            width={w.o === 'v' ? k[2] - k[0] + 0.12 : v.b - v.a}
                            height={w.o === 'v' ? v.b - v.a : k[3] - k[1] + 0.12}
                            fill="transparent"
                            className={`${cuerpoAgarrable(activo)} cursor-move`}
                            onPointerDown={(e) => tomarVano(e, w, v)}
                          />
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {/* Los puntos de las puntas van en una capa APARTE, encima de
                  todas las paredes. Dentro del grupo de su pared quedaban
                  debajo de la zona de agarre —12 cm de ancho— de las paredes
                  que se dibujan después, y justo en las esquinas, que es donde
                  más se usan, el toque agarraba la vecina y la movía entera.

                  Sólo salen con UNA pared tomada: en selección múltiple el
                  gesto es elegir, y agarrar una punta volvería a dejar una. */}
              {uno != null && (() => {
                const w = planta.paredes.find((x) => x.id === uno);
                if (!w) return null;
                return ([0, 1] as const).map((fin) => {
                  const cx = w.o === 'v' ? w.c : (fin ? w.b : w.a);
                  const cy = w.o === 'v' ? (fin ? w.b : w.a) : w.c;
                  return (
                    <circle
                      key={fin}
                      data-punta={`${w.id}:${fin}`}
                      cx={cx} cy={cy} r={0.16}
                      fill="#8a5a3b"
                      className={`${AGARRE} cursor-pointer`}
                      onPointerDown={(e) => tomar(e, w, fin)}
                    />
                  );
                });
              })()}

              {/* La vista previa del trazo del DIVISOR: la línea tal cual va a
                  quedar, con su largo, mientras se arrastra. Daniel la pidió
                  así —«que sea visible a la hora que uno está como cortando»—. */}
              {trazo && modo === 'area' && (() => {
                const largo = Math.hypot(trazo.x1 - trazo.x0, trazo.y1 - trazo.y0);
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <line
                      x1={trazo.x0} y1={trazo.y0} x2={trazo.x1} y2={trazo.y1}
                      stroke="#8a5a3b" strokeWidth={0.06}
                      strokeDasharray="0.3 0.18" strokeLinecap="round"
                    />
                    <text
                      x={(trazo.x0 + trazo.x1) / 2} y={(trazo.y0 + trazo.y1) / 2 - 0.2}
                      textAnchor="middle" fontSize={0.26}
                      paintOrder="stroke" stroke="#f8fafc" strokeWidth={0.12}
                      strokeLinejoin="round" fill="#8a5a3b"
                      style={SOLO_PLANO}
                    >
                      {m2(largo)} m
                    </text>
                  </g>
                );
              })()}

              {/* La vista previa del ÁREA LIBRE: el rectángulo tal cual va a
                  quedar, con sus dos lados y su área, mientras se estira.

                  Faltaba, y el síntoma era peor que la ausencia: el bloque de
                  abajo pinta la pared del trazo para cualquier modo que no sea
                  «area», así que dibujando una terraza se veía una PARED fina
                  creciendo en diagonal. Daniel lo pidió sin rodeos —«que
                  realmente uno pueda previsualizar el cuadrado o rectángulo
                  antes de que se esté haciendo»—.

                  Los números van a los lados y no al centro a propósito: el
                  centro es donde después va el rótulo del área, y mientras se
                  dibuja el dedo está justo encima de una esquina. */}
              {trazo && dibujaCaja(modo) && (() => {
                const x = Math.min(trazo.x0, trazo.x1);
                const y = Math.min(trazo.y0, trazo.y1);
                const w = Math.abs(trazo.x1 - trazo.x0);
                const h = Math.abs(trazo.y1 - trazo.y0);
                const chico = w < MIN_LARGO || h < MIN_LARGO;
                // La caja de PAREDES se previsualiza del color de las paredes y
                // sin el m² del centro: lo que va a nacer no es un área, y
                // pintarla de verde con su superficie sería prometer otra cosa.
                const deParedes = modo === 'rect-pared';
                const tinta = chico ? '#a8a29e' : deParedes ? '#8a5a3b' : '#4f9d78';
                return (
                  <g data-previa-libre="" style={{ pointerEvents: 'none' }}>
                    <rect
                      x={x} y={y} width={w} height={h}
                      fill={tinta} fillOpacity={chico ? 0.08 : 0.18}
                      stroke={tinta} strokeWidth={0.06}
                      strokeDasharray="0.28 0.16"
                    />
                    {/* El halo del color del fondo es lo que deja leer la cota
                        encima de un muro negro o de una zona rayada. */}
                    <text
                      x={x + w / 2} y={y - 0.1}
                      textAnchor="middle" fontSize={0.26}
                      paintOrder="stroke" stroke="#f8fafc" strokeWidth={0.12}
                      strokeLinejoin="round" fill={tinta}
                      style={SOLO_PLANO}
                    >
                      {m2(w)} m
                    </text>
                    <text
                      x={x - 0.12} y={y + h / 2}
                      textAnchor="end" dominantBaseline="middle" fontSize={0.26}
                      paintOrder="stroke" stroke="#f8fafc" strokeWidth={0.12}
                      strokeLinejoin="round" fill={tinta}
                      style={SOLO_PLANO}
                    >
                      {m2(h)} m
                    </text>
                    {!chico && !deParedes && (
                      <text
                        x={x + w / 2} y={y + h / 2}
                        textAnchor="middle" dominantBaseline="middle" fontSize={0.3}
                        paintOrder="stroke" stroke="#f8fafc" strokeWidth={0.14}
                        strokeLinejoin="round" fill="#2f6b52" fontWeight={600}
                        style={SOLO_PLANO}
                      >
                        {m2(w * h)} m²
                      </text>
                    )}
                  </g>
                );
              })()}

              {/* La vista previa de la COTA que se está poniendo: la línea con
                  sus dos marcas y el número, igual que va a quedar.

                  Va aparte y el modo se excluye del bloque de abajo por lo
                  mismo que ya pasó con el área libre: ese bloque pinta una
                  PARED para cualquier modo que no conozca, y midiendo se vería
                  nacer un muro en vez de una cota. */}
              {trazo && modo === 'cota' && (() => {
                const largo = Math.hypot(trazo.x1 - trazo.x0, trazo.y1 - trazo.y0);
                const corta = largo < MIN_COTA;
                return (
                  <g data-previa-cota="" style={{ pointerEvents: 'none' }}>
                    <Acote
                      x1={trazo.x0} y1={trazo.y0} x2={trazo.x1} y2={trazo.y1}
                      tinta={corta ? '#a8a29e' : TINTA_COTA}
                    />
                  </g>
                );
              })()}

              {/* La vista previa del trazo: la pared que va a nacer, en el
                  mismo espesor con el que va a nacer. */}
              {trazo && modo !== 'area' && !dibujaCaja(modo) && modo !== 'cota' && (() => {
                const prev = paredDelTrazo(trazo, espesorTipico(planta.paredes), -1);
                if (!prev) return null;
                const k = caja(prev);
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <rect
                      x={k[0]} y={k[1]} width={k[2] - k[0]} height={k[3] - k[1]}
                      fill="#8a5a3b" opacity={0.6}
                    />
                    <Cinta w={prev} texto={`${m2(prev.b - prev.a)} m`} />
                  </g>
                );
              })()}

              {/* LA ACOTACIÓN. Va al final de todo el dibujo porque una cota se
                  lee ENCIMA del plano: debajo de las paredes quedaría tachada
                  justo por la pared que mide.

                  Dos clases de cota y la diferencia es de fondo:

                  - Las de PARED salen de `cotasAuto`, se recalculan en cada
                    repintado y no se pueden tocar. Es la «función en automático
                    que acote todas las paredes que se vean existentes a centro
                    de pared» que pidió Daniel, y por eso no hay botón de
                    generar: estirar una pared mueve su cota en el mismo cuadro.
                  - Las PROPIAS viven en `planta.cotas`, se dibujan con su banda
                    de agarre —el mismo truco que el divisor, porque una línea de
                    3 cm no se acierta con el dedo— y se borran desde su paleta.

                  Se ven con el ojo encendido o mientras se está acotando, igual
                  que las líneas divisorias con las áreas: midiendo hay que ver
                  lo que ya se midió para no repetirlo. */}
              {(ver.cota || modo === 'cota') && (
                <g data-cotas="" style={dedo('cota')}>
                  {/* `data-geo` publica las dos puntas VIVAS en metros, igual que
                      las paredes y los muebles: es lo que deja afirmar que la
                      cota derivada cae en el EJE de su pared y no en una cara. */}
                  {cotasAuto.map((k) => {
                    // ¿Esta cota es de una pared que cuelga de la tomada? Si
                    // sí, su NÚMERO se toca y se le escribe la medida —ver
                    // `PaletaCotaVecina`— y se pinta del pardo de las paredes
                    // para que se vea que pertenece al gesto en curso. Las
                    // demás siguen siendo rótulo y nada más.
                    const colgada = vecinas.some((g) => g.id === k.id);
                    return (
                      <g key={`p${k.id}`} data-cota-pared={k.id}
                         data-geo={`${k.x1},${k.y1},${k.x2},${k.y2}`}
                         data-colgada={colgada ? '' : undefined}
                         style={{ pointerEvents: 'none' }}>
                        <Acote
                          x1={k.x1} y1={k.y1} x2={k.x2} y2={k.y2}
                          tinta={TINTA_COTA}
                          tintaNumero={colgada ? '#8a5a3b' : undefined}
                          gruesa={k.id === vecinaSel}
                          onToque={colgada ? (e) => tomarCotaVecina(e, k.id) : undefined}
                        />
                      </g>
                    );
                  })}
                  {/* LAS DE LOS LADOS DE UN ÁREA CREADA. Mismo trato que las de
                      pared y por la misma razón de fondo: también se derivan de
                      la geometría viva, así que empujar un lado mueve su cota en
                      el mismo cuadro.

                      El número se toca —y se escribe— sólo en los lados que
                      cuelgan del tomado; el resto es rótulo. Una DIAGONAL nunca
                      cuelga: se ve cuánto mide y se cambia arrastrando su
                      vértice (decisión de Daniel, 8-oct-2026). */}
                  {cotasLibres.map((k) => {
                    const colgada = ladoSel?.id === k.id && ladosVecinos.includes(k.lado);
                    return (
                      <g key={`l${k.id}:${k.lado}`} data-cota-libre={`${k.id}:${k.lado}`}
                         data-geo={`${k.x1},${k.y1},${k.x2},${k.y2}`}
                         data-colgada={colgada ? '' : undefined}
                         style={{ pointerEvents: 'none' }}>
                        <Acote
                          x1={k.x1} y1={k.y1} x2={k.x2} y2={k.y2}
                          tinta={TINTA_COTA}
                          tintaNumero={colgada ? '#8a5a3b' : undefined}
                          gruesa={colgada && ladoVecinoSel === k.lado}
                          onToque={colgada ? (e) => tomarCotaLadoVecino(e, k.lado) : undefined}
                        />
                      </g>
                    );
                  })}
                  {planta.cotas?.map((k) => {
                    // La cota es la ÚNICA de las seis que no tiene martillo en
                    // su paleta, y es a propósito: acotar es anotar sobre el
                    // dibujo, no construir. El filtro queda igual —cuesta una
                    // línea— para que el día que una cota herede el estado de
                    // lo que acota, el dibujo ya esté de acuerdo con la
                    // medición en vez de contradecirla.
                    if (!seVe(k.estado, vistaObra)) return null;
                    const act = k.id === cotaSel;
                    const off = k.off ?? 0;
                    // La banda de agarre va sobre la LÍNEA DE COTA, no sobre lo
                    // medido: separada, el dedo busca la línea que ve.
                    const dx = k.x2 - k.x1, dy = k.y2 - k.y1;
                    const largo = Math.max(Math.hypot(dx, dy), 1e-6);
                    const nx = -dy / largo, ny = dx / largo;
                    const a1 = k.x1 + nx * off, b1 = k.y1 + ny * off;
                    const a2 = k.x2 + nx * off, b2 = k.y2 + ny * off;
                    return (
                      <g key={`m${k.id}`} data-acote={k.id}
                         data-geo={`${k.x1},${k.y1},${k.x2},${k.y2}`}
                         data-off={off || undefined}>
                        <g style={{ pointerEvents: 'none' }}>
                          <Acote x1={k.x1} y1={k.y1} x2={k.x2} y2={k.y2} off={off}
                                 tinta={TINTA_COTA} gruesa={act} />
                        </g>
                        {/* La banda hace las DOS cosas: el toque corto toma o
                            suelta la cota —el toggle de siempre—, y el arrastre
                            la separa. No hacen falta dos gestos porque no se
                            pisan: el umbral de `ARRASTRE_PX` los reparte, igual
                            que en un área. */}
                        <line
                          x1={a1} y1={b1} x2={a2} y2={b2}
                          stroke="transparent" strokeWidth={0.3}
                          className={`${AGARRE} cursor-move`}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            if (!act) { soltarTodo(); setAviso(null); }
                            const p = enMetros(e);
                            setCotaSel(k.id);
                            if (!p) return;
                            marcadoRef.current = false;
                            dedoRef.current = e.pointerId;
                            setArrastrando(true);
                            agarreRef.current = {
                              tipo: 'cotaoff', id: k.id, off0: off, mx: p.x, my: p.y,
                            };
                            // `soloToque` en false aunque la cota no estuviera
                            // tomada, y es la única excepción al primer tiempo
                            // del dedo. El motivo es de dibujo: la cota no es
                            // parte de la casa —es una anotación encima—, así
                            // que separarla no corre riesgo de romper nada y
                            // pedir dos gestos para moverla sería cobrarle a
                            // Daniel un toque por cada cota que acomoda.
                            umbralRef.current = {
                              x: e.clientX, y: e.clientY, ok: false, soloToque: false,
                            };
                            try { (e.currentTarget as Element).setPointerCapture(e.pointerId); }
                            catch { /* el puntero ya se fue: el lienzo recibe los move igual */ }
                          }}
                        />
                        {/* LOS NODOS. Sólo en la tomada, por lo mismo que el
                            área no muestra sus vértices hasta elegirla: con
                            veinte cotas en pantalla, cuarenta puntos de agarre
                            serían un campo de minas para el dedo.

                            Van sobre lo MEDIDO y no sobre la línea de cota, que
                            es lo que son: el punto que se está midiendo. Por eso
                            con la cota separada quedan en la punta de cada
                            testigo. */}
                        {act && ([[k.x1, k.y1, 0], [k.x2, k.y2, 1]] as const).map(
                          ([px, py, fin]) => (
                            <circle
                              key={fin} cx={px} cy={py} r={enMetrosPx(11)}
                              data-nodo-cota={`${k.id}:${fin}`}
                              fill="#fff" stroke={TINTA_COTA}
                              strokeWidth={enMetrosPx(2)}
                              className={`${AGARRE} cursor-pointer`}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                setAviso(null);
                                marcadoRef.current = false;
                                dedoRef.current = e.pointerId;
                                setArrastrando(true);
                                agarreRef.current = { tipo: 'cotanodo', id: k.id, fin };
                                umbralRef.current = {
                                  x: e.clientX, y: e.clientY, ok: false, soloToque: false,
                                };
                                try { (e.currentTarget as Element).setPointerCapture(e.pointerId); }
                                catch { /* ídem */ }
                              }}
                            />
                          ))}
                      </g>
                    );
                  })}
                </g>
              )}

              {/* La cota de la pared tomada. Va después de las paredes para que
                  nunca quede tachada por un muro.

                  No escucha el toque: la medida se escribe en la paleta, y la
                  paleta se planta midiendo DÓNDE quedó esta cinta. Durante el
                  arrastre es lo único que queda en pantalla, y hace de cota
                  viva mientras la pared se mueve. */}
              {(() => {
                const w = uno == null ? null : planta.paredes.find((x) => x.id === uno);
                // Trazando una pared nueva la cinta de otra sólo estorba; pero
                // dividiendo hay que seguir viendo CUÁL se va a partir.
                if (!w || modo === 'pared') return null;
                return (
                  // Se apaga —pero NO se desmonta— mientras la paleta está
                  // arriba: repetiría la misma medida a dos centímetros del
                  // campo, y es justo el elemento que la paleta mide para
                  // saber dónde plantarse. Vuelve a verse en el arrastre.
                  <g data-cota={w.id} opacity={paleta ? 0 : 1} style={{ pointerEvents: 'none' }}>
                    <Cinta w={w} texto={`${m2(w.b - w.a)} m`} activa />
                  </g>
                );
              })()}

              {/* La guía del imán: la línea a la que se acaba de pegar lo que
                  se está moviendo. Sin ella el tirón se siente como que el
                  editor se resiste; con ella se lee como lo que es —se enganchó
                  con ESA pared—. Cruza todo el encuadre, igual que en Archicad. */}
              {guiaRef.current && (() => {
                const u = unPx();
                return (
                  <line
                    x1={guiaRef.current.eje === 'x' ? guiaRef.current.v : vista.x0}
                    y1={guiaRef.current.eje === 'x' ? vista.y0 : guiaRef.current.v}
                    x2={guiaRef.current.eje === 'x' ? guiaRef.current.v : vista.x1}
                    y2={guiaRef.current.eje === 'x' ? vista.y1 : guiaRef.current.v}
                    stroke="#8a5a3b" strokeWidth={MIRA_PX.linea * u}
                    strokeDasharray={`${MIRA_PX.raya * u} ${MIRA_PX.hueco * u}`}
                    style={{ pointerEvents: 'none' }}
                  />
                );
              })()}

              {/* ── la mira ──
                  Dónde va a caer lo que se está por hacer, antes de hacerlo.
                  Daniel lo pidió porque sin esto hay que adivinar: «para cuando
                  voy a crear una pared, saber dónde la voy a crear […] no tengo
                  visibilidad».

                  El punto se pinta MACIZO cuando el imán agarró un nodo y hueco
                  cuando sólo cayó en la rejilla. Esa diferencia es toda la
                  información que hace falta para confiar: lleno quiere decir
                  «esto arranca exactamente en la esquina de esa pared». */}
              {/* EL FANTASMA: el objeto elegido dibujado donde caería, antes de
                  soltarlo. Es el pedido de Daniel del 11-oct-2026 —«previsualizar
                  el objeto en el plano antes de colocarlo»— y lo que hace que el
                  sello sea un sello: se ve la huella antes de estampar.

                  Se dibuja con su medida real y su propio símbolo, no con un
                  cuadrito genérico, porque la pregunta que uno trae al apoyar el
                  dedo es «¿cabe acá y queda mirando para allá?». Y pasa por el
                  mismo imán a pared que el nacimiento, así que si se va a sentar
                  contra la pared, se ve sentado.

                  Translúcido y punteado es todo lo que lo separa de una pieza
                  puesta; con eso basta y no hace falta ningún rótulo. */}
              {modo === 'objeto' && armado && fantasma && (() => {
                const g = fantasmaDe(fantasma);
                if (!g) return null;
                return (
                  <g data-fantasma={g.tipo} data-geo={`${g.x},${g.y},${g.w},${g.h}`}
                     style={{ pointerEvents: 'none' }} opacity={0.5}>
                    <SimboloMueble m={g} />
                    <rect
                      x={g.x} y={g.y} width={g.w} height={g.h}
                      fill="none" stroke={TINTA_MUEBLE}
                      strokeWidth={unPx()} strokeDasharray={`${unPx() * 4} ${unPx() * 3}`}
                    />
                  </g>
                );
              })()}
              {/* La marca del corte: una raya que CRUZA la pared, para que se
                  lea como un corte y no como un punto cualquiera. Maciza una vez
                  que el toque la confirmó —el siguiente toque ahí corta—, hueca
                  mientras es sólo previsión del puntero. */}
              {corte && (() => {
                const w = planta.paredes.find((x) => x.id === corte.pared);
                if (!w) return null;
                const cx = w.o === 'v' ? w.c : corte.v;
                const cy = w.o === 'v' ? corte.v : w.c;
                // El LARGO sí es del plano: la raya tiene que cruzar la pared de
                // cara a cara, y eso depende de su espesor. El GROSOR y el botón
                // no —son puntería, y van en píxeles de pantalla como la mira—.
                const l = w.e / 2 + 0.2;
                const u = unPx();
                const raya = w.o === 'v'
                  ? { x1: cx - l, y1: cy, x2: cx + l, y2: cy }
                  : { x1: cx, y1: cy - l, x2: cx, y2: cy + l };
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <line {...raya} stroke="#f8fafc"
                          strokeWidth={MIRA_PX.borde * 2 * u} strokeLinecap="round" />
                    <line {...raya} stroke="#8a5a3b"
                          strokeWidth={MIRA_PX.borde * u} strokeLinecap="round" />
                    <circle
                      cx={cx} cy={cy} r={MIRA_PX.corte * u}
                      fill={corte.firme ? '#8a5a3b' : '#f8fafc'}
                      stroke="#8a5a3b" strokeWidth={MIRA_PX.borde * u}
                    />
                  </g>
                );
              })()}

              {/* Los rótulos van al final: el centro de un recinto cae muchas
                  veces encima de un muro y si no quedan tachados.

                  Los enciende y los apaga el ojo de la barra. Apagados por
                  defecto: lo primero que uno hace al abrir el editor es mirar
                  el plano, y el plano con un nombre y un número encima de cada
                  cuarto es menos plano. */}
              {verAreas && medicion?.regiones.map((g, i) => (
                <text
                  key={i} x={g.x} y={g.y}
                  textAnchor="middle"
                  fontSize={0.28}
                  paintOrder="stroke"
                  stroke="#f8fafc" strokeWidth={0.1} strokeLinejoin="round"
                  // El rótulo se tiñe de la columna donde está sumando: casi
                  // negro mientras sea interna —que es lo normal en una región
                  // cerrada— y el color de su clase en cuanto el que vende la
                  // saque de ahí, sea a externa, a lote o a azotea.
                  //
                  // Salvo que tenga estado: un cuarto no tiene borde propio
                  // —lo dibujan las paredes de sus vecinos— así que el rótulo es
                  // su única voz, y ahí el estado le gana a la clase. Es el
                  // único lugar donde los dos ejes compiten por el mismo píxel.
                  fill={tintaDe(planta.estados?.[g.nombre], vistaObra)
                    ?? TINTA_ROTULO[g.clase]}
                  style={{ ...SOLO_PLANO, pointerEvents: 'none' }}
                >
                  {g.nombre}
                  <tspan x={g.x} dy={0.34} fontSize={0.24}
                         fill={Math.abs(g.area - g.referencia) < 0.005 ? '#78716c' : '#8a5a3b'}>
                    {m2(g.area)} m²
                  </tspan>
                </text>
              ))}

              {/* La capa que se come el gesto mientras se traza o se parte. Lleva
                  la clase de agarre para que el pan no se la lleve, y el
                  hit-test lo hace `paredEn` en metros: así «dividir» acierta la
                  pared aunque el dedo caiga 20 cm al lado.

                  Desborda el encuadre un kilómetro por lado A PROPÓSITO. Ceñida
                  al viewBox no alcanza: el SVG conserva la proporción, así que
                  deja bandas vacías a los costados —y con el zoom afuera, más
                  todavía—. Tocar ahí caía en el vacío y el gesto no arrancaba
                  nunca (medido: el trazo no creaba nada y el modo quedaba
                  encendido, como si el botón no hiciera nada). */}
              {modo && (
                <rect
                  x={vista.x0 - 1000} y={vista.y0 - 1000}
                  width={ancho + 2000} height={alto + 2000}
                  fill="transparent"
                  data-fondo-modo=""
                  className={`${AGARRE} cursor-crosshair`}
                  onPointerDown={trazarDesde}
                  onPointerMove={trazarHasta}
                  onPointerUp={trazarFin}
                  onPointerCancel={() => { setTrazo(null); setMira(null); setFantasma(null); }}
                />
              )}

              {/* LA GUÍA DEL IMÁN ORTOGONAL. Va al final de todo porque es lo
                  único que tiene que verse por encima del plano entero: es la
                  respuesta a un gesto en curso, no una capa del dibujo.

                  Sin agarres y sin relleno (`pointer-events:none`): el dedo
                  está arrastrando un vértice y cualquier cosa que reciba el
                  puntero acá se lo robaría a mitad de camino.

                  Cruza la vista de lado a lado para que se lea como un EJE y no
                  como un segmento: lo que dice es «estás en la misma columna
                  que aquel vértice», y eso no tiene largo. Ver `imantarVertice`. */}
              {iman?.x != null && (
                <line
                  x1={iman.x} y1={vista.y0 - 1000} x2={iman.x} y2={vista.y0 + alto + 1000}
                  stroke="#5f6f80" strokeWidth={0.02} strokeDasharray="0.12 0.1"
                  pointerEvents="none" data-guia-iman="x"
                />
              )}
              {iman?.y != null && (
                <line
                  x1={vista.x0 - 1000} y1={iman.y} x2={vista.x0 + ancho + 1000} y2={iman.y}
                  stroke="#5f6f80" strokeWidth={0.02} strokeDasharray="0.12 0.1"
                  pointerEvents="none" data-guia-iman="y"
                />
              )}
              </g>{/* fin de `ESCENA` — ver el comentario de su apertura */}

              {/* ── LA MIRA, Y VA FUERA DE `ESCENA` A PROPÓSITO ──────────────
                  Está acá abajo, hermana del grupo y no adentro, por UNA razón
                  concreta: la lupa es un `<use href="#escena-planta">`, así que
                  clona todo lo que viva dentro de ese grupo. La mira adentro se
                  clonaba a sí misma ×3 y se comía la lupa.

                  El número que lo cierra, medido el 22-oct-2026 en el iPhone de
                  Daniel: el aro mide 14 px de pantalla, que dentro de la lupa
                  salen 84 px sobre un disco de 104 — el 65 % de la superficie,
                  con un halo blanco encima. La lupa existe para ver el detalle
                  y lo único que mostraba era su propia mira. Palabras suyas:
                  «el marcador es muy grande y tapa todo lo que está detrás, que
                  es lo que se supone que uno quiere ver».

                  Sacarla de acá es lo que deja la lupa con SÓLO la cruz fija
                  —la que dibuja el `<svg>` de la lupa, en píxeles de pantalla,
                  y con su hueco en el medio—. El plano la sigue viendo igual.

                  Mudarla NO cambia dónde se dibuja: el zoom y el paneo los
                  aplica la librería sobre el `<div>` que envuelve al `<svg>`
                  entero, no sobre este grupo, así que las coordenadas siguen
                  siendo las mismas de siempre en metros.

                  Y queda ARRIBA del plano, que antes no: estaba a media lista y
                  los rótulos de área la tachaban. Una mira tachada por un
                  rótulo no sirve para apuntar. */}
              {mira && !mira.muda && (() => {
                // Todos los tamaños salen de acá: la mira se dibuja en píxeles
                // de pantalla llevados a metros al zoom vivo, para que mida lo
                // mismo con el plano completo que con un pinch de ×8. Ver
                // `MIRA_PX`. Lo único en metros son `mira.x`/`mira.y` —dónde
                // cae— y el largo de las guías, que cruzan el encuadre.
                const u = unPx();
                const cruz = MIRA_PX.cruz * u;
                return (
                  // `data-mira` publica a cuántas líneas se pegó, para que el
                  // banco mida el imán por el DOM y no por píxeles ni colores.
                  <g data-mira={mira.guias.length} style={{ pointerEvents: 'none' }}>
                    {mira.guias.map((g) => (
                      <line
                        key={g.eje}
                        x1={g.eje === 'x' ? g.v : vista.x0}
                        y1={g.eje === 'x' ? vista.y0 : g.v}
                        x2={g.eje === 'x' ? g.v : vista.x1}
                        y2={g.eje === 'x' ? vista.y1 : g.v}
                        stroke="#8a5a3b" strokeWidth={MIRA_PX.linea * u}
                        strokeDasharray={`${MIRA_PX.raya * u} ${MIRA_PX.hueco * u}`}
                      />
                    ))}
                    {/* El halo es lo que deja ver la mira encima de un muro
                        negro, que es justo donde más se la busca. VELADO y no
                        macizo desde el 22-oct-2026: opaco era un tapón del
                        tamaño del aro justo donde uno está mirando. Daniel:
                        «hacerlo traslúcido para que se vea un poco lo que está
                        detrás; al final es importante tener ese contexto, para
                        eso es el valor de este zoom». A 0,45 el aro sigue
                        despegándose de un muro negro y el muro se sigue
                        leyendo por debajo. */}
                    <circle cx={mira.x} cy={mira.y} r={MIRA_PX.aro * u}
                            fill="#f8fafc" opacity={VELO_MIRA} />
                    <circle
                      cx={mira.x} cy={mira.y} r={MIRA_PX.aro * u}
                      fill="none" stroke="#8a5a3b" strokeWidth={MIRA_PX.borde * u}
                    />
                    {mira.guias.length > 0 && (
                      <circle cx={mira.x} cy={mira.y} r={MIRA_PX.macizo * u} fill="#8a5a3b" />
                    )}
                    <line
                      x1={mira.x - cruz} y1={mira.y} x2={mira.x + cruz} y2={mira.y}
                      stroke="#8a5a3b" strokeWidth={MIRA_PX.linea * u}
                    />
                    <line
                      x1={mira.x} y1={mira.y - cruz} x2={mira.x} y2={mira.y + cruz}
                      stroke="#8a5a3b" strokeWidth={MIRA_PX.linea * u}
                    />
                  </g>
                );
              })()}
            </svg>
          </TransformComponent>
        </TransformWrapper>

        {/* ── LA LUPA ──────────────────────────────────────────────────────
            Lo pidió Daniel el 22-oct-2026 mirando la de Láminas: «repliques
            esta función de lupa […] para aplicarla aquí en la parte de planos
            cuando se agregan paredes o áreas o cotas […] en teléfono, para
            poder ver a detalle; uno no va a elegir el punto como tal».

            Tres decisiones heredadas de Láminas tal cual, porque allá ya se
            pagaron:

            - FIJA arriba a la izquierda, NO pegada al dedo. La lupa pegada al
              dedo —la de Procore— tapa justo el destino: cuando buscás una
              esquina, lo que necesitás ver es lo que la lupa está cubriendo.
              Fija, el ojo sabe siempre dónde mirar; y a la izquierda porque la
              mano derecha nunca cruza hasta ahí.
            - `pointer-events-none` no es estilo: la mano pasa por encima
              mientras traza, y si la lupa cobrara el evento el trazo se
              cortaría solo al cruzarla.
            - La mira lleva un HUECO en el medio. Una cruz entera taparía el
              píxel que se está tratando de ver, que es para lo único que la
              lupa existe.

            Va FUERA del `TransformWrapper` a propósito: adentro el zoom y el
            paneo se la llevarían puesta, y lo que tiene que quedarse quieto es
            precisamente ella.

            Se cuelga de `mira`, que ya existía y ya se fija en todos los gestos
            de puntería: trazar una pared, un rectángulo de paredes, un área,
            una cota, mover un vértice y ahora mover un nodo de cota. Sin dedo
            abajo no hay `mira` y la lupa no se monta, así que no cuesta nada. */}
        {mira && (() => {
          // La talla se decide contra el LIENZO y no contra la ventana: este
          // editor también vive dentro de un modal, y ahí el lienzo queda
          // angosto aunque la pantalla sea grande. En esa caja la lupa estorba
          // por exactamente la misma razón que en un celular.
          const lupaPx = marcoLienzo.w && marcoLienzo.w < LIENZO_ANGOSTO
            ? LUPA_PX_ANGOSTA : LUPA_PX;
          // El lado de la ventana, en metros: lo que `lupaPx` de pantalla
          // abarcan AL ZOOM VIVO, dividido por el aumento. `enMetrosPx` ya lee
          // contra la caja del lienzo, así que el pinch de la librería y el
          // zoom nativo de Safari quedan los dos contemplados.
          const lado = enMetrosPx(lupaPx) / LUPA_ZOOM;
          if (!lado || !Number.isFinite(lado)) return null;
          return (
            <div
              data-lupa=""
              className="pointer-events-none absolute left-3 z-30 overflow-hidden rounded-full shadow-lg"
              // Arriba a la izquierda, pero DEBAJO de la barra de áreas: en
              // Láminas esa esquina está vacía y acá viven Interna/Externa/Lote.
              // Medido a 1400 × 959: la banda de pastillas ocupa de 8 a 50 px
              // del lienzo, y con `top-3` la lupa le caía encima justo cuando
              // Daniel necesita ver los dos. `ALTO_BARRA_SUP + 12` es el mismo
              // tope que ya respetan las paletas flotantes.
              style={{
                top: ALTO_BARRA_SUP + 12,
                width: lupaPx, height: lupaPx, border: `2px solid ${TINTA_LUPA}`,
                // VELADA, no maciza (22-oct-2026). El disco era `bg-stone-50`
                // opaco, así que la lupa se plantaba como un parche ciego sobre
                // su propia esquina del plano: todo lo que cubría dejaba de
                // existir. Velado, el plano a ×1 se sigue leyendo por debajo
                // del aumento a ×3 y la lupa deja de costar lo que muestra.
                // Daniel: «ya que al final es importante tener ese contexto;
                // para eso realmente es el valor de este zoom».
                backgroundColor: `rgba(250, 250, 249, ${VELO_MIRA})`,
              }}
            >
              <svg
                width={lupaPx} height={lupaPx}
                viewBox={`${mira.x - lado / 2} ${mira.y - lado / 2} ${lado} ${lado}`}
                // Sin esto el clon hereda `touch-none` y nada más, pero el
                // `data-tick` sí hace falta: React no vuelve a pintar este
                // `<svg>` cuando sólo cambia la geometría mutada en sitio, y la
                // lupa se quedaría un cuadro atrás del plano.
                data-tick={tick}
              >
                {/* El interruptor del texto. La variable baja por herencia al
                    shadow tree del clon —es lo único que cruza esa frontera—,
                    así que los rótulos y los números se apagan ACÁ ADENTRO sin
                    que el plano se entere. Ver `SOLO_PLANO`. */}
                <use href={`#${ESCENA}`}
                     style={{ '--lupa-texto': 'hidden' } as CSSProperties} />
              </svg>
              <svg width={lupaPx} height={lupaPx} className="absolute left-0 top-0">
                {/* Los brazos y el hueco van en píxeles FIJOS, no en proporción
                    del círculo: el hueco existe para no tapar el punto que se
                    está mirando, y ese punto no se achica porque la lupa se
                    achique. */}
                {[[-20, -6], [6, 20]].map(([a, b], i) => (
                  <g key={i} stroke={TINTA_LUPA} strokeWidth={2}>
                    <line x1={lupaPx / 2 + a} y1={lupaPx / 2} x2={lupaPx / 2 + b} y2={lupaPx / 2} />
                    <line x1={lupaPx / 2} y1={lupaPx / 2 + a} x2={lupaPx / 2} y2={lupaPx / 2 + b} />
                  </g>
                ))}
                {/* EL ACUSE DEL IMÁN, mudado del centro AL ARO (22-oct-2026).
                    Era un disco macizo de 8 px plantado exactamente sobre el
                    píxel que la lupa existe para mostrar: tapaba el punto justo
                    en el momento en que uno lo está colocando. En el aro no
                    tapa nada y encima dice MÁS que antes —CUÁL eje se pegó—:
                    arriba y abajo cuando el punto quedó en la misma columna que
                    su vecino, a los lados cuando quedó en el mismo renglón. */}
                {iman?.x != null && [true, false].map((arriba) => (
                  <line
                    key={`imx-${arriba}`} data-lupa-iman="x"
                    x1={lupaPx / 2} y1={arriba ? 0 : lupaPx}
                    x2={lupaPx / 2} y2={arriba ? 10 : lupaPx - 10}
                    stroke={TINTA_LUPA} strokeWidth={3}
                  />
                ))}
                {iman?.y != null && [true, false].map((izq) => (
                  <line
                    key={`imy-${izq}`} data-lupa-iman="y"
                    x1={izq ? 0 : lupaPx} y1={lupaPx / 2}
                    x2={izq ? 10 : lupaPx - 10} y2={lupaPx / 2}
                    stroke={TINTA_LUPA} strokeWidth={3}
                  />
                ))}
              </svg>
            </div>
          );
        })()}

        {/* LA FRANJA. En el teléfono es la banda clavada arriba de la barra de
            capas —la gráfica de la app de referencia—; en la compu no existe y
            cada paleta de acá adentro se planta junto a su elemento como
            siempre. Envuelve a TODAS porque la franja es UN solo sitio: nunca
            hay dos bandas, y abrir una capa ya cerraba el creador de las otras.
            Ver `Franja`. */}
        <Franja angosta={angosta} ranura={ranura}>

        {/* La paleta. La `key` es sólo el id de la pared —NO su largo—: con el
            largo dentro, aplicar la medida remontaba el componente y el campo
            perdía el foco a media palabra, justo ahora que se aplica sola. El
            número lo refresca un efecto que respeta el foco. */}
        {paleta && quePaleta === 'pared' && uno != null && (() => {
          const w = planta.paredes.find((x) => x.id === uno);
          if (!w) return null;
          return (
            <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
              <PaletaPared
                key={w.id}
                valor={m2(w.b - w.a)}
                onAplicar={(v) => aplicarCota(v, w.id)}
                onGirar={girar}
                onDividir={() => cambiarModo('dividir')}
                onBorrar={borrar}
                onVano={agregarVano}
                estado={w.estado ?? 'existente'}
                onEstado={(e) => ponerEstadoPared(w.id, e)}
              />
            </Flotante>
          );
        })()}

        {paleta && quePaleta === 'paredes' && (() => {
          const ws = planta.paredes.filter((w) => sel.includes(w.id));
          const total = ws.reduce((s, w) => s + (w.b - w.a), 0);
          return (
            <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
              <PaletaGrupo
                cuantas={ws.length}
                total={m2(total)}
                motivoUnir={motivoUnir()}
                motivoInterseccion={motivoInterseccion()}
                motivoAjustar={motivoAjustar()}
                onUnir={unir}
                onInterseccion={interseccion}
                onAjustar={ajustar}
                onGirar={girar}
                onBorrar={borrar}
              />
            </Flotante>
          );
        })()}

        {/* La `key` es la pared más el vano: cambiar de vano tiene que remontar
            el campo para que traiga el ancho del nuevo, y no el del anterior. */}
        {paleta && quePaleta === 'vano' && vano && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaVano
              key={`${vano.w.id}:${vano.v.id}`}
              ancho={m2(vano.v.b - vano.v.a)}
              tipo={vano.v.tipo}
              onAncho={aplicarAncho}
              onTipo={cambiarTipoVano}
              onBorrar={borrarVano}
              estado={vano.v.estado ?? 'existente'}
              onEstado={(e) => ponerEstadoVano(vano.w.id, vano.v.id, e)}
            />
          </Flotante>
        )}

        {paleta && quePaleta === 'region' && region && (() => {
          const d = region.area - region.referencia;
          return (
            <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
              <PaletaRecinto
                key={region.indice}
                nombre={region.nombre}
                area={m2(region.area)}
                delta={Math.abs(d) < 0.005
                  ? null
                  : `${d > 0 ? '+' : '−'}${m2(Math.abs(d))} m²`}
                clase={region.clase}
                unida={Object.values(planta.grupos ?? {}).includes(region.nombre)}
                onRenombrar={renombrar}
                onClase={(c) => clasificarRegion(region.nombre, c)}
                onDividir={() => cambiarModo('area')}
                onSeparar={separarAreas}
                onQuitar={quitarArea}
                estado={planta.estados?.[region.nombre] ?? 'existente'}
                onEstado={(e) => ponerEstadoRegion(region.nombre, e)}
              />
            </Flotante>
          );
        })()}

        {paleta && quePaleta === 'areas' && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaAreas
              cuantas={regiones.length}
              total={m2(regiones.reduce((s, g) => s + g.area, 0))}
              motivoUnir={motivoUnirAreas()}
              onUnir={unirAreas}
              onQuitar={quitarArea}
            />
          </Flotante>
        )}

        {paleta && quePaleta === 'divisor' && divisor && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaDivisor
              estado={divisor.estado ?? 'existente'}
              onBorrar={borrarDivisor}
              onEstado={(e) => ponerEstadoDivisor(divisor.id, e)}
            />
          </Flotante>
        )}

        {paleta && quePaleta === 'cota' && cota && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaCota
              medida={m2(Math.hypot(cota.x2 - cota.x1, cota.y2 - cota.y1))}
              onBorrar={borrarCota}
            />
          </Flotante>
        )}

        {/* La `key` es la pared vecina: cambiar de cota tiene que remontar el
            campo para que traiga el largo de la nueva. */}
        {paleta && quePaleta === 'vecina' && paredVecina && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaCotaVecina
              key={paredVecina.id}
              valor={m2(paredVecina.b - paredVecina.a)}
              onAplicar={(v) => medidaVecina(v, paredVecina.id)}
            />
          </Flotante>
        )}

        {/* La `key` es el lado: cambiar de cota tiene que remontar el campo para
            que traiga el largo del nuevo. */}
        {paleta && quePaleta === 'ladoVecino' && cotaLadoVecino && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaCotaVecina
              key={`${cotaLadoVecino.id}:${cotaLadoVecino.lado}`}
              valor={m2(Math.hypot(
                cotaLadoVecino.x2 - cotaLadoVecino.x1,
                cotaLadoVecino.y2 - cotaLadoVecino.y1,
              ))}
              nota="empuja el lado tomado"
              onAplicar={(v) => medidaLadoVecino(v, cotaLadoVecino.lado)}
            />
          </Flotante>
        )}

        {paleta && quePaleta === 'libre' && libre && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaLibre
              key={libre.id}
              nombre={libre.nombre}
              ancho={libre.celdas?.length || libre.vertices?.length ? null : m2(libre.w)}
              alto={libre.celdas?.length || libre.vertices?.length ? null : m2(libre.h)}
              area={m2(areaLibre(libre))}
              clase={libre.clase ?? 'externa'}
              forma={metodoLibre(libre)}
              vertices={libre.vertices?.length ?? 0}
              fijo={!!libre.fijo}
              nodo={nodo}
              onRenombrar={renombrarLibre}
              onAncho={(v) => medidaLibre('w', v)}
              onAlto={(v) => medidaLibre('h', v)}
              onClase={(c) => clasificarLibre(libre.id, c)}
              onBorrar={borrarLibre}
              onNodo={cambiarNodo}
              onFijar={fijarLibre}
              onFrente={() => ordenarLibre('frente')}
              onAtras={() => ordenarLibre('atras')}
              estado={libre.estado ?? 'existente'}
              onEstado={(e) => ponerEstadoLibre(libre.id, e)}
            />
          </Flotante>
        )}

        {paleta && quePaleta === 'mueble' && mueble && (
          <Flotante x={paleta.x} y={paleta.y} corrimiento={corrimiento} onCorrer={setCorrimiento}>
            <PaletaMueble
              key={mueble.id}
              nombre={nombreMueble(mueble)}
              ancho={m2(mueble.w)}
              alto={m2(mueble.h)}
              radio={esquinaSel == null || esElectrica(mueble.tipo)
                ? null : m2(mueble.radios?.[esquinaSel] ?? 0)}
              candado={muebleFlexible(mueble) || esElectrica(mueble.tipo) ? null : candado}
              grupos={gruposVivos()}
              electrico={esElectrica(mueble.tipo)
                ? { altura: m2(alturaDe(mueble) ?? 0), anclada: !!mueble.ancla }
                : null}
              onAncho={(v) => medidaMueble('w', v)}
              onAlto={(v) => medidaMueble('h', v)}
              onRadio={radioEsquina}
              espejo={!!mueble.espejo}
              onGirar={girarMueble}
              onEspejo={espejarMueble}
              onCopiar={copiarMueble}
              onCandado={() => setCandado((v) => !v)}
              onReemplazar={abrirReemplazo}
              onGuardar={guardarMueblePropio}
              onBorrar={borrarMueble}
              onAltura={alturaMueble}
              onVoltear={voltearMueble}
              estado={mueble.estado ?? 'existente'}
              onEstado={(e) => ponerEstadoMueble(mueble.id, e)}
              forma={muebleFlexible(mueble)}
              nodos={mueble.vertices?.length ?? null}
              nodo={nodo}
              onNodo={cambiarNodoObjeto}
            />
          </Flotante>
        )}

        {/* El catálogo de mobiliario. Se planta abajo y al centro como la paleta
            de agregar área —es del mismo tipo: no cuelga de nada del dibujo— y
            se arrastra igual. Es la más alta de todas, así que arranca más
            arriba del borde para que entre entera en un teléfono.

            «Abajo» es el borde de abajo MENOS la barra de capas: desde que la
            barra flota encima del lienzo, el borde real del lienzo está detrás
            de ella y plantar ahí deja la paleta tapada. Ver `ALTO_BARRA_PIE`. */}
        {catalogo && marcoLienzo.h > 0 && (
          <Flotante
            x={marcoLienzo.w / 2} y={marcoLienzo.h - 6 - altoPie}
            corrimiento={corrCat} onCorrer={setCorrCat}
          >
            <PaletaCatalogo
              copiado={copiado}
              reemplazando={reemplazando && muebleSel != null}
              armada={armado?.modelo.clave ?? null}
              onCrear={crearMueble}
              onArmar={armarObjeto}
              onPegar={pegarMueble}
              onArmarPegado={armarPegado}
              onArrastrar={arrastrar}
              onSoltar={soltar}
              onReemplazar={reemplazarMueble}
              onCambioPropio={avisarCambioPropio}
              onPublicarPropio={puedePublicar ? publicarPropio : undefined}
            />
          </Flotante>
        )}

        {/* La paleta de agregar área. Va al final de las paletas y se planta
            abajo y al centro del lienzo —cerca del pulgar, lejos del dibujo que
            se está mirando—, de donde se puede arrastrar como todas. */}
        {herramientas && marcoLienzo.h > 0 && (
          <Flotante
            x={marcoLienzo.w / 2} y={marcoLienzo.h - 6 - altoPie}
            corrimiento={corrHerr} onCorrer={setCorrHerr}
          >
            <PaletaHerramientas
              modo={modo}
              onRectangulo={() => cambiarModo('libre')}
              onVarita={() => cambiarModo('varita')}
            />
          </Flotante>
        )}

        {/* La paleta de agregar pared. Mismo sitio que la de áreas —nunca están
            las dos a la vez: abrir una capa cierra el creador de las otras—. */}
        {herrPared && marcoLienzo.h > 0 && (
          <Flotante
            x={marcoLienzo.w / 2} y={marcoLienzo.h - 6 - altoPie}
            corrimiento={corrHerrP} onCorrer={setCorrHerrP}
          >
            <PaletaCrearPared
              metodo={metodoPared}
              onUna={() => elegirMetodoPared('una')}
              onSeguidas={() => elegirMetodoPared('seguidas')}
              onRect={() => elegirMetodoPared('rect')}
              onVarita={() => elegirMetodoPared('varita')}
            />
          </Flotante>
        )}

        </Franja>

        {/* La pastilla del modo. Cuelga del borde de arriba MÁS la barra que
            ahora flota ahí: pegada a `top-3` quedaba justo detrás del selector
            de nivel. Ver `ALTO_BARRA_SUP`. */}
        {/* Acá vivía la pastilla café que narraba el gesto de cada modo
            («Apoyá de nuevo y estirá: largo y dirección de la pared»). Se fue
            entera el 19-oct-2026: Daniel la señaló en una captura tapándole el
            medio del plano, sin forma de cerrarla.
            Lo único que se queda es el botón CANCELAR del objeto armado, y se
            queda porque no es un texto de apoyo: es la única salida para el que
            eligió la pieza equivocada y no quiere dejarla puesta para borrarla
            después. Por eso la condición ahora es `armado`, no `modo`: sin
            objeto en la mano no se pinta nada. */}
        {modo === 'objeto' && armado && (
          <div className="pointer-events-none absolute inset-x-0 flex justify-center"
               style={{ top: ALTO_BARRA_SUP + 12 }}>
            <p data-pastilla-modo={modo}
               className="flex items-center gap-2 rounded-full bg-[#8a5a3b] px-3 py-1
                          text-xs font-medium text-white">
              {/* El texto describe el gesto de DOS APOYOS, que es el que
                  funciona en los dos aparatos: apoyar y ubicar, soltar, apoyar
                  y estirar. El arrastre de una pasada del mouse sigue andando y
                  no hace falta anunciarlo —quien lo tiene en la mano lo prueba
                  solo—; anunciar los dos caminos a la vez es lo que volvía la
                  pastilla ilegible en una pantalla de teléfono. */}
              {/* Para el objeto el texto nombra las dos mitades del gesto: el
                  apoyo muestra la pieza y el levantar la deja. Decirlo importa
                  porque invita a NO soltar enseguida, que es donde vive toda la
                  previsualización: apoyado, el fantasma se corre. */}
              {/* El NOMBRE de la pieza que está en la mano, y nada más. No
                  explica el gesto —eso era lo que molestaba—; dice QUÉ vas a
                  dejar, que es lo que no se puede deducir mirando el fantasma
                  cuando el catálogo tiene dos camas parecidas. */}
              {armado.modelo.nombre}
              {/* La salida SIN poner nada: el que eligió el módulo equivocado
                  tiene que poder arrepentirse sin dejar una pieza en el plano
                  para después borrarla. `pointer-events-auto` porque el
                  envoltorio de la pastilla no recibe toques —tiene que dejar
                  dibujar debajo— y este botón sí. */}
              {modo === 'objeto' && armado && (
                <button
                  type="button"
                  data-soltar-armado=""
                  onClick={desarmar}
                  className="pointer-events-auto -mr-1.5 rounded-full bg-white px-2 py-0.5
                             text-[0.68rem] font-semibold text-[#8a5a3b] active:bg-stone-200"
                >
                  Cancelar
                </button>
              )}
            </p>
          </div>
        )}

        {errorPdf && (
          <p className="absolute left-3 rounded bg-white/90 px-2 py-1 text-xs text-red-700"
             style={{ top: ALTO_BARRA_SUP + 12 }}>
            No se pudo cargar el plano de fondo: {errorPdf}
          </p>
        )}

      </div>

      {/* ── resultado ──
          FLOTA sobre el lienzo, pegada abajo, por el mismo pedido del
          8-oct-2026 y los mismos dos motivos que la de arriba: ver la nota de
          allá.

          Se ancla al BORDE DE ABAJO (`self-end`), y de ahí que los dos avisos
          vivan acá adentro y por encima de las capas: cuando aparecen, la barra
          crece hacia arriba y las cuatro capas no se corren ni un píxel. Eso
          importa porque los avisos van y vienen solos, a mitad de un gesto, y
          lo que está bajo el pulgar no se puede mover solo. Antes el de recinto
          abierto tenía que flotar aparte sobre el lienzo justo para esquivar
          ese problema —en el flujo de la barra le cambiaba el alto al lienzo
          16 px a mitad de gesto y la pared se iba 40 cm a donde nadie la
          llevó—; ahora la barra ya no le come alto a nadie y el caso especial
          sobra. */}
      {/* El `min-w-0` es lo que mantiene el pie DENTRO de la pantalla. Como
          casilla de una rejilla, su mínimo por defecto es `auto` —el ancho de
          su contenido—, así que la fila de la paleta lo estiraba: medido el
          18-oct-2026, el pie salía 452 px en una pantalla de 390 y la pastilla
          se iba 50 px fuera por la derecha. No se notaba antes porque la franja
          era `absolute` y no contaba para el ancho; ahora va en el flujo, dentro
          del pill. Con el mínimo en 0 manda la pantalla y es la FILA la que se
          desliza, que es justo la propuesta que eligió Daniel. */}
      {/* El respiro de abajo lo pone el TELÉFONO, no un número elegido a ojo.
          Daniel lo reportó el 19-oct-2026 en un iPhone 17 Pro Max: la franja
          «se ve cortada por el radio de las esquinas». ArquiFlow está instalada
          como app —sus capturas no tienen barra de Safari—, y en ese modo iOS
          dibuja de borde a borde: el indicador de inicio y el redondeo de la
          esquina se comen lo último de la pantalla, que desde que planta va a
          pantalla completa es justo esta franja.
          `--franja-abajo` es cuánto reserva ESE aparato; en la compu y en un
          teléfono sin muesca vale 0 y la franja queda igual que antes. Por eso
          se suma al padding en vez de achicar la barra: subirla un número fijo
          acierta en un modelo y falla en el siguiente.
          El respiro PROPIO bajó de 1,25 rem a 0,5 rem el 21-oct-2026: con el
          recorte sumado eran 54 px de aire bajo la pastilla, y Daniel lo contó
          entre lo que «sobra demasiado».

          Y el 29-oct-2026 se tocó el RECORTE, que hasta ahora era intocable.
          Medido sobre su captura: entre el borde de abajo de la pastilla y el
          borde de la pantalla quedaban 140 px físicos = 47 pt, de los cuales 34
          son el `env(safe-area-inset-bottom)` entero. Esos 34 pt son la reserva
          que Apple pide para una barra OPACA pegada al borde; esta pastilla no
          lo es —flota, con sus esquinas redondeadas y el plano pasando por
          debajo—, y lo único que de verdad hay que esquivar es el indicador de
          inicio: una barra de 5 pt a unos 8 del borde. Descontarle 0,75 rem al
          recorte deja la pastilla a 22 pt del borde y sus botones a 28, bien
          por encima de los ~20 pt donde iOS se queda el gesto. El `max` es el
          que mantiene el piso de 0,5 rem en la compu y en un teléfono sin
          muesca, donde `--franja-abajo` vale 0 y restar daría negativo. */}
      <div ref={pieRef}
           className="pointer-events-none z-10 col-start-1 row-start-2 min-w-0 self-end px-3 pt-2"
           style={{ paddingBottom: 'max(0.5rem, calc(var(--franja-abajo) - 0.75rem))' }}>
        {/* El recinto abierto al exterior: un defecto de la planta que hay que
            ver en el momento en que aparece, no un número que se consulta. */}
        {(() => {
          if (!medicion || medicion.sueltos.length === 0) return null;
          const texto = `${medicion.sueltos.join(', ')} quedó abierto al exterior.`;
          if (texto === callado) return null;
          // Este NO se borra como se borraron los carteles del gesto: no enseña
          // nada, avisa de un defecto REAL de la planta —un recinto que no
          // cierra— y eso cambia la medición. Lo que gana es la salida: se toca
          // y se calla. `pointer-events-auto` porque el envoltorio del pie no
          // recibe toques, para poder dibujar debajo.
          return (
            <button type="button" data-sueltos="" onClick={() => setCallado(texto)}
                    className="pointer-events-auto mx-auto mb-1 flex w-fit max-w-full items-center gap-1.5
                               rounded bg-white/90 px-2 py-1 text-center text-xs text-amber-700">
              <span className="truncate">{texto}</span>
              <span aria-hidden className="shrink-0 font-semibold text-amber-900/50">✕</span>
            </button>
          );
        })()}

        {/* El aviso del modo va SIEMPRE, no en el else de la medición. Estaba
            como alternativa —medición o aviso— y eso lo volvía invisible justo
            cuando más hace falta: con las áreas en pantalla, «punto puesto,
            tocá el otro extremo» y «trazo muy corto» no se veían, y el editor
            parecía no responder.

            Con fondo propio desde que la barra es transparente: sin él, estas
            letras ámbar caen sobre el dibujo y se vuelven ilegibles. */}
        {/* Se queda porque por acá pasan los ERRORES y los resultados —«Trazo
            muy corto», «Cuarto de 3,00 × 4,00 m levantado», «no se pudo
            publicar»—, no las instrucciones: esas ya se fueron del editor. Pero
            ahora se toca y se va, que era la queja. */}
        {aviso && (
          <button type="button" data-aviso="" onClick={() => setAviso(null)}
                  className="pointer-events-auto mx-auto mb-1 flex w-fit max-w-full items-center gap-1.5
                             rounded bg-white/90 px-2 py-1 text-center text-xs text-amber-700">
            <span className="truncate">{aviso}</span>
            <span aria-hidden className="shrink-0 font-semibold text-amber-900/50">✕</span>
          </button>
        )}

        {/* LA OFERTA DE APRENDER. Va con botón y no como aviso de texto porque
            no informa: pregunta. Y va acá, en la misma franja que el aviso, para
            que la respuesta esté donde Daniel ya está mirando —el aviso de
            «Quedó Cama king de …» sale justo encima— y no al otro lado de la
            pantalla. */}
        {oferta && (
          <p className="mx-auto mb-1 flex w-fit max-w-full items-center gap-2 rounded
                        bg-white/90 px-2 py-1 text-xs text-stone-700"
             data-oferta-huella="">
            <span className="truncate">
              ¿Que {m2(oferta.w)} × {m2(oferta.h)} m sea siempre {oferta.nombre}?
            </span>
            <button type="button" onClick={aprenderOferta}
                    className="shrink-0 rounded bg-stone-900 px-2 py-0.5 font-semibold text-white"
                    data-aprender-huella="">
              Aprender
            </button>
            <button type="button" onClick={() => setOferta(null)} aria-label="Ahora no"
                    className="shrink-0 px-1 text-stone-400">
              ✕
            </button>
          </p>
        )}
        {/* LAS CUATRO CAPAS, en UNA fila con deshacer y rehacer, que es como las
            dibujó Daniel el 6-oct-2026. Antes eran dos filas —tres capas arriba
            y acotar abajo con las flechas— porque cada botón llevaba su palabra
            y su ojo pegados y no entraban cuatro. Sin rótulo y sin ojo entran
            los cuatro con aire.

            El orden es el del dibujo —mobiliario, acotar, área, pared—, y la
            fila de abajo es la que cae bajo el pulgar: ahí va lo que se usa todo
            el tiempo, no lo que borra el trabajo.

            Dividir y Borrar NO están acá: son operaciones SOBRE la pared tomada
            y su lugar es la paleta flotante, al lado de la pared. Deshacer y
            rehacer sí, por lo contrario: actúan sobre la planta entera y tienen
            que estar a mano aunque no haya nada tomado —que es justo el momento
            en que uno se arrepiente—. */}
        {/* Las cuatro capas van CENTRADAS en la barra, y deshacer/rehacer
            flotan a la izquierda fuera del flujo.

            Con `ml-auto` las cuatro quedaban pegadas al borde derecho: el pulgar
            las alcanzaba, pero sólo el pulgar derecho, y en una pantalla ancha
            el grupo se iba al rincón. Un `justify-center` simple tampoco sirve:
            con las flechas en el flujo el ancho de las flechas se descuenta de
            un lado solo y el grupo vuelve a correrse a la derecha. De ahí la
            rejilla 1fr·auto·1fr con una columna vacía de contrapeso: las capas
            quedan al centro REAL de la barra y las flechas a la izquierda sin
            empujarlas. Las flechas siguen chicas a propósito: deshacer se toca
            cuando ya salió mal, no veinte veces por minuto.

            El `gap-3` es de Daniel, el 8-oct-2026: «esos cuatro íconos que estén
            un poquito a la derecha, porque se ve un poco pegado a lo que son las
            flechas de deshacer y rehacer». En un teléfono de 390 px las cuatro
            capas son 232 y las dos flechas 76, así que las columnas de 1fr se
            quedaban en nada y las capas arrancaban pegadas a la flecha sin un
            píxel de aire. Los 12 px salen del lado que sobra —la columna vacía—,
            de modo que el grupo se corre justo lo que hace falta. */}
        {/* EL PILL ÚNICO. Pedido de Daniel el 18-oct-2026: «falta envolver toda
            la franja de iconos de abajo en el pill, como en la maqueta».

            Antes eran DOS cajas apoyadas una contra otra —la franja flotando
            sobre la barra de capas—, y dos cajas apoyadas no son un pill: entre
            ellas se cuela el lienzo, y cuando aparece un aviso de los de arriba
            el alto de la barra cambia y las parte en el medio. Acá son UNA sola
            caja: el borde, el fondo y el redondeo son de este div, la franja
            entra por el portal en la ranura de arriba y las cuatro capas van
            abajo.

            El `overflow-hidden` es lo que recorta la franja mientras crece, y
            acá es seguro: ningún botón de capa abre un desplegable `absolute
            top-full` —el ojo se dibuja DENTRO del ícono—, así que no hay nada
            que clipear por error.

            En la compu no va nada: ahí la paleta sigue naciendo flotante al lado
            de su elemento y la barra de capas no lleva pastilla. */}
        <div
          data-pill-pie={angosta ? '' : undefined}
          className={angosta
            ? 'pointer-events-auto overflow-hidden rounded-[18px] border border-stone-200'
              + ' bg-white/95 px-2 py-1.5 shadow-[0_8px_28px_rgba(0,0,0,0.10)] backdrop-blur-sm'
            : ''}
        >
          {/* La ranura: el hueco de arriba donde la `Franja` portea la paleta
              contextual. Vacío cuando no hay nada abierto —no ocupa alto— y por
              eso el pill, sin paleta, es exactamente la barra de capas. */}
          <div ref={setRanura} />
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          {/* CON CAJA, desde el 8-oct-2026. Lo anticipó el propio Daniel al
              pedir la barra transparente: «probablemente deberíamos generar como
              si fueran botones donde estarían el de deshacer y rehacer; esto
              ¿por qué lo digo? si no quedaría un poquito perdido».

              Tenía razón y es consecuencia directa del otro cambio: mientras la
              barra era gris, el fondo gris claro del `hover` ya dibujaba un
              botón implícito y dos íconos pelados se leían como parte de la
              barra. Con la barra del mismo color que el lienzo, sin caja los dos
              íconos quedan flotando sobre el dibujo sin nada que los sostenga.

              Caja CHICA y no la de las capas: siguen siendo 36 px contra 52, que
              es la jerarquía que ya estaba puesta a propósito —deshacer se toca
              cuando ya salió mal, no veinte veces por minuto—. */}
          <div className="pointer-events-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => viajar('atras')}
            disabled={!atras.length}
            className={CAJA_CHICA}
            aria-label="Deshacer"
            title={atras.length ? 'Deshacer' : 'Nada que deshacer'}
          >
            <ArrowUUpLeft size={18} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => viajar('adelante')}
            disabled={!adelante.length}
            className={CAJA_CHICA}
            aria-label="Rehacer"
            title={adelante.length ? 'Rehacer' : 'Nada que rehacer'}
          >
            <ArrowUUpRight size={18} weight="bold" />
          </button>
          </div>
          {/* Descartar vivía ACÁ, con las flechas, porque la cabecera de la
              maqueta son exactamente cuatro cosas y no le quedaba lugar. Desde
              el 6-oct-2026 se fue al menú de los tres puntos, que es el lugar
              que Daniel le abrió a propósito. Abajo quedan sólo deshacer y
              rehacer, que es lo que se usa con el dedo sin mirar. */}

          {/* El `gap-4` lo pidió Daniel el 8-oct-2026: «que estén más separados
              entre sí para que sea más fácil a nivel del celular poder no
              apretar uno u otro». Con los 8 px de antes, dos cajas de 52 px
              dejaban un pasillo de 8 entre centros a 60 px: un pulgar mide más
              que eso y el error caía en la capa de al lado —que no es un error
              inocuo, porque cambia con qué se está trabajando—.

              16 px es lo máximo que entra sin partir la fila en un teléfono de
              390: 24 de padding + 24 de la rejilla + 68 de las flechas + 208 de
              las cuatro cajas + 48 de los tres huecos = 372 de 390. Subir a
              gap-5 manda la última capa abajo. */}
          <div className="pointer-events-auto flex items-center gap-4">
            {/* «Objetos» y ya no «Mobiliario»: la capa dejó de ser sólo muebles
                —tiene sanitarios y toda la lámina eléctrica— y Daniel lo
                corrigió el 10-oct-2026, «ya que mobiliario no abarca todo». */}
            {botonCapa('mueble', <Armchair size={24} weight="bold" />, 'Objetos')}
            {/* ACOTAR es una capa como las otras tres, y de ahí le sale gratis
                el prender y apagar que pidió Daniel, más la regla de la capa
                activa: acotando no se mueve una pared por accidente. */}
            {botonCapa('cota', <Ruler size={24} weight="bold" />, 'Acotar')}
            {botonCapa('area', <Polygon size={24} weight="bold" />, 'Área')}
            {botonCapa('pared', <LineSegment size={24} weight="bold" />, 'Pared')}
          </div>
          {/* La columna de contrapeso. Vacía a propósito: es lo que mantiene a
              las capas en el centro. */}
          <div />
        </div>
        </div>

        {/* Los dos totales se fueron ARRIBA, a la pastilla, y su diferencia
            contra el modelo vive en la tabla, que es donde se la va a leer con
            calma. El aviso de recinto abierto se fue FLOTANDO sobre el lienzo
            —arriba en este archivo—: ver el comentario de allá. */}
        {/* Acá vivía un párrafo que explicaba el editor entero. Lo sacó Daniel:
            «quita mucha área de trabajo y realmente no aporta». Tenía razón —en
            un celular se comía un cuarto de la pantalla, y lo que explicaba ya
            lo dicen los gestos, los avisos del modo y los `title` de cada
            botón—. Si algo del editor necesita explicarse por escrito, el lugar
            es la GUIA del proyecto, no el lienzo. */}
      </div>

      {/* La tabla, al final del árbol: va encima de todo lo demás. */}
      {tabla && medicion && (
        <TablaAreas
          medicion={medicion}
          recinto={recinto}
          libreSel={libreSel}
          omitidas={planta.omitidas ?? []}
          vista={vistaObra}
          final={medicionFinal}
          onDevolver={devolverArea}
          onRegion={(nombre) => {
            soltarTodo();
            setAviso(null);
            // Elegirla desde la tabla enciende el ojo: si no, la hoja se cierra
            // y la región elegida no se ve en ninguna parte.
            encenderAreas();
            setRecintos(nombre === recinto ? [] : [nombre]);
          }}
          onLibre={(id) => {
            soltarTodo();
            setAviso(null);
            setLibreSel(id === libreSel ? null : id);
          }}
          onClaseRegion={clasificarRegion}
          onClaseLibre={clasificarLibre}
          onCerrar={() => setTabla(false)}
        />
      )}

      {/* LA LÁMINA, a pantalla completa sobre el editor.
          No abre en otra pestaña ni se baja sola: lo primero que uno hace con
          un render es MIRARLO, y recién si sirve se lo lleva. Bajar siempre
          llenaría la carpeta de descargas de intentos.

          ⚠️ Es una ilustración, no un plano. El motor es generativo y puede
          correr un muro aun con la fidelidad en alto, así que el cartel de
          abajo no es un adorno legal: una lámina con un muro movido que sale
          con un precio al lado es una tergiversación comercial. */}
      {render && (
        <div
          className="absolute inset-0 z-50 flex flex-col bg-stone-900/80 p-4"
          data-render-lamina=""
        >
          {render === 'eligiendo' ? (
            /* El paso de elección. Existe porque la referencia es lo que más
               mueve el resultado y «sin referencia» es hoy la que mejor respeta
               la planta: esconderla detrás de un archivo en disco obligaba a
               elegir por el de al lado. */
            <div className="m-auto w-full max-w-lg rounded-2xl bg-white p-5">
              <h3 className="text-sm font-semibold text-brand-gray-500">
                Referencia de estilo
              </h3>
              <p className="mt-1 text-xs text-brand-gray-400">
                La planta manda la geometría; esto sólo pinta. Una referencia que
                sea una lámina entera le presta también su lote y sus autos.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {[
                  ...ESTILOS_RENDER,
                  ...(subida
                    ? [{ id: 'subida', nombre: subida.nombre, nota: 'La que subiste.',
                         url: subida.url, miniatura: subida.url }]
                    : []),
                ].map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setEstiloId(e.id)}
                    className={`flex items-center gap-3 rounded-xl border p-2 text-left ${
                      estiloId === e.id
                        ? 'border-brand-green-100 bg-brand-green-100/10'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-estilo={e.id}
                  >
                    {e.miniatura ? (
                      <img src={e.miniatura} alt="" className="h-12 w-12 rounded-lg object-cover" />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-lg
                                       border border-dashed border-stone-300 text-stone-400">
                        <X size={14} weight="bold" />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-brand-gray-500">
                        {e.nombre}
                      </span>
                      <span className="block text-[11px] leading-tight text-brand-gray-400">
                        {e.nota}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <label className="mt-3 flex cursor-pointer items-center justify-center gap-2
                                rounded-xl border border-dashed border-stone-300 px-3 py-2
                                text-xs font-semibold text-brand-gray-400 hover:border-brand-green-100">
                <ArrowLineDown size={14} weight="bold" className="rotate-180" />
                Subir una referencia…
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(ev) => tomarSubida(ev.target.files?.[0])}
                />
              </label>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRender(null)}
                  className="rounded-xl border border-stone-300 px-4 py-2 text-xs
                             font-semibold text-brand-gray-500"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => { void generarRender(); }}
                  className="ml-auto flex items-center gap-2 rounded-xl bg-brand-green-100
                             px-4 py-2 text-xs font-semibold text-black"
                  data-generar-render=""
                >
                  <Cube size={15} weight="bold" />
                  Generar lámina
                </button>
              </div>
            </div>
          ) : render === 'generando' ? (
            <div className="m-auto flex flex-col items-center gap-3 rounded-2xl bg-white px-8 py-10">
              <SpinnerGap size={30} weight="bold" className="animate-spin text-brand-green-200" />
              <span className="text-sm font-semibold text-brand-gray-500">
                Generando la lámina…
              </span>
              {/* El tiempo se dice de entrada. Sin esto el que espera piensa que
                  se colgó a los 20 s y toca otra vez, que gasta otro render. */}
              <span className="max-w-[16rem] text-center text-xs text-brand-gray-400">
                Toma cerca de un minuto. Podés dejar la pantalla abierta.
              </span>
              <button
                type="button"
                onClick={() => renderAbortRef.current?.abort()}
                className="mt-1 rounded-xl border border-stone-300 px-4 py-2 text-xs
                           font-semibold text-brand-gray-500 hover:border-brand-green-100"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <>
              <div className="mx-auto flex w-full max-w-4xl items-center gap-2 pb-3">
                <span className="mr-auto text-sm font-semibold text-white">Planta render</span>
                <a
                  href={render.url}
                  download={`${(titulo || 'planta').replace(/[^\w-]+/g, '-')}-render.png`}
                  className="flex items-center gap-2 rounded-xl bg-brand-green-100 px-4 py-2
                             text-xs font-semibold text-black"
                >
                  <ArrowLineDown size={15} weight="bold" />
                  Descargar
                </a>
                <button
                  type="button"
                  onClick={() => { void generarRender(); }}
                  className="flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-xs
                             font-semibold text-brand-gray-500"
                  title="Generar otra con la misma referencia: el motor no repite dos veces lo mismo"
                >
                  <ArrowClockwise size={15} weight="bold" />
                  Otra
                </button>
                {/* Volver a elegir referencia sin perder la lámina que ya está
                    en pantalla: comparar dos estilos es el uso real. */}
                <button
                  type="button"
                  onClick={() => setRender('eligiendo')}
                  className="flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-xs
                             font-semibold text-brand-gray-500"
                  title="Cambiar la referencia de estilo"
                >
                  <Cube size={15} weight="bold" />
                  Estilo
                </button>
                <button
                  type="button"
                  onClick={() => setRender(null)}
                  className="rounded-xl bg-white p-2 text-brand-gray-500"
                  aria-label="Cerrar"
                >
                  <X size={15} weight="bold" />
                </button>
              </div>
              <img
                src={render.url}
                alt="Planta renderizada"
                className="mx-auto min-h-0 flex-1 rounded-xl bg-white object-contain"
              />
              <p className="mx-auto mt-3 max-w-4xl text-center text-[11px] text-white/70">
                Ilustración de venta. La generó un modelo y puede no respetar al
                centímetro la planta: no sustituye al plano ni a las áreas medidas.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
